import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useSSE } from "../hooks/useSSE";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, TrendingDown, TrendingUp, ShoppingCart, Calendar, Users, Clock,
  Plus, Search, Edit, Trash2, Save, X, Check, ChevronDown,
  AlertTriangle, FileText, DollarSign, Building2, Filter,
  CreditCard, Receipt, ExternalLink, Banknote, Package, ToggleLeft, ToggleRight,
  HandCoins, Eye, ChevronRight, ChevronLeft, Loader2,
  CheckCircle, AlertCircle, Star, UserCheck, Pencil, Shield, Printer, Settings, Tag, RefreshCw, PackageCheck, Wallet
} from "lucide-react";
import EntregasPanel from "../components/EntregasPanel";
import RetencionPreview from "../components/RetencionPreview";
import ComisionesVentasPanel from "../components/ComisionesVentasPanel";
import CanalPagoGastoFields, { CANAL_PAGO_VACIO, canalPagoDe } from "../components/CanalPagoGastoFields";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MoneyInput from "../components/ui/MoneyInput";
import { AuthContext } from "../App";
import { toast } from "sonner";
import { svgDocumentHeaderLogoHtml } from "../lib/marcaLogoSvg";
import ChequeEgresoForm from "../components/ChequeEgresoForm";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import { EntidadModal } from "./CatastroPage";
import DateInput from "../components/ui/DateInput";
import useDepositos from "../hooks/useDepositos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, moneda = "PYG") => {
  if (n == null) return "−";
  if (moneda === "BRL") return `R$ ${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (moneda === "USD") return `USD ${Number(n).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `₲ ${Math.round(n).toLocaleString("es-PY")}`;
};

const fmtPYG = (n) => fmt(n, "PYG");

const ivaSaldoTexto = (monto) => {
  const n = Number(monto || 0);
  if (n > 0) return `${fmtPYG(n)} a pagar`;
  if (n < 0) return `${fmtPYG(Math.abs(n))} a favor`;
  return "₲ 0 al día";
};

const fmtMixedAmounts = (items, field = "monto") => {
  const pyg = items.reduce((s, x) => {
    const monto = Number(x[field] || 0);
    if ((x.moneda || "PYG") === "PYG") return s + monto;
    const tc = Number(x.tipo_cambio || 0);
    return s + (tc > 0 ? monto * tc : 0);
  }, 0);
  const usd = items.reduce((s, x) => {
    if ((x.moneda || "PYG") !== "USD" || Number(x.tipo_cambio || 0) > 0) return s;
    return s + Number(x[field] || 0);
  }, 0);
  const brl = items.reduce((s, x) => {
    if ((x.moneda || "PYG") !== "BRL" || Number(x.tipo_cambio || 0) > 0) return s;
    return s + Number(x[field] || 0);
  }, 0);
  return `${fmt(pyg)}${usd ? ` / USD ${usd.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}${brl ? ` / R$ ${brl.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}`;
};

const hoy = () => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
const mesActual = () => new Date().toISOString().slice(0, 7);
const prevDia = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() - 1); return dt.toISOString().slice(0, 10); };
const nextDia = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() + 1); return dt.toISOString().slice(0, 10); };
const diaLabel = (d) => { if (!d) return ""; return new Date(d + "T12:00:00").toLocaleDateString("es-PY", { weekday: "short", day: "numeric", month: "short" }); };

/** Extrae un mensaje legible de una respuesta de error de FastAPI/Pydantic.
 *  Si e.detail es un array (error 422 de validación), muestra "campo: mensaje" por cada error.
 *  Si es string, lo devuelve directamente. */
async function parseFetchError(res, fallback = "Error al guardar") {
  try {
    const e = await res.json();
    if (typeof e.detail === "string") return e.detail;
    if (Array.isArray(e.detail)) {
      return e.detail.map(err => {
        const field = err.loc ? err.loc[err.loc.length - 1] : "campo";
        return `${field}: ${err.msg}`;
      }).join(" | ");
    }
    if (e.detail) return JSON.stringify(e.detail);
    if (e.message) return e.message;
  } catch {}
  return fallback;
}

function prevMes(m) {
  const [y, mo] = m.split("-").map(Number);
  if (mo === 1) return `${y - 1}-12`;
  return `${y}-${String(mo - 1).padStart(2, "0")}`;
}
function nextMes(m) {
  const [y, mo] = m.split("-").map(Number);
  if (mo === 12) return `${y + 1}-01`;
  return `${y}-${String(mo + 1).padStart(2, "0")}`;
}
function mesLabel(m) {
  const [y, mo] = m.split("-");
  const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${meses[parseInt(mo, 10) - 1]} ${y}`;
}

const productoVarianteSearchText = (prod = {}, variante = null) => {
  const detalleVariante = Array.isArray(variante?.atributos_detalle)
    ? variante.atributos_detalle.flatMap(d => [
        d.atributo_nombre,
        d.opcion_nombre,
        `${d.atributo_nombre || ""} ${d.opcion_nombre || ""}`,
        `${d.atributo_nombre || ""}: ${d.opcion_nombre || ""}`,
      ])
    : [];
  return [
    prod.nombre,
    prod.sku,
    prod.codigo_barras,
    prod.codigo,
    prod.codigo_fabricante,
    prod.descripcion,
    variante?.atributos_texto,
    ...detalleVariante,
  ].filter(Boolean).join(" ").toLowerCase();
};

// ─── Buscador de proveedores con creación inline ──────────────────────────────
const ProveedorSearchSelect = ({ proveedores, value, onChange, onProveedorCreado, token, logo }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [proveedorModalNombre, setProveedorModalNombre] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return proveedores.slice(0, 12);
    const q = query.toLowerCase();
    return proveedores.filter(p =>
      (p.nombre || "").toLowerCase().includes(q) ||
      (p.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [proveedores, query]);

  const exactMatch = proveedores.some(p => (p.nombre || "").toLowerCase() === query.trim().toLowerCase());

  const handleSelect = (prov) => {
    onChange({ id: prov.id, nombre: prov.nombre, ruc: prov.ruc || "" });
    setQuery(""); setOpen(false); setProveedorModalNombre("");
  };

  // Proveedor ya seleccionado → mostrar chip con botón limpiar
  if (value?.id) {
    return (
      <div className="flex items-center gap-2 bg-arandu-dark border border-orange-500/30 rounded-lg px-3 py-2 min-h-[38px]">
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm font-medium truncate block">{value.nombre}</span>
          {value.ruc && <span className="text-slate-400 text-xs">RUC: {value.ruc}</span>}
        </div>
        <button type="button" onClick={() => onChange(null)} className="text-slate-500 hover:text-white shrink-0 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar proveedor por nombre o RUC..."
          className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50 placeholder:text-slate-500"
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-arandu-dark-light border border-white/10 rounded-lg shadow-xl overflow-hidden">
          {filtered.length > 0 && (
            <ul className="max-h-48 overflow-y-auto divide-y divide-white/3">
              {filtered.map(p => (
                <li key={p.id} onMouseDown={() => handleSelect(p)}
                  className="px-3 py-2.5 hover:bg-white/5 cursor-pointer flex items-center justify-between">
                  <span className="text-white text-sm">{p.nombre}</span>
                  {p.ruc && <span className="text-slate-500 text-xs ml-2 shrink-0">RUC: {p.ruc}</span>}
                </li>
              ))}
            </ul>
          )}
          {filtered.length === 0 && query.trim() && (
            <div className="px-3 py-2 text-slate-500 text-xs">Sin resultados para "{query.trim()}"</div>
          )}
          {query.trim() && !exactMatch && (
            <div className={filtered.length > 0 ? "border-t border-white/10" : ""}>
              <button type="button" onMouseDown={() => setProveedorModalNombre(query.trim())}
                className="w-full px-3 py-2.5 text-left text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors">
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Crear "<strong>{query.trim()}</strong>" como nuevo proveedor
              </button>
            </div>
          )}
        </div>
      )}
      {proveedorModalNombre !== "" && (
        <EntidadModal
          entidad={{
            _presetRol: "es_proveedor",
            _initialNombre: proveedorModalNombre,
            _initialRazonSocial: proveedorModalNombre,
          }}
          logo={logo}
          token={token}
          allowedRoles={{ es_cliente: false, es_proveedor: true, es_empleado: false }}
          onClose={() => setProveedorModalNombre("")}
          onSaved={nuevo => {
            if (nuevo?.id) {
              onProveedorCreado?.(nuevo);
              handleSelect(nuevo);
            } else {
              setProveedorModalNombre("");
            }
          }}
        />
      )}
    </div>
  );
};

// ─── Estado inicial del form de compra ────────────────────────────────────────
const emptyCompra = () => ({
  logo_tipo: "arandujar",
  proveedor_id: "",
  proveedor_nombre: "",
  proveedor_ruc: "",
  crear_proveedor: false,
  fecha: hoy(),
  tipo_pago: "contado",
  tiene_factura: false,
  factura_electronica: false,
  numero_factura: "",
  nro_timbrado: "",
  fecha_vigencia_timbrado: "",
  tipo_comprobante: "normal",
  solo_iva: false,
  monto_total: "",
  moneda: "PYG",
  tipo_cambio: "",
  descuento_modo: "none",
  descuento_global_tipo: "monto",
  descuento_global_monto: "",
  descuento_global_pct: "",
  cuenta_id: "",
  cuenta_nombre: "",
  plan_cuenta_id: "",
  plan_cuenta_nombre: "",
  es_credito_cuotas: false,
  num_cuotas: 3,
  fecha_primer_vencimiento: "",
  tasa_interes_cuotas: 0,
  // Si está activo, el monto y vencimiento de cada cuota se cargan a mano
  // (cuotas_manual) en vez de dividir el total parejo entre num_cuotas.
  cuotas_manual_activo: false,
  cuotas_manual: [],  // [{numero, monto, fecha_vencimiento}]
  items: [],
  afecta_stock: true,
  entrega_posterior: false,
  notas: "",
  fecha_vencimiento: "",
  fecha_pago: "",
  presupuesto_ids: [],
  anticipos_aplicados: [],  // [{anticipo_id, monto_aplicado, tipo_cambio_compra}]
});

const emptyItem = () => ({
  descripcion: "",
  cantidad: 1,
  precio_unitario: "",
  precio_venta: "",  // opcional — fija el Precio de Venta en el catálogo (compras.editar_precio_venta)
  subtotal: 0,
  iva: 10,          // % IVA del ítem (0, 5, 10)
  producto_id: "",
  producto_sku: "",
  variante_id: "",
  variante_texto: "",
});

// IVA de un ítem a partir de su subtotal (precio incluye IVA)
const calcItemIva = (it, moneda = "PYG") => {
  const sub = parseFloat(it.subtotal) || 0;
  const tasa = Number(it.iva ?? 10);
  const raw = tasa === 10 ? sub / 11 : tasa === 5 ? sub / 21 : 0;
  return moneda !== "PYG" ? Math.round(raw * 100) / 100 : Math.round(raw);
};

const roundCompra = (n, moneda = "PYG") => {
  const value = Number(n || 0);
  return moneda === "PYG" ? Math.round(value) : Math.round(value * 100) / 100;
};

const compraSubtotalBruto = (items = []) => (
  items.reduce((s, it) => s + ((parseFloat(it.cantidad) || 0) * (parseFloat(it.precio_unitario) || 0)), 0)
);

// Descuento propio de cada ítem: tipo "pct" (valor = %) o "monto" (valor = importe del renglón)
const compraDescuentoItem = (it, moneda = "PYG") => {
  const bruto = (parseFloat(it.cantidad) || 0) * (parseFloat(it.precio_unitario) || 0);
  const valor = Math.max(0, Number(it.descuento_item_valor || 0));
  if (valor <= 0 || bruto <= 0) return 0;
  const raw = (it.descuento_item_tipo || "pct") === "pct" ? bruto * Math.min(valor, 100) / 100 : valor;
  return Math.min(bruto, Math.max(0, roundCompra(raw, moneda)));
};

const compraTotalDescuentosItems = (form) => (form.items || []).reduce((s, it) => s + compraDescuentoItem(it, form.moneda || "PYG"), 0);

const compraDescuentoGlobal = (form) => {
  const subtotal = compraSubtotalBruto(form.items || []) - compraTotalDescuentosItems(form);
  if ((form.descuento_modo || "none") !== "global" || subtotal <= 0) return 0;
  const tipo = form.descuento_global_tipo || "monto";
  const raw = tipo === "pct"
    ? subtotal * Math.max(0, Number(form.descuento_global_pct || 0)) / 100
    : Number(form.descuento_global_monto || 0);
  return Math.min(subtotal, Math.max(0, roundCompra(raw, form.moneda || "PYG")));
};

const compraItemsConDescuentoGlobal = (form) => {
  const items = form.items || [];
  const subtotal = compraSubtotalBruto(items) - compraTotalDescuentosItems(form);
  const descuento = compraDescuentoGlobal(form);
  let descuentoAsignado = 0;
  return items.map((it, idx) => {
    const cantidad = Number(it.cantidad) || 1;
    const precioUnitario = parseFloat(it.precio_unitario) || 0;
    const bruto = cantidad * precioUnitario;
    const descItem = compraDescuentoItem(it, form.moneda || "PYG");
    const baseItem = bruto - descItem;
    const prorrateado = idx === items.length - 1
      ? descuento - descuentoAsignado
      : roundCompra(subtotal > 0 ? descuento * (baseItem / subtotal) : 0, form.moneda || "PYG");
    descuentoAsignado += prorrateado;
    const neto = Math.max(0, roundCompra(baseItem - prorrateado, form.moneda || "PYG"));
    return {
      ...it,
      cantidad,
      precio_unitario: precioUnitario,
      precio_unitario_bruto: precioUnitario,
      precio_unitario_neto: cantidad > 0 ? roundCompra(neto / cantidad, form.moneda || "PYG") : 0,
      subtotal_bruto: roundCompra(bruto, form.moneda || "PYG"),
      descuento_item_tipo: it.descuento_item_tipo || "pct",
      descuento_item_valor: Number(it.descuento_item_valor || 0),
      descuento_item_monto: descItem,
      descuento_global_prorrateado: roundCompra(prorrateado, form.moneda || "PYG"),
      subtotal: neto,
      subtotal_neto: neto,
      iva: Number(it.iva ?? 10),
      precio_venta: it.precio_venta !== "" && it.precio_venta != null ? Number(it.precio_venta) || null : null,
    };
  });
};

// ─── Colores de estado ────────────────────────────────────────────────────────
const ESTADO_STYLES = {
  pagado:   "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  parcial:  "bg-amber-500/15 text-amber-400 border-amber-500/30",
  pendiente:"bg-blue-500/15 text-blue-400 border-blue-500/30",
  vencido:  "bg-red-500/15 text-red-400 border-red-500/30",
  registrado:"bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
};

const ESTADO_LABELS = {
  pagado: "Pagado", parcial: "Parcial", pendiente: "Pendiente", vencido: "Vencido", registrado: "Solo IVA",
};

// ─── Componente búsqueda con chips (Enter para agregar filtro) ───────────────
function ChipSearch({ chips, setChips, inputVal, setInputVal, placeholder, accentColor = "orange", actionButton = null }) {
  const addChip = () => {
    const term = inputVal.trim().replace(/,$/, "");
    if (term && !chips.includes(term)) setChips(prev => [...prev, term]);
    setInputVal("");
  };
  return (
    <div className="space-y-2 flex-1">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {chips.map((chip, idx) => (
            <span key={idx} className={`flex items-center gap-1.5 bg-${accentColor}-500/20 text-${accentColor}-300 border border-${accentColor}-500/40 rounded-full px-3 py-1 text-xs`}>
              <Search className="w-3 h-3" />
              {chip}
              <button onClick={() => setChips(prev => prev.filter((_, i) => i !== idx))} className="ml-1 hover:text-white transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button onClick={() => setChips([])} className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded-full border border-white/10 hover:border-white/20 transition-all">
            Limpiar
          </button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => {
              if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) { e.preventDefault(); addChip(); }
              if (e.key === "Backspace" && inputVal === "" && chips.length > 0) setChips(prev => prev.slice(0, -1));
            }}
            placeholder={placeholder}
            className={`w-full bg-arandu-dark-light border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-${accentColor}-500/60 placeholder-slate-500`}
          />
        </div>
        {actionButton}
      </div>
    </div>
  );
}

// Helper para filtrar con chips (AND lógico — todos los chips deben matchear)
function matchChips(chips, inputVal, texto) {
  const allChips = [...chips, ...(inputVal.trim() ? [inputVal.trim()] : [])];
  if (allChips.length === 0) return true;
  const t = texto.toLowerCase();
  return allChips.every(chip => t.includes(chip.toLowerCase()));
}


// ── Sueldos helpers (defined outside component to avoid re-creation) ──────────
// Valor actualizable vía endpoint /admin/empleados/config-sueldo-minimo
let SUELDO_MINIMO = 2899048;
const LOGO_LABEL_EMP = {
  arandujar: { label: "Arandu&JAR", chip: "bg-blue-600/20 text-blue-300 border border-blue-600/30" },
  arandu:    { label: "Arandu",     chip: "bg-emerald-600/20 text-emerald-300 border border-emerald-600/30" },
  jar:       { label: "JAR",        chip: "bg-red-600/20 text-red-300 border border-red-600/30" },
};
function calcularIPS(emp) {
  if (!emp.aplica_ips) return 0;
  if (emp.base_calculo_ips === "manual") return Math.round((parseFloat(emp.ips_monto_manual)||0)*0.09);
  const base = emp.base_calculo_ips === "sueldo" ? (parseFloat(emp.sueldo_base)||0) : (parseFloat(emp.sueldo_minimo_vigente)||SUELDO_MINIMO);
  return Math.round(base*0.09);
}
function calcMontoAPagar(emp, adelantos, extras, descuentos) {
  const base = parseFloat(emp.sueldo_base)||0;
  const ips = emp.aplica_ips !== false ? calcularIPS(emp) : 0;
  const totAd = (adelantos||[]).reduce((s,a)=>s+(a.monto||0),0);
  const totEx = (extras||[]).reduce((s,e)=>s+(e.monto||0),0);
  const totDe = (descuentos||[]).reduce((s,d)=>s+(parseFloat(d.monto)||0),0);
  return Math.max(0, base+totEx-ips-totAd-totDe);
}
function fmtSueldo(monto, moneda="PYG") {
  if (!monto && monto!==0) return "-";
  if (moneda==="PYG") return `\u20b2 ${Number(monto).toLocaleString("es-PY")}`;
  return `${moneda} ${Number(monto).toLocaleString("es-PY",{minimumFractionDigits:2,maximumFractionDigits:3})}`;
}
function sueldoBaseSnapshot(emp, sueldo) {
  if (sueldo?.sueldo_base != null) return parseFloat(sueldo.sueldo_base) || 0;
  return (parseFloat(sueldo?.monto_pagado) || 0)
    + (parseFloat(sueldo?.descuento_ips) || 0)
    + (parseFloat(sueldo?.descuentos_adicionales) || 0)
    + (parseFloat(sueldo?.total_adelantos) || 0)
    - (parseFloat(sueldo?.total_extras) || 0)
    || (parseFloat(emp?.sueldo_base) || 0);
}
function fechaPy(fecha) {
  if (!fecha) return "";
  const [y, m, d] = String(fecha).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : fecha;
}
function periodoPagoTexto(periodo) {
  if (!periodo || !String(periodo).includes("-")) return periodo || "";
  const [y, m] = String(periodo).split("-").map(Number);
  const ultimo = new Date(y, m, 0).getDate();
  return `Del 01/${String(m).padStart(2, "0")}/${y} al ${String(ultimo).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}
function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
function reciboSueldoHtml({ emp, sueldo, adelantos = [], extras = [], empresa = null, periodo }) {
  const moneda = sueldo?.moneda || emp?.moneda_sueldo || emp?.moneda || "PYG";
  const logoTipo = emp?.logo_tipo || empresa?.slug || "arandujar";
  const empresaNombre = empresa?.razon_social || empresa?.nombre || LOGO_LABEL_EMP[logoTipo]?.label || "Empresa";
  const empleadoNombre = [emp?.nombre, emp?.apellido].filter(Boolean).join(" ") || sueldo?.empleado_nombre || "";
  const base = sueldoBaseSnapshot(emp, sueldo);
  const ips = parseFloat(sueldo?.descuento_ips) || 0;
  const totAd = adelantos.reduce((s, a) => s + (parseFloat(a.monto) || 0), 0) || parseFloat(sueldo?.total_adelantos) || 0;
  const totEx = extras.reduce((s, e) => s + (parseFloat(e.monto) || 0), 0) || parseFloat(sueldo?.total_extras) || 0;
  const desAd = parseFloat(sueldo?.descuentos_adicionales) || 0;
  const ingresos = base + totEx;
  const egresos = ips + totAd + desAd;
  const neto = parseFloat(sueldo?.monto_pagado) || Math.max(0, ingresos - egresos);
  const rows = [
    { concepto: "001-SALARIO MENSUAL", ingreso: base, egreso: null, monedaIngreso: moneda },
    ...(extras.length > 0
      ? extras.map((e, i) => ({
          concepto: `010.${String(i + 1).padStart(2, "0")}-${String(e.descripcion || "Extra / bonificación").toUpperCase()}${e.fecha ? ` (${fechaPy(e.fecha)})` : ""}`,
          ingreso: parseFloat(e.monto) || 0, egreso: null, monedaIngreso: e.moneda || moneda,
        }))
      : (totEx > 0 ? [{ concepto: "010-EXTRAS Y BONIFICACIONES", ingreso: totEx, egreso: null, monedaIngreso: moneda }] : [])),
    ...(ips > 0 ? [{ concepto: "016-IPS - Empleado", ingreso: null, egreso: ips, monedaEgreso: "PYG" }] : []),
    ...(totAd > 0 ? [{ concepto: `030-ADELANTOS DESCONTADOS${adelantos.length ? ` (${adelantos.length})` : ""}`, ingreso: null, egreso: totAd, monedaEgreso: moneda }] : []),
    ...(desAd > 0 ? [{ concepto: "040-DESCUENTOS ADICIONALES", ingreso: null, egreso: desAd, monedaEgreso: "PYG" }] : []),
  ];
  const docLogo = svgDocumentHeaderLogoHtml(logoTipo, empresa?.logo_url || null);
  const infoLine = [empresa?.direccion, empresa?.telefono, empresa?.email].filter(Boolean).join(" · ");
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Recibo de salario - ${escapeHtml(empleadoNombre)}</title>
  <style>
    @page { size: A4; margin: 1.2cm; }
    * { box-sizing: border-box; }
    body { margin: 0; background: #f3f4f6; color: #111827; font-family: Arial, Helvetica, sans-serif; }
    .page { width: 100%; min-height: 100vh; background: white; padding: 26px 30px; }
    .top { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #111827; padding-bottom: 14px; margin-bottom: 18px; }
    .brand { display: flex; align-items: center; gap: 18px; min-width: 0; }
    .brand .logo { flex: 0 0 auto; display: flex; align-items: center; justify-content: flex-start; overflow: visible; }
    .brand .logo img { max-height: 60px; max-width: 190px; object-fit: contain; }
    .brand h1 { margin: 0; font-size: 17px; letter-spacing: 0; text-transform: uppercase; }
    .brand p, .meta p { margin: 3px 0; font-size: 11px; color: #4b5563; }
    .meta { text-align: right; min-width: 190px; }
    .title { text-align: center; margin: 20px 0 22px; }
    .title h2 { margin: 0; font-size: 16px; letter-spacing: 0; text-transform: uppercase; }
    .title p { margin: 5px 0 0; color: #6b7280; font-size: 11px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 36px; margin-bottom: 18px; }
    .field { display: grid; grid-template-columns: 155px 1fr; gap: 8px; font-size: 12px; line-height: 1.35; }
    .label { font-weight: 700; color: #111827; }
    .value { color: #374151; border-bottom: 1px dotted #cbd5e1; min-height: 18px; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
    th, td { border: 1px solid #111827; padding: 9px 10px; }
    th { background: #f8fafc; text-transform: uppercase; font-size: 11px; letter-spacing: 0; }
    td.amount, th.amount { text-align: right; width: 21%; white-space: nowrap; }
    .subtotal td { font-weight: 700; background: #f8fafc; }
    .neto td { font-weight: 800; font-size: 13px; background: #eef2ff; }
    .note { margin-top: 12px; font-size: 11px; color: #4b5563; }
    .payment { margin-top: 14px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; font-size: 12px; }
    .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 70px; margin-top: 72px; text-align: center; font-size: 12px; }
    .line { border-top: 1px solid #111827; padding-top: 7px; }
    .muted { color: #6b7280; font-size: 11px; }
    @media print { body { background: white; } .page { padding: 0; min-height: auto; } }
  </style>
</head>
<body>
  <main class="page">
    <section class="top">
      <div class="brand">
        <div class="logo">${docLogo}</div>
        <div>
          <h1>${escapeHtml(empresaNombre)}</h1>
          <p>${empresa?.ruc ? `RUC: ${escapeHtml(empresa.ruc)}` : ""}</p>
          <p>${escapeHtml(infoLine)}</p>
        </div>
      </div>
      <div class="meta">
        <p><strong>Fecha de pago:</strong> ${escapeHtml(fechaPy(sueldo?.fecha_pago))}</p>
        <p><strong>Periodo:</strong> ${escapeHtml(mesLabel(periodo || sueldo?.periodo || ""))}</p>
        <p><strong>Moneda:</strong> ${escapeHtml(moneda)}</p>
      </div>
    </section>

    <section class="title">
      <h2>Recibo de salario</h2>
      <p>Art. 235 del Código de Trabajo</p>
    </section>

    <section class="grid">
      ${[
        ["Razón social:", empresaNombre],
        ["IPS Patronal:", empresa?.nro_patronal_ips],
        ["Empleado:", empleadoNombre],
        ["MTESS Patronal:", empresa?.nro_patronal_mtess],
        ["Periodo del pago:", periodoPagoTexto(periodo || sueldo?.periodo)],
        ["Cargo:", emp?.cargo],
        ["Salario básico:", fmtSueldo(base, moneda)],
        ["C.I. N°:", emp?.ci || emp?.documento || emp?.ruc],
      ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "")
       .map(([l, v]) => `<div class="field"><span class="label">${escapeHtml(l)}</span><span class="value">${escapeHtml(v)}</span></div>`).join("")}
    </section>

    <table>
      <thead>
        <tr><th>Concepto</th><th class="amount">Ingresos</th><th class="amount">Egresos</th></tr>
      </thead>
      <tbody>
        ${rows.map(r => `<tr><td>${escapeHtml(r.concepto)}</td><td class="amount">${r.ingreso != null ? escapeHtml(fmtSueldo(r.ingreso, r.monedaIngreso || moneda)) : ""}</td><td class="amount">${r.egreso != null ? escapeHtml(fmtSueldo(r.egreso, r.monedaEgreso || moneda)) : ""}</td></tr>`).join("")}
        <tr class="subtotal"><td>SUB-TOTALES</td><td class="amount">${escapeHtml(fmtSueldo(ingresos, moneda))}</td><td class="amount">${escapeHtml(fmtSueldo(egresos, moneda))}</td></tr>
        <tr class="neto"><td colspan="2">NETO A PERCIBIR</td><td class="amount">${escapeHtml(fmtSueldo(neto, moneda))}</td></tr>
      </tbody>
    </table>

    <section class="payment">
      <div><strong>Fecha:</strong> ${escapeHtml(fechaPy(sueldo?.fecha_pago))}</div>
      <div><strong>Forma / cuenta:</strong> ${escapeHtml(sueldo?.cuenta_nombre || "Efectivo / caja")}</div>
    </section>
    ${sueldo?.notas ? `<p class="note"><strong>Notas:</strong> ${escapeHtml(sueldo.notas)}</p>` : ""}
    <p class="note">Recibí conforme el importe acreditado correspondiente al periodo indicado.</p>

    <section class="signatures">
      <div class="line">Firma Empleador<br><span class="muted">${escapeHtml(empresaNombre)}${empresa?.ruc ? ` · RUC ${escapeHtml(empresa.ruc)}` : ""}</span></div>
      <div class="line">Firma del Empleado<br><span class="muted">${escapeHtml(empleadoNombre)}${(emp?.ci || emp?.documento || emp?.ruc) ? ` · C.I. N° ${escapeHtml(emp?.ci || emp?.documento || emp?.ruc)}` : ""}</span></div>
    </section>
  </main>
</body>
</html>`;
}
function escribirReciboSueldo(w, data) {
  if (!w) return;
  w.document.open();
  w.document.write(reciboSueldoHtml(data));
  w.document.close();
  setTimeout(() => {
    w.focus();
    w.print();
  }, 450);
}
function abrirVentanaReciboSueldo() {
  const w = window.open("", "_blank");
  if (!w) {
    toast.error("El navegador bloqueó la ventana de impresión");
    return null;
  }
  w.document.open();
  w.document.write(`<!doctype html><html><head><meta charset="utf-8" /><title>Preparando recibo...</title>
    <style>
      body{margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:Arial,Helvetica,sans-serif;background:#f8fafc;color:#334155}
      .box{border:1px solid #e2e8f0;border-radius:14px;background:#fff;padding:26px 30px;box-shadow:0 10px 30px rgba(15,23,42,.08);text-align:center}
      .title{font-weight:700;margin-bottom:6px}.muted{font-size:13px;color:#64748b}
    </style></head><body><div class="box"><div class="title">Preparando recibo de sueldo</div><div class="muted">Un momento...</div></div></body></html>`);
  w.document.close();
  return w;
}
function imprimirReciboSueldo(data, targetWindow = null) {
  const w = targetWindow || abrirVentanaReciboSueldo();
  if (!w) return;
  escribirReciboSueldo(w, data);
}
// ─── Impresión de una Compra (comprobante interno de compra) ─────────────────
// Sin logo: es la factura de un PROVEEDOR, no un documento propio. Se destaca el
// nombre del proveedor y se deja el nombre de nuestra empresa en chico.
function compraPrintHtml(c, empresaNombre, modelo = "a4") {
  const mon = c.moneda || "PYG";
  const m = (n) => {
    const v = Number(n || 0);
    if (mon === "BRL") return `R$ ${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (mon === "USD") return `USD ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `₲ ${Math.round(v).toLocaleString("es-PY")}`;
  };
  const e = escapeHtml;
  const items = c.items || [];
  const descuentoGlobalDoc = Number(c.total_descuentos || c.descuento_global_monto || 0);
  const descuentoItemsDoc = Number(c.total_descuentos_items || 0);
  const descuento = descuentoGlobalDoc + descuentoItemsDoc;
  const creditos = (c.creditos || []).reduce((a, n) => a + Number(n.monto || 0), 0);
  const pagado = Number(c.total_pagado || 0);
  const pie = `<div class="pie">Este documento fue generado por Sistema AranduJAR-ERP</div>`;
  const ticket = modelo === "ticket";
  const filas = items.map(it => ticket
    ? `<tr><td colspan="3" class="d">${e(it.descripcion || "")}${it.variante_texto ? ` (${e(it.variante_texto)})` : ""}</td></tr>
       <tr><td>${e(it.cantidad)} x ${m(it.precio_unitario)}</td><td></td><td class="r">${m(it.subtotal != null ? it.subtotal : Number(it.cantidad || 0) * Number(it.precio_unitario || 0))}</td></tr>`
    : `<tr><td>${e(it.producto_sku || "")}</td><td>${e(it.descripcion || "")}${it.variante_texto ? `<br><span class="mu">Variante: ${e(it.variante_texto)}</span>` : ""}</td><td class="r">${e(it.cantidad)}</td><td class="r">${m(it.precio_unitario)}${Number(it.descuento_item_monto || 0) > 0 ? `<br><span class="mu">Desc. -${m(it.descuento_item_monto)}</span>` : ""}</td><td class="r">${it.iva != null ? e(it.iva) + "%" : "-"}</td><td class="r">${m(it.subtotal != null ? it.subtotal : Number(it.cantidad || 0) * Number(it.precio_unitario || 0))}</td></tr>`).join("");
  const totales = `
    ${descuento > 0 ? `<div class="tr"><span>Subtotal</span><span>${m(c.subtotal_bruto || 0)}</span></div>${descuentoItemsDoc > 0 ? `<div class="tr"><span>Descuentos por ítem</span><span>- ${m(descuentoItemsDoc)}</span></div>` : ""}${descuentoGlobalDoc > 0 ? `<div class="tr"><span>Descuento global</span><span>- ${m(descuentoGlobalDoc)}</span></div>` : ""}` : ""}
    ${Number(c.monto_iva || 0) > 0 ? `<div class="tr"><span>IVA incluido</span><span>${m(c.monto_iva)}</span></div>` : ""}
    ${creditos > 0 ? `<div class="tr"><span>Notas de crédito</span><span>- ${m(creditos)}</span></div>` : ""}
    <div class="tr tot"><span>TOTAL</span><span>${m(c.monto_total)}</span></div>
    ${pagado > 0 ? `<div class="tr"><span>Pagado</span><span>${m(pagado)}</span></div>` : ""}
    ${Number(c.saldo_pendiente || 0) > 0 ? `<div class="tr"><span>Pendiente</span><span>${m(c.saldo_pendiente)}</span></div>` : ""}`;
  const datos = [
    ["Fecha", c.fecha], ["N° factura", c.numero_factura], ["Timbrado", c.nro_timbrado],
    ["Condición", c.tipo_pago ? String(c.tipo_pago).charAt(0).toUpperCase() + String(c.tipo_pago).slice(1) : "Contado"],
    ...(mon !== "PYG" && c.tipo_cambio ? [["T. cambio", `₲ ${Number(c.tipo_cambio).toLocaleString("es-PY")}`]] : []),
  ].filter(([, v]) => v);

  if (ticket) {
    return `<!doctype html><html><head><meta charset="utf-8"><title>Compra</title><style>
      @page{size:80mm auto;margin:3mm}*{box-sizing:border-box}
      body{font-family:'Courier New',monospace;font-size:11px;width:74mm;margin:0 auto;color:#000}
      h1{font-size:14px;text-align:center;margin:0 0 2px}.c{text-align:center}.mu{font-size:9px;color:#444}
      hr{border:0;border-top:1px dashed #000;margin:5px 0}table{width:100%;border-collapse:collapse}td{padding:1px 0;vertical-align:top}
      .r{text-align:right}.d{font-weight:bold}.tr{display:flex;justify-content:space-between}.tot{font-weight:bold;font-size:13px;border-top:1px dashed #000;margin-top:3px;padding-top:3px}
      .pie{margin-top:8px;text-align:center;font-size:8px;color:#555}
    </style></head><body>
      <h1>${e(c.proveedor_nombre || "Proveedor")}</h1>
      ${c.proveedor_ruc ? `<div class="c">RUC: ${e(c.proveedor_ruc)}</div>` : ""}
      <div class="c mu">Comprobante de compra · ${e(empresaNombre)}</div><hr>
      ${datos.map(([k, v]) => `<div class="tr"><span>${e(k)}</span><span>${e(v)}</span></div>`).join("")}<hr>
      <table>${filas}</table><hr>${totales}${pie}
    </body></html>`;
  }
  return `<!doctype html><html><head><meta charset="utf-8"><title>Compra ${e(c.numero_factura || "")}</title><style>
    @page{size:A4;margin:14mm}*{box-sizing:border-box}
    body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#0f172a;margin:0}
    .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:12px}
    h1{font-size:22px;margin:0}.mu{color:#64748b;font-size:11px}
    .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px}
    .box{border:1px solid #e2e8f0;border-radius:6px;padding:6px 8px}.box b{display:block;font-size:10px;color:#64748b;text-transform:uppercase;margin-bottom:2px}
    table{width:100%;border-collapse:collapse}th{background:#f1f5f9;text-align:left;font-size:10px;text-transform:uppercase;padding:6px;border:1px solid #e2e8f0}
    td{padding:6px;border:1px solid #e2e8f0;vertical-align:top}.r{text-align:right}
    .tot-box{margin-left:auto;width:260px;margin-top:12px}.tr{display:flex;justify-content:space-between;padding:3px 0}
    .tot{font-weight:bold;font-size:15px;border-top:2px solid #0f172a;margin-top:4px;padding-top:6px}
    .pie{margin-top:26px;padding-top:8px;border-top:1px solid #e2e8f0;text-align:center;font-size:9px;color:#94a3b8}
  </style></head><body>
    <div class="top"><div><div class="mu">COMPRA · PROVEEDOR</div><h1>${e(c.proveedor_nombre || "Proveedor")}</h1>${c.proveedor_ruc ? `<div class="mu">RUC: ${e(c.proveedor_ruc)}</div>` : ""}</div>
      <div style="text-align:right"><div class="mu">Registrada en</div><b>${e(empresaNombre)}</b></div></div>
    <div class="grid">${datos.map(([k, v]) => `<div class="box"><b>${e(k)}</b>${e(v)}</div>`).join("")}</div>
    <table><thead><tr><th>SKU</th><th>Descripción</th><th class="r">Cant.</th><th class="r">Precio</th><th class="r">IVA</th><th class="r">Subtotal</th></tr></thead><tbody>${filas || `<tr><td colspan="6" class="mu">Sin ítems detallados</td></tr>`}</tbody></table>
    <div class="tot-box">${totales}</div>${pie}
  </body></html>`;
}
function imprimirCompra(c, empresaNombre, modelo) {
  const w = window.open("", "_blank");
  if (!w) { toast.error("El navegador bloqueó la ventana de impresión"); return; }
  w.document.open();
  w.document.write(compraPrintHtml(c, empresaNombre, modelo));
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 450);
}
function estadoBadgeSueldo(estado) {
  if (estado==="pagado") return React.createElement("span",{className:"flex items-center gap-1 text-emerald-400 text-xs font-medium"},
    React.createElement(CheckCircle,{className:"w-3.5 h-3.5"}),"Pagado");
  if (estado==="vencido") return React.createElement("span",{className:"flex items-center gap-1 text-red-400 text-xs font-medium"},
    React.createElement(AlertCircle,{className:"w-3.5 h-3.5"}),"Vencido");
  return React.createElement("span",{className:"flex items-center gap-1 text-amber-400 text-xs font-medium"},
    React.createElement(Clock,{className:"w-3.5 h-3.5"}),"Pendiente");
}

function SueldosTab({ sueldosVenc, loadingSueldos,
  adelantosMap, extrasMap, sueldosChips, setSueldosChips, sueldosInput, setSueldosInput,
  filtroTipo, filtroMes, hasPermission,
  openSueldo, openAdelanto, openExtra, handleDeleteSueldo,
  openSueldoView, handlePrintSueldo }) {
  const sueldosSafe = Array.isArray(sueldosVenc) ? sueldosVenc : [];
  const adelantosSafe = adelantosMap || {};
  const extrasSafe = extrasMap || {};
  const canPagarSueldo = hasPermission("empleados.editar") || hasPermission("sueldos.pagar");
  const canDeshacerSueldo = hasPermission("empleados.editar") || hasPermission("sueldos.deshacer_pago");
  const canGestionarAdelantos = hasPermission("empleados.editar") || hasPermission("sueldos.gestionar_adelantos");
  const canGestionarExtras = hasPermission("empleados.editar") || hasPermission("sueldos.gestionar_extras");
  const filtradosSueldos = sueldosSafe.filter(e => {
    const texto = [e.nombre, e.apellido, e.cargo, String(e.sueldo_base||""), e.logo_tipo,
      LOGO_LABEL_EMP[e.logo_tipo]?.label||"", e.estado].filter(Boolean).join(" ");
    return matchChips(sueldosChips, sueldosInput, texto);
  });

  return (
    <div>
      {/* Sub-tabs + buscador + botón nuevo */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-1 bg-arandu-dark-light border border-white/5 rounded-xl p-1">
            <span className="px-4 py-1.5 rounded-lg text-xs font-medium bg-violet-600 text-white shadow">
              Sueldos del período
            </span>
          </div>
          <Link to="/sistema/catastro?tab=empleados"
            className="flex items-center gap-2 bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 text-violet-300 text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap">
            <ExternalLink className="w-4 h-4"/> Gestionar empleados en Catastro
          </Link>
        </div>
        <ChipSearch
          chips={sueldosChips} setChips={setSueldosChips}
          inputVal={sueldosInput} setInputVal={setSueldosInput}
          placeholder="Buscar por nombre, cargo, empresa, estado…"
          accentColor="purple"
        />
      </div>

      {filtroTipo === "todos" ? (
          <div className="text-center py-12 bg-arandu-dark-light border border-white/5 rounded-xl">
            <Users className="w-12 h-12 text-slate-700 mx-auto mb-2"/>
            <p className="text-slate-400">Seleccioná "Por mes" para ver los sueldos del período</p>
          </div>
        ) : (
          <>
            {loadingSueldos ? (
              <div className="text-center py-10 text-violet-400 animate-pulse">Cargando sueldos...</div>
            ) : filtradosSueldos.length === 0 ? (
              <div className="text-center py-12 bg-arandu-dark-light border border-white/5 rounded-xl">
                <Users className="w-12 h-12 text-slate-700 mx-auto mb-2"/>
                <p className="text-slate-400">Sin empleados para este período</p>
              </div>
            ) : (
              <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-slate-400 text-xs uppercase">
                      <th className="px-4 py-3 text-left">Empleado</th>
                      <th className="px-4 py-3 text-left">Empresa</th>
                      <th className="px-4 py-3 text-left">Cargo</th>
                      <th className="px-4 py-3 text-right">Sueldo base</th>
                      <th className="px-4 py-3 text-right">Pagado</th>
                      <th className="px-4 py-3 text-center">Estado</th>
                      <th className="px-4 py-3 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtradosSueldos.map(emp => (
                      <tr key={emp.id}
                        onClick={() => emp.estado==="pagado" && openSueldoView && openSueldoView(emp)}
                        className={`border-b border-white/5 transition-colors ${emp.estado==="vencido"?"bg-red-500/5 hover:bg-red-500/10":"hover:bg-white/[0.03]"} ${emp.estado==="pagado"?"cursor-pointer":""}`}>
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{emp.nombre} {emp.apellido}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {adelantosSafe[emp.id] && (
                              <span className="inline-flex items-center gap-1 bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs px-1.5 py-0.5 rounded-full">
                                <DollarSign className="w-3 h-3"/> Adelanto: {fmtSueldo(adelantosSafe[emp.id].reduce((s,a)=>s+(a.monto||0),0), adelantosSafe[emp.id][0]?.moneda||"PYG")}
                              </span>
                            )}
                            {extrasSafe[emp.id] && (
                              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-300 text-xs px-1.5 py-0.5 rounded-full">
                                <Star className="w-3 h-3"/> Extra: {fmtSueldo(extrasSafe[emp.id].reduce((s,e)=>s+(e.monto||0),0), extrasSafe[emp.id][0]?.moneda||"PYG")}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {emp.logo_tipo && LOGO_LABEL_EMP[emp.logo_tipo] && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${LOGO_LABEL_EMP[emp.logo_tipo].chip}`}>
                              {LOGO_LABEL_EMP[emp.logo_tipo].label}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{emp.cargo||"—"}</td>
                        <td className="px-4 py-3 text-right text-slate-300 text-xs">
                          {emp.estado === "pagado" && emp.sueldo_registrado
                            ? fmtSueldo(
                                emp.sueldo_registrado.sueldo_base != null
                                  ? emp.sueldo_registrado.sueldo_base
                                  // reconstruir para registros viejos sin snapshot
                                  : (emp.sueldo_registrado.monto_pagado || 0)
                                    + (emp.sueldo_registrado.descuento_ips || 0)
                                    + (emp.sueldo_registrado.descuentos_adicionales || 0)
                                    + (emp.sueldo_registrado.total_adelantos || 0)
                                    - (emp.sueldo_registrado.total_extras || 0),
                                emp.moneda_sueldo || emp.moneda || "PYG"
                              )
                            : fmtSueldo(emp.sueldo_base, emp.moneda_sueldo || emp.moneda || "PYG")
                          }
                        </td>
                        <td className="px-4 py-3 text-right text-xs">
                          {emp.estado==="pagado" && emp.sueldo_registrado?.monto_pagado != null
                            ? <span className="text-emerald-300 font-medium">{fmtSueldo(emp.sueldo_registrado.monto_pagado, emp.moneda_sueldo || emp.moneda || "PYG")}</span>
                            : <span className="text-slate-600">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">{estadoBadgeSueldo(emp.estado)}</td>
                        <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                          {emp.estado === "pagado" ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handlePrintSueldo?.(emp)}
                                className="text-slate-400 hover:text-violet-300 transition-colors"
                                title="Imprimir recibo de sueldo"
                              >
                                <Printer className="w-4 h-4"/>
                              </button>
                              <span className="text-slate-500 text-xs">{emp.sueldo_registrado?.fecha_pago||""}</span>
                              {canDeshacerSueldo && (
                                <button onClick={() => handleDeleteSueldo(emp.sueldo_registrado?.id)} className="text-slate-500 hover:text-red-400 transition-colors" title="Deshacer pago">
                                  <X className="w-4 h-4"/>
                                </button>
                              )}
                            </div>
                          ) : (canPagarSueldo || canGestionarAdelantos || canGestionarExtras) && (
                            <div className="flex flex-col items-center gap-1.5">
                              {canPagarSueldo && (
                                <button onClick={() => openSueldo(emp)} className="flex items-center gap-1.5 mx-auto bg-violet-600/20 hover:bg-violet-600/40 text-violet-300 text-xs px-3 py-1.5 rounded-lg transition-colors">
                                  <DollarSign className="w-3.5 h-3.5"/> Registrar pago
                                </button>
                              )}
                              <div className="flex gap-1.5">
                                {canGestionarAdelantos && (
                                  <button onClick={() => openAdelanto(emp)} className="flex items-center gap-1 bg-amber-600/20 hover:bg-amber-600/40 text-amber-300 text-xs px-2 py-1 rounded-lg transition-colors">
                                    <DollarSign className="w-3 h-3"/> Adelanto
                                  </button>
                                )}
                                {canGestionarExtras && (
                                  <button onClick={() => openExtra(emp)} className="flex items-center gap-1 bg-green-600/20 hover:bg-green-600/40 text-green-300 text-xs px-2 py-1 rounded-lg transition-colors">
                                    <Star className="w-3 h-3"/> Extra
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
    </div>
  );
}


// ─── localStorage cache (persiste entre sesiones) ────────────────
const LS_TTL = 24 * 60 * 60 * 1000;
const lsGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > LS_TTL) { localStorage.removeItem(key); return null; }
    return data;
  } catch { return null; }
};
const lsSet = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
};
const lsDel = (key) => { try { localStorage.removeItem(key); } catch {} };
const lsDelPrefix = (prefix) => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(prefix)) localStorage.removeItem(key);
    });
  } catch {}
};

// ─────────────────────────────────────────────────────────────────────────────
// Modo embebido (Constructora): la misma pantalla de Egresos, pero acotada a un
// proyecto de obra. `proyectoCtx` = { id, nombre, numero }, `initialNuevo` = "compra" | "gasto"
// abre directo el formulario, `lockTab` fija la pestaña (compras | costos).
const EgresosPage = ({ embedded = false, proyectoCtx = null, initialNuevo = null, lockTab = null, onEmbeddedClose = null } = {}) => {
  const { token, user, hasPermission, hasModule, activeEmpresaPropia } = useContext(AuthContext);
  const depos = useDepositos(token, activeEmpresaPropia?.slug);
  const [realSearchParams, realSetSearchParams] = useSearchParams();
  const embeddedParams = useMemo(() => new URLSearchParams(initialNuevo ? { nuevo: initialNuevo } : {}), [initialNuevo]);
  const searchParams = embedded ? embeddedParams : realSearchParams;
  const setSearchParams = embedded ? (() => {}) : realSetSearchParams;
  const proyectoId = embedded ? (proyectoCtx?.id || null) : null;
  const [gruposConstructora, setGruposConstructora] = useState([]);
  // Insumos del presupuesto de obra del proyecto (para cargar compras desde el presupuesto)
  const [insumosObra, setInsumosObra] = useState({ presupuesto: null, provisorio: false, lineas: [] });
  const [insumosSel, setInsumosSel] = useState({});   // { clave: true }
  const [insumosBusq, setInsumosBusq] = useState("");
  const [insumosAbierto, setInsumosAbierto] = useState(true);
  const cargarInsumosObra = async () => {
    if (!proyectoId) return;
    try {
      const r = await fetch(`${API}/admin/constructora/proyectos/${proyectoId}/insumos-presupuesto`, { headers: { Authorization: `Bearer ${token}` } });
      setInsumosObra(r.ok ? await r.json() : { presupuesto: null, provisorio: false, lineas: [] });
    } catch { setInsumosObra({ presupuesto: null, provisorio: false, lineas: [] }); }
    setInsumosSel({});
  };
  useEffect(() => {
    if (!proyectoId) return;
    fetch(`${API}/admin/constructora/grupos?solo_activos=true${activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setGruposConstructora).catch(() => {});
  }, [proyectoId]); // eslint-disable-line
  // Pesaje de Báscula (dirección "compra") que se está registrando como Compra
  // — el formulario se abre precargado con el proveedor y una referencia al
  // pesaje en las notas; al guardarse, se vincula de vuelta al pesaje.
  const [pesajeBasculaCompra, setPesajeBasculaCompra] = useState(null);
  const [ordenesTransporteCompra, setOrdenesTransporteCompra] = useState([]); // órdenes de carga que originan esta Compra
  const isAdmin = user?.role === "admin";
  // Compra ya registrada: proveedor y fecha solo los corrige un admin; la factura, si ya fue cargada, no se toca.
  const compraBloqueaProvFecha = (c) => !!c && !(user?.role === "admin" || user?.role === "super_admin");
  const compraFacturaCargada = (c) => !!c && !!(c.tiene_factura || c.numero_factura);
  const canViewDeleted = (perm) => isAdmin || user?.role === "gerente" || user?.role === "super_admin" || hasPermission(perm);
  const canEditTcDoc = isAdmin || user?.role === "gerente" || user?.role === "super_admin" || hasPermission("cotizaciones.editar_en_compras");
  const puedeUsarStockProductos = hasModule?.("productos_stock");
  const puedeCambiarAfectaStockCompra = isAdmin || hasPermission("compras.afectar_stock");
  const puedeEditarPrecioVentaCompra = puedeUsarStockProductos && (isAdmin || hasPermission("compras.editar_precio_venta"));
  const puedeCompraModoLibre = isAdmin || hasPermission("compras.modo_libre");
  const puedeDescuentosCompra = isAdmin || user?.role === "gerente" || user?.role === "super_admin" || hasPermission("compras.descuentos");
  const puedeUsarCheque = isAdmin || user?.role === "gerente" || user?.role === "super_admin" || (hasPermission("cheques.ver") && hasPermission("cheques.emitir"));

  const addCompraItemFromProduct = (prod, incrementIfExists = true, variante = null) => {
    if (!prod) return false;
    if (prod.variantes_habilitadas && !variante) {
      toast.error("Elegí la variante del producto");
      return false;
    }
    const ivaFromTipo = prod.iva_tipo === "exenta" ? 0 : prod.iva_tipo === "5" ? 5 : 10;
    const pu = variante?.precio_costo ?? prod.precio_costo ?? prod.precio_venta ?? 0;
    setCompraForm(p => {
      const items = [...(p.items || [])];
      if (incrementIfExists) {
        const idx = items.findIndex(it => String(it.producto_id) === String(prod.id) && String(it.variante_id || "") === String(variante?.id || ""));
        if (idx >= 0) {
          const cant = (parseFloat(items[idx].cantidad) || 0) + 1;
          const sub = cant * (parseFloat(items[idx].precio_unitario) || 0);
          items[idx] = { ...items[idx], cantidad: cant, subtotal: sub };
          return { ...p, items, afecta_stock: true };
        }
      }
      items.push({
        descripcion: prod.nombre,
        cantidad: 1,
        precio_unitario: pu,
        precio_venta: "",
        subtotal: pu,
        producto_id: prod.id,
        producto_sku: prod.sku || "",
        variante_id: variante?.id || "",
        variante_texto: variante?.atributos_texto || "",
        iva: ivaFromTipo,
        _precio_venta_actual: variante?.precio_venta ?? prod.precio_venta ?? 0,
      });
      return { ...p, items, afecta_stock: true };
    });
    return true;
  };

  const claveInsumo = (l) => `${l.ref.item_id}|${l.ref.subitem_id || ""}`;
  const agregarInsumosObra = (lineas) => {
    if (!lineas.length) return;
    setModoCompra("libre");
    setCompraForm(prev => {
      const items = (prev.items || []).filter(i => i.descripcion || i.producto_id);
      lineas.forEach(l => {
        const cant = Number(l.pendiente) > 0 ? Number(l.pendiente) : Number(l.cantidad_presup) || 1;
        const k = claveInsumo(l);
        const ex = items.findIndex(i => i.obra_ref && `${i.obra_ref.item_id}|${i.obra_ref.subitem_id || ""}` === k);
        if (ex >= 0) {
          const nueva = Number(items[ex].cantidad || 0) + cant;
          items[ex] = { ...items[ex], cantidad: nueva, subtotal: nueva * Number(items[ex].precio_unitario || 0) };
        } else {
          const pu = Number(l.costo_unitario) || 0;
          items.push({ ...emptyItem(), descripcion: l.descripcion || l.item || "", cantidad: cant, precio_unitario: pu, subtotal: cant * pu, iva: 10, obra_ref: l.ref });
        }
      });
      return { ...prev, items };
    });
    setInsumosSel({});
    toast.success(`${lineas.length} ítem(s) del presupuesto de obra agregados`);
  };

  // Carga en la compra los ítems de los presupuestos vinculados (solo lo aprobado que todavía no se compró)
  const cargarItemsPresupuestosCompra = async () => {
    const ids = compraForm.presupuesto_ids || [];
    if (ids.length === 0) { toast.error("Primero vinculá al menos un presupuesto"); return; }
    const nuevos = [];
    for (const pid of ids) {
      let pres = null;
      try {
        const r = await fetch(`${API}/admin/presupuestos/${pid}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) pres = await r.json();
      } catch {}
      if (!pres) continue;
      const parcial = (pres.aprobaciones || []).length > 0;
      (pres.items || []).forEach((it, idx) => {
        const cantTotal = Number(it.cantidad || 0);
        const cantidad = parcial ? Number(it.pendiente_comprar || 0) : Math.max(0, cantTotal - Number(it.cantidad_comprada || 0));
        if (!(cantidad > 0)) return;
        const precio = Number(it.precio_costo) > 0 ? Number(it.precio_costo) : Number(it.precio_unitario ?? it.precio ?? 0);
        nuevos.push({
          ...emptyItem(),
          descripcion: it.descripcion || "",
          cantidad,
          precio_unitario: precio,
          subtotal: cantidad * precio,
          iva: it.iva_tipo === "exenta" ? 0 : it.iva_tipo === "5" ? 5 : 10,
          producto_id: it.producto_id || "",
          producto_sku: it.producto_sku || "",
          variante_id: it.variante_id || "",
          variante_texto: it.variante_texto || "",
          presupuesto_item_ref: { presupuesto_id: pid, idx },
        });
      });
    }
    if (nuevos.length === 0) { toast.error("Los presupuestos vinculados no tienen ítems aprobados pendientes de comprar"); return; }
    setCompraForm(p => {
      const prevItems = (p.items || []).filter(i => i.descripcion || i.producto_id);
      const sinDuplicar = prevItems.filter(i => !i.presupuesto_item_ref || !nuevos.some(n => n.presupuesto_item_ref.presupuesto_id === i.presupuesto_item_ref.presupuesto_id && n.presupuesto_item_ref.idx === i.presupuesto_item_ref.idx));
      return { ...p, items: [...sinDuplicar, ...nuevos], afecta_stock: nuevos.some(n => n.producto_id) ? true : p.afecta_stock };
    });
    toast.success(`${nuevos.length} ítem(s) del presupuesto cargados en la compra`);
  };

  const handleCompraBarcodeSubmit = async () => {
    const code = (compraBarcodeInput || "").trim();
    if (!code) return;
    let prod = productos.find(p => (p.codigo_barras || "").trim() === code && p.activo !== false);
    if (!prod) {
      try {
        setCompraBarcodeLoading(true);
        const q = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
        const res = await fetch(`${API}/admin/productos/by-barcode/${encodeURIComponent(code)}${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Producto no encontrado");
        prod = await res.json();
      } catch (e) {
        toast.error(e.message || "No se encontró el producto");
        return;
      } finally {
        setCompraBarcodeLoading(false);
      }
    }
    const variantesActivas = (prod.variantes || []).filter(v => v.activo !== false);
    // Mismo código de barras con más de una variante activa (ej. mismo
    // modelo, distintos talles/colores): no podemos adivinar cuál es,
    // mostramos un selector rápido para que el usuario elija.
    if (prod.variantes_habilitadas && variantesActivas.length > 1) {
      setVarianteCompraScanPending({ prod, variantesActivas });
      setCompraBarcodeInput("");
      return;
    }
    const varianteMatch = prod.variantes_habilitadas && variantesActivas.length === 1 ? variantesActivas[0] : null;
    const agregado = addCompraItemFromProduct(prod, true, varianteMatch);
    if (agregado) toast.success(`${prod.nombre} agregado`);
    setCompraBarcodeInput("");
  };

  // Confirma la variante elegida en el selector rápido de escaneo de Compras.
  const handleVarianteCompraScanSelect = (variante) => {
    if (!varianteCompraScanPending) return;
    const { prod } = varianteCompraScanPending;
    const etiqueta = variante.atributos_texto || "variante";
    const agregado = addCompraItemFromProduct(prod, true, variante);
    if (agregado) toast.success(`${prod.nombre} (${etiqueta}) agregado`);
    setVarianteCompraScanPending(null);
  };

  // ── Tab activo ──────────────────────────────────────────────────────────────
  const [tab, setTab] = useState(() => {
    if (lockTab) return lockTab;
    try { const t = new URLSearchParams(window.location.search).get("tab"); if (t && ["anticipos","compras","configuracion","costos","pago-iva","recepcion","sueldos"].includes(t)) return t; } catch {}
    return "compras";
  });

  // ── Logos accesibles ────────────────────────────────────────────────────────
  const [empresasPropias, setEmpresasPropias] = useState([]);

  // ── Compras state ───────────────────────────────────────────────────────────
  const [compras, setCompras] = useState([]);
  const [comprasParaNota, setComprasParaNota] = useState([]); // todas las compras con factura (sin filtro período)
  const [proveedores, setProveedores] = useState([]);
  const [loadingCompras, setLoadingCompras] = useState(false);
  const [mostrarEliminadasCompras, setMostrarEliminadasCompras] = useState(false);
  const [mostrarEliminadasCostos, setMostrarEliminadasCostos] = useState(false);
  const [mostrarEliminadasIva, setMostrarEliminadasIva] = useState(false);
  const [filtroMes, setFiltroMes] = useState(mesActual());
  const [filtroTipo, setFiltroTipo] = useState(() => embedded ? "todos" : (localStorage.getItem("ard_egresos_filtro_tipo") || "dia")); // "dia" | "todos" | "mes" | "anio"
  const [filtroAnio, setFiltroAnio] = useState(String(new Date().getFullYear()));
  const [filtroDia, setFiltroDia] = useState(hoy);
  const setFiltroTipoP = (v) => { setFiltroTipo(v); localStorage.setItem("ard_egresos_filtro_tipo", v); };
  const [showCompraForm, setShowCompraForm] = useState(false);
  const [editingCompra, setEditingCompra] = useState(null);
  const [facturaCompraTarget, setFacturaCompraTarget] = useState(null);
  const [facturaCompraForm, setFacturaCompraForm] = useState({ factura_electronica: false, numero_factura: "", nro_timbrado: "", fecha_vigencia_timbrado: "" });
  const [savingFacturaCompra, setSavingFacturaCompra] = useState(false);
  const [compraForm, setCompraForm] = useState(emptyCompra());
  const [modoCompra, setModoCompra] = useState("catalogo"); // "catalogo" | "libre"
  const [presupuestosForm, setPresupuestosForm] = useState([]); // para el selector en el form de compra
  const [presupuestosDisp, setPresupuestosDisp] = useState([]); // presupuestos del proveedor con mercadería pendiente de comprar
  const [vincConfirm, setVincConfirm] = useState(null); // { pres, modo: "form"|"view", compraItems }
  const [vincMap, setVincMap] = useState({}); // { idxItemCompra: idxItemPresupuesto | "" }
  const [presDetailEgresos, setPresDetailEgresos] = useState(null); // presupuesto vinculado a mostrar inline
  const [presupuestoSearch, setPresupuestoSearch] = useState(""); // búsqueda en el selector de presupuesto del form
  const [showVincularPresModal, setShowVincularPresModal] = useState(false); // selector post-creación en detalle compra
  const [vincularPresSearch, setVincularPresSearch] = useState(""); // búsqueda en el selector post-creación
  const [vincularPresLoading, setVincularPresLoading] = useState(false);
  const [showPagoModal, setShowPagoModal] = useState(null); // compra object
  const [pagoMedioCompra, setPagoMedioCompra] = useState("transferencia"); // "transferencia" | "cheque"
  const [pagoForm, setPagoForm] = useState({ monto_pagado: "", fecha_pago: hoy(), cuenta_id: "", cuenta_nombre: "", cuenta_moneda: "PYG", tipo_cambio: "", notas: "", sin_retencion: false });
  const [productos, setProductos] = useState([]);
  const [showProductoBrowser, setShowProductoBrowser] = useState(false);
  const [productoSearch, setProductoSearch] = useState("");
  const [compraBarcodeInput, setCompraBarcodeInput] = useState("");
  const [compraBarcodeLoading, setCompraBarcodeLoading] = useState(false);
  const [varianteCompraScanPending, setVarianteCompraScanPending] = useState(null); // { prod, variantesActivas }
  const [notasCompra, setNotasCompra] = useState([]);
  const [showNotaCompraForm, setShowNotaCompraForm] = useState(false);
  const [editingNotaCompra, setEditingNotaCompra] = useState(null);
  const [selectedNotaCompraView, setSelectedNotaCompraView] = useState(null);
  const [notaCompraDocModal, setNotaCompraDocModal] = useState(null); // modal imprimir nota compra
  const [notaCompraForm, setNotaCompraForm] = useState({ numero: "", fecha: hoy(), compra_id: "", compra_numero_factura: "", proveedor_id: "", proveedor_nombre: "", tipo_motivo: "descuento", motivo_detalle: "", monto: "", moneda: "PYG", tipo_cambio: "", notas: "", cuenta_id: "", cuenta_nombre: "", items_devueltos: [], tipo_cobro: "saldo_favor" });
  const [tcCompraInfo, setTcCompraInfo] = useState(null);
  const [tcNotaCompraInfo, setTcNotaCompraInfo] = useState(null);
  const [tcCostoPagoInfo, setTcCostoPagoInfo] = useState(null);
  const [tcSueldoPagoInfo, setTcSueldoPagoInfo] = useState(null);
  // ── Anticipos de proveedor ────────────────────────────────────────────────
  const [anticiposDisponibles, setAnticiposDisponibles] = useState([]);  // anticipos del proveedor seleccionado
  const [loadingAnticipos, setLoadingAnticipos] = useState(false);
  // Form para crear un nuevo anticipo (tab Anticipos)
  const emptyAnticipoForm = () => ({ proveedor_id: "", proveedor_nombre: "", proveedor_ruc: "", monto: "", moneda: "PYG", tipo_cambio: "", fecha: hoy(), cuenta_id: "", cuenta_nombre: "", notas: "" });
  const [anticipoForm, setAnticipoForm] = useState(emptyAnticipoForm());
  const [savingAnticipo, setSavingAnticipo] = useState(false);
  const [anticiposList, setAnticiposList] = useState([]);   // todos los anticipos de la empresa (tab)
  const [loadingAnticiposList, setLoadingAnticiposList] = useState(false);
  const [anticiposFilter, setAnticiposFilter] = useState("disponible"); // disponible | parcial | agotado | todos

  // ── Costos Fijos state ──────────────────────────────────────────────────────
  const [costos, setCostos] = useState([]);
  const [loadingCostos, setLoadingCostos] = useState(false);
  const [vencimientos, setVencimientos] = useState([]);
  const [pagosCostos, setPagosCostos] = useState([]);
  const [loadingVenc, setLoadingVenc] = useState(false);
  const [costosLogoFilter, setCostosLogoFilter] = useState("todas");
  const COSTOS_FRECUENCIAS = [
    { value: "unica", label: "\u00danica vez" }, { value: "mensual", label: "Mensual" },
    { value: "trimestral", label: "Trimestral" }, { value: "semestral", label: "Semestral" }, { value: "anual", label: "Anual" },
  ];
  const COSTOS_CATEGORIAS = [
    "Hosting / Servidores","Dominios","Software / Licencias","Telef\u00f3n\u00eda / Internet",
    "Servicios B\u00e1sicos","Impuestos / Tasas","Alquiler","Publicidad","Seguros","Otros",
  ];
  const LOGO_LABEL_MAP = { arandujar: "Arandu&JAR", arandu: "Arandu", jar: "JAR" };
  const emptyCostoForm = () => ({
    logo_tipo: "arandujar", nombre: "", descripcion: "", categoria: "Otros", monto: "", moneda: "PYG",
    tipo_cambio: "", frecuencia: "mensual", dia_vencimiento: 1,
    fecha_inicio: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10), fecha_fin: "", activo: true, notas: "",
    ...CANAL_PAGO_VACIO,
  });
  const costoFechaVigenciaEdicion = () => filtroTipo === "mes" && filtroMes ? `${filtroMes}-01` : new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
  const [showCostoForm, setShowCostoForm] = useState(false);
  const [editingCostoId, setEditingCostoId] = useState(null);
  const [costoFormData, setCostoFormData] = useState(emptyCostoForm());
  const [showPagoCostoModal, setShowPagoCostoModal] = useState(null);
  const [pagoMedioCosto, setPagoMedioCosto] = useState("transferencia"); // "transferencia" | "cheque"
  const [pagoCostoForm, setPagoCostoForm] = useState({ monto_pagado: "", fecha_pago: hoy(), notas: "", tiene_factura: false, nro_factura: "", nro_timbrado: "", fecha_vigencia_timbrado: "", iva_tipo: "", items: [], cuenta_id: "", cuenta_nombre: "", tipo_cambio: "", cuenta_moneda: "" });
  const [categoriasGastos, setCategoriasGastos] = useState([]);
  const [showCategoriasPanel, setShowCategoriasPanel] = useState(false);
  // Configuración: pago de compras al contado (automático vs. desde Pagos Proveedores)
  const [cfgEgresos, setCfgEgresos] = useState({ pago_automatico_contado: false, cuenta_pago_automatico_id: null });
  const [savingCfgEgresos, setSavingCfgEgresos] = useState(false);
  const [catForm, setCatForm] = useState({ nombre: "" });
  const [editingCatId, setEditingCatId] = useState(null);
  const [savingCat, setSavingCat] = useState(false);
  // Sueldo mínimo global
  const [sueldoMinimoGlobal, setSueldoMinimoGlobal] = useState(SUELDO_MINIMO);
  const [historialMinimos, setHistorialMinimos] = useState([]);
  const [nuevoMinimo, setNuevoMinimo] = useState("");
  const [fechaVigenciaMinimo, setFechaVigenciaMinimo] = useState("");
  const [savingMinimo, setSavingMinimo] = useState(false);

  // ── Sueldos state ───────────────────────────────────────────────────────────
  const emptySueldoForm = () => ({ periodo: filtroMes, moneda: "PYG", tipo_cambio: "", fecha_pago: hoy(), descuento_ips: "", notas: "", descuentos_adicionales: [], cuenta_id: "", cuenta_nombre: "", cuenta_moneda: "", tipo_factura_id: "", tipo_factura_nombre: "" });
  const [empleados, setEmpleados] = useState([]);
  const [loadingEmpleados, setLoadingEmpleados] = useState(false);
  const [sueldosVenc, setSueldosVenc] = useState([]);
  const [loadingSueldos, setLoadingSueldos] = useState(false);
  const [adelantosMap, setAdelantosMap] = useState({});
  const [extrasMap, setExtrasMap] = useState({});
  const [showSueldoModal, setShowSueldoModal] = useState(false);
  const [sueldoMedio, setSueldoMedio] = useState("transferencia"); // "transferencia" | "cheque"
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [formSueldo, setFormSueldo] = useState(emptySueldoForm());
  const [savingSueldo, setSavingSueldo] = useState(false);
  const [adelantosPeriodo, setAdelantosPeriodo] = useState([]);
  const [extrasPeriodo, setExtrasPeriodo] = useState([]);
  // Cuotas de préstamos a funcionario pendientes para este empleado
  const [cuotasPrestEmp, setCuotasPrestEmp] = useState([]);
  const [deudasFactEmp, setDeudasFactEmp] = useState([]);              // facturas pendientes del empleado (compras en el local)
  const [descFacturasSel, setDescFacturasSel] = useState([]);          // [{factura_id,numero,monto}]
  const [cuotasPrestSeleccionadas, setCuotasPrestSeleccionadas] = useState([]); // [{prestamo_id,cuota_numero,monto}]
  const [showAdelantoModal, setShowAdelantoModal] = useState(false);
  const [adelantoEmp, setAdelantoEmp] = useState(null);
  const [formAdelanto, setFormAdelanto] = useState({ monto: "", fecha: hoy(), notas: "", cuenta_id: "", cuenta_nombre: "" });
  const [savingAdelanto, setSavingAdelanto] = useState(false);
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [extraEmp, setExtraEmp] = useState(null);
  const [formExtra, setFormExtra] = useState({ monto: "", descripcion: "Extra", fecha: hoy(), notas: "", moneda: "PYG", tipo_cambio: "" });
  const [savingExtra, setSavingExtra] = useState(false);

  // Cache de compras por clave logo+mes+eliminadas — evita re-fetchear al volver
  const comprasDataCache = useRef(new Map());
  const invalidateEgresosCache = () => {
    comprasDataCache.current.clear();
    lsDelPrefix("ard_compras_");
  };

  const [selectedCompraView, setSelectedCompraView] = useState(null);
  // Modelos de impresión habilitados para Compras (A4 / Ticket) — configurable por empresa
  const [modelosImpCompra, setModelosImpCompra] = useState(["a4", "ticket"]);
  const [cfgImpTmp, setCfgImpTmp] = useState(["a4", "ticket"]);
  useEffect(() => { setCfgImpTmp(modelosImpCompra); }, [modelosImpCompra]);
  useEffect(() => {
    if (!token) return;
    const lg = activeEmpresaPropia?.slug;
    fetch(`${API}/admin/configuracion-impresion-compras${lg ? `?logo_tipo=${encodeURIComponent(lg)}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.modelos_habilitados?.length) setModelosImpCompra(d.modelos_habilitados); })
      .catch(() => {});
  }, [token, activeEmpresaPropia?.slug]); // eslint-disable-line
  const guardarCfgImpCompra = async (modelos) => {
    try {
      const r = await fetch(`${API}/admin/configuracion-impresion-compras`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ logo_tipo: activeEmpresaPropia?.slug, modelos_habilitados: modelos }),
      });
      if (r.ok) { setModelosImpCompra(modelos); toast.success("Configuración de impresión guardada"); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "No se pudo guardar"); }
    } catch { toast.error("Error de conexión"); }
  };
  const [selectedPagoProveedorDesdeCompra, setSelectedPagoProveedorDesdeCompra] = useState(null);
  const [loadingPagoProveedorDesdeCompra, setLoadingPagoProveedorDesdeCompra] = useState(false);
  const [compraViewLoading, setCompraViewLoading] = useState(false);
  // Items del compra seleccionada para el form de nota de crédito (devolución)
  const [compraNotaItems, setCompraNotaItems] = useState([]);
  // Cuotas panel for compra detail
  const [cuotasCompra, setCuotasCompra] = useState([]);
  const [loadingCuotasCompra, setLoadingCuotasCompra] = useState(false);
  const [selectedCuotaCompraIds, setSelectedCuotaCompraIds] = useState(new Set());
  const [cobrarCuotasCompraModal, setCobrarCuotasCompraModal] = useState(false);
  const [cobrarCuotasCompraFecha, setCobrarCuotasCompraFecha] = useState(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [cobrarCuotasCompraCuentaId, setCobrarCuotasCompraCuentaId] = useState("");
  const [cobrandoCuotasCompra, setCobrandoCuotasCompra] = useState(false);
  const [selectedCostoView, setSelectedCostoView] = useState(null);  // gasto (Costo Fijo)
  const [canalEdit, setCanalEdit] = useState(null);  // edición rápida de canal de pago del gasto abierto
  const [editFacturaPagoMode, setEditFacturaPagoMode] = useState(false); // editar factura del pago del gasto
  const [editFacturaPagoForm, setEditFacturaPagoForm] = useState({ tiene_factura: false, nro_factura: "", items: [] });
  const [editarMontoCostoMode, setEditarMontoCostoMode] = useState(false);
  const [ajusteAlcance, setAjusteAlcance] = useState("periodo");
  const [costosOmitidos, setCostosOmitidos] = useState([]);
  const [editarMontoCostoVal, setEditarMontoCostoVal] = useState("");
  const [editarMontoCostoMotivo, setEditarMontoCostoMotivo] = useState("");
  const [showHistorialAjustes, setShowHistorialAjustes] = useState(false);
  // Detalle de un sueldo pagado
  const [sueldoView, setSueldoView] = useState(null);   // { emp, sueldo, adelantos, extras }
  const [loadingSueldoView, setLoadingSueldoView] = useState(false);
  // Aumento de sueldo / historial
  const [aumentoEmp, setAumentoEmp] = useState(null);
  const [aumentoForm, setAumentoForm] = useState({ sueldo_nuevo: "", motivo: "", fecha: hoy() });
  const [savingAumento, setSavingAumento] = useState(false);
  const [historialEmp, setHistorialEmp] = useState(null);
  const [historialData, setHistorialData] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // ── Cuentas bancarias ───────────────────────────────────────────────────────
  const [cuentasDisp, setCuentasDisp] = useState([]);
  // Pago automático de compras al contado (config de Egresos por empresa)
  const [pagoAutoCfg, setPagoAutoCfg] = useState({ pago_automatico_contado: false, cuenta_pago_automatico_id: null });
  // ── Obras (Tipo de Factura) — para imputar el costo de personal por obra ────
  const [tiposFacturaDisp, setTiposFacturaDisp] = useState([]);
  const cuentaLogoOk = (c, logo = activeEmpresaPropia?.slug) => !logo || !c.logo_tipo || c.logo_tipo === logo;
  const cuentaPredeterminada = (moneda = "PYG", logo = activeEmpresaPropia?.slug) => (
    cuentasDisp.find(c => c.activa !== false && c.moneda === moneda && c.es_predeterminada && cuentaLogoOk(c, logo))
    || cuentasDisp.find(c => c.activa !== false && c.moneda === moneda && cuentaLogoOk(c, logo))
    || cuentasDisp.find(c => c.activa !== false && c.es_predeterminada && cuentaLogoOk(c, logo))
    || cuentasDisp.find(c => c.activa !== false && cuentaLogoOk(c, logo))
    || cuentasDisp[0]
  );
  const pagoAutoActivo = showCompraForm && !editingCompra && !!pagoAutoCfg.pago_automatico_contado
    && compraForm.tipo_pago === "contado" && !compraForm.solo_iva;
  const cuentaPagoSel = cuentasDisp.find(c => String(c.id) === String(compraForm.cuenta_id));
  const pagoAutoNecesitaTc = pagoAutoActivo && !!cuentaPagoSel && (cuentaPagoSel.moneda || "PYG") !== (compraForm.moneda || "PYG");
  useEffect(() => {
    if (!pagoAutoActivo || compraForm.cuenta_manual) return;
    const cfgCuenta = pagoAutoCfg.cuenta_pago_automatico_id
      ? cuentasDisp.find(c => String(c.id) === String(pagoAutoCfg.cuenta_pago_automatico_id)) : null;
    const def = cfgCuenta || cuentaPredeterminada(compraForm.moneda || "PYG", compraForm.logo_tipo);
    if (def && String(def.id) !== String(compraForm.cuenta_id)) {
      setCompraForm(p => ({ ...p, cuenta_id: def.id, cuenta_nombre: def.nombre }));
    }
  }, [pagoAutoActivo, compraForm.moneda, compraForm.cuenta_manual, cuentasDisp, pagoAutoCfg]); // eslint-disable-line
  // El TC de pago se sugiere con el de la compra cuando la compra es en moneda extranjera
  useEffect(() => {
    if (!pagoAutoNecesitaTc || compraForm.tipo_cambio_pago) return;
    if ((compraForm.moneda || "PYG") !== "PYG" && compraForm.tipo_cambio) {
      setCompraForm(p => ({ ...p, tipo_cambio_pago: p.tipo_cambio }));
    }
  }, [pagoAutoNecesitaTc, compraForm.tipo_cambio, compraForm.moneda]); // eslint-disable-line
  const renderCuentasPorMoneda = (cuentas = [], logo = activeEmpresaPropia?.slug) => {
    const labels = { PYG: "Cuenta bancaria Guaraníes (PYG)", USD: "Cuenta bancaria Dólares (USD)", BRL: "Cuenta bancaria Reales (BRL)", ARS: "Cuenta bancaria Pesos (ARS)" };
    const activas = (cuentas || []).filter(c => c.activa !== false && cuentaLogoOk(c, logo));
    const monedas = ["PYG", "USD", "BRL", "ARS", ...Array.from(new Set(activas.map(c => c.moneda || "PYG"))).filter(m => !["PYG","USD","BRL","ARS"].includes(m))];
    return monedas.map(moneda => {
      const grupo = activas.filter(c => (c.moneda || "PYG") === moneda);
      if (!grupo.length) return null;
      return (
        <optgroup key={moneda} label={labels[moneda] || moneda}>
          {grupo.map(c => (
            <option key={c.id} value={c.id}>
              {c.nombre}{c.banco ? ` · ${c.banco}` : ""} ({c.moneda || "PYG"}){c.es_predeterminada ? " ★" : ""}
            </option>
          ))}
        </optgroup>
      );
    });
  };
  const [planCuentas, setPlanCuentas] = useState([]);
  const planCuentaAnticipoProveedor = useMemo(() => (
    (planCuentas || []).find(c =>
      c.activa !== false &&
      c.uso === "anticipo_proveedores" &&
      (!c.logo_tipo || c.logo_tipo === (activeEmpresaPropia?.slug || ""))
    ) || null
  ), [planCuentas, activeEmpresaPropia?.slug]);

  const addDays = (fecha, dias) => {
    const base = fecha ? new Date(`${fecha}T00:00:00`) : new Date();
    if (Number.isNaN(base.getTime())) return fecha || hoy();
    base.setDate(base.getDate() + Math.max(Number(dias || 0), 0));
    return base.toISOString().slice(0, 10);
  };

  const usoPlanCompra = compraForm.tipo_pago === "credito" ? "compra_credito" : "compra_contado";
  const planCuentasCompra = useMemo(() => (
    (planCuentas || []).filter(c => c.activa !== false && c.uso === usoPlanCompra && (!c.logo_tipo || c.logo_tipo === compraForm.logo_tipo))
  ), [planCuentas, usoPlanCompra, compraForm.logo_tipo]);

  const aplicarPlanCuentaCompra = (cuentaId, recalcularFecha = true) => {
    const cuenta = planCuentasCompra.find(c => String(c.id) === String(cuentaId));
    setCompraForm(p => ({
      ...p,
      plan_cuenta_id:      cuentaId || "",
      plan_cuenta_nombre:  cuenta?.nombre || "",
      fecha_vencimiento:   cuenta && !cuenta.es_cuotas && recalcularFecha ? (cuenta.fecha_vencimiento_fija || addDays(p.fecha, cuenta.dias_vencimiento)) : p.fecha_vencimiento,
      es_credito_cuotas:   !!cuenta?.es_cuotas,
      tasa_interes_cuotas: cuenta?.tasa_interes ?? 0,
      num_cuotas:          cuenta?.es_cuotas ? Math.min(p.num_cuotas || 3, cuenta.mi_max_cuotas || cuenta.max_cuotas || 6) : p.num_cuotas,
    }));
  };

  const planCuentaCompraSeleccionada = useMemo(
    () => planCuentasCompra.find(c => String(c.id) === String(compraForm.plan_cuenta_id)) || null,
    [planCuentasCompra, compraForm.plan_cuenta_id]
  );

  // Monto total de items para preview de cuotas
  const montoTotalCompra = useMemo(
    () => compraItemsConDescuentoGlobal(compraForm).reduce((s, it) => s + (Number(it.subtotal) || 0), 0),
    [compraForm]
  );

  const cuotasCompraPreview = useMemo(() => {
    if (!compraForm.es_credito_cuotas || !compraForm.num_cuotas || !montoTotalCompra) return [];
    const n = Number(compraForm.num_cuotas);
    const tasa = Number(compraForm.tasa_interes_cuotas || 0);
    const totalConInteres = montoTotalCompra * (1 + tasa / 100);
    const base = Math.floor(totalConInteres / n);
    const resto = Math.round(totalConInteres - base * n);
    return Array.from({ length: n }, (_, i) => ({
      num: i + 1,
      monto: i === n - 1 ? base + resto : base,
    }));
  }, [compraForm.es_credito_cuotas, compraForm.num_cuotas, compraForm.tasa_interes_cuotas, montoTotalCompra]);

  // Suma un número de meses a una fecha YYYY-MM-DD (mismo criterio que el backend
  // en generar_cuotas: mismo día del mes, ajustado si el mes destino es más corto).
  const addMonthsISO = (dateStr, n) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    const year = y + Math.floor((m - 1 + n) / 12);
    const month = ((m - 1 + n) % 12 + 12) % 12 + 1;
    const diasMes = [31, (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const day = Math.min(d, diasMes[month - 1]);
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  // Activa el modo "cargar cuotas a mano": arranca con lo mismo que ya mostraba
  // la vista previa automática (monto parejo + un mes entre vencimientos), para
  // no obligar a tipear todo desde cero — a partir de ahí el usuario edita libre.
  const activarCuotasManual = () => {
    const base = compraForm.cuotas_manual?.length
      ? compraForm.cuotas_manual
      : cuotasCompraPreview.map((c, i) => ({
          numero: c.num,
          monto: c.monto,
          fecha_vencimiento: addMonthsISO(compraForm.fecha_primer_vencimiento, i),
        }));
    setCompraForm(p => ({ ...p, cuotas_manual_activo: true, cuotas_manual: base }));
  };

  const actualizarCuotaManual = (idx, campo, valor) => {
    setCompraForm(p => ({
      ...p,
      cuotas_manual: (p.cuotas_manual || []).map((f, i) => i === idx ? { ...f, [campo]: valor } : f),
    }));
  };

  const agregarCuotaManual = () => {
    setCompraForm(p => {
      const filas = p.cuotas_manual || [];
      const ultima = filas[filas.length - 1];
      return {
        ...p,
        cuotas_manual: [...filas, {
          numero: filas.length + 1,
          monto: 0,
          fecha_vencimiento: ultima?.fecha_vencimiento ? addMonthsISO(ultima.fecha_vencimiento, 1) : "",
        }],
      };
    });
  };

  const eliminarCuotaManual = (idx) => {
    setCompraForm(p => ({ ...p, cuotas_manual: (p.cuotas_manual || []).filter((_, i) => i !== idx) }));
  };

  const totalCuotasManual = useMemo(
    () => (compraForm.cuotas_manual || []).reduce((s, f) => s + (Number(f.monto) || 0), 0),
    [compraForm.cuotas_manual]
  );

  useEffect(() => {
    if (!showCompraForm || compraForm.solo_iva || !planCuentasCompra.length) return;
    const actualOk = planCuentasCompra.some(c => String(c.id) === String(compraForm.plan_cuenta_id));
    if (actualOk) return;
    const pred = planCuentasCompra.find(c => c.predeterminada && !c.es_cuotas) || planCuentasCompra.find(c => !c.es_cuotas) || planCuentasCompra[0];
    setCompraForm(p => ({
      ...p,
      plan_cuenta_id:      pred.id,
      plan_cuenta_nombre:  pred.nombre,
      fecha_vencimiento:   pred.es_cuotas ? p.fecha_vencimiento : (pred.fecha_vencimiento_fija || addDays(p.fecha, pred.dias_vencimiento)),
      es_credito_cuotas:   !!pred.es_cuotas,
      tasa_interes_cuotas: pred.tasa_interes ?? 0,
    }));
  }, [showCompraForm, compraForm.solo_iva, compraForm.plan_cuenta_id, planCuentasCompra]); // eslint-disable-line

  // Fetch cuotas when compra detail opens
  // Fetch items de la compra seleccionada para el form de nota de crédito (tipo devolución)
  useEffect(() => {
    if (!notaCompraForm?.compra_id || notaCompraForm?.tipo_motivo !== "devolucion") {
      setCompraNotaItems([]);
      return;
    }
    fetch(`${API}/admin/compras/${notaCompraForm.compra_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => setCompraNotaItems(data?.items || []))
      .catch(() => setCompraNotaItems([]));
  }, [notaCompraForm?.compra_id, notaCompraForm?.tipo_motivo, token]); // eslint-disable-line

  useEffect(() => {
    if (!selectedCompraView?.es_credito_cuotas || !selectedCompraView?.id) {
      setCuotasCompra([]);
      setSelectedCuotaCompraIds(new Set());
      return;
    }
    setLoadingCuotasCompra(true);
    fetch(`${API}/admin/cuotas?doc_id=${selectedCompraView.id}&limit=60`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCuotasCompra(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => setCuotasCompra([]))
      .finally(() => setLoadingCuotasCompra(false));
  }, [selectedCompraView?.id, selectedCompraView?.es_credito_cuotas, token]); // eslint-disable-line

  const cobrarCuotasCompra = async () => {
    if (selectedCuotaCompraIds.size === 0) return;
    const cuenta = cuentasDisp.find(c => String(c.id) === String(cobrarCuotasCompraCuentaId));
    setCobrandoCuotasCompra(true);
    try {
      const res = await fetch(`${API}/admin/cuotas/cobrar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          cuota_ids: Array.from(selectedCuotaCompraIds),
          fecha_pago: cobrarCuotasCompraFecha,
          cuenta_id: cobrarCuotasCompraCuentaId || null,
          cuenta_nombre: cuenta?.nombre || null,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error"); }
      // Refresh cuotas
      const r2 = await fetch(`${API}/admin/cuotas?doc_id=${selectedCompraView.id}&limit=60`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r2.ok) {
        const d = await r2.json();
        setCuotasCompra(Array.isArray(d) ? d : (d.items || []));
      }
      setSelectedCuotaCompraIds(new Set());
      setCobrarCuotasCompraModal(false);
      fetchCompras(true);
    } catch (e) {
      toast.error(e.message || "Error al registrar pago de cuotas");
    } finally {
      setCobrandoCuotasCompra(false);
    }
  };

  // ── Búsquedas por pestaña (chips = filtros activos, input = texto en curso) ──
  const [comprasChips, setComprasChips]     = useState([]);
  const [comprasInput, setComprasInput]     = useState("");
  const [costosChips, setCostosChips]       = useState([]);
  const [costosInput, setCostosInput]       = useState("");
  const [sueldosChips, setSueldosChips]     = useState([]);
  const [sueldosInput, setSueldosInput]     = useState("");
  const [pagPendChips, setPagPendChips]     = useState([]);
  const [pagPendInput, setPagPendInput]     = useState("");
  const [ivaChips, setIvaChips]             = useState([]);
  const [ivaInput, setIvaInput]             = useState("");

  // ── Pago IVA state ──────────────────────────────────────────────────────────
  const [ivaBalance, setIvaBalance] = useState(null);
  const [ivaResumen, setIvaResumen] = useState(null);
  const [pagosIvaList, setPagosIvaList] = useState([]);
  const [loadingIva, setLoadingIva] = useState(false);
  const [periodoIva, setPeriodoIva] = useState(mesActual());
  const [showPagoIvaForm, setShowPagoIvaForm] = useState(false);
  const [editingPagoIva, setEditingPagoIva] = useState(null);
  const [pagoIvaForm, setPagoIvaForm] = useState({ descripcion: "", monto: "", fecha_pago: hoy(), periodo_iva: mesActual(), cuenta_id: "", cuenta_nombre: "", notas: "" });

  // ── Ajuste de IVA (corrección manual, no toca caja/banco ni el Balance) ──────
  const [ajustesIvaList, setAjustesIvaList] = useState([]);
  const [showAjusteIvaForm, setShowAjusteIvaForm] = useState(false);
  const [editingAjusteIva, setEditingAjusteIva] = useState(null);
  const [ajusteIvaForm, setAjusteIvaForm] = useState({ motivo: "", monto: "", fecha: hoy(), periodo_iva: mesActual(), notas: "" });

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const sugeridoActual = tcCompraInfo ? String(tcCompraInfo.tipo_cambio_sugerido || tcCompraInfo.venta || "") : "";
    const tcActual = String(compraForm.tipo_cambio || "");
    const puedeActualizarTc = !tcActual || (sugeridoActual && tcActual === sugeridoActual);
    if (!showCompraForm || compraForm.moneda !== "USD" || !compraForm.fecha || !puedeActualizarTc) return;
    let cancelled = false;
    fetch(`${API}/admin/cotizaciones/usd?fecha=${compraForm.fecha}${activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const sugerido = data.tipo_cambio_sugerido || data.venta;
        if (sugerido) {
          setCompraForm(prev => {
            const prevTc = String(prev.tipo_cambio || "");
            if (prevTc && sugeridoActual && prevTc !== sugeridoActual) return prev;
            return { ...prev, tipo_cambio: String(sugerido) };
          });
          setTcCompraInfo(data);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showCompraForm, compraForm.moneda, compraForm.fecha, compraForm.tipo_cambio, tcCompraInfo?.tipo_cambio_sugerido, tcCompraInfo?.venta, token, activeEmpresaPropia?.slug]);

  useEffect(() => {
    const sugeridoActual = tcNotaCompraInfo ? String(tcNotaCompraInfo.tipo_cambio_sugerido || tcNotaCompraInfo.venta || "") : "";
    const tcActual = String(notaCompraForm.tipo_cambio || "");
    const puedeActualizarTc = !tcActual || (sugeridoActual && tcActual === sugeridoActual);
    if (!showNotaCompraForm || notaCompraForm.moneda !== "USD" || !notaCompraForm.fecha || !puedeActualizarTc) return;
    let cancelled = false;
    fetch(`${API}/admin/cotizaciones/usd?fecha=${notaCompraForm.fecha}${activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const sugerido = data.tipo_cambio_sugerido || data.venta;
        if (sugerido) {
          setNotaCompraForm(prev => {
            const prevTc = String(prev.tipo_cambio || "");
            if (prevTc && sugeridoActual && prevTc !== sugeridoActual) return prev;
            return { ...prev, tipo_cambio: String(sugerido) };
          });
          setTcNotaCompraInfo(data);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showNotaCompraForm, notaCompraForm.moneda, notaCompraForm.fecha, notaCompraForm.tipo_cambio, tcNotaCompraInfo?.tipo_cambio_sugerido, tcNotaCompraInfo?.venta, token, activeEmpresaPropia?.slug]);

  // TC sugerido para pago de costo fijo cuando cuenta moneda ≠ costo moneda
  useEffect(() => {
    const costoMoneda = showPagoCostoModal?.moneda || "PYG";
    const cuentaMoneda = pagoCostoForm.cuenta_moneda;
    const needsTc = cuentaMoneda && costoMoneda && cuentaMoneda !== costoMoneda;
    const foreignMoneda = needsTc ? (cuentaMoneda === "PYG" ? costoMoneda : cuentaMoneda) : null;
    if (!showPagoCostoModal || !needsTc || foreignMoneda !== "USD" || !pagoCostoForm.fecha_pago) return;
    const sugeridoActual = tcCostoPagoInfo ? String(tcCostoPagoInfo.tipo_cambio_sugerido || tcCostoPagoInfo.venta || "") : "";
    const tcActual = String(pagoCostoForm.tipo_cambio || "");
    const puedeActualizar = !tcActual || (sugeridoActual && tcActual === sugeridoActual);
    if (!puedeActualizar) return;
    let cancelled = false;
    fetch(`${API}/admin/cotizaciones/usd?fecha=${pagoCostoForm.fecha_pago}${activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const sugerido = data.tipo_cambio_sugerido || data.venta;
        if (sugerido) {
          setPagoCostoForm(prev => {
            const prevTc = String(prev.tipo_cambio || "");
            if (prevTc && sugeridoActual && prevTc !== sugeridoActual) return prev;
            return { ...prev, tipo_cambio: String(sugerido) };
          });
          setTcCostoPagoInfo(data);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showPagoCostoModal, pagoCostoForm.cuenta_moneda, pagoCostoForm.fecha_pago, pagoCostoForm.tipo_cambio, tcCostoPagoInfo?.tipo_cambio_sugerido, tcCostoPagoInfo?.venta, token, activeEmpresaPropia?.slug]);

  // TC sugerido para pago de sueldo cuando cuenta moneda ≠ moneda del sueldo
  useEffect(() => {
    const sueldoMoneda = formSueldo.moneda || "PYG";
    const cuentaMoneda = formSueldo.cuenta_moneda;
    const needsTc = sueldoMedio !== "cheque" && cuentaMoneda && sueldoMoneda && cuentaMoneda !== sueldoMoneda;
    const foreignMoneda = needsTc ? (cuentaMoneda === "PYG" ? sueldoMoneda : cuentaMoneda) : null;
    if (!showSueldoModal || !needsTc || foreignMoneda !== "USD" || !formSueldo.fecha_pago) return;
    const sugeridoActual = tcSueldoPagoInfo ? String(tcSueldoPagoInfo.tipo_cambio_sugerido || tcSueldoPagoInfo.venta || "") : "";
    const tcActual = String(formSueldo.tipo_cambio || "");
    const puedeActualizar = !tcActual || (sugeridoActual && tcActual === sugeridoActual);
    if (!puedeActualizar) return;
    let cancelled = false;
    fetch(`${API}/admin/cotizaciones/usd?fecha=${formSueldo.fecha_pago}${activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const sugerido = data.tipo_cambio_sugerido || data.venta;
        if (sugerido) {
          setFormSueldo(prev => {
            const prevTc = String(prev.tipo_cambio || "");
            if (prevTc && sugeridoActual && prevTc !== sugeridoActual) return prev;
            return { ...prev, tipo_cambio: String(sugerido) };
          });
          setTcSueldoPagoInfo(data);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showSueldoModal, sueldoMedio, formSueldo.moneda, formSueldo.cuenta_moneda, formSueldo.fecha_pago, formSueldo.tipo_cambio, tcSueldoPagoInfo?.tipo_cambio_sugerido, tcSueldoPagoInfo?.venta, token, activeEmpresaPropia?.slug]);

  useEffect(() => {
    invalidateEgresosCache(); // Empresa cambia → invalidar cache
    fetchEmpresasPropias();
    fetchProveedores(activeEmpresaPropia?.slug);
    fetchProductos();
    fetchCuentasDisp();
    fetchPlanCuentas();
    fetchCategoriasGastos();
    fetchSueldoMinimoGlobal();
    fetchTiposFacturaDisp();
  }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  useEffect(() => {
    if (tab !== "configuracion" || !puedeVerCfgPagoContado) return;
    const lt = activeEmpresaPropia?.slug || "";
    fetch(`${API}/admin/config-egresos${lt ? `?logo_tipo=${lt}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(c => { if (c) setCfgEgresos(c); })
      .catch(() => {});
    fetchCuentasDisp();
  }, [tab, activeEmpresaPropia?.slug]); // eslint-disable-line

  const guardarCfgEgresos = async (cambios) => {
    if (!puedeGestionarCfgPagoContado) return;
    setSavingCfgEgresos(true);
    try {
      const res = await fetch(`${API}/admin/config-egresos`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ logo_tipo: activeEmpresaPropia?.slug || "", ...cambios }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      setCfgEgresos(await res.json());
      toast.success("Configuración guardada");
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSavingCfgEgresos(false);
  };

  useEffect(() => {
    if (tab === "compras") { fetchCompras(); fetchNotasCompra(); }
    if (tab === "costos") { fetchCostos(); fetchVencimientos(filtroMes, filtroTipo, filtroAnio); }
    if (tab === "sueldos") { fetchEmpleados(); fetchSueldosVenc(filtroMes); }
    if (tab === "pago-iva") fetchIvaData();
  }, [tab, mostrarEliminadasCompras, mostrarEliminadasCostos, mostrarEliminadasIva]);

  useEffect(() => {
    const nuevo = searchParams.get("nuevo");
    const compraId = searchParams.get("compra_id");
    if (compraId) {
      setTab("compras");
      openCompraView({ id: compraId });
      const next = new URLSearchParams(searchParams);
      next.delete("compra_id");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!nuevo) return;
    if (nuevo === "compra" && hasPermission("compras.crear")) {
      setTab("compras");
      openNewCompra();
    } else if (nuevo === "gasto" && hasPermission("costos_fijos.crear")) {
      setTab("costos");
      setEditingCostoId(null);
      setCostoFormData({ logo_tipo: activeEmpresaPropia?.slug || "arandujar", nombre: "", descripcion: "", categoria: "Otros", monto: "", moneda: "PYG", tipo_cambio: "", frecuencia: "mensual", dia_vencimiento: 1, fecha_inicio: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10), fecha_fin: "", activo: true, notas: "", ...CANAL_PAGO_VACIO });
      setShowCostoForm(true);
    } else if (nuevo === "pago_iva" && (hasPermission("pago_iva.crear") || hasPermission("balance.editar"))) {
      setTab("pago-iva");
      setTimeout(() => openNewPagoIva(), 0);
    } else if (nuevo === "compra_transporte" && hasPermission("compras.crear")) {
      const ids = searchParams.get("orden_ids");
      if (ids) {
        fetch(`${API}/admin/transporte/ordenes/lote?ids=${encodeURIComponent(ids)}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => res.ok ? res.json() : null)
          .then(ordenes => {
            if (!ordenes || !ordenes.length) { toast.error("No se pudieron cargar las órdenes de carga"); return; }
            const o0 = ordenes[0];
            const items = ordenes.map(o => {
              const monto = Number(o.chofer_neto_sugerido != null ? o.chofer_neto_sugerido : (o.chofer_comision_sugerida || 0));
              const falt = Number(o.faltante_gs || 0);
              return {
                ...emptyItem(),
                descripcion: `Flete ${o.numero}: ${o.origen || ""} → ${o.destino || ""} (comisión chofer${falt > 0 ? ` − faltante ${falt.toLocaleString("es-PY")}` : ""})`,
                cantidad: 1,
                precio_unitario: monto,
                subtotal: monto,
                iva: 0,
              };
            });
            const total = items.reduce((a, it) => a + (Number(it.subtotal) || 0), 0);
            setTab("compras");
            openNewCompra();
            setModoCompra("libre");
            setOrdenesTransporteCompra(ordenes);
            setCompraForm(f => ({
              ...f,
              proveedor_id: o0.chofer_id || "",
              proveedor_nombre: o0.chofer_nombre || "",
              proveedor_ruc: o0.chofer_documento || "",
              moneda: o0.moneda || "PYG",
              items,
              monto_total: total,
              notas: `Liquidación de fletes al chofer: ${ordenes.map(o => o.numero).join(", ")}`,
            }));
          })
          .catch(() => toast.error("No se pudieron cargar las órdenes de carga"));
      }
    } else if (nuevo === "compra_bascula" && hasPermission("compras.crear")) {
      const pesajeId = searchParams.get("pesaje_id");
      if (pesajeId) {
        fetch(`${API}/admin/bascula/pesajes/${pesajeId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => res.ok ? res.json() : null)
          .then(pesaje => {
            if (!pesaje) { toast.error("No se pudo cargar el pesaje"); return; }
            setTab("compras");
            openNewCompra();
            setModoCompra("libre");
            setPesajeBasculaCompra(pesaje);
            setCompraForm(f => ({
              ...f,
              proveedor_id: pesaje.entidad_id || "",
              proveedor_nombre: pesaje.entidad_nombre || "",
              proveedor_ruc: pesaje.entidad_ruc || "",
              notas: `Pesaje ${pesaje.numero}${pesaje.producto_nombre ? ` — ${pesaje.producto_nombre}` : ""} — Peso comercial final: ${pesaje.peso_neto_final} kg`,
            }));
          })
          .catch(() => toast.error("No se pudo cargar el pesaje"));
      }
    }
    const next = new URLSearchParams(searchParams);
    next.delete("nuevo");
    next.delete("pesaje_id");
    next.delete("orden_ids");
    setSearchParams(next, { replace: true });
  }, [searchParams]); // eslint-disable-line

  // ── Fetches ─────────────────────────────────────────────────────────────────
  const fetchCuentasDisp = async () => {
    try {
      const logo = activeEmpresaPropia?.slug;
      const hdr = { headers: { Authorization: `Bearer ${token}` } };
      const res = await fetch(`${API}/admin/cuentas-bancarias${logo ? `?logo_tipo=${logo}` : ""}`, hdr);
      if (!res.ok) return;
      let cuentas = await res.json();
      // Sucursales con RRHH compartido: sumar las cuentas de las otras empresas del grupo
      // (el sueldo de un empleado de la sucursal se paga desde las cuentas de SU empresa).
      try {
        const rg = await fetch(`${API}/admin/grupos-empresas/mi-grupo${logo ? `?logo_tipo=${logo}` : ""}`, hdr);
        const g = rg.ok ? await rg.json() : null;
        if (g && (g.compartidos || []).includes("rrhh")) {
          const otras = (g.slugs || []).filter(sl => sl !== logo);
          const extra = await Promise.all(otras.map(async sl => {
            const r = await fetch(`${API}/admin/cuentas-bancarias?logo_tipo=${sl}`, hdr);
            return r.ok ? r.json() : [];
          }));
          const ids = new Set(cuentas.map(c => c.id));
          extra.flat().forEach(c => { if (!ids.has(c.id)) { ids.add(c.id); cuentas.push(c); } });
        }
      } catch (e) {}
      setCuentasDisp(cuentas);
    } catch (e) {}
  };

  const fetchTiposFacturaDisp = async () => {
    try {
      const logo = activeEmpresaPropia?.slug;
      const res = await fetch(`${API}/admin/tipos-factura${logo ? `?logo_tipo=${logo}` : ""}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTiposFacturaDisp(await res.json());
    } catch (e) {}
  };

  const fetchPlanCuentas = async () => {
    try {
      const logo = activeEmpresaPropia?.slug;
      const res = await fetch(`${API}/admin/plan-cuentas${logo ? `?logo_tipo=${logo}` : ""}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPlanCuentas(await res.json());
    } catch (e) {}
  };

  const fetchSueldoMinimoGlobal = async () => {
    try {
      const logoParam = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
      const res = await fetch(`${API}/admin/empleados/config-sueldo-minimo${logoParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        SUELDO_MINIMO = data.valor;
        setSueldoMinimoGlobal(data.valor);
        setHistorialMinimos(data.historial || []);
      }
    } catch (_) {}
  };

  const fetchCategoriasGastos = async () => {
    try {
      const qs = activeEmpresaPropia?.slug ? `?logo_tipo=${encodeURIComponent(activeEmpresaPropia.slug)}` : "";
      const res = await fetch(`${API}/admin/categorias-gastos${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCategoriasGastos(await res.json());
    } catch (e) {}
  };

  const handleSaveCategoria = async (e) => {
    e.preventDefault();
    const nombre = catForm.nombre.trim();
    if (!nombre) return;
    setSavingCat(true);
    try {
      const url = editingCatId ? `${API}/admin/categorias-gastos/${editingCatId}` : `${API}/admin/categorias-gastos`;
      const method = editingCatId ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ nombre, logo_tipo: activeEmpresaPropia?.slug || "arandujar" }) });
      if (res.ok) {
        toast.success(editingCatId ? "Categoría actualizada" : "Categoría creada");
        setEditingCatId(null);
        setCatForm({ nombre: "" });
        fetchCategoriasGastos();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSavingCat(false); }
  };

  const handleDeleteCategoria = async (cat) => {
    if (!window.confirm(`¿Eliminar categoría "${cat.nombre}"? Los gastos de esta categoría pasarán a "Otros".`)) return;
    try {
      const res = await fetch(`${API}/admin/categorias-gastos/${cat.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Categoría eliminada"); fetchCategoriasGastos(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Error al eliminar"); }
    } catch { toast.error("Error de conexión"); }
  };

  const handleActualizarSueldoMinimo = async () => {
    const val = parseFloat(nuevoMinimo);
    if (!val || val <= 0) { toast.error("Ingresá un monto válido"); return; }
    if (!fechaVigenciaMinimo) { toast.error("Ingresá la fecha de vigencia"); return; }
    const accion = `¿Actualizar el sueldo mínimo a ₲ ${val.toLocaleString("es-PY")} vigente desde ${fechaVigenciaMinimo}?\n\nEsto actualizará:\n• El IPS de TODOS los empleados\n• El sueldo base de empleados que cobran el mínimo actual\n• Quedará registrado en el historial de cada empleado afectado`;
    if (!window.confirm(accion)) return;
    setSavingMinimo(true);
    try {
      const res = await fetch(`${API}/admin/empleados/actualizar-sueldo-minimo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          nuevo_minimo: val,
          actualizar_sueldos_base: true,
          fecha_vigencia: fechaVigenciaMinimo,
          logo_tipo: activeEmpresaPropia?.slug || null,
        })
      });
      if (res.ok) {
        const data = await res.json();
        SUELDO_MINIMO = val;
        setSueldoMinimoGlobal(val);
        setNuevoMinimo("");
        setFechaVigenciaMinimo("");
        await fetchSueldoMinimoGlobal(); // refresca historial
        toast.success(`Sueldo mínimo actualizado desde ${data.fecha_vigencia}. IPS: ${data.empleados_ips_actualizados} emp. | Sueldos: ${data.empleados_base_actualizados} emp.`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al actualizar");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSavingMinimo(false); }
  };

  const fetchEmpresasPropias = async () => {
    try {
      const res = await fetch(`${API}/admin/empresas-propias`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setEmpresasPropias(await res.json());
    } catch (e) {}
  };

  const fetchProveedores = async (logoTipo = null) => {
    if (!hasPermission("proveedores.ver")) {
      setProveedores([]);
      return;
    }
    try {
      const params = new URLSearchParams({ activo: "true", es_proveedor: "true" });
      if (logoTipo && logoTipo !== "todas") params.set("logo_tipo", logoTipo);
      const res = await fetch(`${API}/admin/entidades?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setProveedores(await res.json());
    } catch (e) {}
  };

  const fetchProductos = async () => {
    if (!puedeUsarStockProductos) {
      setProductos([]);
      return;
    }
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/productos?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setProductos(d.productos || d || []); }
    } catch (e) {}
  };

  const fetchCompras = async (invalidate = false) => {
    const cacheKey = `${proyectoId || ""}|${activeEmpresaPropia?.slug}|${filtroTipo === "mes" ? filtroMes : ""}|${filtroTipo === "anio" ? filtroAnio : ""}|${filtroTipo === "dia" ? filtroDia : ""}|${mostrarEliminadasCompras}`;
    const lsKey = `ard_compras_${cacheKey}`;

    const doFetch = async () => {
      const params = new URLSearchParams();
      if (filtroTipo === "mes" && filtroMes) params.set("mes", filtroMes);
      if (filtroTipo === "anio" && filtroAnio) params.set("anio", filtroAnio);
      if (filtroTipo === "dia" && filtroDia) { params.set("desde", filtroDia); params.set("hasta", filtroDia); }
      if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
      if (mostrarEliminadasCompras) params.set("incluir_eliminadas", "true");
      if (proyectoId) params.set("proyecto_constructora_id", proyectoId);
      const res = await fetch(`${API}/admin/compras?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const filtered = mostrarEliminadasCompras ? data.filter(c => c.eliminada) : data.filter(c => !c.eliminada);
      comprasDataCache.current.set(cacheKey, filtered);
      lsSet(lsKey, filtered);
      setCompras(filtered);
    };

    // 1. In-memory cache (sesión actual)
    if (!invalidate && comprasDataCache.current.has(cacheKey)) {
      setCompras(comprasDataCache.current.get(cacheKey));
      setLoadingCompras(false);
      return;
    }

    // 2. localStorage (visitas de retorno) — display inmediato + revalidate background
    if (!invalidate) {
      const lsData = lsGet(lsKey);
      if (lsData) {
        setCompras(lsData);
        comprasDataCache.current.set(cacheKey, lsData);
        setLoadingCompras(false);
        doFetch().catch(() => {});
        return;
      }
    } else {
      invalidateEgresosCache();
      lsDel(lsKey);
    }

    // 3. Full fetch (sin cache)
    setLoadingCompras(true);
    try { await doFetch(); }
    catch (e) { toast.error("Error al cargar compras"); }
    finally { setLoadingCompras(false); }
  };

  // Fetch compra completa (con items) y abre el panel de detalle
  const openCompraView = async (c) => {
    setCompraViewLoading(true);
    setSelectedCompraView(c); // mostrar modal inmediatamente con datos básicos
    try {
      const res = await fetch(`${API}/admin/compras/${c.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setSelectedCompraView(await res.json());
    } catch {}
    setCompraViewLoading(false);
  };

  const openPagoProveedorDesdeCompra = async (pagoId) => {
    if (!pagoId) return;
    setLoadingPagoProveedorDesdeCompra(true);
    try {
      const res = await fetch(`${API}/admin/pagos-proveedores/${pagoId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo abrir el pago");
      setSelectedPagoProveedorDesdeCompra(data);
    } catch (err) {
      toast.error(err.message || "Error al abrir el pago");
    } finally {
      setLoadingPagoProveedorDesdeCompra(false);
    }
  };

  const fetchNotasCompra = async () => {
    try {
      if (!hasPermission("notas_credito.ver")) return;
      const params = new URLSearchParams({ tipo: "compra" });
      if (filtroTipo === "mes" && filtroMes) params.set("mes", filtroMes);
      if (filtroTipo === "anio" && filtroAnio) params.set("anio", filtroAnio);
      if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/notas-credito?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setNotasCompra(await res.json());
    } catch (e) {}
  };

  // Carga compras SIN filtro de período para usarlas en el selector de nota de crédito
  const fetchComprasParaNota = async () => {
    try {
      const params = new URLSearchParams({ tiene_factura: "true" });
      if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/compras?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setComprasParaNota(data.filter(c => !c.eliminada));
      }
    } catch (e) {}
  };

  const fetchCostos = async (logoFilter) => {
    setLoadingCostos(true);
    try {
      const logo = logoFilter || activeEmpresaPropia?.slug;
      const params = new URLSearchParams();
      if (logo) params.set("logo_tipo", logo);
      if (mostrarEliminadasCostos) params.set("incluir_eliminadas", "true");
      if (proyectoId) params.set("proyecto_constructora_id", proyectoId);
      const res = await fetch(`${API}/admin/costos-fijos?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCostos(mostrarEliminadasCostos ? data.filter(c => c.eliminada) : data.filter(c => !c.eliminada));
      }
    } catch (e) {}
    finally { setLoadingCostos(false); }
  };

  const fetchVencimientos = async (periodo, tipo, anio) => {
    setLoadingVenc(true);
    const hdrs = { Authorization: `Bearer ${token}` };
    try {
      const logo = activeEmpresaPropia?.slug;
      const pagosParams = new URLSearchParams();
      if (tipo === "mes" && periodo) pagosParams.set("mes", periodo);
      if (tipo === "anio" && anio) pagosParams.set("anio", anio);
      if (logo) pagosParams.set("logo_tipo", logo);
      const pagosRes = await fetch(`${API}/admin/costos-fijos-pagos?${pagosParams}`, { headers: hdrs });
      setPagosCostos(pagosRes.ok ? await pagosRes.json() : []);

      if (tipo === "todos") {
        // Traer todos los costos de la empresa activa y cruzarlos con su período real.
        const res = await fetch(`${API}/admin/costos-fijos${logo ? `?logo_tipo=${logo}` : ""}`, { headers: hdrs });
        if (res.ok) {
          const todos = await res.json();
          const mesHoy = new Date().toISOString().slice(0, 7);
          // Los gastos de frecuencia "única" solo vencen en SU mes (fecha_inicio),
          // no necesariamente en el mes actual — antes acá siempre se consultaba
          // el mes actual, entonces un gasto único ya pagado en un mes anterior
          // no aparecía en esa consulta y volvía a mostrarse como "pendiente".
          // Consultamos el período real de cada costo (igual que hace el backend
          // en es_due_en_periodo: la fecha_inicio más antigua del historial).
          const periodoRealDeCosto = (c) => {
            const historial = c.historial_montos || [];
            const fechas = historial.map(h => h.fecha_inicio).filter(Boolean);
            const fechaInicioReal = fechas.length ? fechas.reduce((min, f) => (f < min ? f : min)) : c.fecha_inicio;
            return (fechaInicioReal || mesHoy).slice(0, 7);
          };
          const periodosNecesarios = new Set([mesHoy]);
          todos.forEach(c => {
            if (c.frecuencia === "unica") periodosNecesarios.add(periodoRealDeCosto(c));
          });
          const resultsPorPeriodo = await Promise.all(
            Array.from(periodosNecesarios).map(p =>
              fetch(`${API}/admin/costos-fijos/vencimientos?periodo=${p}${logo ? `&logo_tipo=${logo}` : ""}`, { headers: hdrs })
                .then(r => r.ok ? r.json() : [])
            )
          );
          const pagosMap = {};
          resultsPorPeriodo.flat().forEach(v => { pagosMap[v.costo_id] = v; });
          const merged = todos.map(c => ({
            ...c,
            costo_id: c.id,
            estado: pagosMap[c.id]?.estado || (c.activo ? "pendiente" : "inactivo"),
            pago: pagosMap[c.id]?.pago || null,
          }));
          merged.sort((a, b) => (b.fecha_inicio || "").localeCompare(a.fecha_inicio || ""));
          setVencimientos(merged);
        }
      } else if (tipo === "anio") {
        setCostosOmitidos([]);
        // 12 meses del año en paralelo
        const meses = Array.from({length:12}, (_,i) => `${anio}-${String(i+1).padStart(2,"0")}`);
        const results = await Promise.all(
          meses.map(m => fetch(`${API}/admin/costos-fijos/vencimientos?periodo=${m}${logo ? `&logo_tipo=${logo}` : ""}`, { headers: hdrs }).then(r => r.ok ? r.json() : []))
        );
        const seen = new Set();
        const all = results.flat().filter(v => {
          const key = `${v.costo_id}_${v.periodo}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        all.sort((a, b) => (b.periodo||"").localeCompare(a.periodo||"") || (a.nombre||"").localeCompare(b.nombre||""));
        setVencimientos(all);
      } else {
        // Por mes
        const res = await fetch(`${API}/admin/costos-fijos/vencimientos?periodo=${periodo}${logo ? `&logo_tipo=${logo}` : ""}`, { headers: hdrs });
        if (res.ok) setVencimientos(await res.json());
        else setVencimientos([]);
        try {
          const ro = await fetch(`${API}/admin/costos-fijos/omitidos?periodo=${periodo}${logo ? `&logo_tipo=${logo}` : ""}`, { headers: hdrs });
          setCostosOmitidos(ro.ok ? await ro.json() : []);
        } catch { setCostosOmitidos([]); }
      }
    } catch (e) { console.error("fetchVencimientos error:", e); setVencimientos([]); setPagosCostos([]); }
    finally { setLoadingVenc(false); }
  };

  const handleSaveCosto = async (e) => {
    e.preventDefault();
    const toFloat = v => (v === "" || v == null) ? null : parseFloat(v) || null;
    const payload = {
      ...costoFormData,
      monto: parseFloat(costoFormData.monto) || 0,
      tipo_cambio: toFloat(costoFormData.tipo_cambio),
      dia_vencimiento: parseInt(costoFormData.dia_vencimiento) || 1,
      fecha_fin: costoFormData.fecha_fin || null,
      descripcion: costoFormData.descripcion || null,
      categoria: costoFormData.categoria || null,
      notas: costoFormData.notas || null,
      canal_pago: costoFormData.canal_pago || null,
      ...(proyectoId ? {
        proyecto_constructora_id: proyectoId,
        proyecto_constructora_nombre: proyectoCtx?.nombre || null,
        grupo_constructora_id: costoFormData.grupo_constructora_id || null,
        grupo_constructora_nombre: gruposConstructora.find(g => g.id === costoFormData.grupo_constructora_id)?.nombre || null,
      } : {}),
    };
    const url = editingCostoId ? `${API}/admin/costos-fijos/${editingCostoId}` : `${API}/admin/costos-fijos`;
    const method = editingCostoId ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success(editingCostoId ? "Costo actualizado" : "Costo creado");
      setShowCostoForm(false);
      fetchCostos();
      fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar");
    }
  };

  const handleToggleCosto = async (c) => {
    const res = await fetch(`${API}/admin/costos-fijos/${c.id}/toggle`, { method: "PATCH", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success(c.activo ? "Desactivado" : "Activado"); fetchCostos(); fetchVencimientos(filtroMes, filtroTipo, filtroAnio); }
  };

  const handleDeleteCosto = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.nombre}"? Se eliminarán también todos los pagos.`)) return;
    const res = await fetch(`${API}/admin/costos-fijos/${c.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success("Eliminado"); fetchCostos(); fetchVencimientos(filtroMes, filtroTipo, filtroAnio); }
  };

  const handleRegistrarPagoCosto = async (e) => {
    e.preventDefault();
    const v = showPagoCostoModal;
    if (pagoCostoForm.tiene_factura && !pagoCostoForm.nro_timbrado) {
      toast.error("El número de timbrado es obligatorio cuando tiene factura");
      return;
    }
    if (pagoCostoForm.tiene_factura && !pagoCostoForm.fecha_vigencia_timbrado) {
      toast.error("La vigencia del timbrado es obligatoria cuando tiene factura");
      return;
    }
    const itemsIvaValidos = (pagoCostoForm.items || []).filter(it => (parseFloat(it.monto) || 0) > 0);
    if (pagoCostoForm.tiene_factura && itemsIvaValidos.length === 0) {
      toast.error("Agregá al menos un ítem con su monto y tasa de IVA para esta factura");
      return;
    }
    const necesitaTc = pagoCostoForm.cuenta_moneda && v?.moneda && pagoCostoForm.cuenta_moneda !== v.moneda;
    if (necesitaTc && !pagoCostoForm.tipo_cambio) {
      toast.error("Ingresá el tipo de cambio para esta cuenta");
      return;
    }
    const montoPagadoNum = parseFloat(pagoCostoForm.monto_pagado) || v.monto;
    // Equivalente en ₲ — se calcula acá (igual que en Pagos a Proveedores) para que el
    // movimiento en la cuenta bancaria en guaraníes quede con el monto convertido, no
    // el monto crudo en moneda extranjera. Aplica a cualquier moneda del gasto (USD, BRL...).
    const necesitaMontoGs = necesitaTc && pagoCostoForm.cuenta_moneda === "PYG" && v?.moneda !== "PYG";
    const payload = {
      periodo: v.periodo || filtroMes,
      monto_pagado: montoPagadoNum,
      fecha_pago: pagoCostoForm.fecha_pago,
      notas: pagoCostoForm.notas || null,
      tiene_factura: pagoCostoForm.tiene_factura,
      nro_factura: pagoCostoForm.tiene_factura ? (pagoCostoForm.nro_factura || null) : null,
      nro_timbrado: pagoCostoForm.tiene_factura ? (pagoCostoForm.nro_timbrado || null) : null,
      fecha_vigencia_timbrado: pagoCostoForm.tiene_factura ? (pagoCostoForm.fecha_vigencia_timbrado || null) : null,
      iva_tipo: pagoCostoForm.tiene_factura ? (pagoCostoForm.iva_tipo || null) : null,
      items: pagoCostoForm.tiene_factura && itemsIvaValidos.length > 0
        ? itemsIvaValidos.map(it => ({ descripcion: it.descripcion || null, monto: parseFloat(it.monto) || 0, iva: Number(it.iva) }))
        : null,
      cuenta_id: pagoCostoForm.cuenta_id || null,
      cuenta_nombre: pagoCostoForm.cuenta_nombre || null,
      tipo_cambio: necesitaTc ? (parseFloat(pagoCostoForm.tipo_cambio) || null) : null,
      monto_gs: necesitaMontoGs ? Math.round(montoPagadoNum * (parseFloat(pagoCostoForm.tipo_cambio) || 0)) : null,
    };
    const res = await fetch(`${API}/admin/costos-fijos/${v.costo_id}/pagos`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
    if (res.ok) {
      toast.success("Pago registrado");
      setShowPagoCostoModal(null);
      setTcCostoPagoInfo(null);
      // Refresh completo: actualiza vencimientos Y costos para reflejar el nuevo estado en balance
      fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
      fetchCostos();
    } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Error al registrar pago"); }
  };

  const handleAnularPagoCosto = async (v) => {
    const pagosAAnular = v.pagos?.length > 0 ? v.pagos : (v.pago ? [v.pago] : []);
    if (pagosAAnular.length === 0) return;
    const msg = pagosAAnular.length > 1
      ? `¿Anular los ${pagosAAnular.length} pagos parciales de este período?`
      : "¿Anular este pago?";
    if (!window.confirm(msg)) return;
    let anulados = 0;
    for (const p of pagosAAnular) {
      const r = await fetch(`${API}/admin/pagos-costos/${p.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(typeof err.detail === "string" ? err.detail : "No se pudo anular el pago");
        break;
      }
      anulados++;
    }
    if (anulados > 0) toast.success(anulados > 1 ? "Pagos anulados" : "Pago anulado");
    fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
    fetchCostos();
  };

  const handleAjustarMontoCosto = async () => {
    const costo_id = selectedCostoView?.costo_id;
    const periodo = selectedCostoView?.periodo || filtroMes;
    if (!costo_id || !periodo) { toast.error("No se pudo determinar el período"); return; }
    const monto = parseFloat(String(editarMontoCostoVal).replace(/\./g, "").replace(",", "."));
    if (!monto || monto <= 0) { toast.error("Monto inválido"); return; }
    const res = await fetch(`${API}/admin/costos-fijos/${costo_id}/ajustar-monto`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        periodo,
        monto,
        moneda: selectedCostoView.moneda || "PYG",
        tipo_cambio: selectedCostoView.tipo_cambio || null,
        motivo: editarMontoCostoMotivo,
        alcance: ajusteAlcance,
      }),
    });
    if (res.ok) {
      toast.success(ajusteAlcance === "desde_ahora" ? `Monto ajustado desde ${periodo} en adelante` : `Monto ajustado para ${periodo}`);
      setAjusteAlcance("periodo");
      setEditarMontoCostoMode(false);
      setEditarMontoCostoVal("");
      setEditarMontoCostoMotivo("");
      setSelectedCostoView(null);
      fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al ajustar monto");
    }
  };

  const postRecurrencia = async (costo_id, accion, body, okMsg) => {
    const res = await fetch(`${API}/admin/costos-fijos/${costo_id}/${accion}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body || {}),
    });
    if (res.ok) {
      toast.success(okMsg);
      setSelectedCostoView(null);
      fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
      fetchCostos();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(typeof err.detail === "string" ? err.detail : "No se pudo completar la acción");
    }
  };

  const handleOmitirPeriodo = (v) => {
    const periodo = v.periodo || filtroMes;
    if (!window.confirm(`¿Omitir "${v.nombre}" en ${periodo}? No se genera este mes; los demás meses siguen igual.`)) return;
    const motivo = window.prompt("Motivo (opcional):", "") || "";
    postRecurrencia(v.costo_id, "omitir-periodo", { periodo, motivo }, `${v.nombre} omitido en ${periodo}`);
  };

  const handleRestaurarPeriodo = (o) =>
    postRecurrencia(o.costo_id, "restaurar-periodo", { periodo: o.periodo }, `${o.nombre} restaurado en ${o.periodo}`);

  const handleFinalizarGasto = (v) => {
    const periodo = v.periodo || filtroMes;
    if (!window.confirm(`¿Dejar de generar "${v.nombre}" desde ${periodo} en adelante? Los meses anteriores y los ya pagados no se tocan.`)) return;
    const motivo = window.prompt("Motivo (opcional):", "") || "";
    postRecurrencia(v.costo_id, "finalizar", { desde_periodo: periodo, motivo }, `${v.nombre} ya no se generará desde ${periodo}`);
  };

  const handleEditarFacturaPago = async (pagoId, cambios) => {
    const res = await fetch(`${API}/admin/pagos-costos/${pagoId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(cambios),
    });
    if (res.ok) {
      toast.success("Pago actualizado");
      fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
      fetchCostos();
      setSelectedCostoView(null);
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al actualizar pago");
    }
  };

  const fetchEmpleados = async () => {
    setLoadingEmpleados(true);
    try {
      const logo = activeEmpresaPropia?.slug;
      const q = logo ? `&logo_tipo=${logo}` : "";
      const res = await fetch(`${API}/admin/entidades?es_empleado=true${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setEmpleados(await res.json());
    } catch (e) {}
    finally { setLoadingEmpleados(false); }
  };

  const fetchSueldosVenc = async (periodo) => {
    setLoadingSueldos(true);
    try {
      const hdrs = { Authorization: `Bearer ${token}` };
      const logo = activeEmpresaPropia?.slug;
      const logoParam = logo ? `&logo_tipo=${logo}` : "";
      const res = await fetch(`${API}/admin/empleados/sueldos?periodo=${periodo}${logoParam}`, { headers: hdrs });
      if (res.ok) {
        const data = await res.json();
        setSueldosVenc(data);
        const adMap = {}, exMap = {};
        await Promise.all(data.map(async (emp) => {
          try {
            const [rAd, rEx] = await Promise.all([
              fetch(`${API}/admin/empleados/${emp.id}/adelantos?periodo=${periodo}&incluir_consumidos=1`, { headers: hdrs }),
              fetch(`${API}/admin/empleados/${emp.id}/extras?periodo=${periodo}&incluir_consumidos=1`, { headers: hdrs }),
            ]);
            if (rAd.ok) { const ads = await rAd.json(); if (ads.length) adMap[emp.id] = ads; }
            if (rEx.ok) { const exs = await rEx.json(); if (exs.length) exMap[emp.id] = exs; }
          } catch {}
        }));
        setAdelantosMap(adMap);
        setExtrasMap(exMap);
      }
    } catch {}
    finally { setLoadingSueldos(false); }
  };

  const openSueldo = async (emp) => {
    setSelectedEmp(emp);
    const hdrs = { Authorization: `Bearer ${token}` };
    let adelantos = [], extras = [];
    try {
      const [rAd, rEx] = await Promise.all([
        fetch(`${API}/admin/empleados/${emp.id}/adelantos?periodo=${filtroMes}`, { headers: hdrs }),
        fetch(`${API}/admin/empleados/${emp.id}/extras?periodo=${filtroMes}`, { headers: hdrs }),
      ]);
      if (rAd.ok) adelantos = await rAd.json();
      if (rEx.ok) extras = await rEx.json();
    } catch {}
    setAdelantosPeriodo(adelantos);
    setExtrasPeriodo(extras);
    const monedaSueldo = emp.moneda_sueldo || emp.moneda || "PYG";
    const cuentaDef = cuentaPredeterminada(monedaSueldo, emp.logo_tipo || activeEmpresaPropia?.slug);
    setTcSueldoPagoInfo(null);
    setFormSueldo({
      periodo: filtroMes,
      moneda: monedaSueldo,
      tipo_cambio: "",
      fecha_pago: hoy(),
      descuento_ips: String(emp.aplica_ips!==false ? calcularIPS(emp) : 0),
      notas: "",
      descuentos_adicionales: [],
      cuenta_id: cuentaDef?.id || "",
      cuenta_nombre: cuentaDef?.nombre || "",
      cuenta_moneda: cuentaDef?.moneda || "",
      tipo_factura_id: "",
      tipo_factura_nombre: "",
    });
    setSueldoMedio("transferencia");
    setCuotasPrestSeleccionadas([]);
    setDescFacturasSel([]);
    setDeudasFactEmp([]);
    try {
      const rd = await fetch(`${API}/admin/empleados/${emp.id}/deudas-facturas`, { headers: hdrs });
      if (rd.ok) setDeudasFactEmp(await rd.json());
    } catch { setDeudasFactEmp([]); }
    // Cargar cuotas pendientes de préstamos para este empleado
    try {
      const slug = activeEmpresaPropia?.slug;
      const cuotasUrl = `${API}/admin/prestamos/empleado/${emp.id}/cuotas-pendientes${slug ? `?empresa_id=${encodeURIComponent(slug)}` : ""}`;
      const r = await fetch(cuotasUrl, { headers: hdrs });
      if (r.ok) setCuotasPrestEmp(await r.json()); else setCuotasPrestEmp([]);
    } catch { setCuotasPrestEmp([]); }
    setShowSueldoModal(true);
  };

  const handleSaveSueldo = async () => {
    if (!formSueldo.fecha_pago) { toast.error("Fecha de pago requerida"); return; }
    const cuentaSeleccionada = cuentasDisp.find(c => String(c.id) === String(formSueldo.cuenta_id));
    if (!cuentaSeleccionada) { toast.error("Seleccioná la cuenta bancaria de pago"); return; }
    const necesitaTc = cuentaSeleccionada.moneda && formSueldo.moneda && cuentaSeleccionada.moneda !== formSueldo.moneda;
    if (necesitaTc && (!formSueldo.tipo_cambio || parseFloat(formSueldo.tipo_cambio) <= 0)) {
      toast.error("Ingresá el tipo de cambio para esa cuenta");
      return;
    }
    setSavingSueldo(true);
    try {
      const totalDescPrest = cuotasPrestSeleccionadas.reduce((s,c)=>s+(c.monto||0), 0) + descFacturasSel.reduce((s,c)=>s+(parseFloat(c.monto)||0), 0);
      const descAdicBase = formSueldo.descuentos_adicionales.reduce((s,d)=>s+(parseFloat(d.monto)||0),0);
      // Para el cálculo del monto pagado incluimos los descuentos por préstamo
      const descAdicTotal = descAdicBase + totalDescPrest;
      const descAdic_fake = totalDescPrest > 0
        ? [...formSueldo.descuentos_adicionales, { concepto: "Cuotas préstamo", monto: totalDescPrest }]
        : formSueldo.descuentos_adicionales;
      const montoPagado = calcMontoAPagar(selectedEmp, adelantosPeriodo, extrasPeriodo, descAdic_fake);
      const totalExtras = extrasPeriodo.reduce((s,e)=>s+(e.monto||0),0);
      const totalAdelantos = adelantosPeriodo.reduce((s,a)=>s+(a.monto||0),0);
      const payload = {
        periodo: formSueldo.periodo, monto_pagado: montoPagado, moneda: formSueldo.moneda,
        tipo_cambio: necesitaTc ? (parseFloat(formSueldo.tipo_cambio)||null) : null, fecha_pago: formSueldo.fecha_pago,
        descuento_ips: parseFloat(formSueldo.descuento_ips)||0,
        horas_extra: 0, monto_horas_extra: 0,
        total_extras: totalExtras || null,
        total_adelantos: totalAdelantos || null,
        descuentos_adicionales: descAdicTotal || null,
        notas: formSueldo.notas||null,
        cuenta_id: formSueldo.cuenta_id,
        cuenta_nombre: formSueldo.cuenta_nombre || cuentaSeleccionada.nombre,
        cuotas_prestamo: cuotasPrestSeleccionadas.length > 0 ? cuotasPrestSeleccionadas : null,
        descuentos_facturas: descFacturasSel.length > 0 ? descFacturasSel.map(d => ({ factura_id: d.factura_id, monto: parseFloat(d.monto) || 0 })) : null,
        tipo_factura_id: formSueldo.tipo_factura_id || null,
        tipo_factura_nombre: formSueldo.tipo_factura_nombre || null,
      };
      const res = await fetch(`${API}/admin/empleados/${selectedEmp.id}/sueldos`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { throw new Error(await parseFetchError(res, "Error al registrar sueldo")); }
      toast.success("Sueldo registrado");
      setShowSueldoModal(false);
      fetchSueldosVenc(filtroMes);
    } catch (e) { toast.error(e.message); }
    finally { setSavingSueldo(false); }
  };

  // Pago de sueldo con cheque — llamado por ChequeEgresoForm.onSuccess
  const handleSaveSueldoConHoja = async (hoja, chequera, tipoCambio) => {
    setSavingSueldo(true);
    try {
      const totalDescPrest = cuotasPrestSeleccionadas.reduce((s,c)=>s+(c.monto||0), 0) + descFacturasSel.reduce((s,c)=>s+(parseFloat(c.monto)||0), 0);
      const descAdic_fake = totalDescPrest > 0
        ? [...formSueldo.descuentos_adicionales, { concepto: "Cuotas préstamo", monto: totalDescPrest }]
        : formSueldo.descuentos_adicionales;
      const montoPagado = calcMontoAPagar(selectedEmp, adelantosPeriodo, extrasPeriodo, descAdic_fake);
      const totalExtras = extrasPeriodo.reduce((s, e) => s + (e.monto || 0), 0);
      const totalAdelantos = adelantosPeriodo.reduce((s, a) => s + (a.monto || 0), 0);
      const descAdicTotal = formSueldo.descuentos_adicionales.reduce((s, d) => s + (parseFloat(d.monto) || 0), 0) + totalDescPrest;
      const notasCheque = `Cheque ${hoja.serie}${String(hoja.numero).padStart(6, "0")}${formSueldo.notas ? ` — ${formSueldo.notas}` : ""}`;
      const payload = {
        periodo: formSueldo.periodo,
        monto_pagado: montoPagado,
        moneda: chequera.moneda || formSueldo.moneda,
        tipo_cambio: tipoCambio || parseFloat(formSueldo.tipo_cambio) || null,
        fecha_pago: hoja.fecha_emision,
        descuento_ips: parseFloat(formSueldo.descuento_ips) || 0,
        horas_extra: 0,
        monto_horas_extra: 0,
        total_extras: totalExtras || null,
        total_adelantos: totalAdelantos || null,
        descuentos_adicionales: descAdicTotal || null,
        notas: notasCheque,
        cuenta_id: chequera.cuenta_bancaria_id,
        cuenta_nombre: chequera.banco_nombre,
        cuotas_prestamo: cuotasPrestSeleccionadas.length > 0 ? cuotasPrestSeleccionadas : null,
        descuentos_facturas: descFacturasSel.length > 0 ? descFacturasSel.map(d => ({ factura_id: d.factura_id, monto: parseFloat(d.monto) || 0 })) : null,
        tipo_factura_id: formSueldo.tipo_factura_id || null,
        tipo_factura_nombre: formSueldo.tipo_factura_nombre || null,
      };
      const res = await fetch(`${API}/admin/empleados/${selectedEmp.id}/sueldos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { throw new Error(await parseFetchError(res, "Error al registrar sueldo")); }
      toast.success("Cheque emitido y sueldo registrado");
      setShowSueldoModal(false);
      setSueldoMedio("transferencia");
      fetchSueldosVenc(filtroMes);
    } catch (e) {
      toast.error(e.message);
      // Relanzar: ChequeEgresoForm revierte el cheque recién emitido si el
      // sueldo no llegó a registrarse (si no, la hoja queda "emitida" sin
      // ningún pago real y el próximo intento choca con "ya se emitió").
      throw e;
    }
    finally { setSavingSueldo(false); }
  };

  const handleDeleteSueldo = async (sueldoId) => {
    if (!window.confirm("¿Eliminar este registro de sueldo?")) return;
    try {
      const res = await fetch(`${API}/admin/sueldos/${sueldoId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      toast.success("Registro eliminado");
      fetchSueldosVenc(filtroMes);
    } catch { toast.error("Error al eliminar"); }
  };


  const handleToggleEmpleado = async (emp) => {
    const res = await fetch(`${API}/admin/empleados/${emp.id}/toggle`, { method: "PATCH", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success(emp.activo ? "Desactivado" : "Activado"); fetchEmpleados(); }
  };

  const openSueldoView = async (emp) => {
    if (!emp || emp.estado !== "pagado" || !emp.sueldo_registrado) return;
    setSueldoView({ emp, sueldo: emp.sueldo_registrado, adelantos: [], extras: [] });
    setLoadingSueldoView(true);
    try {
      const hdrs = { Authorization: `Bearer ${token}` };
      // Pasamos incluir_consumidos=1 para ver TAMBIÉN los que se descontaron
      // de este sueldo (de lo contrario el filtro por default los oculta).
      const periodo = emp.sueldo_registrado.periodo || filtroMes;
      const [rAd, rEx] = await Promise.all([
        fetch(`${API}/admin/empleados/${emp.id}/adelantos?periodo=${periodo}&incluir_consumidos=1`, { headers: hdrs }),
        fetch(`${API}/admin/empleados/${emp.id}/extras?periodo=${periodo}&incluir_consumidos=1`, { headers: hdrs }),
      ]);
      const adelantos = rAd.ok ? await rAd.json() : [];
      const extras = rEx.ok ? await rEx.json() : [];
      setSueldoView({ emp, sueldo: emp.sueldo_registrado, adelantos, extras });
    } catch {} finally { setLoadingSueldoView(false); }
  };

  const handlePrintSueldo = async (emp) => {
    if (!emp || emp.estado !== "pagado" || !emp.sueldo_registrado) {
      toast.error("Solo se puede imprimir un sueldo pagado");
      return;
    }
    const sueldo = emp.sueldo_registrado;
    const periodo = sueldo.periodo || filtroMes;
    const printWindow = abrirVentanaReciboSueldo();
    if (!printWindow) return;
    try {
      let adelantos = [];
      let extras = [];
      if (sueldoView?.emp?.id === emp.id && sueldoView?.sueldo?.id === sueldo.id) {
        adelantos = sueldoView.adelantos || [];
        extras = sueldoView.extras || [];
      } else {
        const hdrs = { Authorization: `Bearer ${token}` };
        const [rAd, rEx] = await Promise.all([
          fetch(`${API}/admin/empleados/${emp.id}/adelantos?periodo=${periodo}&incluir_consumidos=1`, { headers: hdrs }),
          fetch(`${API}/admin/empleados/${emp.id}/extras?periodo=${periodo}&incluir_consumidos=1`, { headers: hdrs }),
        ]);
        adelantos = rAd.ok ? await rAd.json() : [];
        extras = rEx.ok ? await rEx.json() : [];
      }
      imprimirReciboSueldo({ emp, sueldo, adelantos, extras, empresa: activeEmpresaPropia, periodo }, printWindow);
    } catch {
      try { printWindow.close(); } catch {}
      toast.error("No se pudo preparar el recibo de sueldo");
    }
  };

  const openAumentoSueldo = (emp) => {
    setAumentoEmp(emp);
    setAumentoForm({ sueldo_nuevo: String(emp.sueldo_base || ""), motivo: "Aumento", fecha: hoy() });
  };

  const handleSaveAumento = async () => {
    if (!aumentoEmp) return;
    const nuevo = parseFloat(aumentoForm.sueldo_nuevo) || 0;
    if (nuevo <= 0) { toast.error("El sueldo nuevo debe ser mayor a 0"); return; }
    if (nuevo === Number(aumentoEmp.sueldo_base)) { toast.error("El sueldo nuevo es igual al actual"); return; }
    setSavingAumento(true);
    try {
      const res = await fetch(`${API}/admin/empleados/${aumentoEmp.id}/aumento-sueldo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sueldo_nuevo: nuevo,
          moneda: aumentoEmp.moneda_sueldo || aumentoEmp.moneda || "PYG",
          motivo: aumentoForm.motivo || null,
          fecha: aumentoForm.fecha || hoy(),
        }),
      });
      if (!res.ok) { throw new Error(await parseFetchError(res, "Error al actualizar sueldo")); }
      toast.success(`Sueldo actualizado a ${fmt(nuevo, aumentoEmp.moneda_sueldo || aumentoEmp.moneda || "PYG")}`);
      setAumentoEmp(null);
      fetchEmpleados();
    } catch (e) { toast.error(e.message); }
    finally { setSavingAumento(false); }
  };

  const openHistorialSueldos = async (emp) => {
    setHistorialEmp(emp);
    setLoadingHistorial(true);
    setHistorialData(null);
    try {
      const res = await fetch(`${API}/admin/empleados/${emp.id}/historial-sueldos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setHistorialData(await res.json());
    } catch {}
    finally { setLoadingHistorial(false); }
  };

  const handleDeleteEmpleado = async (emp) => {
    if (!window.confirm(`¿Eliminar a ${emp.nombre} ${emp.apellido}? También se eliminarán todos sus registros.`)) return;
    const res = await fetch(`${API}/admin/empleados/${emp.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success("Empleado eliminado"); fetchEmpleados(); } else toast.error("Error al eliminar");
  };

  // Carga adelantos+extras del período (compartido entre los modales)
  const cargarAdelantosExtrasDeEmp = async (empId) => {
    const hdrs = { Authorization: `Bearer ${token}` };
    try {
      const [rAd, rEx] = await Promise.all([
        fetch(`${API}/admin/empleados/${empId}/adelantos?periodo=${filtroMes}`, { headers: hdrs }),
        fetch(`${API}/admin/empleados/${empId}/extras?periodo=${filtroMes}`, { headers: hdrs }),
      ]);
      setAdelantosPeriodo(rAd.ok ? await rAd.json() : []);
      setExtrasPeriodo(rEx.ok ? await rEx.json() : []);
    } catch { setAdelantosPeriodo([]); setExtrasPeriodo([]); }
  };

  const openAdelanto = async (emp) => {
    setAdelantoEmp(emp);
    setFormAdelanto({ monto: "", fecha: hoy(), notas: "", cuenta_id: "", cuenta_nombre: "" });
    setShowAdelantoModal(true);
    await cargarAdelantosExtrasDeEmp(emp.id);
  };
  const handleSaveAdelanto = async () => {
    if (!formAdelanto.monto) { toast.error("Monto requerido"); return; }
    setSavingAdelanto(true);
    try {
      const payload = { periodo: filtroMes, monto: parseFloat(formAdelanto.monto)||0, moneda: adelantoEmp.moneda_sueldo || adelantoEmp.moneda || "PYG", tipo_cambio: adelantoEmp.tipo_cambio||null, fecha: formAdelanto.fecha, notas: formAdelanto.notas||null, cuenta_id: formAdelanto.cuenta_id||null, cuenta_nombre: formAdelanto.cuenta_nombre||null };
      const res = await fetch(`${API}/admin/empleados/${adelantoEmp.id}/adelantos`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { throw new Error(await parseFetchError(res, "Error al registrar adelanto")); }
      toast.success("Adelanto registrado");
      // No cierro el modal — recargo y limpio el form para poder cargar otro
      setFormAdelanto({ monto: "", fecha: hoy(), notas: "", cuenta_id: "", cuenta_nombre: "" });
      await cargarAdelantosExtrasDeEmp(adelantoEmp.id);
      fetchSueldosVenc(filtroMes);
    } catch (e) { toast.error(e.message); }
    finally { setSavingAdelanto(false); }
  };
  const handleDeleteAdelanto = async (id) => {
    if (!window.confirm("¿Eliminar adelanto?")) return;
    try {
      const res = await fetch(`${API}/admin/adelantos/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setAdelantosPeriodo(prev => prev.filter(a => a.id !== id));
      toast.success("Adelanto eliminado");
      fetchSueldosVenc(filtroMes);
    } catch { toast.error("Error al eliminar"); }
  };

  const openExtra = async (emp) => {
    setExtraEmp(emp);
    setFormExtra({ monto: "", descripcion: "Extra", fecha: hoy(), notas: "", moneda: emp.moneda_sueldo || emp.moneda || "PYG", tipo_cambio: "" });
    setShowExtraModal(true);
    await cargarAdelantosExtrasDeEmp(emp.id);
  };
  const handleSaveExtra = async () => {
    if (!formExtra.monto) { toast.error("Monto requerido"); return; }
    setSavingExtra(true);
    try {
      const payload = { periodo: filtroMes, monto: parseFloat(formExtra.monto)||0, moneda: formExtra.moneda || "PYG", tipo_cambio: formExtra.moneda !== "PYG" ? (parseFloat(formExtra.tipo_cambio) || null) : null, fecha: formExtra.fecha, descripcion: formExtra.descripcion||"Extra", notas: formExtra.notas||null };
      const res = await fetch(`${API}/admin/empleados/${extraEmp.id}/extras`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { throw new Error(await parseFetchError(res, "Error al registrar extra")); }
      toast.success("Extra registrado");
      setFormExtra({ monto: "", descripcion: "Extra", fecha: hoy(), notas: "", moneda: extraEmp.moneda_sueldo || extraEmp.moneda || "PYG", tipo_cambio: "" });
      await cargarAdelantosExtrasDeEmp(extraEmp.id);
      fetchSueldosVenc(filtroMes);
    } catch (e) { toast.error(e.message); }
    finally { setSavingExtra(false); }
  };
  const handleDeleteExtra = async (id) => {
    if (!window.confirm("¿Eliminar extra?")) return;
    try {
      const res = await fetch(`${API}/admin/extras/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setExtrasPeriodo(prev => prev.filter(e => e.id !== id));
      toast.success("Extra eliminado");
      fetchSueldosVenc(filtroMes);
    } catch { toast.error("Error al eliminar"); }
  };



  // ── IVA ──────────────────────────────────────────────────────────────────────
	  const fetchIvaData = async () => {
	    setLoadingIva(true);
	    try {
	      const logo = activeEmpresaPropia?.slug;
	      const deletedParams = new URLSearchParams();
	      if (logo) deletedParams.set("logo_tipo", logo);
	      if (mostrarEliminadasIva) deletedParams.set("incluir_eliminadas", "true");
	      if (mostrarEliminadasIva) {
	        if (filtroTipo === "mes") deletedParams.set("periodo", periodoIva);
	        if (filtroTipo === "anio") deletedParams.set("anio", filtroAnio);
	        const pagosRes = await fetch(`${API}/admin/balance/iva/pagos?${deletedParams}`, { headers: { Authorization: `Bearer ${token}` } });
	        if (pagosRes.ok) {
	          const pagos = await pagosRes.json();
	          setPagosIvaList(pagos.filter(p => p.eliminada));
	        }
	      }
	      if (filtroTipo === "mes") {
	        const ivaRes = await fetch(`${API}/admin/balance/iva?periodo=${periodoIva}${logo ? `&logo_tipo=${logo}` : ""}`, { headers: { Authorization: `Bearer ${token}` } });
	        if (ivaRes.ok) {
	          const iva = await ivaRes.json();
	          setIvaBalance(iva);
	          setIvaResumen(null);
	          if (!mostrarEliminadasIva) setPagosIvaList(iva.pagos_iva_detalle || []);
	          if (!mostrarEliminadasIva) setAjustesIvaList(iva.ajustes_iva_detalle || []);
	        }
	      } else {
	        const params = new URLSearchParams();
	        if (filtroTipo === "anio") params.set("anio", filtroAnio);
	        if (filtroTipo === "todos") params.set("todo", "true");
	        if (logo) params.set("logo_tipo", logo);
	        const res = await fetch(`${API}/admin/balance/iva/resumen?${params}`, { headers: { Authorization: `Bearer ${token}` } });
	        if (res.ok) {
	          const resumen = await res.json();
	          setIvaResumen(resumen);
	          setIvaBalance(null);
	          if (!mostrarEliminadasIva) setPagosIvaList((resumen.meses || []).flatMap(m => m.pagos_iva_detalle || []));
	          if (!mostrarEliminadasIva) setAjustesIvaList((resumen.meses || []).flatMap(m => m.ajustes_iva_detalle || []));
	        }
	      }
	    } catch (e) {}
	    finally { setLoadingIva(false); }
	  };

	  const handlePagoIva = async (e) => {
	    e.preventDefault();
	    const monto = parseFloat(pagoIvaForm.monto) || 0;
	    if (monto <= 0) { toast.error("Ingresá el monto del pago IVA"); return; }
	    if (!pagoIvaForm.periodo_iva) { toast.error("Seleccioná el período IVA"); return; }
	    if (!pagoIvaForm.fecha_pago) { toast.error("Seleccioná la fecha de pago"); return; }
	    if (!pagoIvaForm.cuenta_id) { toast.error("Seleccioná la cuenta bancaria"); return; }
	    const cuenta = cuentasDisp.find(c => c.id === pagoIvaForm.cuenta_id);
	    try {
	      const payload = {
	        descripcion: pagoIvaForm.descripcion || `Pago IVA ${pagoIvaForm.periodo_iva}`,
	        periodo_iva: pagoIvaForm.periodo_iva,
	        fecha_pago: pagoIvaForm.fecha_pago,
	        monto,
	        logo_tipo: activeEmpresaPropia?.slug || "arandujar",
	        cuenta_id: pagoIvaForm.cuenta_id,
	        cuenta_nombre: cuenta?.nombre || pagoIvaForm.cuenta_nombre || null,
	        notas: pagoIvaForm.notas || null,
	      };
	      const url = editingPagoIva ? `${API}/admin/balance/iva/pagos/${editingPagoIva.id}` : `${API}/admin/balance/iva/pagos`;
	      const res = await fetch(url, {
	        method: editingPagoIva ? "PUT" : "POST",
	        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
	        body: JSON.stringify(payload),
	      });
	      if (!res.ok) {
	        const err = await res.json().catch(() => ({}));
	        throw new Error(err.detail || "Error al guardar pago IVA");
	      }
	      toast.success(editingPagoIva ? "Pago IVA actualizado" : "Pago IVA registrado");
	      setShowPagoIvaForm(false);
	      setEditingPagoIva(null);
	      setPagoIvaForm({ descripcion: "", monto: "", fecha_pago: hoy(), periodo_iva: periodoIva, cuenta_id: "", cuenta_nombre: "", notas: "" });
	      fetchIvaData();
	    } catch (err) {
	      toast.error(err.message || "Error al guardar pago IVA");
	    }
	  };

	  const openNewPagoIva = () => {
	    const cuentaDefault = cuentasDisp.find(c => c.moneda === "PYG" && c.activa !== false) || cuentasDisp[0];
	    const sugerido = ivaBalance?.saldo_pendiente_acumulado > 0
	      ? ivaBalance.saldo_pendiente_acumulado
	      : Math.max(0, ivaBalance?.saldo_mes ?? ivaBalance?.iva_neto ?? 0);
	    setEditingPagoIva(null);
	    setPagoIvaForm({
	      descripcion: `Pago IVA ${periodoIva}`,
	      monto: sugerido ? String(Math.round(sugerido)) : "",
	      fecha_pago: hoy(),
	      periodo_iva: periodoIva,
	      cuenta_id: cuentaDefault?.id || "",
	      cuenta_nombre: cuentaDefault?.nombre || "",
	      notas: "",
	    });
	    setShowPagoIvaForm(true);
	  };

	  const openEditPagoIva = (pago) => {
	    setEditingPagoIva(pago);
	    setPagoIvaForm({
	      descripcion: pago.descripcion || `Pago IVA ${pago.periodo_iva || periodoIva}`,
	      monto: String(Math.abs(pago.monto || 0)),
	      fecha_pago: pago.fecha_pago || hoy(),
	      periodo_iva: pago.periodo_iva || periodoIva,
	      cuenta_id: pago.cuenta_id || "",
	      cuenta_nombre: pago.cuenta_nombre || "",
	      notas: pago.notas || "",
	    });
	    setShowPagoIvaForm(true);
	  };

	  const handleDeletePagoIva = async (pagoId) => {
	    if (!window.confirm("¿Eliminar este pago IVA?")) return;
	    const res = await fetch(`${API}/admin/balance/iva/pagos/${pagoId}`, {
	      method: "DELETE",
	      headers: { Authorization: `Bearer ${token}` },
	    });
	    if (res.ok) {
	      toast.success("Pago IVA eliminado");
	      fetchIvaData();
	    } else {
	      toast.error("Error al eliminar pago IVA");
	    }
	  };

	  // ── Ajuste de IVA ────────────────────────────────────────────────────────
	  const openNewAjusteIva = () => {
	    setEditingAjusteIva(null);
	    setAjusteIvaForm({ motivo: "", monto: "", fecha: hoy(), periodo_iva: periodoIva, notas: "" });
	    setShowAjusteIvaForm(true);
	  };

	  const handleAjusteIva = async (e) => {
	    e.preventDefault();
	    const monto = parseFloat(ajusteIvaForm.monto) || 0;
	    if (monto === 0) { toast.error("Ingresá el monto del ajuste (positivo suma al IVA a pagar, negativo lo resta)"); return; }
	    if (!ajusteIvaForm.periodo_iva) { toast.error("Seleccioná el período IVA"); return; }
	    if (!ajusteIvaForm.fecha) { toast.error("Seleccioná la fecha"); return; }
	    if (!ajusteIvaForm.motivo.trim()) { toast.error("Indicá el motivo del ajuste (ej: diferencia informada por el contador)"); return; }
	    try {
	      const payload = {
	        motivo: ajusteIvaForm.motivo,
	        periodo_iva: ajusteIvaForm.periodo_iva,
	        fecha: ajusteIvaForm.fecha,
	        monto,
	        logo_tipo: activeEmpresaPropia?.slug || "arandujar",
	        notas: ajusteIvaForm.notas || null,
	      };
	      const url = editingAjusteIva ? `${API}/admin/balance/iva/ajustes/${editingAjusteIva.id}` : `${API}/admin/balance/iva/ajustes`;
	      const res = await fetch(url, {
	        method: editingAjusteIva ? "PUT" : "POST",
	        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
	        body: JSON.stringify(payload),
	      });
	      if (!res.ok) {
	        const err = await res.json().catch(() => ({}));
	        throw new Error(err.detail || "Error al guardar el ajuste de IVA");
	      }
	      toast.success(editingAjusteIva ? "Ajuste de IVA actualizado" : "Ajuste de IVA registrado");
	      setShowAjusteIvaForm(false);
	      setEditingAjusteIva(null);
	      setAjusteIvaForm({ motivo: "", monto: "", fecha: hoy(), periodo_iva: periodoIva, notas: "" });
	      fetchIvaData();
	    } catch (err) {
	      toast.error(err.message || "Error al guardar el ajuste de IVA");
	    }
	  };

	  const handleDeleteAjusteIva = async (ajusteId) => {
	    if (!window.confirm("¿Eliminar este ajuste de IVA?")) return;
	    const res = await fetch(`${API}/admin/balance/iva/ajustes/${ajusteId}`, {
	      method: "DELETE",
	      headers: { Authorization: `Bearer ${token}` },
	    });
	    if (res.ok) {
	      toast.success("Ajuste de IVA eliminado");
	      fetchIvaData();
	    } else {
	      toast.error("Error al eliminar el ajuste de IVA");
	    }
	  };

  useEffect(() => {
    if (tab === "compras") { fetchCompras(); fetchNotasCompra(); }
    if (tab === "costos") { fetchCostos(); fetchVencimientos(filtroMes, filtroTipo, filtroAnio); }
    if (tab === "sueldos") { fetchEmpleados(); fetchSueldosVenc(filtroMes); }
    if (tab === "pago-iva") fetchIvaData();
  }, [filtroDia, filtroMes, filtroTipo, filtroAnio, activeEmpresaPropia?.slug, mostrarEliminadasCompras, mostrarEliminadasCostos, mostrarEliminadasIva]); // eslint-disable-line

  useEffect(() => {
    if (tab === "pago-iva") fetchIvaData();
  }, [periodoIva, mostrarEliminadasIva]); // eslint-disable-line

  // ── Logos accesibles para selector ─────────────────────────────────────────
  const logosAccesibles = isAdmin
    ? empresasPropias
    : empresasPropias.filter(ep => (user?.logos_asignados || []).map(String).includes(String(ep.id)));

  // ── Compras CRUD ────────────────────────────────────────────────────────────
  const fetchPresupuestosForm = async (logoOverride) => {
    if (!hasModule?.("presupuestos")) return;
    try {
      const logo = logoOverride || activeEmpresaPropia?.slug;
      const params = logo ? `?logo_tipo=${logo}` : "";
      const res = await fetch(`${API}/admin/presupuestos${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPresupuestosForm(
        (await res.json()).filter(p => !p.eliminada && ["aprobado", "cobrado", "facturado"].includes(p.estado))
      );
    } catch {}
  };

  const fetchPresupuestosDisp = async ({ logo, proveedor_id, proveedor_nombre, incluir }) => {
    if (!hasModule?.("presupuestos")) return;
    try {
      const qs = new URLSearchParams();
      if (logo) qs.set("logo_tipo", logo);
      if (proveedor_id) qs.set("proveedor_id", proveedor_id);
      if (proveedor_nombre) qs.set("proveedor_nombre", proveedor_nombre);
      if (incluir?.length) qs.set("incluir_ids", incluir.join(","));
      const res = await fetch(`${API}/admin/presupuestos/para-compra?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setPresupuestosDisp(res.ok ? await res.json() : []);
    } catch { setPresupuestosDisp([]); }
  };

  // Abre la confirmación: qué ítems de la compra cubren qué renglones del presupuesto
  const abrirConfirmVinculo = (pres, modo) => {
    const compraItems = (modo === "form" ? (compraForm.items || []) : (selectedCompraView?.items || []))
      .map((it, i) => ({ ...it, _i: i })).filter(it => it.descripcion || it.producto_id);
    const disp = (pres.items_proveedor || []).filter(x => x.pendiente_comprar > 0);
    const map = {};
    compraItems.forEach(ci => {
      const ref = ci.presupuesto_item_ref;
      if (ref && ref.presupuesto_id === pres.id) { map[ci._i] = String(ref.idx); return; }
      let m = null;
      if (ci.producto_id) m = disp.find(x => x.producto_id === ci.producto_id && (x.variante_id || "") === (ci.variante_id || ""));
      if (!m) {
        const d = (ci.descripcion || "").trim().toLowerCase();
        if (d) m = disp.find(x => (x.descripcion || "").trim().toLowerCase() === d);
      }
      map[ci._i] = m ? String(m.idx) : "";
    });
    setVincMap(map);
    setVincConfirm({ pres, modo, compraItems, disp });
  };

  // Al elegir un presupuesto en una compra nueva/en edición: se vincula y sus ítems aprobados
  // pendientes de comprar se cargan solos (sin pantalla de confirmación).
  const seleccionarPresupuestoForm = (pres) => {
    const disp = (pres.items_proveedor || []).filter(x => Number(x.pendiente_comprar) > 0);
    if (disp.length === 0) {
      setCompraForm(prev => ({ ...prev, presupuesto_ids: Array.from(new Set([...(prev.presupuesto_ids || []), pres.id])) }));
      toast.info("El presupuesto no tiene renglones aprobados pendientes de comprar");
      return;
    }
    const nuevos = disp.map(x => {
      const cantidad = Number(x.pendiente_comprar) || 0;
      const precio = Number(x.precio_costo) > 0 ? Number(x.precio_costo) : Number(x.costo) > 0 ? Number(x.costo) : Number(x.precio_unitario || 0);
      return {
        ...emptyItem(),
        descripcion: x.descripcion || "",
        cantidad,
        precio_unitario: precio,
        subtotal: cantidad * precio,
        iva: x.iva_tipo === "exenta" ? 0 : x.iva_tipo === "5" ? 5 : 10,
        producto_id: x.producto_id || "",
        producto_sku: x.producto_sku || "",
        variante_id: x.variante_id || "",
        variante_texto: x.variante_texto || "",
        presupuesto_item_ref: { presupuesto_id: pres.id, idx: x.idx },
      };
    });
    setCompraForm(prev => {
      const prevItems = (prev.items || []).filter(i => i.descripcion || i.producto_id);
      const sinDuplicar = prevItems.filter(i => !i.presupuesto_item_ref || !nuevos.some(n => n.presupuesto_item_ref.presupuesto_id === i.presupuesto_item_ref.presupuesto_id && n.presupuesto_item_ref.idx === i.presupuesto_item_ref.idx));
      return {
        ...prev,
        presupuesto_ids: Array.from(new Set([...(prev.presupuesto_ids || []), pres.id])),
        items: [...sinDuplicar, ...nuevos],
        afecta_stock: nuevos.some(n => n.producto_id) ? true : prev.afecta_stock,
      };
    });
    toast.success(`${nuevos.length} ítem(s) del presupuesto agregados a la compra`);
  };

  const confirmarVinculo = async () => {
    if (!vincConfirm) return;
    const { pres, modo, compraItems } = vincConfirm;
    const refs = compraItems.filter(ci => vincMap[ci._i] !== "" && vincMap[ci._i] != null)
      .map(ci => ({ item_idx: ci._i, presupuesto_id: pres.id, idx: Number(vincMap[ci._i]) }));
    if (refs.length === 0) { toast.error("Indicá qué ítem de la compra cubre el presupuesto (o cancelá)"); return; }
    if (modo === "form") {
      setCompraForm(prev => ({
        ...prev,
        presupuesto_ids: Array.from(new Set([...(prev.presupuesto_ids || []), pres.id])),
        items: (prev.items || []).map((it, i) => {
          const r = refs.find(x => x.item_idx === i);
          return r ? { ...it, presupuesto_item_ref: { presupuesto_id: pres.id, idx: r.idx } } : it;
        }),
      }));
    } else {
      const ids = Array.from(new Set([...(selectedCompraView?.presupuesto_ids || []), pres.id]));
      await handleVincularPresupuestoCompra(ids, refs);
    }
    setVincConfirm(null);
  };

  const openNewCompra = () => {
    const logo = activeEmpresaPropia?.slug || "arandujar";
    setTcCompraInfo(null);
    const base = { ...emptyCompra(), logo_tipo: logo, afecta_stock: !!puedeUsarStockProductos };
    // Si los chips coinciden con exactamente un proveedor, pre-llenamos
    if (comprasChips.length > 0 && proveedores.length > 0) {
      const chipsLower = comprasChips.map(c => c.toLowerCase());
      const matched = proveedores.filter(p => chipsLower.some(chip => (p.nombre || "").toLowerCase().includes(chip)));
      if (matched.length === 1) {
        base.proveedor_id = matched[0].id || "";
        base.proveedor_nombre = matched[0].nombre || "";
        base.proveedor_ruc = matched[0].ruc || "";
      }
    }
    setCompraForm(base);
    setEditingCompra(null);
    setPresupuestoSearch("");
    setModoCompra(puedeUsarStockProductos ? "catalogo" : "libre");
    fetchProveedores(logo);
    fetchPresupuestosForm(logo);
    setShowCompraForm(true);
    fetchCuentasDisp();
    fetch(`${API}/admin/config-egresos/pago-contado?logo_tipo=${encodeURIComponent(logo)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(c => setPagoAutoCfg(c || { pago_automatico_contado: false, cuenta_pago_automatico_id: null }))
      .catch(() => setPagoAutoCfg({ pago_automatico_contado: false, cuenta_pago_automatico_id: null }));
    // Entrega posterior preseleccionada por defecto según la configuración de la empresa
    if (puedeUsarStockProductos) {
      fetch(`${API}/admin/entregas/config?logo_tipo=${encodeURIComponent(logo)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(cfg => { if (cfg?.entrega_posterior_default_compras) setCompraForm(p => ({ ...p, entrega_posterior: true })); })
        .catch(() => {});
    }
  };

  const openEditCompra = async (c) => {
    setTcCompraInfo(null);
    // El listado de Compras no trae 'items' (se excluye a propósito por
    // performance en GET /admin/compras). Si "c" viene de una fila de esa
    // lista (p.ej. el lápiz de editar), hay que pedir el detalle completo
    // antes de precargar el formulario — si no, se abre con items: [] y
    // guardar así borra todos los ítems de la compra real.
    if (!Array.isArray(c.items)) {
      try {
        const res = await fetch(`${API}/admin/compras/${c.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) c = await res.json();
      } catch {}
    }
    setCompraForm({
      grupo_constructora_id: c.grupo_constructora_id || "",
      logo_tipo: c.logo_tipo || activeEmpresaPropia?.slug || "arandujar",
      proveedor_id: c.proveedor_id || "",
      proveedor_nombre: c.proveedor_nombre || "",
      proveedor_ruc: c.proveedor_ruc || "",
      crear_proveedor: false,
      fecha: c.fecha || hoy(),
      tipo_pago: c.tipo_pago || "contado",
      tiene_factura: c.tiene_factura || false,
      factura_electronica: c.factura_electronica || false,
      numero_factura: c.numero_factura || "",
      nro_timbrado: c.nro_timbrado || "",
      fecha_vigencia_timbrado: c.fecha_vigencia_timbrado || "",
      tipo_comprobante: c.tipo_comprobante || "normal",
      solo_iva: !!c.solo_iva,
      monto_total: c.monto_total ?? "",
      moneda: c.moneda || "PYG",
      tipo_cambio: c.tipo_cambio || "",
      descuento_modo: c.descuento_modo || (Number(c.total_descuentos || 0) > 0 ? "global" : "none"),
      descuento_global_tipo: c.descuento_global_pct ? "pct" : "monto",
      descuento_global_monto: c.descuento_global_monto ?? c.total_descuentos ?? "",
      descuento_global_pct: c.descuento_global_pct ?? "",
      cuenta_id: "",
      cuenta_nombre: "",
      plan_cuenta_id: c.plan_cuenta_id || "",
      plan_cuenta_nombre: c.plan_cuenta_nombre || "",
      items: (c.items || []).map(it => ({ ...it, iva: it.iva ?? 10 })),
      afecta_stock: !!puedeUsarStockProductos && (puedeCambiarAfectaStockCompra ? c.afecta_stock !== false : true),
      entrega_posterior: !!c.entrega_posterior,
      notas: c.notas || "",
      fecha_vencimiento: c.fecha_vencimiento || "",
      fecha_pago: "",
      presupuesto_ids: c.presupuesto_ids || (c.presupuesto_id ? [c.presupuesto_id] : []),
    });
    // El modo (Catálogo/Libre) debe reflejar los ítems reales de la compra que se está editando,
    // nunca quedar con el valor que haya quedado de una compra anterior — si no, el toggle
    // aparece "mal marcado" y tocarlo vacía los ítems ya cargados.
    const itemsTienenProducto = (c.items || []).some(it => !!it.producto_id);
    setModoCompra(puedeUsarStockProductos && itemsTienenProducto ? "catalogo" : "libre");
    setEditingCompra(c);
    setPresupuestoSearch("");
    fetchProveedores(c.logo_tipo || activeEmpresaPropia?.slug || "arandujar");
    fetchPresupuestosForm(c.logo_tipo || activeEmpresaPropia?.slug);
    setShowCompraForm(true);
  };

  const openFacturaCompra = (c) => {
    if (c?.tiene_factura || c?.numero_factura) {
      toast.error("Esta compra ya tiene factura. Para cambiarla, eliminá la compra y volvé a registrarla.");
      return;
    }
    setFacturaCompraTarget(c);
    setFacturaCompraForm({
      factura_electronica: !!c.factura_electronica,
      numero_factura: c.numero_factura || "",
      nro_timbrado: c.nro_timbrado || "",
      fecha_vigencia_timbrado: c.fecha_vigencia_timbrado || "",
    });
  };

  const handleSaveFacturaCompra = async (e) => {
    e.preventDefault();
    if (!facturaCompraTarget?.id) return;
    if (!String(facturaCompraForm.numero_factura || "").trim()) {
      toast.error("Ingresá el número de factura");
      return;
    }
    if (!String(facturaCompraForm.nro_timbrado || "").trim()) {
      toast.error("Ingresá el número de timbrado");
      return;
    }
    if (!facturaCompraForm.factura_electronica && !facturaCompraForm.fecha_vigencia_timbrado) {
      toast.error("Ingresá la vigencia del timbrado");
      return;
    }
    setSavingFacturaCompra(true);
    try {
      const payload = {
        tiene_factura: true,
        factura_electronica: !!facturaCompraForm.factura_electronica,
        numero_factura: String(facturaCompraForm.numero_factura || "").trim(),
        nro_timbrado: String(facturaCompraForm.nro_timbrado || "").trim(),
        fecha_vigencia_timbrado: facturaCompraForm.factura_electronica ? null : facturaCompraForm.fecha_vigencia_timbrado,
      };
      const res = await fetch(`${API}/admin/compras/${facturaCompraTarget.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo guardar la factura");
      toast.success("Factura cargada en la compra");
      setFacturaCompraTarget(null);
      setCompras(prev => prev.map(c => c.id === data.id ? data : c));
      if (selectedCompraView?.id === data.id) setSelectedCompraView(data);
      fetchCompras(true);
    } catch (err) {
      toast.error(err.message || "Error al guardar factura");
    } finally {
      setSavingFacturaCompra(false);
    }
  };

  useEffect(() => {
    if (showCompraForm && proyectoId) cargarInsumosObra();
  }, [showCompraForm, proyectoId]); // eslint-disable-line

  useEffect(() => {
    if (!showCompraForm) return;
    fetchPresupuestosDisp({
      logo: compraForm.logo_tipo || activeEmpresaPropia?.slug,
      proveedor_id: compraForm.proveedor_id, proveedor_nombre: compraForm.proveedor_nombre,
      incluir: compraForm.presupuesto_ids || [],
    });
  }, [showCompraForm, compraForm.proveedor_id, compraForm.logo_tipo]); // eslint-disable-line

  const handleVincularPresupuestoCompra = async (nuevoIds, itemRefs) => {
    if (!selectedCompraView) return;
    setVincularPresLoading(true);
    try {
      const res = await fetch(`${API}/admin/compras/${selectedCompraView.id}/presupuesto`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ presupuesto_ids: nuevoIds, item_refs: itemRefs || null }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setSelectedCompraView(updated);
      setCompras(prev => prev.map(c => c.id === updated.id ? updated : c));
      toast.success(nuevoIds?.length ? `${nuevoIds.length} presupuesto(s) vinculado(s)` : "Presupuestos desvinculados");
    } catch {
      toast.error("Error al actualizar el presupuesto");
    } finally {
      setVincularPresLoading(false);
    }
  };

  const handleSaveCompra = async (e) => {
    e.preventDefault();
    const tieneItems = (compraForm.items || []).length > 0;
    const subtotalBrutoCompra = compraSubtotalBruto(compraForm.items || []);
    const descuentoGlobalCompra = compraDescuentoGlobal(compraForm);
    const itemsNorm = compraItemsConDescuentoGlobal(compraForm);
    const montoFinal = tieneItems ? itemsNorm.reduce((s, it) => s + (parseFloat(it.subtotal) || 0), 0) : 0;
    if (!compraForm.proveedor_id) {
      toast.error("Seleccioná un proveedor");
      return;
    }
    if (!tieneItems) {
      toast.error("Agregá al menos un ítem a la compra");
      return;
    }
    if (montoFinal <= 0) {
      toast.error("El monto total debe ser mayor a 0");
      return;
    }
    if ((descuentoGlobalCompra > 0 || compraTotalDescuentosItems(compraForm) > 0) && !puedeDescuentosCompra) {
      toast.error("No tenés permiso para aplicar descuentos en compras");
      return;
    }
    if (compraForm.tiene_factura && !String(compraForm.numero_factura || "").trim()) {
      toast.error("El número de factura es obligatorio cuando tiene factura");
      return;
    }
    if (compraForm.tiene_factura && !compraForm.nro_timbrado) {
      toast.error("El número de timbrado es obligatorio cuando tiene factura");
      return;
    }
    if (compraForm.tiene_factura && !compraForm.factura_electronica && !compraForm.fecha_vigencia_timbrado) {
      toast.error("La vigencia del timbrado es obligatoria cuando tiene factura");
      return;
    }
    if (!compraForm.solo_iva && !compraForm.plan_cuenta_id) {
      toast.error("Seleccioná una cuenta del plan de cuentas");
      return;
    }
    if (pagoAutoActivo) {
      if (!compraForm.cuenta_id) { toast.error("Elegí la cuenta desde la que se paga la compra"); return; }
      if (pagoAutoNecesitaTc && !(Number(compraForm.tipo_cambio_pago) > 0)) { toast.error("Ingresá el tipo de cambio del pago (la cuenta es de otra moneda)"); return; }
    }
    if (!compraForm.solo_iva && compraForm.es_credito_cuotas && compraForm.cuotas_manual_activo) {
      const filas = compraForm.cuotas_manual || [];
      if (filas.length === 0 || filas.some(f => !f.fecha_vencimiento || !(Number(f.monto) > 0))) {
        toast.error("Completá el monto y el vencimiento de cada cuota");
        return;
      }
    } else if (!compraForm.solo_iva && compraForm.es_credito_cuotas && !compraForm.fecha_primer_vencimiento) {
      toast.error("Ingresá la fecha del primer vencimiento para las cuotas");
      return;
    }
    if (puedeUsarStockProductos && modoCompra === "catalogo" && (compraForm.items || []).some(it => !it.producto_id)) {
      toast.error("Todos los ítems deben estar vinculados al catálogo. Usá 'Buscar producto' o el escáner de código de barras.");
      return;
    }
    if (puedeUsarStockProductos && modoCompra === "catalogo" && (compraForm.items || []).some(it => {
      const prod = productos.find(p => String(p.id) === String(it.producto_id));
      return prod?.variantes_habilitadas && !it.variante_id;
    })) {
      toast.error("Hay productos con variantes sin variante seleccionada");
      return;
    }
    const totalIvaAuto = itemsNorm.reduce((s, it) => s + calcItemIva(it, compraForm.moneda), 0);
    const payload = {
      ...compraForm,
      items: itemsNorm,
      monto_total: montoFinal,
      subtotal_bruto: subtotalBrutoCompra,
      descuento_modo: descuentoGlobalCompra > 0 ? "global" : "none",
      descuento_global_monto: descuentoGlobalCompra > 0 ? descuentoGlobalCompra : 0,
      descuento_global_pct: (compraForm.descuento_global_tipo === "pct" && descuentoGlobalCompra > 0) ? Number(compraForm.descuento_global_pct || 0) : 0,
      total_descuentos: descuentoGlobalCompra,
      monto_iva: totalIvaAuto > 0 ? totalIvaAuto : null,
      tasa_iva: null, // ahora es por ítem
      afecta_stock: compraForm.solo_iva ? false : (!!puedeUsarStockProductos && (puedeCambiarAfectaStockCompra ? compraForm.afecta_stock : true)),
      ...(!editingCompra && depos.activo && depos.cfg.modo === "por_compra" && compraForm.deposito_id ? { deposito_id: compraForm.deposito_id } : {}),
      entrega_posterior: !compraForm.solo_iva && !!puedeUsarStockProductos && modoCompra === "catalogo" && !!compraForm.entrega_posterior,
      cuenta_id: pagoAutoActivo ? (compraForm.cuenta_id || null) : null,
      cuenta_nombre: pagoAutoActivo ? (compraForm.cuenta_nombre || null) : null,
      tipo_cambio_pago: pagoAutoNecesitaTc && compraForm.tipo_cambio_pago ? Number(compraForm.tipo_cambio_pago) : null,
      plan_cuenta_id: compraForm.solo_iva ? null : compraForm.plan_cuenta_id,
      plan_cuenta_nombre: compraForm.solo_iva ? null : compraForm.plan_cuenta_nombre,
      es_credito_cuotas: !compraForm.solo_iva && compraForm.es_credito_cuotas,
      num_cuotas: (!compraForm.solo_iva && compraForm.es_credito_cuotas) ? Number(compraForm.num_cuotas || 1) : null,
      fecha_primer_vencimiento: (!compraForm.solo_iva && compraForm.es_credito_cuotas) ? (compraForm.fecha_primer_vencimiento || null) : null,
      tasa_interes_cuotas: (!compraForm.solo_iva && compraForm.es_credito_cuotas) ? Number(compraForm.tasa_interes_cuotas || 0) : null,
      // Si el usuario cargó las cuotas a mano, mandamos esa lista tal cual — el
      // backend usa esto en vez de dividir el total parejo entre num_cuotas.
      cuotas_manual: (!compraForm.solo_iva && compraForm.es_credito_cuotas && compraForm.cuotas_manual_activo)
        ? (compraForm.cuotas_manual || []).map((f, i) => ({ numero: i + 1, monto: Number(f.monto) || 0, fecha_vencimiento: f.fecha_vencimiento }))
        : null,
      fecha_pago: null,
      tipo_cambio: compraForm.tipo_cambio !== "" ? Number(compraForm.tipo_cambio) : null,
      numero_factura: compraForm.tiene_factura ? String(compraForm.numero_factura || "").trim() : null,
      presupuesto_ids: compraForm.presupuesto_ids || [],
      ...(proyectoId ? {
        proyecto_constructora_id: proyectoId,
        proyecto_constructora_nombre: proyectoCtx?.nombre || null,
        grupo_constructora_id: compraForm.grupo_constructora_id || null,
        grupo_constructora_nombre: gruposConstructora.find(g => g.id === compraForm.grupo_constructora_id)?.nombre || null,
      } : {}),
      factura_electronica: compraForm.tiene_factura ? !!compraForm.factura_electronica : false,
      nro_timbrado: compraForm.tiene_factura ? (compraForm.nro_timbrado || null) : null,
      fecha_vigencia_timbrado: (compraForm.tiene_factura && !compraForm.factura_electronica) ? (compraForm.fecha_vigencia_timbrado || null) : null,
      anticipos_aplicados: editingCompra ? [] : ((compraForm.anticipos_aplicados || []).filter(a => a.monto_aplicado > 0)),
    };
    try {
      const url = editingCompra ? `${API}/admin/compras/${editingCompra.id}` : `${API}/admin/compras`;
      const res = await fetch(url, {
        method: editingCompra ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const saved = await res.json().catch(() => ({}));
        if (!editingCompra && saved?.pago_automatico) toast.success("Compra registrada y pagada automáticamente");
        else if (!editingCompra && saved?.pago_automatico_error) { toast.success("Compra registrada"); toast.warning(saved.pago_automatico_error); }
        else toast.success(editingCompra ? "Compra actualizada" : "Compra registrada");
        setShowCompraForm(false);
        fetchCompras(true);
        // Si esta compra vino de "Registrar Compra" de un Pesaje de Báscula,
        // la vinculamos de vuelta al pesaje.
        if (pesajeBasculaCompra?.id && saved?.id) {
          try {
            await fetch(`${API}/admin/bascula/pesajes/${pesajeBasculaCompra.id}/vincular-compra`, {
              method: "PATCH",
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
              body: JSON.stringify({ compra_id: saved.id, compra_numero: saved.numero }),
            });
          } catch { /* no bloquea: la compra ya se guardó */ }
          setPesajeBasculaCompra(null);
        }
        // Si la compra vino de "Liquidar al chofer" (Transporte), dejamos las
        // órdenes de carga marcadas como liquidadas y vinculadas a esta compra.
        if (ordenesTransporteCompra.length && saved?.id) {
          try {
            await fetch(`${API}/admin/transporte/ordenes/vincular-compra-lote`, {
              method: "PATCH",
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
              body: JSON.stringify({ orden_ids: ordenesTransporteCompra.map(o => o.id), compra_id: saved.id, compra_numero: saved.numero }),
            });
          } catch { /* no bloquea: la compra ya se guardó */ }
          setOrdenesTransporteCompra([]);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al guardar");
      }
    } catch (e) { toast.error("Error de conexión"); }
  };

  // ── Anticipos helpers ────────────────────────────────────────────────────────
  const fetchAnticiposProveedor = async (proveedorId) => {
    if (!proveedorId) { setAnticiposDisponibles([]); return; }
    setLoadingAnticipos(true);
    try {
      const q = new URLSearchParams({ logo_tipo: compraForm.logo_tipo || activeEmpresaPropia?.slug || "" });
      const res = await fetch(`${API}/admin/anticipos-proveedores/disponibles/${proveedorId}?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAnticiposDisponibles(await res.json());
    } catch { /* silencioso */ }
    finally { setLoadingAnticipos(false); }
  };

  const fetchAnticiposList = async () => {
    if (!hasPermission("anticipos_proveedores.ver")) return;
    setLoadingAnticiposList(true);
    try {
      const q = new URLSearchParams({ logo_tipo: activeEmpresaPropia?.slug || "" });
      if (anticiposFilter !== "todos") q.set("estado", anticiposFilter);
      const res = await fetch(`${API}/admin/anticipos-proveedores?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAnticiposList(await res.json());
    } catch { /* silencioso */ }
    finally { setLoadingAnticiposList(false); }
  };

  const handleSaveAnticipo = async (e) => {
    e.preventDefault();
    if (!anticipoForm.proveedor_id) return toast.error("Seleccioná el proveedor");
    if (!anticipoForm.monto || Number(anticipoForm.monto) <= 0) return toast.error("El monto debe ser mayor a 0");
    if (!anticipoForm.cuenta_id) return toast.error("Seleccioná la cuenta bancaria");
    if (!planCuentaAnticipoProveedor?.id) return toast.error("No se encontró la cuenta título 'Anticipos a proveedores' en el plan de cuentas");
    if (anticipoForm.moneda !== "PYG" && !anticipoForm.tipo_cambio) return toast.error("El tipo de cambio es obligatorio para monedas distintas al guaraní");
    setSavingAnticipo(true);
    try {
      const payload = {
        logo_tipo: activeEmpresaPropia?.slug || "arandujar",
        proveedor_id: anticipoForm.proveedor_id,
        proveedor_nombre: anticipoForm.proveedor_nombre,
        proveedor_ruc: anticipoForm.proveedor_ruc || null,
        monto: Number(anticipoForm.monto),
        moneda: anticipoForm.moneda,
        tipo_cambio: anticipoForm.moneda !== "PYG" ? Number(anticipoForm.tipo_cambio) : null,
        fecha: anticipoForm.fecha,
        cuenta_id: anticipoForm.cuenta_id,
        cuenta_nombre: anticipoForm.cuenta_nombre || "",
        plan_cuenta_id: planCuentaAnticipoProveedor.id,
        plan_cuenta_nombre: planCuentaAnticipoProveedor.nombre,
        notas: anticipoForm.notas || null,
      };
      const res = await fetch(`${API}/admin/anticipos-proveedores`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Anticipo registrado");
        setAnticipoForm(emptyAnticipoForm());
        fetchAnticiposList();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al registrar anticipo");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSavingAnticipo(false); }
  };

  const handleDeleteAnticipo = async (id) => {
    if (!window.confirm("¿Eliminar este anticipo?")) return;
    try {
      const res = await fetch(`${API}/admin/anticipos-proveedores/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Anticipo eliminado"); fetchAnticiposList(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "No se puede eliminar"); }
    } catch { toast.error("Error de conexión"); }
  };

  // Cuando cambia el proveedor en el form de compra → cargar sus anticipos disponibles
  useEffect(() => {
    if (showCompraForm && compraForm.proveedor_id) {
      fetchAnticiposProveedor(compraForm.proveedor_id);
    } else {
      setAnticiposDisponibles([]);
      setCompraForm(p => ({ ...p, anticipos_aplicados: [] }));
    }
  }, [showCompraForm, compraForm.proveedor_id]); // eslint-disable-line

  // Cargar lista de anticipos cuando el tab "anticipos" está activo
  useEffect(() => {
    if (tab === "anticipos") fetchAnticiposList();
  }, [tab, anticiposFilter, activeEmpresaPropia?.id]); // eslint-disable-line

  const handleDeleteCompra = async (id) => {
    if (!window.confirm("¿Eliminar esta compra?")) return false;
    try {
      const res = await fetch(`${API}/admin/compras/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        toast.success("Compra eliminada");
        fetchCompras(true);
        return true;
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se puede eliminar esta compra");
        return false;
      }
    } catch (e) { toast.error("Error al eliminar la compra"); return false; }
  };

  const openNewNotaCompra = () => {
    setEditingNotaCompra(null);
    setTcNotaCompraInfo(null);
    setNotaCompraForm({ numero: "", fecha: hoy(), compra_id: "", compra_numero_factura: "", proveedor_id: "", proveedor_nombre: "", tipo_motivo: "descuento", motivo_detalle: "", monto: "", moneda: "PYG", tipo_cambio: "", notas: "", cuenta_id: "", cuenta_nombre: "", items_devueltos: [], tipo_cobro: "saldo_favor" });
    fetchComprasParaNota();
    setShowNotaCompraForm(true);
  };

  const notaCompraProveedorKey = (notaCompraForm.proveedor_id || notaCompraForm.proveedor_nombre || "").trim().toLowerCase();
  const comprasNotaDisponibles = compras.filter(c => {
    if (!c.tiene_factura) return false;
    if (!notaCompraProveedorKey) return true;
    return String(c.proveedor_id || "").toLowerCase() === notaCompraProveedorKey
      || String(c.proveedor_nombre || "").toLowerCase() === notaCompraProveedorKey;
  });

  const openEditNotaCompra = (nota) => {
    setEditingNotaCompra(nota);
    setTcNotaCompraInfo(null);
    setNotaCompraForm({
      numero: nota.numero || "",
      fecha: nota.fecha || hoy(),
      compra_id: nota.compra_id || "",
      compra_numero_factura: nota.compra_numero_factura || "",
      proveedor_id: nota.proveedor_id || "",
      proveedor_nombre: nota.proveedor_nombre || "",
      tipo_motivo: nota.tipo_motivo || "descuento",
      motivo_detalle: nota.motivo_detalle || "",
      monto: String(nota.monto || ""),
      moneda: nota.moneda || "PYG",
      tipo_cambio: nota.tipo_cambio ? String(nota.tipo_cambio) : "",
      notas: nota.notas || "",
      cuenta_id: nota.cuenta_id || "",
      cuenta_nombre: nota.cuenta_nombre || "",
      items_devueltos: nota.items_devueltos || [],
      tipo_cobro: nota.tipo_cobro || "saldo_favor",
    });
    fetchComprasParaNota();
    setShowNotaCompraForm(true);
  };

  const handleSaveNotaCompra = async (e) => {
    e.preventDefault();
    const monto = parseFloat(notaCompraForm.monto) || 0;
    if (!notaCompraForm.numero.trim()) { toast.error("Ingresá el número de nota"); return; }
    if (!notaCompraForm.fecha) { toast.error("Ingresá la fecha"); return; }
    if (!notaCompraForm.proveedor_id && !notaCompraForm.proveedor_nombre.trim()) { toast.error("Seleccioná el proveedor"); return; }
    if (monto <= 0) { toast.error("El monto debe ser mayor a 0"); return; }
    const motivoLabel = notaCompraForm.tipo_motivo === "devolucion" ? "Devolución" : "Descuento";
    const motivoTexto = notaCompraForm.motivo_detalle ? `${motivoLabel} — ${notaCompraForm.motivo_detalle}` : motivoLabel;
    const compraVinculada = compras.find(c => c.id === notaCompraForm.compra_id);
    const compraEsPagada = compraVinculada && compraVinculada.estado_pago === "pagado";
    const payload = {
      ...notaCompraForm,
      tipo: "compra",
      motivo: motivoTexto,
      monto,
      tipo_cambio: notaCompraForm.tipo_cambio ? Number(notaCompraForm.tipo_cambio) : null,
      logo_tipo: activeEmpresaPropia?.slug || "arandujar",
      tipo_motivo: notaCompraForm.tipo_motivo,
      motivo_detalle: notaCompraForm.motivo_detalle || null,
      items_devueltos: notaCompraForm.tipo_motivo === "devolucion" ? notaCompraForm.items_devueltos : [],
      // tipo_cobro solo relevante cuando la compra ya fue pagada
      tipo_cobro: compraEsPagada ? (notaCompraForm.tipo_cobro || "saldo_favor") : null,
    };
    const url = editingNotaCompra ? `${API}/admin/notas-credito/${editingNotaCompra.id}` : `${API}/admin/notas-credito`;
    const res = await fetch(url, {
      method: editingNotaCompra ? "PUT" : "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      toast.success(editingNotaCompra ? "Nota de crédito actualizada" : "Nota de crédito registrada");
      setShowNotaCompraForm(false);
      fetchNotasCompra();
      fetchCompras(true);
      fetchIvaData();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Error al guardar nota");
    }
  };

  const handleDeleteNotaCompra = async (notaId) => {
    if (!window.confirm("¿Eliminar esta nota de crédito?")) return;
    const res = await fetch(`${API}/admin/notas-credito/${notaId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      toast.success("Nota de crédito eliminada");
      fetchNotasCompra();
      fetchCompras(true);
      fetchIvaData();
    } else {
      toast.error("Error al eliminar nota");
    }
  };

  const handlePagoCompra = async (e) => {
    e.preventDefault();
    if (!pagoForm.monto_pagado || !pagoForm.fecha_pago) { toast.error("Completá monto y fecha"); return; }
    if (!pagoForm.cuenta_id) { toast.error("Seleccioná la cuenta bancaria"); return; }
    const cuentaSeleccionada = cuentasDisp.find(c => c.id === pagoForm.cuenta_id);
    const monedaCompra = showPagoModal?.moneda || "PYG";
    // Se necesita TC en ambas direcciones: compra extranjera pagada con cuenta en
    // guaraníes (multiplica), o compra en guaraníes pagada con cuenta extranjera (divide).
    const necesitaTc = !!cuentaSeleccionada?.moneda && monedaCompra !== cuentaSeleccionada.moneda;
    if (necesitaTc && !pagoForm.tipo_cambio) { toast.error("Ingresá el tipo de cambio"); return; }
    try {
      const res = await fetch(`${API}/admin/compras/${showPagoModal.id}/pagos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...pagoForm,
          moneda: monedaCompra,
          monto_pagado: Number(pagoForm.monto_pagado),
          sin_retencion: !!pagoForm.sin_retencion && hasPermission("retenciones.omitir_retencion"),
          tipo_cambio: necesitaTc ? Number(pagoForm.tipo_cambio) : null,
          cuenta_nombre: cuentaSeleccionada?.nombre || pagoForm.cuenta_nombre || null,
        }),
      });
      if (res.ok) {
        toast.success("Pago registrado");
        setShowPagoModal(null);
        setPagoForm({ monto_pagado: "", fecha_pago: hoy(), cuenta_id: "", cuenta_nombre: "", cuenta_moneda: "PYG", tipo_cambio: "", notas: "", sin_retencion: false });
        fetchCompras(true);
      }
    } catch (e) { toast.error("Error"); }
  };

  // Pago de compra con cheque — llamado por ChequeEgresoForm.onSuccess
  const handlePagoCompraConCheque = async (hoja, chequera, tipoCambio) => {
    try {
      const monedaCompra = showPagoModal?.moneda || "PYG";
      const res = await fetch(`${API}/admin/compras/${showPagoModal.id}/pagos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          monto_pagado: hoja.valor,
          moneda: chequera.moneda || monedaCompra,
          tipo_cambio: tipoCambio || null,
          fecha_pago: hoja.fecha_emision,
          cuenta_id: chequera.cuenta_bancaria_id,
          cuenta_nombre: chequera.banco_nombre,
          cheque_hoja_id: hoja.id,
          cheque_numero: `${hoja.serie}${String(hoja.numero).padStart(6, "0")}`,
          notas: `Cheque ${hoja.serie}${String(hoja.numero).padStart(6, "0")}`,
        }),
      });
      if (res.ok) {
        toast.success("Cheque emitido y pago registrado");
        setShowPagoModal(null);
        setPagoMedioCompra("transferencia");
        fetchCompras(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al registrar pago de compra");
        // Relanzar (ya avisado): ChequeEgresoForm revierte el cheque recién
        // emitido si el pago no llegó a registrarse, para no dejarlo
        // huérfano bloqueando el próximo intento.
        throw new Error("__ya_avisado__");
      }
    } catch (e) {
      if (e?.message !== "__ya_avisado__") toast.error("Error al registrar pago");
      throw e;
    }
  };

  // Pago de costo fijo con cheque
  const handlePagoCostoConCheque = async (hoja, chequera, tipoCambio) => {
    const v = showPagoCostoModal;
    try {
      const res = await fetch(`${API}/admin/costos-fijos/${v.costo_id}/pagos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          periodo: v.periodo || filtroMes,
          monto_pagado: hoja.valor,
          moneda: chequera.moneda || v.moneda || "PYG",
          tipo_cambio: tipoCambio || null,
          fecha_pago: hoja.fecha_emision,
          cheque_hoja_id: hoja.id,
          cheque_numero: `${hoja.serie}${String(hoja.numero).padStart(6, "0")}`,
          notas: `Cheque ${hoja.serie}${String(hoja.numero).padStart(6, "0")}`,
          tiene_factura: false,
          cuenta_id: chequera.cuenta_bancaria_id,
          cuenta_nombre: chequera.banco_nombre,
        }),
      });
      if (res.ok) {
        toast.success("Cheque emitido y pago registrado");
        setShowPagoCostoModal(null);
        setPagoMedioCosto("transferencia");
        fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
        fetchCostos();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al registrar pago de costo");
        throw new Error("__ya_avisado__");
      }
    } catch (e) {
      if (e?.message !== "__ya_avisado__") toast.error("Error al registrar pago");
      throw e;
    }
  };

	  // ── Estadísticas por pestaña ───────────────────────────────────────────────
	  const egresoStatCards = (() => {
	    const sumPYG = (items, field = "monto_total") => items.reduce((s, x) => {
	      const monto = Number(x[field] || 0);
	      if ((x.moneda || "PYG") === "PYG") return s + monto;
	      const tc = Number(x.tipo_cambio || 0);
	      return s + (tc > 0 ? monto * tc : 0);
	    }, 0);
	    const sumUSD = (items, field = "monto_total") => items.reduce((s, x) => {
	      if ((x.moneda || "PYG") !== "USD" || Number(x.tipo_cambio || 0) > 0) return s;
	      return s + Number(x[field] || 0);
	    }, 0);
	    const fmtMixed = (items, field = "monto_total") => {
	      const pyg = sumPYG(items, field);
	      const usd = sumUSD(items, field);
	      return `${fmt(pyg)}${usd ? ` / USD ${usd.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}`;
	    };
	    const compraIva = (c) => {
	      const directo = Number(c.monto_iva || 0);
	      if (directo > 0) return directo;
	      const porItems = (c.items || []).reduce((s, it) => s + calcItemIva(it, c.moneda), 0);
	      if (porItems > 0) return porItems;
	      const total = Number(c.monto_total || 0);
	      const tasa = Number(c.tasa_iva ?? 10);
	      return tasa === 10 ? total / 11 : tasa === 5 ? total / 21 : 0;
	    };
	    const fmtIvaCompras = (items) => {
	      const pyg = items.reduce((s, c) => {
	        const iva = compraIva(c);
	        if ((c.moneda || "PYG") === "PYG") return s + iva;
	        const tc = Number(c.tipo_cambio || 0);
	        return s + (tc > 0 ? iva * tc : 0);
	      }, 0);
	      const usd = items.reduce((s, c) => {
	        if ((c.moneda || "PYG") !== "USD" || Number(c.tipo_cambio || 0) > 0) return s;
	        return s + compraIva(c);
	      }, 0);
	      return `${fmt(pyg)}${usd ? ` / USD ${usd.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}`;
	    };
	    const matchesPeriodoGlobal = (fecha) => {
	      if (filtroTipo === "todos") return true;
	      if (!fecha) return false;
	      if (filtroTipo === "mes") return String(fecha).startsWith(filtroMes);
	      if (filtroTipo === "anio") return String(fecha).startsWith(filtroAnio);
	      return true;
	    };
	    if (tab === "compras") {
	      const contado = compras.filter(c => (c.tipo_pago || "contado") === "contado");
	      const credito = compras.filter(c => c.tipo_pago === "credito");
	      const pendientes = compras.filter(c => ["pendiente", "vencido", "parcial"].includes(c.estado_pago));
	      const conFactura = compras.filter(c => c.tiene_factura);
	      return [
	        { label: "Compras contado", value: contado.length, sub: fmtMixed(contado), icon: ShoppingCart, color: "text-orange-400" },
	        { label: "Compras crédito", value: credito.length, sub: fmtMixed(credito), icon: CreditCard, color: "text-amber-400" },
	        { label: "Pendientes", value: pendientes.length, sub: fmtMixed(pendientes, "saldo_pendiente"), icon: Clock, color: "text-red-400" },
	        { label: "Con factura", value: conFactura.length, sub: fmtIvaCompras(conFactura), icon: FileText, color: "text-blue-400" },
	      ];
	    }
	    if (tab === "costos") {
	      const pagosPeriodo = pagosCostos.filter(p => matchesPeriodoGlobal(p.periodo || p.fecha_pago || p.created_at));
	      const pagados = filtroTipo === "todos" ? pagosPeriodo : vencimientos.filter(v => v.estado === "pagado");
	      const pendientes = vencimientos.filter(v => v.estado === "pendiente");
	      const vencidos = vencimientos.filter(v => v.estado === "vencido");
	      return [
	        { label: "Gastos del período", value: vencimientos.length, sub: fmtMixed(vencimientos, "monto"), icon: Calendar, color: "text-blue-400" },
	        { label: "Pagados", value: pagados.length, sub: fmt(filtroTipo === "todos" ? pagados.reduce((s, p) => s + (p.monto_pagado || 0), 0) : pagados.reduce((s, v) => s + (v.pago?.monto_pagado || 0), 0)), icon: CheckCircle, color: "text-emerald-400" },
	        { label: "Pendientes", value: pendientes.length, sub: fmtMixed(pendientes, "monto"), icon: Clock, color: "text-amber-400" },
	        { label: "Vencidos", value: vencidos.length, sub: fmtMixed(vencidos, "monto"), icon: AlertTriangle, color: "text-red-400" },
	      ];
	    }
	    if (tab === "sueldos") {
	      const activos = empleados.filter(e => e.activo);
	      const pagados = sueldosVenc.filter(s => s.estado === "pagado");
	      const pendientes = sueldosVenc.filter(s => s.estado !== "pagado");
	      const adelantosFlat = [].concat(...Object.values(adelantosMap || {}));
	      return [
	        { label: "Empleados activos", value: activos.length, sub: fmt(activos.reduce((s, e) => s + (e.sueldo_base || 0), 0)), icon: Users, color: "text-purple-400" },
	        { label: "Sueldos pagados", value: pagados.length, sub: fmt(pagados.reduce((s, e) => s + (e.sueldo_registrado?.monto_pagado || 0), 0)), icon: CheckCircle, color: "text-emerald-400" },
	        { label: "Pendientes", value: pendientes.length, sub: fmt(pendientes.reduce((s, e) => s + (e.sueldo_base || 0), 0)), icon: Clock, color: "text-amber-400" },
	        { label: "Adelantos", value: adelantosFlat.length, sub: fmt(adelantosFlat.reduce((s, a) => s + (a.monto || 0), 0)), icon: HandCoins, color: "text-blue-400" },
	      ];
	    }
	    const ivaDebito = filtroTipo === "mes" ? (ivaBalance?.iva_debito || 0) : (ivaResumen?.total_debito || 0);
	    const ivaCredito = filtroTipo === "mes" ? (ivaBalance?.iva_credito || 0) : (ivaResumen?.total_credito || 0);
	    const ivaNeto = filtroTipo === "mes" ? (ivaBalance?.iva_neto || 0) : (ivaResumen?.total_neto || 0);
	    const ivaPagado = filtroTipo === "mes" ? (ivaBalance?.pagos_iva_mes || 0) : (ivaResumen?.total_pagado || 0);
	    // Ajustes manuales de IVA — en el filtro "Mes" ya vienen incluidos dentro
	    // de ivaBalance.saldo_mes (ver /admin/balance/iva), pero en "Día/Año/Todos"
	    // el saldo de acá se recalculaba como (neto - pagado) sin sumar el ajuste.
	    // Como "Día" es el filtro que arranca por defecto en Egresos (compartido
	    // con las demás pestañas vía localStorage), la tarjeta "Saldo del
	    // mes/actual" de arriba terminaba ignorando el ajuste la mayoría de las
	    // veces, aunque el ajuste sí se guardaba bien en la base.
	    const ivaAjustado = filtroTipo === "mes" ? (ivaBalance?.ajustes_iva_mes || 0) : (ivaResumen?.total_ajustado || 0);
	    const ivaSaldoActual = filtroTipo === "mes" ? (ivaBalance?.saldo_mes ?? (ivaNeto - ivaPagado + ivaAjustado)) : (ivaNeto - ivaPagado + ivaAjustado);
	    const cantFacturas = filtroTipo === "mes" ? (ivaBalance?.cantidad_facturas || 0) : (ivaResumen?.meses || []).reduce((s, m) => s + (m.cantidad_facturas || 0), 0);
	    const cantCompras = filtroTipo === "mes" ? (ivaBalance?.cantidad_compras_con_factura || 0) : (ivaResumen?.meses || []).reduce((s, m) => s + (m.cantidad_compras_con_factura || 0), 0);
	    const retencionesTxt = filtroTipo === "mes" && ivaBalance?.iva_credito_retenciones > 0
	      ? ` + Ret.: ${fmtPYG(ivaBalance.iva_credito_retenciones)}`
	      : "";
	    return [
	      { label: "IVA débito", value: fmt(ivaDebito), sub: `${cantFacturas} facturas`, icon: Receipt, color: "text-cyan-400" },
	      { label: "IVA crédito", value: fmt(ivaCredito), sub: `${cantCompras} compras${retencionesTxt}`, icon: FileText, color: "text-violet-400" },
	      { label: "Pagado", value: fmt(ivaPagado), sub: `${pagosIvaList.length} pagos`, icon: CheckCircle, color: "text-amber-400" },
	      { label: filtroTipo === "mes" ? "Saldo del mes" : "Saldo actual", value: fmt(Math.abs(ivaSaldoActual)), sub: ivaSaldoActual > 0 ? "Debe SET" : ivaSaldoActual < 0 ? "A favor" : "Al día", icon: AlertCircle, color: ivaSaldoActual > 0 ? "text-red-400" : ivaSaldoActual < 0 ? "text-emerald-400" : "text-slate-300" },
	    ];
	  })();
  const puedeVerCfgPagoContado = hasPermission("configuracion_egresos.ver_pago_contado") || hasPermission("configuracion_egresos.gestionar_pago_contado");
  const puedeGestionarCfgPagoContado = hasPermission("configuracion_egresos.gestionar_pago_contado");
  const puedeVerConfigEgresos = hasPermission("configuracion_egresos.gestionar_categorias") || puedeVerCfgPagoContado || ["admin","gerente","super_admin"].includes(user?.role);
  const puedeVerSueldos = hasPermission("empleados.ver") || hasPermission("sueldos.ver");
  const puedeAjustarSueldo = hasPermission("empleados.editar") || hasPermission("sueldos.ajustar_sueldo");
  const puedeDeshacerSueldo = hasPermission("empleados.editar") || hasPermission("sueldos.deshacer_pago");
  const puedeVerAnticipos = hasPermission("anticipos_proveedores.ver");
  // El tab "IVA" vive físicamente en Egresos y tiene su propia sección de permisos
  // ("Pago IVA", separada de "Costos fijos" para no confundir al asignar accesos).
  // Se mantiene balance.ver/editar como fallback por compatibilidad con usuarios
  // que ya tenían acceso configurado antes de que existiera este permiso propio.
  const puedeVerPagoIva = hasPermission("pago_iva.ver") || hasPermission("balance.ver");
  const puedeGestionarPagoIva = hasPermission("pago_iva.crear") || hasPermission("balance.editar");
  const puedeVerComisiones = ["admin","gerente","super_admin"].includes(user?.role) || hasPermission("vendedores.pagar_comisiones") || hasPermission("vendedores.ver_comisiones");
  const egresosTabs = [
    { id: "compras", label: "Compras", icon: ShoppingCart, color: "bg-orange-500", perm: "compras.ver" },
    { id: "recepcion", label: "Mercaderías por recibir", icon: PackageCheck, color: "bg-violet-600", perm: "entregas.ver" },
    { id: "costos", label: "Gastos", icon: Calendar, color: "bg-blue-600", perm: "costos_fijos.ver" },
    ...(puedeVerSueldos ? [{ id: "sueldos", label: "Sueldos", icon: Users, color: "bg-purple-600", perm: null }] : []),
    ...(puedeVerPagoIva ? [{ id: "pago-iva", label: "IVA", icon: Receipt, color: "bg-amber-600", perm: null }] : []),
    ...(puedeVerComisiones ? [{ id: "comisiones", label: "Comisiones", icon: HandCoins, color: "bg-pink-600", perm: null }] : []),
    ...(puedeVerAnticipos ? [{ id: "anticipos", label: "Anticipos", icon: HandCoins, color: "bg-teal-600", perm: null }] : []),
    ...(puedeVerConfigEgresos ? [{ id: "configuracion", label: "Configuración", icon: Settings, color: "bg-slate-600", perm: null }] : []),
  ].filter(t => t.perm === null || hasPermission(t.perm));

  useEffect(() => {
    if (egresosTabs.length && !egresosTabs.some(t => t.id === tab)) {
      setTab(egresosTabs[0].id);
    }
  }, [activeEmpresaPropia?.id, user?.permisos, tab]); // eslint-disable-line

  const hayModalAbierto = !!(showCompraForm || facturaCompraTarget || showVincularPresModal || showPagoModal || showProductoBrowser || showNotaCompraForm || showCostoForm || showPagoCostoModal || showCategoriasPanel || showSueldoModal || showAdelantoModal || showExtraModal || showHistorialAjustes || showPagoIvaForm);
  useAutoRefresh(fetchCompras, 30_000, !hayModalAbierto);
  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo !== "modulo_actualizado") return;
      const modulo = event.data?.modulo;
      if (["compras", "pagos_proveedores", "productos", "stock"].includes(modulo)) {
        invalidateEgresosCache();
        fetchCompras(true);
        fetchNotasCompra();
        fetchProductos();
      }
      if (modulo === "ingresos_varios" && tab === "pago-iva") fetchIvaData();
      if (modulo === "financiero") fetchCuentasDisp();
    }, [tab]), // eslint-disable-line
  });

  const handleRefreshManual = () => {
    invalidateEgresosCache();
    fetchCuentasDisp();
    if (tab === "compras") {
      fetchCompras(true);
      fetchNotasCompra();
      fetchProductos();
    } else if (tab === "costos") {
      fetchCostos();
      fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
      fetchCategoriasGastos();
    } else if (tab === "sueldos") {
      fetchEmpleados();
      fetchSueldosVenc(filtroMes);
      fetchSueldoMinimoGlobal();
    } else if (tab === "pago-iva") {
      fetchIvaData();
    } else if (tab === "anticipos") {
      fetchAnticiposList();
      fetchProveedores(activeEmpresaPropia?.slug);
    } else if (tab === "configuracion") {
      fetchCategoriasGastos();
      fetchPlanCuentas();
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-arandu-dark p-3 sm:p-4 md:p-8">
      <div className="max-w-[1800px] mx-auto">

        {/* Header */}
        <div className="mb-6">
          {embedded ? (
            <button onClick={onEmbeddedClose} className="text-slate-400 hover:text-arandu-blue flex items-center gap-2 mb-3 text-sm w-fit">
              <ArrowLeft className="w-4 h-4" /> Volver al proyecto {proyectoCtx?.numero} — {proyectoCtx?.nombre}
            </button>
          ) : (
          <Link to="/sistema" className="text-slate-400 hover:text-arandu-blue flex items-center gap-2 mb-3 text-sm w-fit">
            <ArrowLeft className="w-4 h-4" /> Volver al Dashboard
          </Link>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <TrendingDown className="w-8 h-8 text-red-400" />
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">{embedded ? (lockTab === "costos" ? "Gastos de la obra" : "Compras de la obra") : "Egresos"}</h1>
                <p className="text-slate-400 text-sm">{embedded ? `Proyecto ${proyectoCtx?.numero || ""} — ${proyectoCtx?.nombre || ""}` : "Compras, gastos, sueldos y pagos pendientes"}</p>
              </div>
            </div>
            <button
              onClick={handleRefreshManual}
              disabled={loadingCompras || loadingCostos || loadingSueldos || loadingIva}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all disabled:opacity-50"
              title="Actualizar"
            >
              <RefreshCw className={`w-4 h-4 ${(loadingCompras || loadingCostos || loadingSueldos || loadingIva) ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Selector de período global */}
        {!embedded && <div className="flex items-center gap-2 flex-wrap mb-5 bg-arandu-dark-light border border-white/10 rounded-xl px-4 py-3">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="text-slate-400 text-sm mr-1">Período:</span>
          {[
            { v: "dia",   label: "Hoy" },
            { v: "mes",   label: "Por mes" },
            { v: "anio",  label: "Por año" },
            { v: "todos", label: "Todos" },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => setFiltroTipoP(opt.v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                filtroTipo === opt.v
                  ? "bg-arandu-blue border-arandu-blue text-white"
                  : "border-white/10 text-slate-400 hover:text-white bg-white/5"
              }`}
            >
              {opt.label}
            </button>
          ))}
          {filtroTipo === "dia" && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-1 py-0.5">
                <button onClick={() => setFiltroDia(prevDia(filtroDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-white text-xs font-body min-w-[110px] text-center px-1 capitalize">{diaLabel(filtroDia)}</span>
                <button onClick={() => setFiltroDia(nextDia(filtroDia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              {filtroDia !== hoy() && (
                <button onClick={() => setFiltroDia(hoy())}
                  className="text-xs text-arandu-blue hover:text-blue-300 transition-colors">
                  Hoy
                </button>
              )}
            </div>
          )}
          {filtroTipo === "mes" && (
            <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-1 py-0.5">
              <button
                onClick={() => { const m = prevMes(filtroMes); setFiltroMes(m); setPeriodoIva(m); }}
                className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-white text-sm font-medium min-w-[90px] text-center px-1">{mesLabel(filtroMes)}</span>
              <button
                onClick={() => { const m = nextMes(filtroMes); setFiltroMes(m); setPeriodoIva(m); }}
                className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          {filtroTipo === "anio" && (
            <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-1 py-0.5">
              <button onClick={() => setFiltroAnio(a => String(parseInt(a) - 1))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-white text-sm font-medium min-w-[50px] text-center px-1">{filtroAnio}</span>
              <button onClick={() => setFiltroAnio(a => String(parseInt(a) + 1))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <button
            onClick={() => { setFiltroTipoP("mes"); setFiltroMes(mesActual()); setPeriodoIva(mesActual()); }}
            className="ml-auto text-xs text-slate-500 hover:text-white border border-white/10 rounded-lg px-3 py-1.5 transition-all"
          >
            Mes actual
          </button>
        </div>}

	        {/* Resumen cards */}
	        {!embedded && <div className="grid grid-cols-2 md:grid-cols-2 md:grid-cols-4 gap-3 mb-6">
	          {egresoStatCards.map(({ label, value, sub, icon: Icon, color }) => (
	            <div key={label} className="bg-arandu-dark-light border border-white/5 rounded-xl p-4">
	              <div className="flex items-center gap-2 mb-2">
	                <Icon className={`w-4 h-4 ${color}`} />
	                <span className="text-slate-500 text-xs">{label}</span>
	              </div>
	              <p className={`font-heading font-bold text-lg ${color}`}>{value}</p>
	              {sub && <p className="text-slate-500 text-xs mt-0.5">{sub}</p>}
	            </div>
	          ))}
	        </div>}

        {/* Tabs */}
        {!embedded && <div className="flex gap-1 bg-arandu-dark-light border border-white/5 rounded-xl p-1 mb-6 flex-wrap overflow-x-auto">
          {egresosTabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? `${t.color} text-white shadow` : "text-slate-400 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>}

        {tab === "recepcion" && (
          <EntregasPanel
            tipo="compra" token={token} logoTipo={activeEmpresaPropia?.slug}
            hasPermission={hasPermission} isAdmin={["admin", "gerente", "super_admin"].includes(user?.role)}
            initialOrigenId={searchParams.get("recibir_compra") || null}
          />
        )}

        {/* ═══════════════ TAB: COMPRAS ═══════════════════════════════════════ */}
        {tab === "compras" && (
          <div>
            {/* Buscador chips + acción */}
            <div className="flex flex-col md:flex-row gap-3 mb-5">
              <ChipSearch
                chips={comprasChips} setChips={setComprasChips}
                inputVal={comprasInput} setInputVal={setComprasInput}
                placeholder="Buscar por proveedor, monto, fecha, estado, factura, notas… (Enter para agregar filtro)"
                accentColor="orange"
                actionButton={(
                  <div className="flex gap-2">
                    {hasPermission("notas_credito.crear") && (
                      <Button onClick={openNewNotaCompra} className="bg-rose-600 hover:bg-rose-700 text-white whitespace-nowrap">
                        <Receipt className="w-4 h-4 mr-2" /> Nota crédito
                      </Button>
                    )}
                    {hasPermission("compras.crear") && (
                      <Button onClick={openNewCompra} className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap">
                        <Plus className="w-4 h-4 mr-2" /> Nueva Compra
                      </Button>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Lista — tabla estilo Gastos pero con columnas tailored a compras */}
            {(() => {
              const comprasFiltradas = compras.filter(c => {
                const texto = [
                  c.proveedor_nombre, c.fecha, String(c.monto_total||""),
                  c.notas, c.numero_factura, c.moneda, c.estado_pago, c.tipo_pago,
                  c.concepto,
                ].filter(Boolean).join(" ");
                return matchChips(comprasChips, comprasInput, texto);
              }).sort((a, b) => {
                const fa = String(a.fecha || "");
                const fb = String(b.fecha || "");
                const byFecha = fb.localeCompare(fa, "es", { numeric: true });
                if (byFecha !== 0) return byFecha;
                return String(b.created_at || b.updated_at || b.id || "").localeCompare(
                  String(a.created_at || a.updated_at || a.id || ""),
                  "es",
                  { numeric: true }
                );
              });
              // Toggle ver eliminadas (dentro del render de compras)
              const toggleVerEliminadas = (
                canViewDeleted("compras.eliminar") && (
                  <button
                    onClick={() => setMostrarEliminadasCompras(v => !v)}
                    className={`px-3 py-1.5 rounded-lg text-xs border flex items-center gap-1.5 ${mostrarEliminadasCompras ? "bg-red-500/20 border-red-500/40 text-red-300" : "border-white/10 text-slate-400 hover:text-white"}`}
                  >
                    <Trash2 className="w-3 h-3" /> {mostrarEliminadasCompras ? "Ocultar eliminadas" : "Ver eliminadas"}
                  </button>
                )
              );
              if (loadingCompras) {
                return <div className="text-center py-12 text-orange-400 animate-pulse">Cargando...</div>;
              }
              if (compras.length === 0) {
                return (
                  <>
                    <div className="flex justify-end mb-2">{toggleVerEliminadas}</div>
                    <div className="text-center py-16 bg-arandu-dark-light border border-white/5 rounded-xl">
                      <ShoppingCart className="w-14 h-14 text-slate-700 mx-auto mb-3" />
                      <p className="text-slate-400 mb-4">{mostrarEliminadasCompras ? "No hay compras eliminadas" : "No hay compras registradas"}</p>
                      {!mostrarEliminadasCompras && hasPermission("compras.crear") && (
                        <Button onClick={openNewCompra} className="bg-orange-500 hover:bg-orange-600 text-white">
                          <Plus className="w-4 h-4 mr-2" /> Registrar primera compra
                        </Button>
                      )}
                    </div>
                  </>
                );
              }
              return (
                <>
                  {/* Toggle ver eliminadas */}
                  <div className="flex justify-end mb-2">{toggleVerEliminadas}</div>

                  {/* Tabla */}
                  {comprasFiltradas.length === 0 ? (
                    <div className="text-center py-12 bg-arandu-dark-light border border-white/5 rounded-xl">
                      <ShoppingCart className="w-12 h-12 text-slate-700 mx-auto mb-2" />
                      <p className="text-slate-400">No hay compras que coincidan con el filtro</p>
                    </div>
                  ) : (
                    <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10 text-slate-400 text-xs uppercase">
                            <th className="px-4 py-3 text-left">Fecha</th>
                            <th className="px-4 py-3 text-left">Empresa</th>
                            <th className="px-4 py-3 text-left">Proveedor</th>
                            <th className="px-4 py-3 text-left">Concepto</th>
                            <th className="px-4 py-3 text-left">Factura</th>
                            <th className="px-4 py-3 text-center">Tipo</th>
                            <th className="px-4 py-3 text-right">Monto</th>
                            <th className="px-4 py-3 text-center">Estado</th>
                            <th className="px-4 py-3 text-center">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comprasFiltradas.map(c => {
                            const concepto = c.concepto || c.notas || "—";
                            return (
                              <tr key={c.id}
                                onClick={() => openCompraView(c)}
                                className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${c.eliminada ? "bg-red-900/20" : ""}`}
                              >
                                <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">
                                  {c.fecha}
                                  {c.eliminada && (
                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">Eliminada</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-slate-300">
                                    {LOGO_LABEL_MAP[c.logo_tipo] || c.logo_tipo}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-white font-medium max-w-[180px] truncate">{c.proveedor_nombre}</td>
                                <td className="px-4 py-3 text-slate-400 text-xs max-w-[220px]">
                                  <span className="truncate block">{concepto}</span>
                                  {(c.presupuesto_ids?.length || (c.presupuesto_id ? 1 : 0)) > 0 && hasModule?.("presupuestos") && (
                                    <button
                                      onClick={e => {
                                        e.stopPropagation();
                                        const ids = c.presupuesto_ids?.length ? c.presupuesto_ids : (c.presupuesto_id ? [c.presupuesto_id] : []);
                                        const firstId = ids[0];
                                        const pres = presupuestosForm.find(p => p.id === firstId);
                                        if (pres) setPresDetailEgresos(pres);
                                        else fetch(`${API}/admin/presupuestos/${firstId}`, { headers: { Authorization: `Bearer ${token}` } })
                                          .then(r => r.ok ? r.json() : null).then(d => d && setPresDetailEgresos(d)).catch(() => {});
                                      }}
                                      className="mt-1 inline-flex items-center gap-1 text-[10px] bg-blue-500/15 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 rounded-full hover:bg-blue-500/25 transition-colors"
                                    >
                                      📄 {(c.presupuesto_ids?.length || 1) > 1 ? `${c.presupuesto_ids.length} pres.` : "pres."}
                                    </button>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-xs">
                                  {c.tiene_factura ? (
                                    <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5">
                                      <Receipt className="w-3 h-3" />
                                      {c.numero_factura || "c/factura"}
                                    </span>
                                  ) : (
                                    <span className="text-slate-600">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${
                                    c.solo_iva
                                      ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/30"
                                      : c.tipo_pago === "contado"
                                      ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                      : "bg-amber-500/10 text-amber-300 border-amber-500/30"
                                  }`}>
                                    {c.solo_iva ? "Solo IVA" : c.tipo_pago}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <p className="text-white font-bold whitespace-nowrap">{fmt(c.monto_total, c.moneda)}</p>
                                  {!c.solo_iva && c.saldo_pendiente > 0 && (
                                    <p className="text-red-400 text-xs">Debe {fmt(c.saldo_pendiente, c.moneda)}</p>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {(() => {
                                    const pagoConFecha = (c.pagos || []).find(p => p.fecha_pago || p.fecha);
                                    const fechaPago = c.fecha_pago || pagoConFecha?.fecha_pago || pagoConFecha?.fecha;
                                    const fechaInferida = !!(c.fecha_pago_inferida || pagoConFecha?.fecha_pago_inferida);
                                    return (
                                      <div className="inline-flex flex-col items-center gap-1">
                                        <span className={`px-2 py-0.5 rounded-full text-xs border ${ESTADO_STYLES[c.estado_pago]}`}>
                                          {ESTADO_LABELS[c.estado_pago]}
                                        </span>
                                        {c.estado_pago === "pagado" && fechaPago && (
                                          <span
                                            className={`text-[11px] whitespace-nowrap ${fechaInferida ? "text-amber-400" : "text-emerald-400"}`}
                                            title={fechaInferida ? "Fecha completada automáticamente desde la fecha de compra" : "Fecha de pago registrada"}
                                          >
                                            {fechaPago}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                  <div className="flex items-center justify-center gap-1">
                                    {!mostrarEliminadasCompras && !c.eliminada && c.estado_pago !== "pagado" && !c.solo_iva && (
                                      <Link
                                        to={`/sistema/financiero?tab=pagos_proveedores&proveedor_id=${c.proveedor_id || ""}&compra_id=${c.id}`}
                                        className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 hover:underline transition-colors whitespace-nowrap"
                                        title="Pagar en Pagos Proveedores"
                                      >
                                        <Banknote className="w-3 h-3" /> Pagar
                                      </Link>
                                    )}
                                    {!mostrarEliminadasCompras && !c.eliminada && c.estado_pago !== "pagado" && c.estado_pago !== "parcial" && hasPermission("compras.editar_completa") && (
                                      <button
                                        onClick={() => openEditCompra(c)}
                                        className="text-slate-400 hover:text-cyan-400 transition-colors p-1"
                                        title="Editar compra (proveedor, ítems, montos)"
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                    )}
                                    {!mostrarEliminadasCompras && !c.eliminada && !c.tiene_factura && !c.numero_factura && hasPermission("compras.editar") && (
                                      <button
                                        onClick={() => openFacturaCompra(c)}
                                        className="text-slate-400 hover:text-orange-400 transition-colors p-1"
                                        title="Cargar factura"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                    )}
                                    {!mostrarEliminadasCompras && !c.eliminada && hasPermission("compras.eliminar") && (
                                      <button onClick={() => handleDeleteCompra(c.id)}
                                        className="text-slate-400 hover:text-red-400 transition-colors p-1">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                    <AuditoriaRegistroBtn registro={c} />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {notasCompra.length > 0 && (
                    <div className="mt-5 bg-rose-500/5 border border-rose-500/20 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-rose-500/20 flex items-center justify-between">
                        <p className="text-rose-300 text-sm font-medium">Notas de crédito de compras</p>
                        <p className="text-rose-200 font-heading font-bold text-sm">{fmtMixedAmounts(notasCompra, "monto")}</p>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {notasCompra.map(n => (
                            <tr key={n.id} onClick={() => setSelectedNotaCompraView(n)} className="border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors">
                              <td className="px-4 py-3 text-slate-300 text-xs">{n.fecha}</td>
                              <td className="px-4 py-3 text-white font-medium">{n.proveedor_nombre || "—"}</td>
                              <td className="px-4 py-3 text-slate-400 text-xs">{n.numero}</td>
                              <td className="px-4 py-3 text-slate-400 text-xs">{n.compra_numero_factura || (n.compra_id ? "Compra vinculada" : "Sin vincular")}</td>
                              <td className="px-4 py-3 text-slate-400 text-xs">{n.motivo}</td>
                              <td className="px-4 py-3 text-right text-rose-300 font-bold">{fmt(n.monto, n.moneda)}</td>
                              <td className="px-4 py-3 text-right">
                                <button onClick={(e) => { e.stopPropagation(); setSelectedNotaCompraView(n); }} className="p-1 text-slate-400 hover:text-blue-300" title="Ver nota"><Eye className="w-4 h-4" /></button>
                                <button onClick={(e) => { e.stopPropagation(); setNotaCompraDocModal(n); }} className="p-1 text-slate-400 hover:text-rose-300" title="Ver / Imprimir"><Printer className="w-4 h-4" /></button>
                                {/* editar nota crédito deshabilitado — para corregir, eliminar y recrear */}
                                {hasPermission("notas_credito.eliminar") && (
                                  <button onClick={(e) => { e.stopPropagation(); handleDeleteNotaCompra(n.id); }} className="p-1 text-slate-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                                )}
                                <AuditoriaRegistroBtn registro={n} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ═══════════════ TAB: COSTOS FIJOS ══════════════════════════════════ */}
        {tab === "costos" && (() => {
          const costosEliminados = costos
            .filter(c => c.eliminada)
            .map(c => ({ ...c, costo_id: c.id, estado: "eliminado" }));
          const baseCostos = mostrarEliminadasCostos ? costosEliminados : vencimientos;
          const vencFiltradas = baseCostos.filter(v => {
            const texto = [v.nombre, v.logo_tipo, LOGO_LABEL_MAP[v.logo_tipo]||"", v.categoria, String(v.monto||""), v.moneda, v.estado, v.frecuencia].filter(Boolean).join(" ");
            return matchChips(costosChips, costosInput, texto);
          });
          const toggleEliminadasCostos = canViewDeleted("costos_fijos.eliminar") ? (
            <button
              onClick={() => setMostrarEliminadasCostos(v => !v)}
              className={`px-3 py-2 rounded-lg text-xs border flex items-center gap-1.5 ${mostrarEliminadasCostos ? "bg-red-500/20 border-red-500/40 text-red-300" : "border-white/10 text-slate-400 hover:text-white"}`}
            >
              <Trash2 className="w-3.5 h-3.5" /> {mostrarEliminadasCostos ? "Ocultar eliminadas" : "Ver eliminadas"}
            </button>
          ) : null;
          return (
            <div>
              {/* Buscador + botón nuevo */}
              <div className="flex flex-col gap-3 mb-4">
                <ChipSearch
                  chips={costosChips} setChips={setCostosChips}
                  inputVal={costosInput} setInputVal={setCostosInput}
                  placeholder="Buscar por nombre, categoría, empresa, monto, estado… (Enter para filtrar)"
                  accentColor="blue"
                  actionButton={(
                    <div className="flex gap-2">
                      {toggleEliminadasCostos}
                      {!mostrarEliminadasCostos && hasPermission("costos_fijos.crear") && (
                        <Button onClick={() => { setEditingCostoId(null); setCostoFormData({ logo_tipo: activeEmpresaPropia?.slug || "arandujar", nombre: "", descripcion: "", categoria: "Otros", monto: "", moneda: "PYG", tipo_cambio: "", frecuencia: "mensual", dia_vencimiento: 1, fecha_inicio: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10), fecha_fin: "", activo: true, notas: "", ...CANAL_PAGO_VACIO }); setShowCostoForm(true); }}
                          className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap">
                          <Plus className="w-4 h-4 mr-2" /> Nuevo Gasto
                        </Button>
                      )}
                    </div>
                  )}
                />
              </div>

              {(() => {
              return (
                  <>
                  {costosOmitidos.length > 0 && filtroTipo === "mes" && (
                    <div className="mb-3 bg-slate-500/10 border border-white/10 rounded-xl p-3">
                      <p className="text-slate-400 text-xs mb-2">Omitidos en {filtroMes} (no se generaron este mes)</p>
                      <div className="flex flex-wrap gap-2">
                        {costosOmitidos.map(o => (
                          <span key={o.costo_id} className="inline-flex items-center gap-2 text-xs bg-arandu-dark border border-white/10 rounded-full pl-3 pr-1 py-1 text-slate-300">
                            {o.nombre}{o.motivo ? ` · ${o.motivo}` : ""}
                            {hasPermission("costos_fijos.gestionar_recurrencia") && (
                              <button onClick={() => handleRestaurarPeriodo(o)} className="px-2 py-0.5 rounded-full bg-blue-600/80 hover:bg-blue-600 text-white">Restaurar</button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Tabla */}
                  {loadingVenc ? (
                    <div className="text-center py-10 text-blue-400 animate-pulse">Cargando vencimientos...</div>
                  ) : vencFiltradas.length === 0 ? (
                    <div className="text-center py-12 bg-arandu-dark-light border border-white/5 rounded-xl">
                      <Calendar className="w-12 h-12 text-slate-700 mx-auto mb-2" />
                      <p className="text-slate-400">{mostrarEliminadasCostos ? "No hay gastos eliminados" : "Sin vencimientos para este período"}</p>
                    </div>
                  ) : (
                    <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10 text-slate-400 text-xs uppercase">
                            {filtroTipo === "anio" && <th className="px-4 py-3 text-left text-xs">Período</th>}
                            <th className="px-4 py-3 text-left">Costo</th>
                            <th className="px-4 py-3 text-left">Empresa</th>
                            <th className="px-4 py-3 text-left">Categoría</th>
                            <th className="px-4 py-3 text-right">Monto</th>
                            <th className="px-4 py-3 text-center">Día Vcto.</th>
                            <th className="px-4 py-3 text-center">Estado</th>
                            <th className="px-4 py-3 text-center">Pago</th>
                            <th className="px-4 py-3 text-center">Editar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vencFiltradas.map((v, i) => (
                            <tr key={i} onClick={() => setSelectedCostoView(v)}
                              className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                              {filtroTipo === "anio" && <td className="px-4 py-3 text-slate-500 text-xs">{v.periodo || v.fecha_inicio?.slice(0,7) || "—"}</td>}
                              <td className="px-4 py-3">
                                <p className="text-white font-medium">{v.nombre}</p>
                                {v.descripcion && <p className="text-slate-500 text-xs">{v.descripcion}</p>}
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-slate-300">{LOGO_LABEL_MAP[v.logo_tipo] || v.logo_tipo}</span>
                              </td>
                              <td className="px-4 py-3 text-slate-400 text-xs">{v.categoria || "—"}</td>
                              <td className="px-4 py-3 text-right font-bold text-white">{fmt(v.monto, v.moneda)}</td>
                              <td className="px-4 py-3 text-center text-slate-400 text-xs">{v.dia_vencimiento}</td>
                              <td className="px-4 py-3 text-center">
                                {v.estado === "eliminado" ? (
                                  <span className="flex items-center justify-center gap-1 text-red-300 text-xs font-semibold">
                                    <Trash2 className="w-3.5 h-3.5" /> Eliminado
                                  </span>
                                ) : v.estado === "pagado" ? (
                                  <span className="flex items-center justify-center gap-1 text-emerald-400 text-xs font-semibold">
                                    <Check className="w-3.5 h-3.5" /> Pagado
                                  </span>
                                ) : v.estado === "vencido" ? (
                                  <span className="flex items-center justify-center gap-1 text-red-400 text-xs font-semibold">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Vencido
                                  </span>
                                ) : v.estado === "pago_parcial" ? (
                                  <span className="flex items-center justify-center gap-1 text-blue-400 text-xs font-semibold">
                                    <Clock className="w-3.5 h-3.5" /> Parcial
                                  </span>
                                ) : (
                                  <span className="flex items-center justify-center gap-1 text-amber-400 text-xs font-semibold">
                                    <Clock className="w-3.5 h-3.5" /> Pendiente
                                  </span>
                                )}
                                {(v.estado === "pagado" || v.estado === "pago_parcial") && v.pago?.fecha_pago && (
                                  <p className="text-slate-500 text-xs mt-0.5">{v.pago.fecha_pago}</p>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                                {v.estado === "eliminado" ? null : v.estado === "pagado" ? (
                                  hasPermission("costos_fijos.editar") && (
                                    <button onClick={() => handleAnularPagoCosto(v)}
                                      className="flex items-center gap-1 mx-auto px-3 py-1 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/40 transition-colors text-xs">
                                      <X className="w-3.5 h-3.5" /> Anular
                                    </button>
                                  )
                                ) : (
                                  hasPermission("costos_fijos.editar") && (
                                    <div className="flex flex-col items-center gap-1">
                                      <button onClick={() => {
                                        const remaining = v.monto - (v.total_pagado || 0);
                                        setPagoCostoForm({ monto_pagado: remaining > 0 ? remaining : v.monto, fecha_pago: hoy(), notas: "", tiene_factura: false, nro_factura: "", nro_timbrado: "", fecha_vigencia_timbrado: "", iva_tipo: "", items: [], cuenta_id: "", cuenta_nombre: "", tipo_cambio: "", cuenta_moneda: "" });
                                        setPagoMedioCosto("transferencia");
                                        setShowPagoCostoModal(v);
                                      }}
                                        className="flex items-center gap-1 mx-auto px-3 py-1 bg-emerald-600/30 text-emerald-300 rounded-lg hover:bg-emerald-600/50 transition-colors text-xs">
                                        <Check className="w-3.5 h-3.5" /> {v.estado === "pago_parcial" ? "Completar" : "Pagar"}
                                      </button>
                                      {v.estado === "pago_parcial" && v.pago && (
                                        <button onClick={() => handleAnularPagoCosto(v)}
                                          className="flex items-center gap-1 mx-auto px-2 py-0.5 text-red-400/70 hover:text-red-400 transition-colors text-xs">
                                          <X className="w-3 h-3" /> Anular pagos
                                        </button>
                                      )}
                                    </div>
                                  )
                                )}
                              </td>
                              <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-center gap-2">
{/* editar costo fijo deshabilitado */}
                                  {!mostrarEliminadasCostos && hasPermission("costos_fijos.eliminar") && (
                                    <button onClick={() => { const src=costos.find(c=>c.id===v.costo_id)||v; if(src) handleDeleteCosto({...src, id: src.id||src.costo_id}); }}
                                      className="text-slate-400 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                                  )}
                                  <AuditoriaRegistroBtn registro={v} />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  </>
                );
              })()}
            </div>
          );
        })()}

        {/* MODAL: Nuevo/Editar Costo Fijo */}
        <AnimatePresence>
          {showCostoForm && (
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
              <motion.div initial={{scale:0.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.95,opacity:0}}
                className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                  <h2 className="font-heading text-lg text-white">{editingCostoId ? "Editar gasto" : "Nuevo gasto"}</h2>
                  <button onClick={() => setShowCostoForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
                </div>
                <form onSubmit={handleSaveCosto} className="p-5 space-y-4">
                  {proyectoId && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 space-y-2">
                      <div className="text-xs text-yellow-300">Proyecto de obra: <strong>{proyectoCtx?.numero} — {proyectoCtx?.nombre}</strong></div>
                      <select value={costoFormData.grupo_constructora_id || ""} onChange={e => setCostoFormData(f => ({ ...f, grupo_constructora_id: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="">Sin grupo</option>
                        {gruposConstructora.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Nombre *</label>
                    <input required type="text" value={costoFormData.nombre} onChange={e=>setCostoFormData(f=>({...f,nombre:e.target.value}))}
                      placeholder="Ej: Hosting VPS, Internet, IPS..."
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60"/>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Categoría *</label>
                      <select required value={costoFormData.categoria || "Otros"} onChange={e=>setCostoFormData(f=>({...f,categoria:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60">
                        {(categoriasGastos.length ? categoriasGastos : [{nombre:"Otros"}]).map(c=>(
                          <option key={c.nombre} value={c.nombre}>{c.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Descripción</label>
                      <input type="text" value={costoFormData.descripcion} onChange={e=>setCostoFormData(f=>({...f,descripcion:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60"/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Monto *</label>
                      <MoneyInput required min="0" step="any" moneda={costoFormData.moneda || "PYG"} value={costoFormData.monto} onChange={value=>setCostoFormData(f=>({...f,monto:value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60"/>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Moneda</label>
                      <select value={costoFormData.moneda} onChange={e=>setCostoFormData(f=>({...f,moneda:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60">
                        <option value="PYG">₲ Guaraníes</option>
                        <option value="USD">$ Dólares</option>
                        {activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") && (
                          <option value="BRL">R$ Reales</option>
                        )}
                      </select>
                    </div>
                  </div>
                  {costoFormData.moneda !== "PYG" && (
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Tipo de cambio (opcional)</label>
                      <input type="number" min="0" step="any" value={costoFormData.tipo_cambio} onChange={e=>setCostoFormData(f=>({...f,tipo_cambio:e.target.value}))}
                        placeholder={costoFormData.moneda === "BRL" ? "Ej: 1500" : "Ej: 7600"} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60"/>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Frecuencia</label>
                      <select value={costoFormData.frecuencia} onChange={e=>setCostoFormData(f=>({...f,frecuencia:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60">
                        {[["unica","Única vez"],["mensual","Mensual"],["trimestral","Trimestral"],["semestral","Semestral"],["anual","Anual"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    {costoFormData.frecuencia !== "unica" && (
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Día de vencimiento</label>
                        <input type="number" min="1" max="31" value={costoFormData.dia_vencimiento} onChange={e=>setCostoFormData(f=>({...f,dia_vencimiento:e.target.value}))}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60"/>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">{editingCostoId ? "Vigente desde *" : "Fecha inicio *"}</label>
                      <DateInput required value={costoFormData.fecha_inicio} onChange={e=>setCostoFormData(f=>({...f,fecha_inicio:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60"/>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Fecha fin (opcional)</label>
                      <DateInput value={costoFormData.fecha_fin} onChange={e=>setCostoFormData(f=>({...f,fecha_fin:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60"/>
                    </div>
                  </div>
                  <CanalPagoGastoFields value={costoFormData} onChange={(patch)=>setCostoFormData(f=>({...f,...patch}))} />
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Notas</label>
                    <textarea value={costoFormData.notas} onChange={e=>setCostoFormData(f=>({...f,notas:e.target.value}))} rows={2}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/60 resize-none"/>
                  </div>
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={() => setShowCostoForm(false)}
                      className="flex-1 py-2.5 rounded-lg border border-white/10 text-slate-400 text-sm hover:text-white transition-all">Cancelar</button>
                    <button type="submit"
                      className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-all flex items-center justify-center gap-2">
                      <Save className="w-4 h-4"/> {editingCostoId ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* MODAL: Registrar Pago de Costo Fijo */}
        <AnimatePresence>
          {showPagoCostoModal && (
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
              <motion.div initial={{scale:0.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.95,opacity:0}}
                className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg text-white">Registrar pago</h2>
                  <button onClick={() => setShowPagoCostoModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
                </div>
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4 text-sm space-y-1">
                  <p className="text-slate-400">Costo: <span className="text-white">{showPagoCostoModal.nombre}</span></p>
                  <p className="text-slate-400">Período: <span className="text-white">{showPagoCostoModal.periodo || mesLabel(filtroMes)}</span></p>
                  <p className="text-slate-400">Total del período: <span className="text-white">{fmt(showPagoCostoModal.monto, showPagoCostoModal.moneda)}</span></p>
                </div>
                {showPagoCostoModal.pagos?.length > 0 && (
                  <div className="bg-arandu-dark/60 border border-blue-500/20 rounded-xl p-3 mb-4 text-sm">
                    <p className="text-blue-300 text-xs font-medium mb-2">Pagos anteriores en este período:</p>
                    {showPagoCostoModal.pagos.map((p, i) => (
                      <div key={i} className="flex justify-between items-center text-xs py-1 border-b border-white/5 last:border-0">
                        <span className="text-slate-400">{p.fecha_pago || "—"}{p.registrado_por ? ` · por ${p.registrado_por}` : ""}{p.notas ? ` · ${p.notas}` : ""}{p.cheque_numero && !String(p.notas || "").includes(p.cheque_numero) ? ` · Cheque ${p.cheque_numero}` : ""}</span>
                        <span className="text-emerald-300 font-medium">{fmt(p.monto_pagado, showPagoCostoModal.moneda)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center text-xs pt-2 mt-1">
                      <span className="text-slate-400">Saldo restante:</span>
                      <span className="text-amber-300 font-bold">{fmt(showPagoCostoModal.monto - (showPagoCostoModal.total_pagado || 0), showPagoCostoModal.moneda)}</span>
                    </div>
                  </div>
                )}
                {/* Tabs medio de pago */}
                {puedeUsarCheque && (
                  <div className="flex gap-2 mb-3">
                    <button type="button" onClick={() => setPagoMedioCosto("transferencia")}
                      className={`flex-1 text-xs py-2 rounded-lg border transition-all ${pagoMedioCosto === "transferencia" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}>
                      Transf. / Efectivo
                    </button>
                    <button type="button" onClick={() => setPagoMedioCosto("cheque")}
                      className={`flex-1 text-xs py-2 rounded-lg border transition-all ${pagoMedioCosto === "cheque" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}>
                      Cheque
                    </button>
                  </div>
                )}

                {pagoMedioCosto === "cheque" ? (
                  <ChequeEgresoForm
                    token={token}
                    logoTipo={showPagoCostoModal?.logo_tipo || activeEmpresaPropia?.slug}
                    beneficiario={showPagoCostoModal?.nombre || ""}
                    monto={showPagoCostoModal?.monto - (showPagoCostoModal?.total_pagado || 0)}
                    egresoMoneda={showPagoCostoModal?.moneda || "PYG"}
                    egresoTipo="costo_fijo"
                    cuentaId={pagoCostoForm.cuenta_id}
                    egresoId={showPagoCostoModal?.id}
                    onSuccess={handlePagoCostoConCheque}
                    onCancel={() => setShowPagoCostoModal(null)}
                  />
                ) : (
                  <form onSubmit={handleRegistrarPagoCosto} className="space-y-3">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Monto pagado ({showPagoCostoModal.moneda === "BRL" ? "R$" : showPagoCostoModal.moneda === "USD" ? "US$" : "₲"})</label>
                      <MoneyInput required min="0" step="any" moneda={showPagoCostoModal?.moneda || "PYG"} value={pagoCostoForm.monto_pagado} onChange={value=>setPagoCostoForm(f=>({...f,monto_pagado:value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/60"/>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Fecha de pago</label>
                      <DateInput required value={pagoCostoForm.fecha_pago} onChange={e=>setPagoCostoForm(f=>({...f,fecha_pago:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/60"/>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Cuenta bancaria *</label>
                      <select required value={pagoCostoForm.cuenta_id}
                        onChange={e => {
                          const c = cuentasDisp.find(c => c.id === e.target.value);
                          const nuevaMoneda = c?.moneda || "PYG";
                          const costoMoneda = showPagoCostoModal?.moneda || "PYG";
                          const cambiaCruce = nuevaMoneda !== costoMoneda;
                          setPagoCostoForm(f => ({ ...f, cuenta_id: e.target.value, cuenta_nombre: c?.nombre || "", cuenta_moneda: nuevaMoneda, tipo_cambio: cambiaCruce ? f.tipo_cambio : "" }));
                          if (!cambiaCruce) setTcCostoPagoInfo(null);
                        }}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/60">
                        <option value="">Seleccionar cuenta...</option>
                        {renderCuentasPorMoneda(cuentasDisp, showPagoCostoModal?.logo_tipo || activeEmpresaPropia?.slug)}
                      </select>
                    </div>
                    {puedeUsarCheque && pagoCostoForm.cuenta_id && (
                      <button type="button" onClick={() => setPagoMedioCosto("cheque")}
                        className="w-full text-xs py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-all">
                        Pagar con cheque de esta cuenta
                      </button>
                    )}
                    {/* Tipo de cambio — visible cuando moneda de la cuenta ≠ moneda del costo */}
                    {pagoCostoForm.cuenta_moneda && showPagoCostoModal?.moneda && pagoCostoForm.cuenta_moneda !== showPagoCostoModal.moneda && (
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">
                          Tipo de cambio ({pagoCostoForm.cuenta_moneda !== "PYG" ? pagoCostoForm.cuenta_moneda : showPagoCostoModal.moneda}/PYG)
                          {tcCostoPagoInfo && <span className="text-emerald-400 ml-1">· Sugerido: {(tcCostoPagoInfo.tipo_cambio_sugerido || tcCostoPagoInfo.venta)?.toLocaleString()}</span>}
                        </label>
                        <input required type="number" min="0" step="any" value={pagoCostoForm.tipo_cambio}
                          onChange={e => { setTcCostoPagoInfo(null); setPagoCostoForm(f => ({ ...f, tipo_cambio: e.target.value })); }}
                          placeholder="Ej: 7800"
                          className="w-full bg-arandu-dark border border-amber-500/40 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/60"/>
                        {pagoCostoForm.cuenta_moneda === "PYG" && showPagoCostoModal.moneda !== "PYG" && pagoCostoForm.tipo_cambio && pagoCostoForm.monto_pagado && (
                          <p className="text-[11px] text-emerald-400 mt-1">
                            Equivalente: ₲ {Math.round(parseFloat(pagoCostoForm.monto_pagado || 0) * parseFloat(pagoCostoForm.tipo_cambio || 0)).toLocaleString("es-PY")}
                          </p>
                        )}
                      </div>
                    )}
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Notas</label>
                      <input type="text" value={pagoCostoForm.notas} onChange={e=>setPagoCostoForm(f=>({...f,notas:e.target.value}))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/60"/>
                    </div>
                    <div className="bg-arandu-dark/40 border border-white/5 rounded-xl p-3 space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <div onClick={() => setPagoCostoForm(f=>({...f, tiene_factura: !f.tiene_factura, nro_factura: "", nro_timbrado: "", fecha_vigencia_timbrado: "", iva_tipo: ""}))}
                          className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center ${pagoCostoForm.tiene_factura ? "bg-emerald-500" : "bg-slate-600"}`}>
                          <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${pagoCostoForm.tiene_factura ? "translate-x-4" : "translate-x-0"}`}/>
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">Tiene factura</p>
                          <p className="text-slate-500 text-xs">IVA crédito fiscal aplicable</p>
                        </div>
                      </label>
                      {pagoCostoForm.tiene_factura && (
                        <div className="space-y-2">
                          <div>
                            <label className="text-slate-400 text-xs mb-1 block">Número de factura *</label>
                            <input required type="text" value={pagoCostoForm.nro_factura} onChange={e=>setPagoCostoForm(f=>({...f,nro_factura:e.target.value}))}
                              placeholder="Ej: 001-001-0000123"
                              className="w-full bg-arandu-dark border border-emerald-500/30 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/60"/>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-slate-400 text-xs mb-1 block">Nro. Timbrado</label>
                              <input type="text" value={pagoCostoForm.nro_timbrado} onChange={e=>setPagoCostoForm(f=>({...f,nro_timbrado:e.target.value}))}
                                placeholder="Ej: 12345678"
                                className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/40"/>
                            </div>
                            <div>
                              <label className="text-slate-400 text-xs mb-1 block">Vigencia timbrado</label>
                              <DateInput value={pagoCostoForm.fecha_vigencia_timbrado} onChange={e=>setPagoCostoForm(f=>({...f,fecha_vigencia_timbrado:e.target.value}))}
                                className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/40"/>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-slate-400 text-xs block">Desglose de IVA de la factura *</label>
                              <button type="button"
                                onClick={() => setPagoCostoForm(f => ({ ...f, items: [...(f.items || []), { descripcion: "", monto: "", iva: 10 }] }))}
                                className="text-emerald-400 text-xs hover:text-emerald-300 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> Agregar ítem
                              </button>
                            </div>
                            <p className="text-slate-600 text-[11px] mb-2">
                              Una misma factura (ej. una compra de supermercado) puede tener ítems a distintas tasas — cargá cada uno por separado, con su monto (IVA incluido) y su tasa.
                            </p>
                            {(pagoCostoForm.items || []).length === 0 && (
                              <p className="text-amber-400 text-[11px] mb-2">Agregá al menos un ítem para discriminar el IVA de esta factura.</p>
                            )}
                            <div className="space-y-1.5">
                              {(pagoCostoForm.items || []).map((it, idx) => (
                                <div key={idx} className="flex items-center gap-1.5">
                                  <input type="text" value={it.descripcion}
                                    onChange={e => setPagoCostoForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, descripcion: e.target.value } : x) }))}
                                    placeholder="Descripción (ej: Verdulería, Higiene...)"
                                    className="flex-1 min-w-0 bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-emerald-500/40" />
                                  <MoneyInput min="0" step="any" moneda={showPagoCostoModal?.moneda || "PYG"} value={it.monto}
                                    onChange={value => setPagoCostoForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, monto: value } : x) }))}
                                    className="w-28 flex-shrink-0 bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-emerald-500/40"
                                    placeholder="Monto" />
                                  <select value={it.iva}
                                    onChange={e => setPagoCostoForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, iva: Number(e.target.value) } : x) }))}
                                    className="w-20 flex-shrink-0 bg-arandu-dark border border-white/10 rounded-lg px-1.5 py-2 text-white text-xs focus:outline-none focus:border-emerald-500/40">
                                    <option value={10}>10%</option>
                                    <option value={5}>5%</option>
                                    <option value={0}>Exenta</option>
                                  </select>
                                  <button type="button" onClick={() => setPagoCostoForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                                    className="p-1.5 text-slate-500 hover:text-red-400 flex-shrink-0">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            {(pagoCostoForm.items || []).length > 0 && (() => {
                              const moneda = showPagoCostoModal?.moneda || "PYG";
                              const items = pagoCostoForm.items || [];
                              const t10 = items.filter(it => Number(it.iva) === 10).reduce((s, it) => s + (calcItemIva({ subtotal: it.monto, iva: it.iva }, moneda)), 0);
                              const t5 = items.filter(it => Number(it.iva) === 5).reduce((s, it) => s + (calcItemIva({ subtotal: it.monto, iva: it.iva }, moneda)), 0);
                              const totalItems = items.reduce((s, it) => s + (parseFloat(it.monto) || 0), 0);
                              const totalIva = t10 + t5;
                              const fmtM = (n) => moneda === "PYG" ? fmtPYG(n) : Number(n || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              const montoPagado = parseFloat(pagoCostoForm.monto_pagado) || 0;
                              const difiere = montoPagado > 0 && Math.abs(totalItems - montoPagado) > (moneda === "PYG" ? 1 : 0.01);
                              return (
                                <div className="text-[11px] text-slate-400 mt-2 space-y-0.5 border-t border-white/5 pt-2">
                                  {t10 > 0 && <p>IVA 10%: <span className="text-emerald-300">{fmtM(t10)}</span></p>}
                                  {t5 > 0 && <p>IVA 5%: <span className="text-emerald-300">{fmtM(t5)}</span></p>}
                                  <p className="text-slate-300 font-medium">Suma de ítems: {fmtM(totalItems)} · IVA total: {fmtM(totalIva)}</p>
                                  {difiere && (
                                    <p className="text-amber-400">La suma de ítems ({fmtM(totalItems)}) no coincide con el monto pagado ({fmtM(montoPagado)}) — revisá los montos.</p>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button type="button" onClick={() => setShowPagoCostoModal(null)}
                        className="flex-1 py-2.5 rounded-lg border border-white/10 text-slate-400 text-sm hover:text-white transition-all">Cancelar</button>
                      <button type="submit"
                        className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-all flex items-center justify-center gap-2">
                        <Check className="w-4 h-4"/> Confirmar pago
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══════════════ TAB: SUELDOS ════════════════════════════════════════ */}
        {tab === "sueldos" && (
          <SueldosTab
            sueldosVenc={sueldosVenc} loadingSueldos={loadingSueldos}
            adelantosMap={adelantosMap} extrasMap={extrasMap}
            sueldosChips={sueldosChips} setSueldosChips={setSueldosChips}
            sueldosInput={sueldosInput} setSueldosInput={setSueldosInput}
            filtroTipo={filtroTipo} filtroMes={filtroMes}
            hasPermission={hasPermission}
            openSueldo={openSueldo} openAdelanto={openAdelanto} openExtra={openExtra}
            handleDeleteSueldo={handleDeleteSueldo}
            openSueldoView={openSueldoView}
            handlePrintSueldo={handlePrintSueldo}
          />
        )}


        {/* MODAL: Registrar Sueldo */}
        {showSueldoModal && selectedEmp && (
          <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
            <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <div>
                  <h2 className="text-white font-heading text-lg">Registrar sueldo</h2>
                  <p className="text-slate-400 text-sm">{selectedEmp.nombre} {selectedEmp.apellido} — {mesLabel(filtroMes)}</p>
                </div>
                <button onClick={() => setShowSueldoModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
              </div>
              <div className="p-5 space-y-4">
                {/* Desglose */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2 text-sm">
                  <p className="text-slate-400 text-xs font-medium mb-1">Desglose del pago</p>
                  <div className="flex justify-between text-slate-300"><span>Sueldo base</span><span>{fmtSueldo(parseFloat(selectedEmp.sueldo_base)||0, selectedEmp.moneda_sueldo || selectedEmp.moneda || "PYG")}</span></div>
                  {extrasPeriodo.length > 0 && <div className="flex justify-between text-green-300"><span>+ Extras ({extrasPeriodo.length})</span><span>+ {fmtSueldo(extrasPeriodo.reduce((s,e)=>s+(e.monto||0),0), selectedEmp.moneda_sueldo || selectedEmp.moneda || "PYG")}</span></div>}
                  {selectedEmp.aplica_ips !== false && <div className="flex justify-between text-blue-300"><span>− IPS</span><span>− {fmtSueldo(parseFloat(formSueldo.descuento_ips)||0, "PYG")}</span></div>}
                  {adelantosPeriodo.length > 0 && <div className="flex justify-between text-amber-300"><span>− Adelantos ({adelantosPeriodo.length})</span><span>− {fmtSueldo(adelantosPeriodo.reduce((s,a)=>s+(a.monto||0),0), selectedEmp.moneda_sueldo || selectedEmp.moneda || "PYG")}</span></div>}
                  {formSueldo.descuentos_adicionales.length > 0 && <div className="flex justify-between text-red-300"><span>− Descuentos adicionales</span><span>− {fmtSueldo(formSueldo.descuentos_adicionales.reduce((s,d)=>s+(parseFloat(d.monto)||0),0), "PYG")}</span></div>}
                  {cuotasPrestSeleccionadas.length > 0 && <div className="flex justify-between text-orange-300"><span>− Cuotas préstamos ({cuotasPrestSeleccionadas.length})</span><span>− {fmtSueldo(cuotasPrestSeleccionadas.reduce((s,c)=>s+(c.monto||0),0), "PYG")}</span></div>}
                  {descFacturasSel.length > 0 && <div className="flex justify-between text-rose-300"><span>− Deuda de compras ({descFacturasSel.length})</span><span>− {fmtSueldo(descFacturasSel.reduce((s,c)=>s+(parseFloat(c.monto)||0),0), "PYG")}</span></div>}
                  <div className="border-t border-white/10 pt-2 flex justify-between font-semibold">
                    <span className="text-white">Total a pagar (sueldo)</span>
                    <span className="text-emerald-300">{fmtSueldo(calcMontoAPagar(selectedEmp, adelantosPeriodo, extrasPeriodo, [...formSueldo.descuentos_adicionales, ...cuotasPrestSeleccionadas.map(c=>({monto:c.monto})), ...descFacturasSel.map(c=>({monto:parseFloat(c.monto)||0}))]), selectedEmp.moneda_sueldo || selectedEmp.moneda || "PYG")}</span>
                  </div>
                </div>

                {/* Tabs medio de pago */}
                {puedeUsarCheque && (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setSueldoMedio("transferencia")}
                      className={`flex-1 text-xs py-2 rounded-lg border transition-all ${sueldoMedio === "transferencia" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}>
                      Transf. / Efectivo
                    </button>
                    <button type="button" onClick={() => setSueldoMedio("cheque")}
                      className={`flex-1 text-xs py-2 rounded-lg border transition-all ${sueldoMedio === "cheque" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}>
                      Cheque
                    </button>
                  </div>
                )}

                {sueldoMedio === "cheque" ? (
                  <ChequeEgresoForm
                    token={token}
                    logoTipo={selectedEmp?.logo_tipo || activeEmpresaPropia?.slug}
                    beneficiario={`${selectedEmp.nombre} ${selectedEmp.apellido}`}
                    monto={calcMontoAPagar(selectedEmp, adelantosPeriodo, extrasPeriodo, [...formSueldo.descuentos_adicionales, ...cuotasPrestSeleccionadas.map(c=>({monto:c.monto})), ...descFacturasSel.map(c=>({monto:parseFloat(c.monto)||0}))])}
                    egresoMoneda={formSueldo.moneda || "PYG"}
                    egresoTipo="sueldo"
                    egresoId={selectedEmp.id}
                    onSuccess={handleSaveSueldoConHoja}
                    onCancel={() => setShowSueldoModal(false)}
                  />
                ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Moneda</label>
                    <select value={formSueldo.moneda} onChange={e => {
                      const moneda = e.target.value;
                      const cuentaDef = cuentaPredeterminada(moneda, selectedEmp?.logo_tipo || activeEmpresaPropia?.slug);
                      setTcSueldoPagoInfo(null);
                      setFormSueldo(f=>({
                        ...f,
                        moneda,
                        cuenta_id: cuentaDef?.id || "",
                        cuenta_nombre: cuentaDef?.nombre || "",
                        cuenta_moneda: cuentaDef?.moneda || "",
                        tipo_cambio: "",
                      }));
                    }}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/60">
                      {["PYG","USD",...(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") ? ["BRL"] : []), ...(activeEmpresaPropia?.monedas_habilitadas?.includes("ARS") ? ["ARS"] : [])].map(m=><option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Fecha de pago *</label>
                    <DateInput value={formSueldo.fecha_pago} onChange={e=>setFormSueldo(f=>({...f,fecha_pago:e.target.value}))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/60"/>
                  </div>
                </div>
                )}
                {sueldoMedio !== "cheque" && (
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Cuenta bancaria de pago *</label>
                  <select
                    required
                    value={formSueldo.cuenta_id}
                    onChange={e => {
                      const c = cuentasDisp.find(x => x.id === e.target.value);
                      const cambiaCruce = c?.moneda && formSueldo.moneda && c.moneda !== formSueldo.moneda;
                      setTcSueldoPagoInfo(null);
                      setFormSueldo(f => ({
                        ...f,
                        cuenta_id: e.target.value,
                        cuenta_nombre: c?.nombre || "",
                        cuenta_moneda: c?.moneda || "",
                        tipo_cambio: cambiaCruce ? f.tipo_cambio : "",
                      }));
                    }}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/60"
                  >
                    <option value="">Seleccionar cuenta...</option>
                    {renderCuentasPorMoneda(cuentasDisp, selectedEmp?.logo_tipo || activeEmpresaPropia?.slug)}
                  </select>
                </div>
                )}
                {tiposFacturaDisp.length > 0 && (
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Obra / Tipo de Factura (para imputar el costo de personal)</label>
                    <select
                      value={formSueldo.tipo_factura_id || ""}
                      onChange={e => {
                        const t = tiposFacturaDisp.find(x => x.id === e.target.value);
                        setFormSueldo(f => ({ ...f, tipo_factura_id: e.target.value, tipo_factura_nombre: t?.nombre || "" }));
                      }}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/60"
                    >
                      <option value="">Sin obra asignada</option>
                      {tiposFacturaDisp.map(t => (
                        <option key={t.id} value={t.id}>{t.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
                {sueldoMedio !== "cheque" && formSueldo.cuenta_moneda && formSueldo.cuenta_moneda !== formSueldo.moneda && (
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">
                      Tipo de cambio ({formSueldo.cuenta_moneda !== "PYG" ? formSueldo.cuenta_moneda : formSueldo.moneda}/PYG)
                      {tcSueldoPagoInfo && <span className="text-emerald-400 ml-1">· Sugerido: {Number(tcSueldoPagoInfo.tipo_cambio_sugerido || tcSueldoPagoInfo.venta).toLocaleString("es-PY")}</span>}
                    </label>
                    <input
                      required
                      type="number"
                      min="0"
                      step="any"
                      value={formSueldo.tipo_cambio}
                      onChange={e => { if (!canEditTcDoc) return; setTcSueldoPagoInfo(null); setFormSueldo(f=>({...f,tipo_cambio:e.target.value})); }}
                      placeholder="Ej: 7800"
                      className="w-full bg-arandu-dark border border-amber-500/40 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/60"
                    />
                    {!canEditTcDoc && (
                      <p className="text-slate-500 text-[11px] mt-1">Sin permiso para modificar el tipo de cambio manualmente.</p>
                    )}
                  </div>
                )}
                {sueldoMedio !== "cheque" && (<>
                {selectedEmp.aplica_ips !== false && (
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                    <label className="text-blue-300 text-xs mb-1 block">Descuento IPS</label>
                    <input type="number" value={formSueldo.descuento_ips} onChange={e=>setFormSueldo(f=>({...f,descuento_ips:e.target.value}))}
                      className="w-full bg-white/5 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
                  </div>
                )}
                {extrasPeriodo.length > 0 && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 space-y-2">
                    <p className="text-green-300 text-xs font-medium">Extras este período</p>
                    {extrasPeriodo.map(e => (
                      <div key={e.id} className="flex items-center justify-between text-xs">
                        <span className="text-slate-300">{e.fecha} — {e.descripcion||"Extra"}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-green-300 font-medium">{fmtSueldo(e.monto, e.moneda)}</span>
                          <button onClick={() => handleDeleteExtra(e.id)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3"/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {adelantosPeriodo.length > 0 && (
                  <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3 space-y-2">
                    <p className="text-amber-300 text-xs font-medium">Adelantos este período</p>
                    {adelantosPeriodo.map(a => (
                      <div key={a.id} className="flex items-center justify-between text-xs">
                        <span className="text-slate-300">{a.fecha}{a.notas?` — ${a.notas}`:""}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-amber-300 font-medium">{fmtSueldo(a.monto, a.moneda)}</span>
                          <button onClick={() => handleDeleteAdelanto(a.id)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3"/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-red-300 text-xs font-medium">Descuentos adicionales</p>
                    <button type="button" onClick={() => setFormSueldo(f=>({...f,descuentos_adicionales:[...f.descuentos_adicionales,{descripcion:"",monto:""}]}))}
                      className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"><Plus className="w-3 h-3"/> Agregar</button>
                  </div>
                  {formSueldo.descuentos_adicionales.length === 0 && <p className="text-slate-600 text-xs">Sin descuentos adicionales</p>}
                  {formSueldo.descuentos_adicionales.map((d,idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input type="text" value={d.descripcion}
                        onChange={e=>setFormSueldo(f=>{const a=[...f.descuentos_adicionales];a[idx]={...a[idx],descripcion:e.target.value};return{...f,descuentos_adicionales:a};})}
                        placeholder="Descripción" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none"/>
                      <MoneyInput value={d.monto} moneda="PYG"
                        onChange={value=>setFormSueldo(f=>{const a=[...f.descuentos_adicionales];a[idx]={...a[idx],monto:value};return{...f,descuentos_adicionales:a};})}
                        placeholder="Monto" className="w-28 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none"/>
                      <button onClick={()=>setFormSueldo(f=>({...f,descuentos_adicionales:f.descuentos_adicionales.filter((_,i)=>i!==idx)}))} className="text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5"/></button>
                    </div>
                  ))}
                </div>
                {deudasFactEmp.length > 0 && (
                  <div className="border border-rose-500/40 bg-rose-900/10 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-rose-300 text-xs font-medium">Este empleado tiene deuda de compras en el local</p>
                      <span className="text-rose-300 text-xs font-semibold">Total: ₲ {deudasFactEmp.reduce((s,f)=>s+f.saldo,0).toLocaleString("es-PY")}</span>
                    </div>
                    <p className="text-slate-500 text-[11px]">Marcá las facturas que querés descontar del sueldo y ajustá el monto si es parcial. Si no marcás ninguna, no se descuenta nada.</p>
                    {deudasFactEmp.map(f => {
                      const sel = descFacturasSel.find(x => x.factura_id === f.id);
                      const mismaMoneda = (f.moneda || "PYG") === (formSueldo.moneda || "PYG");
                      return (
                        <div key={f.id} className="flex items-center gap-2">
                          <input type="checkbox" disabled={!mismaMoneda} checked={!!sel} className="accent-rose-400"
                            onChange={() => setDescFacturasSel(prev => sel
                              ? prev.filter(x => x.factura_id !== f.id)
                              : [...prev, { factura_id: f.id, numero: f.numero, monto: String(f.saldo) }])} />
                          <span className="text-slate-300 text-xs flex-1">
                            Factura {f.numero}{f.fecha ? ` · ${String(f.fecha).slice(0,10)}` : ""}
                            {!mismaMoneda && <span className="text-amber-400"> ({f.moneda}: no descontable)</span>}
                          </span>
                          {sel ? (
                            <MoneyInput value={sel.monto} moneda="PYG"
                              onChange={value => setDescFacturasSel(prev => prev.map(x => x.factura_id === f.id
                                ? { ...x, monto: String(Math.min(parseFloat(value) || 0, f.saldo)) } : x))}
                              className="w-28 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white text-xs focus:outline-none"/>
                          ) : null}
                          <span className="text-rose-300 text-xs font-medium whitespace-nowrap">Saldo ₲ {Number(f.saldo).toLocaleString("es-PY")}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {cuotasPrestEmp.length > 0 && (
                  <div className="border border-orange-500/30 bg-orange-900/10 rounded-xl p-3 space-y-2">
                    <p className="text-orange-300 text-xs font-medium">Descuentos por préstamos</p>
                    {cuotasPrestEmp.map(c => {
                      const key = `${c.prestamo_id}-${c.cuota_numero}`;
                      const selected = cuotasPrestSeleccionadas.some(x => x.prestamo_id === c.prestamo_id && x.cuota_numero === c.cuota_numero);
                      return (
                        <label key={key} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={selected} onChange={() => {
                            setCuotasPrestSeleccionadas(prev =>
                              selected
                                ? prev.filter(x => !(x.prestamo_id === c.prestamo_id && x.cuota_numero === c.cuota_numero))
                                : [...prev, { prestamo_id: c.prestamo_id, cuota_numero: c.cuota_numero, monto: c.monto_total }]
                            );
                          }} className="accent-orange-400"/>
                          <span className="text-slate-300 text-xs flex-1">Cuota {c.cuota_numero} — {c.entidad_nombre}</span>
                          {c.estado === "vencida" && <span className="text-red-400 text-[10px] bg-red-400/10 px-1.5 py-0.5 rounded">Vencida</span>}
                          <span className="text-orange-300 text-xs font-medium">₲ {Number(c.monto_total).toLocaleString("es-PY")}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Notas</label>
                  <textarea value={formSueldo.notas} onChange={e=>setFormSueldo(f=>({...f,notas:e.target.value}))} rows={2}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none resize-none"/>
                </div>
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setShowSueldoModal(false)} className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">Cancelar</button>
                  <button onClick={handleSaveSueldo} disabled={savingSueldo}
                    className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                    {savingSueldo ? "Guardando..." : `Confirmar — ${fmtSueldo(calcMontoAPagar(selectedEmp, adelantosPeriodo, extrasPeriodo, [...formSueldo.descuentos_adicionales, ...cuotasPrestSeleccionadas.map(c=>({monto:c.monto})), ...descFacturasSel.map(c=>({monto:parseFloat(c.monto)||0}))]), formSueldo.moneda)}`}
                  </button>
                </div>
                </>)}
              </div>
            </div>
          </div>
        )}

        {/* MODAL: Adelanto */}
        {showAdelantoModal && adelantoEmp && (
          <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
            <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-white font-heading text-lg">Adelantos</h2>
                  <p className="text-slate-400 text-sm">{adelantoEmp.nombre} {adelantoEmp.apellido} — {mesLabel(filtroMes)}</p>
                </div>
                <button onClick={() => setShowAdelantoModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300 mb-4">
                Sueldo neto: <strong>{fmtSueldo((parseFloat(adelantoEmp.sueldo_base)||0) - calcularIPS(adelantoEmp), adelantoEmp.moneda_sueldo || adelantoEmp.moneda || "PYG")}</strong>
              </div>

              {/* Lista de adelantos existentes */}
              {(adelantosPeriodo || []).length > 0 && (
                <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3 mb-4 space-y-1.5">
                  <p className="text-amber-300 text-xs font-medium mb-1">Adelantos del período ({adelantosPeriodo.length})</p>
                  {adelantosPeriodo.map(a => (
                    <div key={a.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-amber-500/10 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-amber-200 text-xs">{a.fecha}</p>
                        {a.notas && <p className="text-slate-500 text-[11px] truncate">{a.notas}</p>}
                      </div>
                      <span className="text-amber-300 text-sm font-medium">{fmtSueldo(a.monto, a.moneda || "PYG")}</span>
                      <button onClick={() => handleDeleteAdelanto(a.id)}
                        className="text-slate-500 hover:text-red-400 transition-colors p-1"
                        title="Eliminar adelanto">
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between pt-1.5 text-xs">
                    <span className="text-slate-400">Total descontado al sueldo:</span>
                    <strong className="text-amber-300">{fmtSueldo(adelantosPeriodo.reduce((s,a)=>s+(a.monto||0),0), adelantoEmp.moneda_sueldo || adelantoEmp.moneda || "PYG")}</strong>
                  </div>
                </div>
              )}

              <p className="text-slate-400 text-xs font-medium mb-2">Nuevo adelanto</p>
              <div className="space-y-3">
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Monto *</label>
                  <MoneyInput min="0" step="any" autoFocus moneda="PYG" value={formAdelanto.monto} onChange={value=>setFormAdelanto(f=>({...f,monto:value}))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/60"/>
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Fecha *</label>
                  <DateInput value={formAdelanto.fecha} onChange={e=>setFormAdelanto(f=>({...f,fecha:e.target.value}))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/60"/>
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Cuenta bancaria de pago</label>
                  <select
                    value={formAdelanto.cuenta_id}
                    onChange={e => {
                      const c = cuentasDisp.find(x => x.id === e.target.value);
                      setFormAdelanto(f => ({ ...f, cuenta_id: e.target.value, cuenta_nombre: c?.nombre || "" }));
                    }}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/60"
                  >
                    <option value="">— Sin especificar —</option>
                    {renderCuentasPorMoneda(cuentasDisp, selectedEmp?.logo_tipo || activeEmpresaPropia?.slug)}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Notas (opcional)</label>
                  <input type="text" value={formAdelanto.notas} onChange={e=>setFormAdelanto(f=>({...f,notas:e.target.value}))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/60"/>
                </div>
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setShowAdelantoModal(false)} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">Cerrar</button>
                  <button onClick={handleSaveAdelanto} disabled={savingAdelanto}
                    className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                    {savingAdelanto ? "Guardando..." : "Registrar adelanto"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: Extra */}
        {showExtraModal && extraEmp && (
          <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
            <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-white font-heading text-lg">Extras</h2>
                  <p className="text-slate-400 text-sm">{extraEmp.nombre} {extraEmp.apellido} — {mesLabel(filtroMes)}</p>
                </div>
                <button onClick={() => setShowExtraModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
              </div>
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-xs text-green-300 mb-4">
                Los extras se <strong>suman</strong> al sueldo al registrar el pago. En el balance entran dentro del sueldo pagado.
              </div>

              {/* Lista de extras existentes */}
              {(extrasPeriodo || []).length > 0 && (
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 mb-4 space-y-1.5">
                  <p className="text-green-300 text-xs font-medium mb-1">Extras del período ({extrasPeriodo.length})</p>
                  {extrasPeriodo.map(e => (
                    <div key={e.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-green-500/10 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-green-200 text-xs">{e.fecha} — {e.descripcion || "Extra"}</p>
                        {e.notas && <p className="text-slate-500 text-[11px] truncate">{e.notas}</p>}
                      </div>
                      <span className="text-green-300 text-sm font-medium">{fmtSueldo(e.monto, e.moneda || "PYG")}</span>
                      <button onClick={() => handleDeleteExtra(e.id)}
                        className="text-slate-500 hover:text-red-400 transition-colors p-1"
                        title="Eliminar extra">
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between pt-1.5 text-xs">
                    <span className="text-slate-400">Total pagado en extras:</span>
                    <strong className="text-green-300">{fmtSueldo(extrasPeriodo.reduce((s,e)=>s+(e.monto||0),0), extraEmp.moneda_sueldo || extraEmp.moneda || "PYG")}</strong>
                  </div>
                </div>
              )}

              <p className="text-slate-400 text-xs font-medium mb-2">Nuevo extra</p>
              <div className="space-y-3">
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Descripción</label>
                  <input type="text" value={formExtra.descripcion} onChange={e=>setFormExtra(f=>({...f,descripcion:e.target.value}))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500/60"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Monto *</label>
                    <MoneyInput min="0" step="any" autoFocus moneda={formExtra.moneda || "PYG"} value={formExtra.monto} onChange={value=>setFormExtra(f=>({...f,monto:value}))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500/60"/>
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Moneda</label>
                    <select value={formExtra.moneda} onChange={e => setFormExtra(f => ({ ...f, moneda: e.target.value, tipo_cambio: "" }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500/60">
                      {["PYG","USD",...(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") ? ["BRL"] : []), ...(activeEmpresaPropia?.monedas_habilitadas?.includes("ARS") ? ["ARS"] : [])].map(m=><option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                {formExtra.moneda !== "PYG" && (
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Tipo de cambio (a PYG)</label>
                    <input type="number" min="0" step="any" value={formExtra.tipo_cambio} onChange={e=>setFormExtra(f=>({...f,tipo_cambio:e.target.value}))}
                      placeholder="Ej: 7500"
                      className="w-full bg-arandu-dark border border-amber-500/40 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/60"/>
                  </div>
                )}
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Fecha *</label>
                  <DateInput value={formExtra.fecha} onChange={e=>setFormExtra(f=>({...f,fecha:e.target.value}))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500/60"/>
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Notas (opcional)</label>
                  <input type="text" value={formExtra.notas} onChange={e=>setFormExtra(f=>({...f,notas:e.target.value}))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500/60"/>
                </div>
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setShowExtraModal(false)} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">Cerrar</button>
                  <button onClick={handleSaveExtra} disabled={savingExtra}
                    className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                    {savingExtra ? "Guardando..." : "Registrar extra"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════ TAB: PAGO IVA ══════════════════════════════════════ */}
        {tab === "pago-iva" && (
          <div>
            {/* Selector de período + buscador */}
            <div className="flex flex-col gap-3 mb-5">
              <div className="flex items-center gap-3 flex-wrap">
                {filtroTipo === "mes" ? (
                  <>
                    <input
                      type="month"
                      value={periodoIva}
                      onChange={e => { setPeriodoIva(e.target.value); setFiltroMes(e.target.value); }}
                      className="bg-arandu-dark-light border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500/50"
                    />
                    <span className="text-slate-500 text-sm">Período IVA</span>
                  </>
                ) : (
                  <span className="text-slate-400 text-sm">
                    {filtroTipo === "anio" ? `IVA del año ${filtroAnio}` : "IVA de todo el tiempo"}
                  </span>
                )}
                {(canViewDeleted("balance.editar") || puedeGestionarPagoIva) && (
                  <button
                    onClick={() => setMostrarEliminadasIva(v => !v)}
                    className={`ml-auto flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border transition-all ${mostrarEliminadasIva ? "bg-red-500/20 border-red-500/40 text-red-300" : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"}`}
                  >
                    <Trash2 className="w-4 h-4" /> {mostrarEliminadasIva ? "Ocultar eliminadas" : "Ver eliminadas"}
                  </button>
                )}
                {!mostrarEliminadasIva && puedeGestionarPagoIva && (
                  <button
                    onClick={openNewAjusteIva}
                    title="Corrige el saldo de IVA a pagar/a favor cuando no coincide con lo que informa el contador — no genera ningún movimiento de caja/banco ni afecta el Balance"
                    className="flex items-center gap-2 bg-arandu-dark-light border border-amber-500/30 hover:border-amber-500/50 text-amber-300 text-sm font-medium px-4 py-2 rounded-lg transition-all"
                  >
                    <Settings className="w-4 h-4" /> Ajuste de IVA
                  </button>
                )}
                {!mostrarEliminadasIva && puedeGestionarPagoIva && (
                  <button
                    onClick={openNewPagoIva}
                    className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-all"
                  >
                    <Plus className="w-4 h-4" /> Registrar pago IVA
                  </button>
                )}
              </div>
              <ChipSearch
                chips={ivaChips} setChips={setIvaChips}
                inputVal={ivaInput} setInputVal={setIvaInput}
                placeholder="Buscar en pagos IVA por descripción, fecha, monto… (Enter para agregar filtro)"
                accentColor="amber"
              />
            </div>

            {loadingIva ? (
              <div className="flex items-center justify-center py-16 text-amber-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Calculando IVA...
              </div>
            ) : (
              <div className="space-y-5">
                {ivaBalance && (
                  <>
                    {/* Saldo acumulado desde enero */}
                    {(ivaBalance.saldo_pendiente_acumulado > 0 || ivaBalance.saldo_a_favor_acumulado > 0) && (
                      <div className={`rounded-xl p-4 border flex items-center justify-between ${
                        ivaBalance.saldo_pendiente_acumulado > 0
                          ? "bg-orange-500/10 border-orange-500/30"
                          : "bg-emerald-500/10 border-emerald-500/30"
                      }`}>
                        <div>
                          <p className={`text-xs font-medium mb-0.5 ${ivaBalance.saldo_pendiente_acumulado > 0 ? "text-orange-400" : "text-emerald-400"}`}>
                            {ivaBalance.saldo_pendiente_acumulado > 0
                              ? `Saldo IVA acumulado a pagar al cierre de ${mesLabel(periodoIva)}`
                              : `Saldo IVA acumulado a favor al cierre de ${mesLabel(periodoIva)}`}
                          </p>
                          <p className="text-slate-400 text-xs">
                            Arrastre anterior: {ivaSaldoTexto(ivaBalance.saldo_anterior)}
                            {" · "}
                            Movimiento del mes: {ivaSaldoTexto(ivaBalance.saldo_mes)}
                          </p>
                          <p className="text-slate-500 text-xs">
                            Débito acum.: {fmtPYG(ivaBalance.iva_debito_acumulado)} · Crédito acum.: {fmtPYG(ivaBalance.iva_credito_acumulado)}
                            {ivaBalance.iva_pagado_acumulado > 0 && ` · Ya pagado: ${fmtPYG(ivaBalance.iva_pagado_acumulado)}`}
                          </p>
                        </div>
                        <p className={`font-heading font-bold text-2xl ml-4 ${ivaBalance.saldo_pendiente_acumulado > 0 ? "text-orange-300" : "text-emerald-300"}`}>
                          {fmtPYG(ivaBalance.saldo_pendiente_acumulado > 0 ? ivaBalance.saldo_pendiente_acumulado : ivaBalance.saldo_a_favor_acumulado)}
                        </p>
                      </div>
                    )}

                    {/* Aviso cuando no hay datos para el período */}
                    {ivaBalance.iva_debito === 0 && ivaBalance.iva_credito === 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-300 text-sm font-medium">Sin facturas ni compras para este período</p>
                          <p className="text-slate-400 text-xs mt-1">
                            Si tenés facturas emitidas, revisá que el mes del selector coincida con la fecha de esas facturas.
                            El IVA se calcula por fecha de emisión de la factura, no por fecha de cobro.
                          </p>
                          <p className="text-slate-500 text-xs mt-1">
                            Para ver el crédito fiscal de compras, asegurate de marcar "Tiene factura" al registrar una compra.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {ivaResumen && (
                  <div className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-amber-300 text-sm font-medium">Detalle IVA por mes</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10 text-slate-400 text-xs uppercase">
                          <th className="px-4 py-3 text-left">Mes</th>
                          <th className="px-4 py-3 text-right">Débito</th>
                          <th className="px-4 py-3 text-right">Crédito</th>
                          <th className="px-4 py-3 text-right">Neto</th>
                          <th className="px-4 py-3 text-right">Pagado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ivaResumen.meses || []).map(m => (
                          <tr key={m.periodo} className="border-b border-white/5">
                            <td className="px-4 py-3 text-white">{mesLabel(m.periodo)}</td>
                            <td className="px-4 py-3 text-right text-cyan-300">{fmtPYG(m.iva_debito || 0)}</td>
                            <td className="px-4 py-3 text-right text-violet-300">{fmtPYG(m.iva_credito || 0)}</td>
                            <td className={`px-4 py-3 text-right ${(m.iva_neto || 0) > 0 ? "text-red-300" : "text-emerald-300"}`}>{fmtPYG(Math.abs(m.iva_neto || 0))}</td>
                            <td className="px-4 py-3 text-right text-amber-300">{fmtPYG(m.pagos_iva || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {ivaBalance?.detalle_retenciones?.length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-amber-500/5">
                      <FileText className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-300 font-medium text-sm">Retenciones IVA de clientes</span>
                      <span className="ml-auto text-amber-300 font-heading font-bold text-sm">
                        {fmtPYG(ivaBalance.iva_credito_retenciones || 0)}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[560px] text-sm">
                        <thead>
                          <tr className="border-b border-white/10 text-slate-400 text-xs uppercase">
                            <th className="px-4 py-3 text-left">Cliente</th>
                            <th className="px-4 py-3 text-right">Factura</th>
                            <th className="px-4 py-3 text-right">IVA factura</th>
                            <th className="px-4 py-3 text-right">% Ret.</th>
                            <th className="px-4 py-3 text-right">Retenido</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ivaBalance.detalle_retenciones.map((r, i) => (
                            <tr key={`${r.numero_factura || i}-${i}`} className="border-b border-white/5">
                              <td className="px-4 py-3 text-white">{r.razon_social || "—"}</td>
                              <td className="px-4 py-3 text-right text-slate-400 text-xs">{r.numero_factura || "—"}</td>
                              <td className="px-4 py-3 text-right text-slate-300">{fmtPYG(r.iva_factura || 0)}</td>
                              <td className="px-4 py-3 text-right text-slate-400 text-xs">{r.porcentaje_retencion || 0}%</td>
                              <td className="px-4 py-3 text-right text-amber-300 font-medium">{fmtPYG(r.monto_retenido || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-2 text-xs text-slate-500 border-t border-white/5">
                      Estas retenciones se suman al crédito fiscal del período y reducen el IVA a pagar.
                    </div>
                  </div>
                )}

                {/* Pagos IVA registrados para este período */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-amber-500/5">
                    <Receipt className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-300 font-medium text-sm">
                      Pagos IVA registrados{filtroTipo === "mes" ? ` — ${periodoIva}` : ""}
                    </span>
                    {pagosIvaList.length > 0 && (
                      <span className="ml-auto text-amber-300 font-heading font-bold text-sm">
                        {fmtPYG(pagosIvaList.reduce((s, p) => s + Math.abs(p.monto), 0))}
                      </span>
                    )}
                  </div>
                  {pagosIvaList.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      <p>{mostrarEliminadasIva ? "No hay pagos IVA eliminados para este período" : "No hay pagos IVA registrados para este período"}</p>
                      {Math.max(0, ivaBalance?.saldo_mes ?? ivaBalance?.iva_neto ?? 0) > 0 && (
                        <p className="text-amber-500 text-xs mt-1">Tenés {fmtPYG(Math.max(0, ivaBalance?.saldo_mes ?? ivaBalance?.iva_neto ?? 0))} a pagar a la SET</p>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
	                      {pagosIvaList.filter(pago => {
	                        const texto = [pago.descripcion, pago.fecha_pago, pago.periodo_iva, pago.cuenta_nombre, String(Math.abs(pago.monto)||""), pago.notas].filter(Boolean).join(" ");
	                        return matchChips(ivaChips, ivaInput, texto);
	                      }).map(pago => (
	                        <div key={pago.id} className={`flex items-center justify-between gap-3 px-4 py-3 ${pago.eliminada ? "bg-red-900/10" : ""}`}>
	                          <div>
	                            <p className="text-white text-sm font-medium">{pago.descripcion}</p>
	                            {pago.eliminada && <span className="inline-flex mt-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">Eliminado</span>}
	                            <p className="text-slate-500 text-xs">{pago.fecha_pago} · IVA {pago.periodo_iva || periodoIva}</p>
	                            {pago.cuenta_nombre && <p className="text-slate-600 text-xs">{pago.cuenta_nombre}</p>}
	                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div>
                              <p className="text-red-300 font-heading font-semibold">{fmtPYG(Math.abs(pago.monto))}</p>
                              {pago.notas && <p className="text-slate-500 text-xs">{pago.notas}</p>}
                            </div>
                            {!mostrarEliminadasIva && puedeGestionarPagoIva && (
                              <div className="flex gap-1">
                                {/* editar pago IVA deshabilitado — para corregir, eliminar y recrear */}
                                <button onClick={() => handleDeletePagoIva(pago.id)} className="p-1.5 text-slate-400 hover:text-red-300" title="Eliminar pago IVA">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Ajustes de IVA registrados para este período */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-amber-500/5">
                    <Settings className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-300 font-medium text-sm">
                      Ajustes de IVA{filtroTipo === "mes" ? ` — ${periodoIva}` : ""}
                    </span>
                    {ajustesIvaList.length > 0 && (
                      <span className="ml-auto text-amber-300 font-heading font-bold text-sm">
                        {ivaSaldoTexto(ajustesIvaList.reduce((s, a) => s + (a.monto || 0), 0))}
                      </span>
                    )}
                  </div>
                  {ajustesIvaList.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 text-sm">
                      <p>No hay ajustes de IVA registrados para este período</p>
                      <p className="text-slate-600 text-xs mt-1">
                        Usá "Ajuste de IVA" cuando el saldo calculado por el sistema no coincida con lo que informa el contador.
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {ajustesIvaList.map(aj => (
                        <div key={aj.id} className="flex items-center justify-between gap-3 px-4 py-3">
                          <div>
                            <p className="text-white text-sm font-medium">{aj.descripcion}</p>
                            <p className="text-slate-500 text-xs">{aj.fecha} · IVA {aj.periodo_iva || periodoIva} {aj.usuario ? `· ${aj.usuario}` : ""}</p>
                            {aj.notas && <p className="text-slate-600 text-xs">{aj.notas}</p>}
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <p className={`font-heading font-semibold ${(aj.monto || 0) > 0 ? "text-red-300" : "text-emerald-300"}`}>
                              {(aj.monto || 0) > 0 ? "+" : ""}{fmtPYG(aj.monto || 0)}
                            </p>
                            {puedeGestionarPagoIva && (
                              <button onClick={() => handleDeleteAjusteIva(aj.id)} className="p-1.5 text-slate-400 hover:text-red-300" title="Eliminar ajuste de IVA">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal registrar pago IVA */}
            {showPagoIvaForm && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                <div className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white">Registrar pago IVA</h3>
                      <p className="text-slate-400 text-sm">Período: {periodoIva}</p>
                    </div>
                    <button onClick={() => setShowPagoIvaForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  {Math.max(0, ivaBalance?.saldo_mes ?? ivaBalance?.iva_neto ?? 0) > 0 && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 flex justify-between">
                      <span className="text-red-300 text-sm">IVA a pagar este período</span>
                      <span className="text-red-400 font-bold">{fmtPYG(Math.max(0, ivaBalance?.saldo_mes ?? ivaBalance?.iva_neto ?? 0))}</span>
                    </div>
                  )}
                  <form onSubmit={handlePagoIva} className="space-y-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Período IVA *</label>
                      <input type="month" value={pagoIvaForm.periodo_iva} onChange={e => setPagoIvaForm(p => ({ ...p, periodo_iva: e.target.value, descripcion: p.descripcion || `Pago IVA ${e.target.value}` }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Descripción</label>
                      <input value={pagoIvaForm.descripcion} onChange={e => setPagoIvaForm(p => ({ ...p, descripcion: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
                        placeholder={`Pago IVA ${pagoIvaForm.periodo_iva || periodoIva}`} />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Monto (₲) *</label>
                      <MoneyInput min="0" step="any" moneda="PYG" value={pagoIvaForm.monto}
                        onChange={value => setPagoIvaForm(p => ({ ...p, monto: value }))}
                        onFocus={e => e.target.select()}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
                        placeholder="0" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Fecha *</label>
                      <DateInput value={pagoIvaForm.fecha_pago} onChange={e => setPagoIvaForm(p => ({ ...p, fecha_pago: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Cuenta bancaria de pago inmediato</label>
                      <select value={pagoIvaForm.cuenta_id} onChange={e => {
                        const cuenta = cuentasDisp.find(c => c.id === e.target.value);
                        setPagoIvaForm(p => ({ ...p, cuenta_id: e.target.value, cuenta_nombre: cuenta?.nombre || "" }));
                      }}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50" required>
                        <option value="">Seleccionar cuenta...</option>
                        {cuentasDisp.filter(c => c.moneda === "PYG").map(c => (
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                      <input value={pagoIvaForm.notas} onChange={e => setPagoIvaForm(p => ({ ...p, notas: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
                        placeholder="Nº de comprobante, banco, etc." />
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button type="button" onClick={() => setShowPagoIvaForm(false)}
                        className="flex-1 py-2.5 rounded-lg border border-white/10 text-slate-400 text-sm hover:text-white">Cancelar</button>
                      <button type="submit"
                        className="flex-1 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium flex items-center justify-center gap-2">
                        <Check className="w-4 h-4" /> {editingPagoIva ? "Actualizar" : "Registrar pago"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Modal Ajuste de IVA — corrección manual, no toca caja/banco ni el Balance */}
            {showAjusteIvaForm && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                <div className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white">Ajuste de IVA</h3>
                      <p className="text-slate-400 text-sm">Período: {ajusteIvaForm.periodo_iva}</p>
                    </div>
                    <button onClick={() => setShowAjusteIvaForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4 text-xs text-amber-200">
                    Corrige el saldo de IVA a pagar/a favor cuando no coincide con lo que informa el contador.
                    No genera ninguna factura, ingreso ni movimiento de caja/banco, y no afecta el Balance general —
                    solo ajusta este cálculo. Monto positivo = suma al IVA a pagar; monto negativo = lo reduce.
                  </div>
                  <form onSubmit={handleAjusteIva} className="space-y-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Período IVA *</label>
                      <input type="month" value={ajusteIvaForm.periodo_iva} onChange={e => setAjusteIvaForm(p => ({ ...p, periodo_iva: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Motivo *</label>
                      <input value={ajusteIvaForm.motivo} onChange={e => setAjusteIvaForm(p => ({ ...p, motivo: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
                        placeholder="Ej: diferencia informada por el contador" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Monto (₲) * — positivo suma, negativo resta</label>
                      <Input type="number" step="any" value={ajusteIvaForm.monto}
                        onChange={e => setAjusteIvaForm(p => ({ ...p, monto: e.target.value }))}
                        onFocus={e => e.target.select()}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
                        placeholder="ej: 300000 o -300000" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Fecha *</label>
                      <DateInput value={ajusteIvaForm.fecha} onChange={e => setAjusteIvaForm(p => ({ ...p, fecha: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50" required />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                      <input value={ajusteIvaForm.notas} onChange={e => setAjusteIvaForm(p => ({ ...p, notas: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
                        placeholder="Detalle adicional, referencia del contador, etc." />
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button type="button" onClick={() => setShowAjusteIvaForm(false)}
                        className="flex-1 py-2.5 rounded-lg border border-white/10 text-slate-400 text-sm hover:text-white">Cancelar</button>
                      <button type="submit"
                        className="flex-1 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium flex items-center justify-center gap-2">
                        <Check className="w-4 h-4" /> Guardar ajuste
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "comisiones" && <ComisionesVentasPanel />}

        {/* ═══════════════ TAB: ANTICIPOS DE PROVEEDOR ═════════════════════════ */}
        {tab === "anticipos" && (
          <div className="space-y-6">
            {/* Form nuevo anticipo */}
            {hasPermission("anticipos_proveedores.gestionar") && (
              <div className="bg-arandu-dark-light border border-white/10 rounded-2xl p-5">
                <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <HandCoins className="w-4 h-4 text-teal-400" /> Registrar anticipo a proveedor
                </h2>
                <form onSubmit={handleSaveAnticipo} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Proveedor *</label>
                      <ProveedorSearchSelect
                        proveedores={proveedores}
                        value={anticipoForm.proveedor_id ? { id: anticipoForm.proveedor_id, nombre: anticipoForm.proveedor_nombre, ruc: anticipoForm.proveedor_ruc } : null}
                        onChange={sel => setAnticipoForm(p => ({ ...p, proveedor_id: sel?.id || "", proveedor_nombre: sel?.nombre || "", proveedor_ruc: sel?.ruc || "" }))}
                        onProveedorCreado={p => setProveedores(prev => [...prev, p].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")))}
                        token={token} logo={activeEmpresaPropia?.slug}
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Fecha *</label>
                      <DateInput value={anticipoForm.fecha} onChange={e => setAnticipoForm(p => ({ ...p, fecha: e.target.value }))} className="bg-arandu-dark border-white/10 text-white" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Monto *</label>
                      <MoneyInput moneda={anticipoForm.moneda || "PYG"} min="0.01" step="any" value={anticipoForm.monto} onChange={value => setAnticipoForm(p => ({ ...p, monto: value }))} placeholder="0" className="w-full rounded-md border px-3 py-2 bg-arandu-dark border-white/10 text-white" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Moneda</label>
                      <div className="flex gap-1">
                        {["PYG","USD",...(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") ? ["BRL"] : [])].map(m => (
                          <button key={m} type="button" onClick={() => setAnticipoForm(p => ({ ...p, moneda: m }))}
                            className={`flex-1 py-2 rounded-lg border text-xs transition-all ${anticipoForm.moneda === m ? "border-teal-500 bg-teal-500/10 text-teal-300" : "border-white/10 text-slate-400 hover:border-white/20"}`}>{m}</button>
                        ))}
                      </div>
                    </div>
                    {anticipoForm.moneda !== "PYG" && (
                      <div>
                        <label className="text-slate-400 text-sm mb-1 block">TC del proveedor *</label>
                        <Input type="number" min="0" step="any" value={anticipoForm.tipo_cambio} onChange={e => setAnticipoForm(p => ({ ...p, tipo_cambio: e.target.value }))} placeholder="Tipo de cambio" className="bg-arandu-dark border-white/10 text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Cuenta bancaria de egreso *</label>
                    <select value={anticipoForm.cuenta_id}
                      onChange={e => {
                        const c = (cuentasDisp || []).find(x => x.id === e.target.value);
                        setAnticipoForm(p => ({ ...p, cuenta_id: e.target.value, cuenta_nombre: c?.nombre || "" }));
                      }}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Seleccionar cuenta...</option>
                      {renderCuentasPorMoneda(cuentasDisp, activeEmpresaPropia?.slug)}
                    </select>
                  </div>
                  <div className="rounded-xl border border-teal-500/20 bg-teal-500/5 p-3">
                    <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Cuenta título deuda</p>
                    <p className="text-teal-200 text-sm font-semibold">
                      {planCuentaAnticipoProveedor?.nombre || "Anticipos a proveedores"}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">
                      El anticipo queda como cuenta por cobrar al proveedor; la caja/banco solo define de dónde sale la plata.
                    </p>
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                    <Input value={anticipoForm.notas} onChange={e => setAnticipoForm(p => ({ ...p, notas: e.target.value }))} placeholder="Observaciones opcionales" className="bg-arandu-dark border-white/10 text-white" />
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" disabled={savingAnticipo} className="bg-teal-600 hover:bg-teal-700 text-white">
                      {savingAnticipo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Registrar anticipo</>}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Lista de anticipos */}
            <div className="bg-arandu-dark-light border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-white font-semibold flex items-center gap-2"><HandCoins className="w-4 h-4 text-teal-400" /> Anticipos registrados</h2>
                <div className="flex gap-1">
                  {[{ v: "disponible", label: "Disponibles" }, { v: "parcial", label: "Parciales" }, { v: "agotado", label: "Agotados" }, { v: "todos", label: "Todos" }].map(f => (
                    <button key={f.v} onClick={() => setAnticiposFilter(f.v)}
                      className={`px-3 py-1 rounded-lg text-xs transition-all border ${anticiposFilter === f.v ? "bg-teal-600 border-teal-600 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>{f.label}</button>
                  ))}
                </div>
              </div>
              {loadingAnticiposList ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
              ) : anticiposList.length === 0 ? (
                <p className="text-center text-slate-500 py-8 text-sm">Sin anticipos registrados.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/5">
                      <th className="text-left text-slate-400 font-normal pb-2 pr-4">Proveedor</th>
                      <th className="text-left text-slate-400 font-normal pb-2 pr-4">Fecha</th>
                      <th className="text-right text-slate-400 font-normal pb-2 pr-4">Original</th>
                      <th className="text-right text-slate-400 font-normal pb-2 pr-4">Saldo</th>
                      <th className="text-center text-slate-400 font-normal pb-2 pr-4">Estado</th>
                      <th className="text-slate-400 font-normal pb-2"></th>
                    </tr></thead>
                    <tbody>
                      {anticiposList.map(ant => {
                        const simbolo = ant.moneda === "USD" ? "$ " : ant.moneda === "BRL" ? "R$ " : "₲ ";
                        const fmt = v => `${simbolo}${Math.round(v).toLocaleString("es-PY")}`;
                        const estadoColor = ant.estado === "disponible" ? "text-emerald-400 bg-emerald-500/10" : ant.estado === "parcial" ? "text-amber-400 bg-amber-500/10" : "text-slate-500 bg-slate-500/10";
                        return (
                          <tr key={ant.id} className="border-b border-white/5 hover:bg-white/2">
                            <td className="py-2 pr-4">
                              <p className="text-white">{ant.proveedor_nombre}</p>
                              {ant.notas && <p className="text-slate-500 text-xs truncate max-w-48">{ant.notas}</p>}
                            </td>
                            <td className="py-2 pr-4 text-slate-400 text-xs">{ant.fecha}</td>
                            <td className="py-2 pr-4 text-right text-white font-mono text-xs">{fmt(ant.monto_original)}</td>
                            <td className="py-2 pr-4 text-right font-mono text-xs">
                              <span className={ant.saldo_disponible > 0 ? "text-emerald-400" : "text-slate-500"}>{fmt(ant.saldo_disponible)}</span>
                            </td>
                            <td className="py-2 pr-4 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${estadoColor}`}>{ant.estado}</span>
                            </td>
                            <td className="py-2 text-right">
                              {ant.estado === "disponible" && hasPermission("anticipos_proveedores.gestionar") && (
                                <button onClick={() => handleDeleteAnticipo(ant.id)} className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded hover:bg-red-500/10 transition-colors">Eliminar</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════ TAB: CONFIGURACIÓN ══════════════════════════════════ */}
        {tab === "configuracion" && (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm">Configuración del módulo Egresos.</p>

            {/* Card: Impresión de compras */}
            {(hasPermission("compras.configurar_impresion") || ["admin","gerente","super_admin"].includes(user?.role)) && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                    <Printer className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Impresión de compras</p>
                    <p className="text-slate-400 text-xs mt-0.5 max-w-md">
                      Elegí qué modelos se ofrecen al imprimir una compra: A4, Ticket o ambos. Se aplica a la empresa activa.
                    </p>
                  </div>
                </div>
                {[["a4", "A4 (hoja completa)"], ["ticket", "Ticket (80 mm)"]].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-slate-200 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={cfgImpTmp.includes(k)}
                      onChange={() => setCfgImpTmp(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k])} className="accent-blue-500" />
                    {lbl}
                  </label>
                ))}
                <button type="button" disabled={cfgImpTmp.length === 0 || (cfgImpTmp.length === modelosImpCompra.length && cfgImpTmp.every(x => modelosImpCompra.includes(x)))}
                  onClick={() => guardarCfgImpCompra(cfgImpTmp)}
                  className="mt-3 px-4 py-2 rounded-lg bg-blue-600/80 hover:bg-blue-600 text-white text-xs font-medium disabled:opacity-40">
                  Guardar
                </button>
              </div>
            )}

            {/* Card: Sueldo Mínimo */}
            {puedeAjustarSueldo && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Sueldo Mínimo Vigente</p>
                    <p className="text-slate-400 text-xs mt-0.5 max-w-md">
                      Actualizá el sueldo mínimo cuando haya un aumento. Esto recalcula el IPS de todos los empleados y opcionalmente sube el sueldo de quienes cobran el mínimo actual.
                    </p>
                  </div>
                </div>

                <div className="bg-arandu-dark rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Mínimo actual registrado</span>
                  <span className="text-white font-semibold font-mono">₲ {sueldoMinimoGlobal.toLocaleString("es-PY")}</span>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Nuevo sueldo mínimo (Gs.)</label>
                      <input
                        type="number"
                        value={nuevoMinimo}
                        onChange={e => setNuevoMinimo(e.target.value)}
                        placeholder={`Ej: ${(sueldoMinimoGlobal * 1.08).toFixed(0)}`}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/60"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Vigente desde *</label>
                      <DateInput
                        value={fechaVigenciaMinimo}
                        onChange={e => setFechaVigenciaMinimo(e.target.value)}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/60"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleActualizarSueldoMinimo}
                    disabled={savingMinimo || !nuevoMinimo || !fechaVigenciaMinimo}
                    className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs font-medium transition-colors disabled:opacity-40"
                  >
                    {savingMinimo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
                    Actualizar sueldo mínimo
                  </button>
                  <p className="text-slate-600 text-xs">
                    Recalcula el IPS y sube el sueldo base de empleados que cobran el mínimo actual (±500 Gs.), registrando el cambio en su historial.
                  </p>

                  {/* Historial de cambios de mínimo */}
                  {historialMinimos.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-slate-500 text-xs mb-2 font-medium">Historial de actualizaciones</p>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {historialMinimos.map((h, i) => (
                          <div key={i} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-white/3 border border-white/5">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">{h.fecha_vigencia}</span>
                              <span className="text-slate-600">•</span>
                              <span className="text-slate-400 font-mono">₲ {(h.valor_anterior||0).toLocaleString("es-PY")} → ₲ {(h.valor||0).toLocaleString("es-PY")}</span>
                              {h.incluye_sueldos_base && <span className="text-emerald-500/70 text-[10px] bg-emerald-500/10 px-1.5 py-0.5 rounded">+sueldos</span>}
                            </div>
                            <span className="text-slate-600">{h.usuario}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Card: Pago de compras al contado */}
            {puedeVerCfgPagoContado && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold text-sm">Pago de compras al contado</p>
                    <p className="text-slate-400 text-xs mt-0.5 max-w-lg">
                      Elegí qué pasa al registrar una compra al contado en esta empresa.
                    </p>
                    <div className="mt-3 space-y-2">
                      {[
                        { v: false, t: "Pagar después desde Pagos Proveedores", d: "La compra queda pendiente y se paga desde Pagos Proveedores (comportamiento por defecto)." },
                        { v: true, t: "Pagar automáticamente al registrar", d: "Se genera el pago con la fecha de la compra, descontando de la cuenta indicada abajo." },
                      ].map(op => (
                        <label key={String(op.v)} className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${!!cfgEgresos.pago_automatico_contado === op.v ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 hover:bg-white/5"} ${!puedeGestionarCfgPagoContado ? "opacity-70 cursor-not-allowed" : ""}`}>
                          <input type="radio" className="mt-1" disabled={!puedeGestionarCfgPagoContado || savingCfgEgresos}
                            checked={!!cfgEgresos.pago_automatico_contado === op.v}
                            onChange={() => guardarCfgEgresos({ pago_automatico_contado: op.v })} />
                          <span>
                            <span className="block text-sm text-white">{op.t}</span>
                            <span className="block text-xs text-slate-400">{op.d}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    {cfgEgresos.pago_automatico_contado && (
                      <div className="mt-3 max-w-sm">
                        <label className="text-slate-400 text-xs block mb-1">Cuenta de pago automático</label>
                        <select
                          disabled={!puedeGestionarCfgPagoContado || savingCfgEgresos}
                          value={cfgEgresos.cuenta_pago_automatico_id || ""}
                          onChange={e => guardarCfgEgresos({ cuenta_pago_automatico_id: e.target.value || null })}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/60"
                        >
                          <option value="">— Cuenta predeterminada de la moneda —</option>
                          {(cuentasDisp || []).map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda})</option>)}
                        </select>
                        <p className="text-slate-500 text-[11px] mt-1">Si la moneda de la compra no coincide con la de la cuenta, se usa el tipo de cambio de la compra.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Card: Categorías de Gastos */}
            {hasPermission("configuracion_egresos.gestionar_categorias") && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <Tag className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Categorías de Gastos</p>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                        Administrá las categorías disponibles para clasificar los gastos fijos. Al eliminar una categoría, sus gastos pasan a "Otros".
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowCategoriasPanel(p => !p); setEditingCatId(null); setCatForm({ nombre: "" }); }}
                    className="h-8 text-xs bg-blue-600/80 hover:bg-blue-600 text-white px-3 flex-shrink-0 ml-4 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" /> Gestionar
                  </button>
                </div>

                {showCategoriasPanel && (
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                    {/* Form nueva/editar categoría */}
                    <form onSubmit={handleSaveCategoria} className="flex gap-2">
                      <input
                        required
                        value={catForm.nombre}
                        onChange={e => setCatForm({ nombre: e.target.value })}
                        placeholder={editingCatId ? "Nuevo nombre..." : "Nueva categoría..."}
                        className="flex-1 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/60"
                      />
                      <button type="submit" disabled={savingCat}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50">
                        {savingCat ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        {editingCatId ? "Guardar" : "Agregar"}
                      </button>
                      {editingCatId && (
                        <button type="button" onClick={() => { setEditingCatId(null); setCatForm({ nombre: "" }); }}
                          className="px-3 py-2 rounded-lg border border-white/10 text-slate-400 hover:text-white text-sm transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </form>

                    {/* Lista de categorías */}
                    <div className="space-y-1 max-h-72 overflow-y-auto">
                      {categoriasGastos.length === 0 && (
                        <p className="text-slate-500 text-xs text-center py-4">No hay categorías cargadas.</p>
                      )}
                      {categoriasGastos.map(cat => (
                        <div key={cat.id} className={`flex items-center justify-between px-3 py-2 rounded-lg ${editingCatId === cat.id ? "bg-blue-500/10 border border-blue-500/30" : "bg-white/3 border border-white/5 hover:bg-white/5"} transition-colors`}>
                          <span className="text-white text-sm flex items-center gap-2">
                            <Tag className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            {cat.nombre}
                            {cat.nombre === "Otros" && <span className="text-xs text-slate-500">(predeterminada)</span>}
                          </span>
                          <div className="flex gap-1">
                            <button onClick={() => { setEditingCatId(cat.id); setCatForm({ nombre: cat.nombre }); }}
                              className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            {cat.nombre !== "Otros" && (
                              <button onClick={() => handleDeleteCategoria(cat)}
                                className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}


      </div>

      {/* ═══ MODAL: Nota de crédito de compra ═════════════════════════════════ */}
      <AnimatePresence>
        {showNotaCompraForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          >
            <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 12 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg text-white">{editingNotaCompra ? "Editar nota de crédito" : "Nota de crédito de compra"}</h3>
                <button onClick={() => setShowNotaCompraForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSaveNotaCompra} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Número *</label>
                    <input value={notaCompraForm.numero} onChange={e => setNotaCompraForm(f => ({ ...f, numero: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm" required />
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Fecha *</label>
                    <DateInput value={notaCompraForm.fecha} onChange={e => setNotaCompraForm(f => ({ ...f, fecha: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm" required />
                  </div>
                </div>
                {/* Proveedor — obligatorio, selector primero */}
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Proveedor *</label>
                  <select value={notaCompraForm.proveedor_id} onChange={e => {
                    const p = proveedores.find(x => x.id === e.target.value);
                    setNotaCompraForm(f => ({
                      ...f,
                      proveedor_id: e.target.value,
                      proveedor_nombre: p?.nombre || "",
                      compra_id: "",
                      compra_numero_factura: "",
                      moneda: "PYG",
                      tipo_cambio: "",
                      items_devueltos: [],
                    }));
                  }} className={`w-full bg-arandu-dark border rounded-lg px-3 py-2 text-white text-sm ${!notaCompraForm.proveedor_id ? "border-rose-500/50" : "border-white/10"}`} required>
                    <option value="">— Seleccioná un proveedor —</option>
                    {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
                {/* Compra vinculada — opcional, filtrada por proveedor */}
                {(notaCompraForm.proveedor_id || notaCompraForm.compra_id) && (() => {
                  const comprasDeProv = comprasParaNota.filter(c => {
                    // Filtrar por proveedor
                    const esDeProv = c.proveedor_id === notaCompraForm.proveedor_id ||
                      (c.proveedor_nombre || "").toLowerCase() === (notaCompraForm.proveedor_nombre || "").toLowerCase();
                    if (!esDeProv) return false;
                    // Siempre mostrar la compra ya vinculada (si estamos editando)
                    if (editingNotaCompra && c.id === notaCompraForm.compra_id) return true;
                    // Solo compras con deuda pendiente en efectivo (no pagadas en caja)
                    const estadoPago = c.estado_pago || c.estado || "";
                    if (estadoPago === "pagado") return false;
                    // Y que tengan saldo disponible para acreditar (no ya cubierto por otras notas)
                    const creditosExist = (c.creditos || []).filter(n => n.id !== editingNotaCompra?.id);
                    const yaAcreditado = creditosExist.reduce((s, n) => s + (n.monto || 0), 0);
                    return (c.monto_total || 0) - yaAcreditado > 0.01;
                  });
                  return (
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">
                        Compra vinculada <span className="text-slate-500 font-normal">(opcional)</span>
                      </label>
                      <select value={notaCompraForm.compra_id} onChange={e => {
                        const c = comprasParaNota.find(x => x.id === e.target.value);
                        const acreditadoC = (c?.creditos || []).reduce((s, n) => s + (n.monto || 0), 0);
                        const disponibleC = c ? Math.max((c.monto_total || 0) - acreditadoC, 0) : null;
                        setNotaCompraForm(f => ({
                          ...f,
                          compra_id: e.target.value,
                          compra_numero_factura: c?.numero_factura || "",
                          moneda: c?.moneda || f.moneda,
                          monto: disponibleC != null ? String(disponibleC) : f.monto,
                          tipo_cambio: c?.tipo_cambio ? String(c.tipo_cambio) : "",
                          items_devueltos: [],
                          tipo_cobro: "saldo_favor",
                        }));
                      }} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="">— Sin vincular (guardar como nota flotante) —</option>
                        {comprasDeProv.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.fecha} · Fac. {c.numero_factura || "s/n"} · {fmt(c.monto_total, c.moneda)}
                          </option>
                        ))}
                      </select>
                      {!notaCompraForm.compra_id && (
                        <div className="flex items-start gap-2 bg-violet-900/30 border border-violet-500/30 rounded-lg px-3 py-2 mt-2">
                          <span className="text-violet-400 text-xs mt-0.5">◈</span>
                          <p className="text-xs text-violet-300">
                            <strong>Nota flotante:</strong> se guardará vinculada al proveedor sin asignar a ninguna compra. Podés aplicarla al pagar una compra futura de este proveedor.
                          </p>
                        </div>
                      )}
                      {notaCompraForm.compra_id && notaCompraForm.compra_numero_factura && (
                        <p className="text-slate-500 text-xs mt-1">Factura: <span className="text-slate-300">{notaCompraForm.compra_numero_factura}</span></p>
                      )}
                    </div>
                  );
                })()}
                {/* Banner: créditos ya aplicados a esta compra */}
                {notaCompraForm.compra_id && (() => {
                  const compraSelec = comprasParaNota.find(c => c.id === notaCompraForm.compra_id);
                  const creditosExist = (compraSelec?.creditos || []).filter(n => n.id !== editingNotaCompra?.id);
                  const yaAcreditadoComp = creditosExist.reduce((s, n) => s + (n.monto || 0), 0);
                  const saldoDispComp = compraSelec ? Math.max((compraSelec.monto_total || 0) - yaAcreditadoComp, 0) : null;
                  if (!compraSelec || yaAcreditadoComp <= 0) return null;
                  return (
                    <div className="flex items-start gap-2 bg-amber-900/30 border border-amber-500/30 rounded-lg px-3 py-2">
                      <span className="text-amber-400 text-sm mt-0.5">⚠</span>
                      <div className="text-xs text-amber-300 space-y-0.5">
                        <p><strong>Esta compra ya tiene notas de crédito aplicadas.</strong></p>
                        <p>Ya acreditado: <strong>{yaAcreditadoComp.toLocaleString("es-PY")} {compraSelec.moneda || "PYG"}</strong> · Disponible: <strong>{saldoDispComp?.toLocaleString("es-PY")} {compraSelec.moneda || "PYG"}</strong></p>
                      </div>
                    </div>
                  );
                })()}

                {/* Banner: compra pendiente → ajuste en plan de cuentas (pagar) */}
                {notaCompraForm.compra_id && (() => {
                  const compraSelec = comprasParaNota.find(c => c.id === notaCompraForm.compra_id);
                  const estadoPendiente = compraSelec && ["pendiente", "parcial"].includes(compraSelec.estado);
                  if (!estadoPendiente) return null;
                  return (
                    <div className="flex items-start gap-2 bg-blue-900/30 border border-blue-500/30 rounded-lg px-3 py-2">
                      <span className="text-blue-400 text-sm mt-0.5">ℹ</span>
                      <p className="text-xs text-blue-300">
                        Esta compra está <strong>pendiente de pago</strong>. La nota de crédito reducirá el saldo a pagar en el plan de cuentas (Pagar). No se genera movimiento de caja.
                      </p>
                    </div>
                  );
                })()}
                {/* Selector tipo_cobro: solo cuando la compra ya fue pagada */}
                {notaCompraForm.compra_id && (() => {
                  const compraSelec = comprasParaNota.find(c => c.id === notaCompraForm.compra_id);
                  if (!compraSelec || compraSelec.estado_pago !== "pagado") return null;
                  return (
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">¿Cómo se gestiona el crédito? *</label>
                      <select
                        value={notaCompraForm.tipo_cobro}
                        onChange={e => setNotaCompraForm(f => ({
                          ...f,
                          tipo_cobro: e.target.value,
                          ...(e.target.value === "saldo_favor" ? { cuenta_id: "", cuenta_nombre: "" } : {}),
                        }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="saldo_favor">Saldo a favor con el proveedor</option>
                        <option value="reembolso">El proveedor nos reembolsa</option>
                      </select>
                      {notaCompraForm.tipo_cobro === "saldo_favor" && (
                        <p className="text-emerald-300 text-xs mt-1">El crédito queda registrado para descontar en futuras compras con este proveedor.</p>
                      )}
                      {notaCompraForm.tipo_cobro === "reembolso" && (
                        <p className="text-amber-300 text-xs mt-1">El proveedor devuelve el dinero. Acordate de registrar el ingreso de caja o transferencia correspondiente.</p>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Motivo *</label>
                  <select value={notaCompraForm.tipo_motivo} onChange={e => setNotaCompraForm(f => ({ ...f, tipo_motivo: e.target.value, items_devueltos: [] }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="descuento">Descuento</option>
                    <option value="devolucion">Devolución</option>
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Detalle (opcional)</label>
                  <input value={notaCompraForm.motivo_detalle} onChange={e => setNotaCompraForm(f => ({ ...f, motivo_detalle: e.target.value }))}
                    placeholder="Descripción adicional..."
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                {notaCompraForm.tipo_motivo === "devolucion" && notaCompraForm.compra_id && (() => {
                  const items = compraNotaItems;
                  if (items.length === 0) return (
                    <p className="text-amber-300 text-xs">Esta compra no tiene ítems detallados. Ingresá el monto manualmente.</p>
                  );
                  return (
                    <div className="bg-white/5 rounded-lg p-3 space-y-2">
                      <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Ítems a devolver</p>
                      {items.map((item, idx) => {
                        const devItem = notaCompraForm.items_devueltos.find(d => d._idx === idx) || { _idx: idx, cantidad: 0 };
                        const cantDev = Number(devItem.cantidad) || 0;
                        return (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <div className="flex-1 text-slate-300 truncate">{item.descripcion}</div>
                            <div className="text-slate-500 text-xs whitespace-nowrap">Máx: {item.cantidad}</div>
                            <input
                              type="number" min="0" max={item.cantidad} step="any"
                              value={cantDev || ""}
                              placeholder="0"
                              onChange={e => {
                                const val = Math.min(parseFloat(e.target.value) || 0, item.cantidad);
                                const newItems = notaCompraForm.items_devueltos.filter(d => d._idx !== idx);
                                if (val > 0) newItems.push({ _idx: idx, producto_id: item.producto_id || null, producto_sku: item.producto_sku || null, variante_id: item.variante_id || null, variante_texto: item.variante_texto || null, descripcion: item.descripcion, cantidad: val, precio_unitario: item.precio_unitario, subtotal: val * item.precio_unitario });
                                const total = newItems.reduce((s, d) => s + (d.subtotal || 0), 0);
                                setNotaCompraForm(f => ({ ...f, items_devueltos: newItems, monto: total > 0 ? String(total) : f.monto }));
                              }}
                              className="w-20 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-xs text-right"
                            />
                          </div>
                        );
                      })}
                      {notaCompraForm.items_devueltos.length > 0 && (
                        <p className="text-emerald-300 text-xs text-right pt-1">
                          Total calculado: {parseFloat(notaCompraForm.monto || 0).toLocaleString("es-PY")} {notaCompraForm.moneda}
                        </p>
                      )}
                    </div>
                  );
                })()}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Moneda</label>
                    <select value={notaCompraForm.moneda} onChange={e => setNotaCompraForm(f => ({ ...f, moneda: e.target.value }))}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="PYG">PYG</option>
                      <option value="USD">USD</option>
                      {activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") && (
                        <option value="BRL">BRL</option>
                      )}
                    </select>
                  </div>
                  <div>
                    {(() => {
                      const compraSelec = comprasParaNota.find(c => c.id === notaCompraForm.compra_id);
                      const creditosExist = (compraSelec?.creditos || []).filter(n => n.id !== editingNotaCompra?.id);
                      const yaAcred = creditosExist.reduce((s, n) => s + (n.monto || 0), 0);
                      const maxMonto = compraSelec ? Math.max((compraSelec.monto_total || 0) - yaAcred, 0) : undefined;
                      return (
                        <>
                          <label className="text-slate-400 text-sm mb-1 block">
                            Monto *{maxMonto != null ? <span className="text-slate-500 font-normal ml-1">(máx: {maxMonto.toLocaleString("es-PY")})</span> : ""}
                          </label>
                          <MoneyInput moneda={compraSelec?.moneda || "PYG"} min="0" max={maxMonto} step="any" value={notaCompraForm.monto} onChange={value => setNotaCompraForm(f => ({ ...f, monto: value }))}
                            className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm" required />
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">TC</label>
                    <input type="number" min="0" step="any" value={notaCompraForm.tipo_cambio} onChange={e => { if (!canEditTcDoc) return; setTcNotaCompraInfo(null); setNotaCompraForm(f => ({ ...f, tipo_cambio: e.target.value })); }}
                      readOnly={!canEditTcDoc || notaCompraForm.moneda === "PYG"}
                      title={!canEditTcDoc && notaCompraForm.moneda !== "PYG" ? "El tipo de cambio se configura en Financiero → Cotizaciones" : undefined}
                      className={`w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm${(!canEditTcDoc && notaCompraForm.moneda !== "PYG") ? " opacity-60 cursor-not-allowed" : ""}`} disabled={notaCompraForm.moneda === "PYG"} placeholder={notaCompraForm.moneda === "BRL" ? "Ej: 1500" : "Ej: 7600"} />
                    {tcNotaCompraInfo && notaCompraForm.moneda === "USD" && (
                      <p className="text-emerald-300 text-xs mt-1">
                        Sugerido: ₲ {Number(tcNotaCompraInfo.tipo_cambio_sugerido || tcNotaCompraInfo.venta).toLocaleString("es-PY")}
                      </p>
                    )}
                  </div>
                </div>
                {/* Cuenta bancaria: solo cuando hay movimiento real de plata (no en saldo_favor) */}
                {notaCompraForm.tipo_cobro !== "saldo_favor" && (
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Cuenta bancaria</label>
                    <select
                      value={notaCompraForm.cuenta_id}
                      onChange={e => {
                        const c = cuentasDisp.find(x => x.id === e.target.value);
                        setNotaCompraForm(f => ({ ...f, cuenta_id: e.target.value, cuenta_nombre: c?.nombre || "" }));
                      }}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="">— Sin especificar —</option>
                      {renderCuentasPorMoneda(cuentasDisp, compraSelec?.logo_tipo || activeEmpresaPropia?.slug)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                  <input value={notaCompraForm.notas} onChange={e => setNotaCompraForm(f => ({ ...f, notas: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowNotaCompraForm(false)} className="flex-1 py-2.5 border border-white/10 rounded-lg text-slate-400">Cancelar</button>
                  <button type="submit" className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium">
                    {editingNotaCompra ? "Actualizar" : "Registrar"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Formulario Compra ══════════════════════════════════════════ */}
      <AnimatePresence>
        {showCompraForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-2xl p-6 my-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-white">
                  {editingCompra ? "Editar Compra" : "Nueva Compra"}
                </h2>
                <button onClick={() => setShowCompraForm(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSaveCompra} className="space-y-4">
                {proyectoId && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 grid md:grid-cols-2 gap-3 items-end">
                    <div className="text-xs text-yellow-300">Proyecto de obra: <strong>{proyectoCtx?.numero} — {proyectoCtx?.nombre}</strong></div>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Grupo de compra (para reportes)</label>
                      <select value={compraForm.grupo_constructora_id || ""} onChange={e => setCompraForm(f => ({ ...f, grupo_constructora_id: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="">Sin grupo</option>
                        {gruposConstructora.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Proveedor */}
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Proveedor *{compraBloqueaProvFecha(editingCompra) && <span className="text-slate-500 text-[10px] ml-1">(no editable)</span>}</label>
                    <div className={compraBloqueaProvFecha(editingCompra) ? "pointer-events-none opacity-60" : ""}>
                    <ProveedorSearchSelect
                      proveedores={proveedores}
                      value={compraForm.proveedor_id ? { id: compraForm.proveedor_id, nombre: compraForm.proveedor_nombre, ruc: compraForm.proveedor_ruc } : null}
                      onChange={sel => setCompraForm(prev => ({
                        ...prev,
                        proveedor_id: sel?.id || "",
                        proveedor_nombre: sel?.nombre || "",
                        proveedor_ruc: sel?.ruc || "",
                      }))}
                      onProveedorCreado={p => setProveedores(prev => [...prev, p].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")))}
                      token={token}
                      logo={activeEmpresaPropia?.slug}
                    />
                    </div>
                  </div>

                  {/* Fecha */}
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Fecha *</label>
                    <DateInput value={compraForm.fecha}
                      disabled={compraBloqueaProvFecha(editingCompra)}
                      onChange={e => setCompraForm(p => ({
                        ...p,
                        fecha: e.target.value,
                        fecha_pago: (!p.fecha_pago || p.fecha_pago === p.fecha) ? e.target.value : p.fecha_pago,
                      }))}
                      className="bg-arandu-dark border-white/10 text-white" />
                  </div>
                </div>

                {/* ── Panel de anticipos disponibles ─────────────────────────────── */}
                {compraForm.proveedor_id && puedeVerAnticipos && (
                  <div className="border border-teal-500/25 rounded-xl p-3 space-y-2 bg-teal-500/5">
                    <p className="text-teal-400 text-xs font-semibold flex items-center gap-1.5">
                      <HandCoins className="w-3.5 h-3.5" />
                      Anticipos disponibles del proveedor
                      {loadingAnticipos && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
                    </p>
                    {!loadingAnticipos && anticiposDisponibles.length === 0 && (
                      <p className="text-slate-500 text-xs">Sin anticipos disponibles para este proveedor.</p>
                    )}
                    {anticiposDisponibles.map(ant => {
                      const applied = (compraForm.anticipos_aplicados || []).find(a => a.anticipo_id === ant.id);
                      const montoAplicado = applied ? Number(applied.monto_aplicado) : 0;
                      const montoDisp = Number(ant.saldo_disponible || 0);
                      const simbolo = ant.moneda === "USD" ? "$" : ant.moneda === "BRL" ? "R$" : "₲";
                      const fmt = v => `${simbolo} ${Math.round(v).toLocaleString("es-PY")}`;
                      return (
                        <div key={ant.id} className="bg-arandu-dark rounded-lg p-2.5 space-y-2 border border-white/5">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <span className="text-white text-xs font-medium">{fmt(montoDisp)} disponible</span>
                              <span className="text-slate-500 text-[10px] ml-2">({ant.moneda}) · {ant.fecha}</span>
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={!!applied}
                                onChange={e => {
                                  setCompraForm(prev => {
                                    const list = (prev.anticipos_aplicados || []).filter(a => a.anticipo_id !== ant.id);
                                    if (e.target.checked) list.push({ anticipo_id: ant.id, monto_aplicado: montoDisp, tipo_cambio_compra: ant.tipo_cambio || null });
                                    return { ...prev, anticipos_aplicados: list };
                                  });
                                }}
                                className="rounded border-white/20 bg-arandu-dark text-teal-500 focus:ring-teal-500/30"
                              />
                              <span className="text-slate-400 text-xs">Aplicar</span>
                            </label>
                          </div>
                          {applied && (
                            <div className="flex items-center gap-2">
                              <label className="text-slate-400 text-[11px] whitespace-nowrap">Monto a aplicar:</label>
                              <MoneyInput min="0.01" step="any" max={montoDisp} moneda={ant.moneda || compraForm.moneda || "PYG"}
                                value={montoAplicado || ""}
                                onChange={value => {
                                  const val = Math.min(Number(value) || 0, montoDisp);
                                  setCompraForm(prev => ({
                                    ...prev,
                                    anticipos_aplicados: (prev.anticipos_aplicados || []).map(a =>
                                      a.anticipo_id === ant.id ? { ...a, monto_aplicado: val } : a
                                    ),
                                  }));
                                }}
                                className="bg-arandu-dark border border-teal-500/30 rounded-md px-2 py-1 text-white text-xs h-7 flex-1"
                              />
                              {ant.moneda !== compraForm.moneda && (
                                <div className="flex-1">
                                  <Input type="number" min="0.01" step="any"
                                    value={applied.tipo_cambio_compra || ""}
                                    onChange={e => setCompraForm(prev => ({
                                      ...prev,
                                      anticipos_aplicados: (prev.anticipos_aplicados || []).map(a =>
                                        a.anticipo_id === ant.id ? { ...a, tipo_cambio_compra: Number(e.target.value) || null } : a
                                      ),
                                    }))}
                                    placeholder={`TC ${ant.moneda}→${compraForm.moneda}`}
                                    className="bg-arandu-dark border-amber-500/30 text-amber-300 text-xs h-7"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {(compraForm.anticipos_aplicados || []).length > 0 && (() => {
                      const totalAnt = (compraForm.anticipos_aplicados || []).reduce((s, a) => s + Number(a.monto_aplicado || 0), 0);
                      const totalCompra = compraItemsConDescuentoGlobal(compraForm).reduce((s, it) => s + (Number(it.subtotal) || 0), 0);
                      const neto = Math.max(0, totalCompra - totalAnt);
                      const simbolo = compraForm.moneda === "USD" ? "$ " : compraForm.moneda === "BRL" ? "R$ " : "₲ ";
                      return (
                        <div className="pt-1 border-t border-white/5 flex justify-between text-xs">
                          <span className="text-teal-400">Anticipo aplicado: {simbolo}{Math.round(totalAnt).toLocaleString("es-PY")}</span>
                          <span className="text-white font-semibold">Pago en caja: {simbolo}{Math.round(neto).toLocaleString("es-PY")}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Moneda */}
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Moneda</label>
                  <div className="flex gap-2">
                    {[{ val: "PYG", label: "₲ Guaraní" }, { val: "USD", label: "$ Dólar" }, ...(activeEmpresaPropia?.monedas_habilitadas?.includes("BRL") ? [{ val: "BRL", label: "R$ Real" }] : [])].map(m => (
                      <button key={m.val} type="button"
                        onClick={() => setCompraForm(p => ({ ...p, moneda: m.val }))}
                        className={`flex-1 py-2 rounded-lg border text-sm transition-all ${compraForm.moneda === m.val ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-white/10 text-slate-400 hover:border-white/20"}`}
                      >{m.label}</button>
                    ))}
                  </div>
                </div>
                {compraForm.moneda !== "PYG" && compraForm.moneda !== "USD" && compraForm.moneda === "BRL" && (
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Tipo de cambio (₲ por R$)</label>
                    <Input type="number" min="0" step="any" value={compraForm.tipo_cambio}
                      onChange={e => { if (!canEditTcDoc) return; setCompraForm(p => ({ ...p, tipo_cambio: e.target.value })); }}
                      readOnly={!canEditTcDoc}
                      title={!canEditTcDoc ? "El tipo de cambio se configura en Financiero → Cotizaciones" : undefined}
                      placeholder="Ej: 1500"
                      className={`bg-arandu-dark border-white/10 text-white${!canEditTcDoc ? " opacity-60 cursor-not-allowed" : ""}`} />
                  </div>
                )}
                {compraForm.moneda === "USD" && (
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Tipo de cambio fiscal (₲ por USD)</label>
                    <Input type="number" min="0" step="any" value={compraForm.tipo_cambio}
                      onChange={e => { if (!canEditTcDoc) return; setTcCompraInfo(null); setCompraForm(p => ({ ...p, tipo_cambio: e.target.value })); }}
                      readOnly={!canEditTcDoc}
                      title={!canEditTcDoc ? "El tipo de cambio se configura en Financiero → Cotizaciones" : undefined}
                      placeholder="Se sugiere por fecha"
                      className={`bg-arandu-dark border-white/10 text-white${!canEditTcDoc ? " opacity-60 cursor-not-allowed" : ""}`} />
                    {tcCompraInfo && (
                      <p className="text-emerald-300 text-xs mt-1">
                        Sugerido DNIT venta: ₲ {Number(tcCompraInfo.tipo_cambio_sugerido || tcCompraInfo.venta).toLocaleString("es-PY")} ({tcCompraInfo.fecha})
                      </p>
                    )}
                  </div>
                )}

                {/* ── Items / Conceptos ── */}
                <div>
                  {/* ── Toggle modo libre/catálogo — solo si tiene ambos permisos ── */}
                  {puedeUsarStockProductos && puedeCompraModoLibre && (
                    <div className="mb-3 flex items-center gap-2 p-2 rounded-lg border border-white/10 bg-white/3">
                      <span className="text-slate-400 text-xs mr-1">Modo:</span>
                      <button type="button"
                        onClick={() => {
                          if (modoCompra === "catalogo") return;
                          if ((compraForm.items || []).length > 0 && !window.confirm("Cambiar a modo Catálogo vaciará los ítems ya cargados. ¿Continuar?")) return;
                          setModoCompra("catalogo"); setCompraForm(p => ({ ...p, items: [] }));
                        }}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all ${modoCompra === "catalogo" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}
                      >
                        <Package className="w-3 h-3" /> Catálogo
                      </button>
                      <button type="button"
                        onClick={() => {
                          if (modoCompra === "libre") return;
                          if ((compraForm.items || []).length > 0 && !window.confirm("Cambiar a modo Libre vaciará los ítems ya cargados. ¿Continuar?")) return;
                          setModoCompra("libre"); setCompraForm(p => ({ ...p, items: [] }));
                        }}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all ${modoCompra === "libre" ? "bg-orange-500/20 text-orange-300 border-orange-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}
                      >
                        <Plus className="w-3 h-3" /> Libre
                      </button>
                    </div>
                  )}

                  {/* ── Escáner de barras — solo en modo catálogo ── */}
                  {puedeUsarStockProductos && modoCompra === "catalogo" && (
                    <div className="mb-3 flex flex-wrap items-end gap-2 p-3 rounded-lg border border-orange-500/25 bg-orange-500/5">
                      <div className="flex-1 min-w-[200px]">
                        <label className="text-slate-400 text-xs mb-1 block">Código de barras</label>
                        <input
                          value={compraBarcodeInput}
                          onChange={e => setCompraBarcodeInput(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleCompraBarcodeSubmit(); } }}
                          placeholder="Escanear o escribir y Enter"
                          disabled={compraBarcodeLoading}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50"
                        />
                      </div>
                      <button type="button" onClick={handleCompraBarcodeSubmit}
                        disabled={compraBarcodeLoading || !compraBarcodeInput.trim()}
                        className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
                        {compraBarcodeLoading ? "..." : "Agregar"}
                      </button>
                      <p className="w-full text-slate-500 text-[11px]">Si el producto ya está en la lista, se suma 1 a la cantidad.</p>
                    </div>
                  )}
                  {proyectoId && (
                    <div className="mb-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                      <button type="button" onClick={() => setInsumosAbierto(v => !v)} className="w-full flex items-center justify-between px-3 py-2 text-left">
                        <span className="text-yellow-300 text-xs font-semibold uppercase tracking-wider">
                          Ítems del presupuesto de obra{insumosObra.presupuesto ? ` — ${insumosObra.presupuesto.nombre}${insumosObra.provisorio ? " (sin aprobar)" : ""}` : ""}
                        </span>
                        <span className="text-slate-500 text-xs">{insumosObra.lineas.length} líneas {insumosAbierto ? "▲" : "▼"}</span>
                      </button>
                      {insumosAbierto && (
                        <div className="px-3 pb-3 space-y-2">
                          {!insumosObra.presupuesto && <p className="text-slate-500 text-xs">Este proyecto no tiene presupuesto de obra cargado.</p>}
                          {insumosObra.provisorio && <p className="text-amber-300/80 text-[11px]">El presupuesto todavía no está aprobado; se muestra el último cargado.</p>}
                          {insumosObra.lineas.length > 0 && (
                            <>
                              <div className="flex gap-2">
                                <input value={insumosBusq} onChange={e => setInsumosBusq(e.target.value)} placeholder="Buscar insumo, ítem o rubro..."
                                  className="flex-1 bg-arandu-dark border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-yellow-400" />
                                <button type="button"
                                  disabled={!Object.values(insumosSel).some(Boolean)}
                                  onClick={() => agregarInsumosObra(insumosObra.lineas.filter(l => insumosSel[claveInsumo(l)]))}
                                  className="px-3 py-1.5 rounded-lg bg-yellow-500 text-black text-xs font-semibold disabled:opacity-40">
                                  Agregar seleccionados
                                </button>
                              </div>
                              <div className="max-h-56 overflow-y-auto border border-white/5 rounded-lg">
                                <table className="w-full text-xs">
                                  <thead className="sticky top-0 bg-arandu-dark text-slate-400">
                                    <tr><th className="p-1.5 w-6"></th><th className="p-1.5 text-left">Insumo</th><th className="p-1.5 text-right">Presup.</th><th className="p-1.5 text-right">Comprado</th><th className="p-1.5 text-right">Pendiente</th><th className="p-1.5 text-right">Costo unit.</th><th className="p-1.5"></th></tr>
                                  </thead>
                                  <tbody>
                                    {insumosObra.lineas
                                      .filter(l => { const q = insumosBusq.trim().toLowerCase(); return !q || `${l.descripcion} ${l.item} ${l.rubro}`.toLowerCase().includes(q); })
                                      .map(l => {
                                        const k = claveInsumo(l);
                                        return (
                                          <tr key={k} className="border-t border-white/5 hover:bg-white/5">
                                            <td className="p-1.5"><input type="checkbox" checked={!!insumosSel[k]} onChange={e => setInsumosSel(m => ({ ...m, [k]: e.target.checked }))} /></td>
                                            <td className="p-1.5 text-slate-200">
                                              {l.descripcion}
                                              <div className="text-[10px] text-slate-500">{l.rubro} › {l.item}</div>
                                            </td>
                                            <td className="p-1.5 text-right text-slate-300">{Number(l.cantidad_presup).toLocaleString("es-PY")} {l.unidad || ""}</td>
                                            <td className="p-1.5 text-right text-slate-400">{Number(l.comprado).toLocaleString("es-PY")}</td>
                                            <td className={`p-1.5 text-right font-medium ${l.pendiente > 0 ? "text-emerald-300" : "text-slate-500"}`}>{Number(l.pendiente).toLocaleString("es-PY")}</td>
                                            <td className="p-1.5 text-right text-slate-300">{l.costo_unitario > 0 ? Math.round(l.costo_unitario).toLocaleString("es-PY") : "—"}</td>
                                            <td className="p-1.5 text-right"><button type="button" onClick={() => agregarInsumosObra([l])} className="text-yellow-300 hover:text-yellow-200 font-semibold">+ Agregar</button></td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-slate-400 text-sm">Ítems de la compra</label>
                    <div className="flex gap-2">
                      {/* Buscar producto: catálogo con acceso a stock */}
                      {puedeUsarStockProductos && modoCompra === "catalogo" && (
                        <button type="button"
                          onClick={() => setShowProductoBrowser(true)}
                          className="flex items-center gap-1.5 text-xs bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-lg px-3 py-1.5 hover:bg-blue-500/25 transition-all"
                        >
                          <Package className="w-3.5 h-3.5" /> Buscar producto
                        </button>
                      )}
                      {/* Agregar ítem libre: sin stock, o tiene modo_libre activo */}
                      {(!puedeUsarStockProductos || modoCompra === "libre") && (
                        <button type="button"
                          onClick={() => setCompraForm(p => ({ ...p, items: [...(p.items || []), emptyItem()] }))}
                          className="flex items-center gap-1.5 text-xs bg-orange-500/15 text-orange-400 border border-orange-500/30 rounded-lg px-3 py-1.5 hover:bg-orange-500/25 transition-all"
                        >
                          <Plus className="w-3.5 h-3.5" /> Agregar ítem
                        </button>
                      )}
                    </div>
                  </div>

                  {(compraForm.items || []).length > 0 ? (
                    <>
                    <div className="bg-arandu-dark rounded-lg border border-white/5 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/5">
                            <th className="text-left text-slate-500 font-normal px-3 py-2">Descripción</th>
                            <th className="text-center text-slate-500 font-normal px-2 py-2 w-14">Cant.</th>
                            <th className="text-right text-slate-500 font-normal px-2 py-2 w-24">P. Unit.</th>
                            {puedeEditarPrecioVentaCompra && modoCompra === "catalogo" && (
                              <th className="text-right text-cyan-400 font-normal px-2 py-2 w-24">P. Venta</th>
                            )}
                            {puedeDescuentosCompra && <th className="text-right text-rose-300 font-normal px-2 py-2 w-28">Desc.</th>}
                            <th className="text-center text-slate-500 font-normal px-2 py-2 w-16">IVA%</th>
                            <th className="text-right text-slate-500 font-normal px-2 py-2 w-24">Subtotal</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {compraForm.items.map((item, idx) => (
                            <tr key={idx} className="border-b border-white/5 last:border-0">
                              <td className="px-2 py-1.5">
                                <input
                                  value={item.descripcion}
                                  readOnly={!!item.producto_id}
                                  onChange={e => {
                                    if (item.producto_id) return;
                                    const items = [...compraForm.items];
                                    items[idx] = { ...items[idx], descripcion: e.target.value };
                                    setCompraForm(p => ({ ...p, items }));
                                  }}
                                  className={`w-full bg-transparent border border-white/10 rounded px-2 py-1 text-white text-xs focus:outline-none ${item.producto_id ? "cursor-default opacity-70" : "focus:border-orange-500/50"}`}
                                  placeholder="Descripción..."
                                />
                                  {item.producto_sku && (
                                    <span className="text-[10px] text-slate-500 font-mono mt-0.5 block">SKU: {item.producto_sku}</span>
                                  )}
                                  {item.variante_texto && (
                                    <span className="text-[10px] text-emerald-400 mt-0.5 block">Variante: {item.variante_texto}</span>
                                  )}
                              </td>
                              <td className="px-1 py-1.5">
                                <input
                                  type="number" min="0.001" step="any"
                                  value={item.cantidad}
                                  onChange={e => {
                                    const items = [...compraForm.items];
                                    const cant = parseFloat(e.target.value) || 0;
                                    const sub = cant * (parseFloat(items[idx].precio_unitario) || 0);
                                    items[idx] = { ...items[idx], cantidad: e.target.value, subtotal: sub };
                                    setCompraForm(p => ({ ...p, items }));
                                  }}
                                  className="w-full bg-transparent border border-white/10 rounded px-1 py-1 text-white text-xs text-center focus:outline-none focus:border-orange-500/50"
                                />
                              </td>
                              <td className="px-1 py-1.5">
                                <MoneyInput
                                  moneda={compraForm.moneda || "PYG"} min="0" step="any"
                                  value={item.precio_unitario}
                                  onChange={value => {
                                    const items = [...compraForm.items];
                                    const pu = parseFloat(value) || 0;
                                    const sub = (parseFloat(items[idx].cantidad) || 1) * pu;
                                    items[idx] = { ...items[idx], precio_unitario: value, subtotal: sub };
                                    setCompraForm(p => ({ ...p, items }));
                                  }}
                                  className="w-full bg-transparent border border-white/10 rounded px-1 py-1 text-white text-xs text-right focus:outline-none focus:border-orange-500/50"
                                  placeholder="0"
                                />
                              </td>
                              {puedeEditarPrecioVentaCompra && modoCompra === "catalogo" && (
                                <td className="px-1 py-1.5">
                                  {item.producto_id ? (
                                    <>
                                      <MoneyInput
                                        moneda={compraForm.moneda || "PYG"} min="0" step="any"
                                        value={item.precio_venta}
                                        onChange={value => {
                                          const items = [...compraForm.items];
                                          items[idx] = { ...items[idx], precio_venta: value };
                                          setCompraForm(p => ({ ...p, items }));
                                        }}
                                        className={`w-full bg-transparent border rounded px-1 py-1 text-xs text-right focus:outline-none ${
                                          parseFloat(item.precio_venta) > 0 && parseFloat(item.precio_venta) <= (parseFloat(item.precio_unitario) || 0)
                                            ? "border-amber-500/50 text-amber-300 focus:border-amber-500"
                                            : "border-white/10 text-white focus:border-cyan-500/50"
                                        }`}
                                        placeholder={item._precio_venta_actual ? `Actual: ${Math.round(item._precio_venta_actual)}` : "Sin definir"}
                                      />
                                      {parseFloat(item.precio_venta) > 0 && parseFloat(item.precio_venta) <= (parseFloat(item.precio_unitario) || 0) && (
                                        <span className="text-[9px] text-amber-400 block mt-0.5">Sin ganancia</span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-slate-600 text-xs block text-center">—</span>
                                  )}
                                </td>
                              )}
                              {puedeDescuentosCompra && (
                                <td className="px-1 py-1.5">
                                  <div className="flex gap-1">
                                    <input
                                      type="number" min="0" step="any"
                                      value={item.descuento_item_valor ?? ""}
                                      onChange={e => {
                                        const items = [...compraForm.items];
                                        items[idx] = { ...items[idx], descuento_item_valor: e.target.value };
                                        setCompraForm(p => ({ ...p, items }));
                                      }}
                                      className="w-full min-w-0 bg-transparent border border-white/10 rounded px-1 py-1 text-rose-200 text-xs text-right focus:outline-none focus:border-rose-400/60"
                                      placeholder="0"
                                    />
                                    <select
                                      value={item.descuento_item_tipo || "pct"}
                                      onChange={e => {
                                        const items = [...compraForm.items];
                                        items[idx] = { ...items[idx], descuento_item_tipo: e.target.value };
                                        setCompraForm(p => ({ ...p, items }));
                                      }}
                                      className="bg-arandu-dark border border-white/10 rounded px-0.5 py-1 text-white text-xs focus:outline-none"
                                    >
                                      <option value="pct">%</option>
                                      <option value="monto">{compraForm.moneda === "USD" ? "US$" : compraForm.moneda === "BRL" ? "R$" : "₲"}</option>
                                    </select>
                                  </div>
                                </td>
                              )}
                              <td className="px-1 py-1.5">
                                <select
                                  value={item.iva ?? 10}
                                  disabled={!!item.producto_id}
                                  onChange={e => {
                                    if (item.producto_id) return;
                                    const items = [...compraForm.items];
                                    items[idx] = { ...items[idx], iva: Number(e.target.value) };
                                    setCompraForm(p => ({ ...p, items }));
                                  }}
                                  title={item.producto_id ? "El IVA viene definido por el producto" : undefined}
                                  className={`w-full bg-arandu-dark border border-white/10 rounded px-1 py-1 text-white text-xs focus:outline-none ${item.producto_id ? "opacity-60 cursor-not-allowed" : "focus:border-orange-500/50"}`}
                                >
                                  <option value={10}>10%</option>
                                  <option value={5}>5%</option>
                                  <option value={0}>0% Exenta</option>
                                </select>
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                {(() => {
                                  const brutoFila = (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio_unitario) || 0);
                                  const v = Math.max(0, brutoFila - compraDescuentoItem(item, compraForm.moneda || "PYG"));
                                  return (
                                    <span className="text-orange-300 font-medium text-xs">
                                      {compraForm.moneda === "USD"
                                        ? `USD ${v.toFixed(2)}`
                                        : compraForm.moneda === "BRL"
                                        ? `R$ ${v.toFixed(2)}`
                                        : `₲ ${Math.round(v).toLocaleString("es-PY")}`}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-1 py-1.5">
                                <button type="button"
                                  onClick={() => {
                                    const items = compraForm.items.filter((_, i) => i !== idx);
                                    setCompraForm(p => ({ ...p, items }));
                                  }}
                                  className="text-slate-500 hover:text-red-400 transition-colors"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          {(() => {
                            const itemsNetos = compraItemsConDescuentoGlobal(compraForm);
                            const subtotalBruto = compraSubtotalBruto(compraForm.items || []);
                            const descuentoGlobal = compraDescuentoGlobal(compraForm);
                            const descuentoItems = compraTotalDescuentosItems(compraForm);
                            const totalItems = itemsNetos.reduce((s, it) => s + (parseFloat(it.subtotal) || 0), 0);
                            const totalIva = itemsNetos.reduce((s, it) => s + calcItemIva(it, compraForm.moneda), 0);
                            const totalCantidadItems = (compraForm.items || []).reduce((s, it) => s + (parseFloat(it.cantidad) || 0), 0);
                            const fmtV = (n) => compraForm.moneda === "USD" ? `USD ${n.toFixed(2)}` : compraForm.moneda === "BRL" ? `R$ ${n.toFixed(2)}` : `₲ ${Math.round(n).toLocaleString("es-PY")}`;
                            return (
                              <>
                                <tr className="border-t border-white/10">
                                  <td colSpan={4} className="px-3 py-1.5 text-slate-500 text-xs text-right">Total ítems</td>
                                  <td className="px-2 py-1.5 text-right text-slate-400 text-xs">{totalCantidadItems}</td>
                                  <td></td>
                                </tr>
                                {(descuentoGlobal > 0 || descuentoItems > 0) && (
                                  <>
                                    <tr>
                                      <td colSpan={4} className="px-3 py-1.5 text-slate-500 text-xs text-right">Subtotal bruto</td>
                                      <td className="px-2 py-1.5 text-right text-slate-400 text-xs">{fmtV(subtotalBruto)}</td>
                                      <td></td>
                                    </tr>
                                    {descuentoItems > 0 && (
                                      <tr>
                                        <td colSpan={4} className="px-3 py-1.5 text-rose-300 text-xs text-right">Descuentos por ítem</td>
                                        <td className="px-2 py-1.5 text-right text-rose-300 text-xs">- {fmtV(descuentoItems)}</td>
                                        <td></td>
                                      </tr>
                                    )}
                                    {descuentoGlobal > 0 && <tr>
                                      <td colSpan={4} className="px-3 py-1.5 text-rose-300 text-xs text-right">Descuento global</td>
                                      <td className="px-2 py-1.5 text-right text-rose-300 text-xs">- {fmtV(descuentoGlobal)}</td>
                                      <td></td>
                                    </tr>}
                                  </>
                                )}
                                <tr className="border-t border-white/10">
                                  <td colSpan={4} className="px-3 py-1.5 text-slate-400 text-xs text-right">IVA incluido</td>
                                  <td className="px-2 py-1.5 text-right text-slate-500 text-xs">{fmtV(totalIva)}</td>
                                  <td></td>
                                </tr>
                                <tr>
                                  <td colSpan={4} className="px-3 py-2 text-slate-300 text-xs text-right font-bold">TOTAL</td>
                                  <td className="px-2 py-2 text-right">
                                    <span className="text-white font-bold text-sm">{fmtV(totalItems)}</span>
                                  </td>
                                  <td></td>
                                </tr>
                              </>
                            );
                          })()}
                        </tfoot>
                      </table>
                    </div>
                    {puedeDescuentosCompra && (
                      <div className="mt-3 border border-rose-500/25 bg-rose-500/5 rounded-xl p-3 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <p className="text-rose-300 text-sm font-semibold flex items-center gap-1.5">
                              <Tag className="w-4 h-4" /> Descuento de compra
                            </p>
                            <p className="text-slate-500 text-xs">Aplica al total del documento y se prorratea en los ítems para IVA y costo.</p>
                          </div>
                          <div className="flex gap-2">
                            {[{ v: "none", l: "Sin descuento" }, { v: "global", l: "Global" }].map(opt => (
                              <button
                                key={opt.v}
                                type="button"
                                onClick={() => setCompraForm(p => ({
                                  ...p,
                                  descuento_modo: opt.v,
                                  descuento_global_monto: opt.v === "none" ? "" : p.descuento_global_monto,
                                  descuento_global_pct: opt.v === "none" ? "" : p.descuento_global_pct,
                                }))}
                                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${compraForm.descuento_modo === opt.v ? "bg-rose-500/20 border-rose-400 text-rose-200" : "border-white/10 text-slate-400 hover:text-white"}`}
                              >
                                {opt.l}
                              </button>
                            ))}
                          </div>
                        </div>
                        {compraForm.descuento_modo === "global" && (
                          <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr_1fr] gap-2 items-end">
                            <div>
                              <label className="text-slate-400 text-xs mb-1 block">Tipo</label>
                              <select
                                value={compraForm.descuento_global_tipo || "monto"}
                                onChange={e => setCompraForm(p => ({
                                  ...p,
                                  descuento_global_tipo: e.target.value,
                                  descuento_global_monto: e.target.value === "pct" ? "" : p.descuento_global_monto,
                                  descuento_global_pct: e.target.value === "monto" ? "" : p.descuento_global_pct,
                                }))}
                                className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-rose-500/50"
                              >
                                <option value="monto">Monto</option>
                                <option value="pct">Porcentaje</option>
                              </select>
                            </div>
                            {compraForm.descuento_global_tipo === "pct" ? (
                              <div>
                                <label className="text-slate-400 text-xs mb-1 block">Descuento %</label>
                                <Input type="number" min="0" max="100" step="any"
                                  value={compraForm.descuento_global_pct}
                                  onChange={e => setCompraForm(p => ({ ...p, descuento_global_pct: e.target.value }))}
                                  className="bg-arandu-dark border-white/10 text-white"
                                  placeholder="Ej: 5"
                                />
                              </div>
                            ) : (
                              <div>
                                <label className="text-slate-400 text-xs mb-1 block">Descuento monto</label>
                                <MoneyInput min="0" step="any" moneda={compraForm.moneda || "PYG"}
                                  value={compraForm.descuento_global_monto}
                                  onChange={value => setCompraForm(p => ({ ...p, descuento_global_monto: value }))}
                                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-rose-500/50"
                                  placeholder="0"
                                />
                              </div>
                            )}
                            <div className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2">
                              <p className="text-slate-500 text-[11px]">Total con descuento</p>
                              <p className="text-white font-bold">
                                {fmt(
                                  compraItemsConDescuentoGlobal(compraForm).reduce((s, it) => s + (Number(it.subtotal) || 0), 0),
                                  compraForm.moneda
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    </>
                  ) : (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      {modoCompra === "catalogo"
                        ? "Usá 'Buscar producto' o el escáner para agregar ítems."
                        : "Usá 'Agregar ítem' para cargar los conceptos de la compra."}
                    </div>
                  )}
                </div>

                {/* Depósito de destino (solo si la empresa elige depósito en cada compra) */}
                {puedeUsarStockProductos && !editingCompra && depos.activo && depos.cfg.modo === "por_compra" && compraForm.afecta_stock && (
                  <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                    <label className="block text-slate-400 text-xs mb-1">Depósito donde ingresa la mercadería</label>
                    <select value={compraForm.deposito_id || depos.cfg.deposito_compras_id || depos.cfg.deposito_principal_id || ""}
                      onChange={e => setCompraForm(p => ({ ...p, deposito_id: e.target.value }))}
                      className="w-full bg-arandu-dark-light border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-cyan-500/50">
                      {depos.depositos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
                    </select>
                  </div>
                )}

                {/* Afecta stock toggle — visible para admin o usuarios con permiso */}
                {puedeUsarStockProductos && (
                  <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                    <button type="button"
                      onClick={() => puedeCambiarAfectaStockCompra && setCompraForm(p => ({ ...p, afecta_stock: !p.afecta_stock }))}
                      disabled={!puedeCambiarAfectaStockCompra}
                      className="flex items-center gap-3 w-full"
                      title={puedeCambiarAfectaStockCompra ? "Cambiar afectación de stock" : "Siempre afecta stock con productos vinculados"}
                    >
                      {compraForm.afecta_stock
                        ? <ToggleRight className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                        : <ToggleLeft className="w-6 h-6 text-slate-500 flex-shrink-0" />}
                      <div className="text-left">
                        <p className={`text-sm font-medium ${compraForm.afecta_stock ? "text-emerald-300" : "text-slate-400"}`}>
                          {compraForm.afecta_stock ? "Afecta stock" : "No afecta stock"}
                        </p>
                        <p className="text-slate-500 text-xs">
                          {compraForm.afecta_stock
                            ? "Los ítems con producto vinculado sumarán al inventario"
                            : "Compra de servicio o gasto sin movimiento de inventario"}
                        </p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Entrega posterior (por recibir) — el stock entra recién al confirmar cada recepción */}
                {puedeUsarStockProductos && modoCompra === "catalogo" && compraForm.afecta_stock && !compraForm.solo_iva && (
                  <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                    <button type="button"
                      onClick={() => !editingCompra && setCompraForm(p => ({ ...p, entrega_posterior: !p.entrega_posterior }))}
                      disabled={!!editingCompra}
                      className="flex items-center gap-3 w-full"
                      title={editingCompra ? "No se puede cambiar después de registrar la compra" : "Entrega posterior"}
                    >
                      {compraForm.entrega_posterior
                        ? <ToggleRight className="w-6 h-6 text-violet-400 flex-shrink-0" />
                        : <ToggleLeft className="w-6 h-6 text-slate-500 flex-shrink-0" />}
                      <div className="text-left">
                        <p className={`text-sm font-medium ${compraForm.entrega_posterior ? "text-violet-300" : "text-slate-400"}`}>
                          {compraForm.entrega_posterior ? "Por recibir (entrega posterior)" : "Mercadería recibida"}
                        </p>
                        <p className="text-slate-500 text-xs">
                          {compraForm.entrega_posterior
                            ? "El stock y el costo promedio se actualizan al confirmar cada recepción en Egresos → Mercaderías por recibir"
                            : "Los ítems suman al inventario ahora, al guardar la compra"}
                        </p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Tipo de pago */}
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Tipo de pago</label>
                  <div className="flex gap-3">
                    {["contado", "credito"].map(tipo => (
                      <button key={tipo} type="button"
                        onClick={() => setCompraForm(p => ({
                          ...p,
                          tipo_pago: tipo,
                          fecha_pago: "",
                          plan_cuenta_id: "",
                          plan_cuenta_nombre: "",
                          fecha_vencimiento: "",
                        }))}
                        className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg border text-sm transition-all capitalize ${compraForm.tipo_pago === tipo ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-white/10 text-slate-400 hover:border-white/20"}`}
                      >
                        {tipo === "contado" ? <Banknote className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                        {tipo}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pago automático al contado: cuenta de la que se descuenta */}
                {pagoAutoActivo && (
                  <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3 space-y-3">
                    <p className="text-emerald-300 text-xs font-medium">Esta compra se paga automáticamente al registrarla</p>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-slate-400 text-sm mb-1 block">Cuenta a descontar *</label>
                        <select
                          value={compraForm.cuenta_id || ""}
                          onChange={e => {
                            const c = cuentasDisp.find(x => String(x.id) === e.target.value);
                            setCompraForm(p => ({ ...p, cuenta_id: e.target.value, cuenta_nombre: c?.nombre || "", cuenta_manual: true, tipo_cambio_pago: "" }));
                          }}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                        >
                          <option value="">Seleccionar cuenta...</option>
                          {cuentasDisp.filter(c => c.activa !== false && cuentaLogoOk(c, compraForm.logo_tipo)).map(c => (
                            <option key={c.id} value={c.id}>{c.nombre} ({c.moneda || "PYG"}){c.es_predeterminada ? " · predeterminada" : ""}</option>
                          ))}
                        </select>
                      </div>
                      {pagoAutoNecesitaTc && (
                        <div>
                          <label className="text-slate-400 text-sm mb-1 block">
                            Tipo de cambio del pago (₲ por {(compraForm.moneda || "PYG") === "PYG" ? cuentaPagoSel?.moneda : compraForm.moneda}) *
                          </label>
                          <Input type="number" min="0" step="any" value={compraForm.tipo_cambio_pago || ""}
                            onChange={e => setCompraForm(p => ({ ...p, tipo_cambio_pago: e.target.value }))}
                            placeholder="Ej: 7300"
                            className="bg-arandu-dark border-white/10 text-white" />
                          <p className="text-slate-500 text-[11px] mt-1">
                            La cuenta es en {cuentaPagoSel?.moneda} y la compra en {compraForm.moneda || "PYG"}.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Plan de cuenta */}
                {!compraForm.solo_iva && (
                  <div className="space-y-3">
                    <div className={`grid gap-4 ${compraForm.es_credito_cuotas ? "grid-cols-1" : "md:grid-cols-2"}`}>
                      <div>
                        <label className="text-slate-400 text-sm mb-1 block">Plan de cuenta *</label>
                        <select
                          value={compraForm.plan_cuenta_id || ""}
                          onChange={e => aplicarPlanCuentaCompra(e.target.value, true)}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500/50"
                        >
                          <option value="">Seleccionar cuenta...</option>
                          {planCuentasCompra.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.nombre}{c.es_cuotas ? " 📋" : ""}{c.predeterminada ? " ★" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      {!compraForm.es_credito_cuotas && (
                        <div>
                          <label className="text-slate-400 text-sm mb-1 block">Vencimiento</label>
                          <DateInput value={compraForm.fecha_vencimiento}
                            onChange={e => setCompraForm(p => ({ ...p, fecha_vencimiento: e.target.value }))}
                            className="bg-arandu-dark border-white/10 text-white" />
                          <p className="text-slate-500 text-xs mt-1">Viene del plan de cuenta, pero podés ajustarlo.</p>
                        </div>
                      )}
                    </div>

                    {/* Cuotas panel para compras */}
                    {compraForm.es_credito_cuotas && (
                      <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-violet-300 flex items-center gap-1.5">
                            <CreditCard className="w-3.5 h-3.5" /> Configuración de cuotas
                          </p>
                          <label className="flex items-center gap-1.5 text-xs text-violet-300 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={compraForm.cuotas_manual_activo}
                              onChange={e => e.target.checked ? activarCuotasManual() : setCompraForm(p => ({ ...p, cuotas_manual_activo: false }))}
                              className="w-3.5 h-3.5 accent-violet-500"
                            />
                            Cargar monto y vencimiento de cada cuota a mano
                          </label>
                        </div>

                        {!compraForm.cuotas_manual_activo && (
                          <>
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="text-slate-400 text-xs mb-1 block">N° cuotas</label>
                                <select
                                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                                  value={compraForm.num_cuotas}
                                  onChange={e => setCompraForm(p => ({ ...p, num_cuotas: Number(e.target.value) }))}
                                >
                                  {Array.from({ length: planCuentaCompraSeleccionada?.mi_max_cuotas || planCuentaCompraSeleccionada?.max_cuotas || 6 }, (_, i) => i + 1).map(n => (
                                    <option key={n} value={n}>{n} cuota{n > 1 ? "s" : ""}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="text-slate-400 text-xs mb-1 block">1° vencimiento</label>
                                <DateInput
                                  value={compraForm.fecha_primer_vencimiento}
                                  onChange={e => setCompraForm(p => ({ ...p, fecha_primer_vencimiento: e.target.value }))}
                                  className="bg-arandu-dark border-white/10 text-white"
                                />
                              </div>
                              <div>
                                <label className="text-slate-400 text-xs mb-1 block">Interés (%)</label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0"
                                  value={compraForm.tasa_interes_cuotas}
                                  onChange={e => setCompraForm(p => ({ ...p, tasa_interes_cuotas: e.target.value }))}
                                  className="bg-arandu-dark border-white/10 text-white"
                                />
                              </div>
                            </div>
                            {cuotasCompraPreview.length > 0 && (
                              <div className="text-xs text-violet-300 space-y-1">
                                <p className="font-medium">Vista previa (montos y vencimientos calculados automáticamente):</p>
                                <div className="flex flex-wrap gap-2">
                                  {cuotasCompraPreview.map(c => (
                                    <span key={c.num} className="px-2 py-0.5 rounded bg-violet-500/15 border border-violet-500/25">
                                      Cuota {c.num}: {compraForm.moneda === "USD" ? "USD" : compraForm.moneda === "BRL" ? "R$" : "₲"} {c.monto.toLocaleString("es-PY")}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {compraForm.cuotas_manual_activo && (
                          <div className="space-y-2">
                            <div className="space-y-1.5">
                              {(compraForm.cuotas_manual || []).map((f, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <span className="text-slate-400 text-xs w-14 shrink-0">Cuota {idx + 1}</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="Monto"
                                    value={f.monto}
                                    onChange={e => actualizarCuotaManual(idx, "monto", e.target.value)}
                                    className="bg-arandu-dark border-white/10 text-white text-sm"
                                  />
                                  <DateInput
                                    value={f.fecha_vencimiento}
                                    onChange={e => actualizarCuotaManual(idx, "fecha_vencimiento", e.target.value)}
                                    className="bg-arandu-dark border-white/10 text-white text-sm"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => eliminarCuotaManual(idx)}
                                    className="text-slate-500 hover:text-red-400 shrink-0 p-1"
                                    title="Quitar cuota"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={agregarCuotaManual}
                              className="text-xs text-violet-300 hover:text-violet-200 flex items-center gap-1"
                            >
                              <Plus className="w-3.5 h-3.5" /> Agregar cuota
                            </button>
                            <div className={`text-xs flex items-center justify-between pt-1 border-t border-white/5 ${Math.abs(totalCuotasManual - montoTotalCompra) > 1 ? "text-amber-300" : "text-emerald-300"}`}>
                              <span>Suma de cuotas: {compraForm.moneda === "USD" ? "USD" : compraForm.moneda === "BRL" ? "R$" : "₲"} {totalCuotasManual.toLocaleString("es-PY")}</span>
                              <span>Total de la compra: {compraForm.moneda === "USD" ? "USD" : compraForm.moneda === "BRL" ? "R$" : "₲"} {montoTotalCompra.toLocaleString("es-PY")}</span>
                            </div>
                            {Math.abs(totalCuotasManual - montoTotalCompra) > 1 && (
                              <p className="text-[11px] text-amber-400">La suma de las cuotas no coincide con el total de la compra — se puede guardar igual, pero conviene revisarlo.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Factura */}
                <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={compraForm.tiene_factura}
                      disabled={compraFacturaCargada(editingCompra)}
                      onChange={e => setCompraForm(p => ({
                        ...p,
                        tiene_factura: e.target.checked,
                        factura_electronica: e.target.checked ? p.factura_electronica : false,
                        solo_iva: e.target.checked ? p.solo_iva : false,
                      }))}
                      className="w-4 h-4 accent-emerald-500" />
                    <span className="text-slate-300 text-sm flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-emerald-400" />
                      Tiene factura del proveedor (IVA crédito fiscal)
                    </span>
                    {compraForm.tiene_factura && (
                      <label className="flex items-center gap-1.5 ml-auto cursor-pointer select-none" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={compraForm.factura_electronica}
                          disabled={compraFacturaCargada(editingCompra) && !(editingCompra && !editingCompra.factura_electronica && !editingCompra.fecha_vigencia_timbrado)}
                          onChange={e => setCompraForm(p => ({
                            ...p,
                            factura_electronica: e.target.checked,
                            fecha_vigencia_timbrado: e.target.checked ? "" : p.fecha_vigencia_timbrado,
                          }))}
                          className="w-3.5 h-3.5 accent-violet-500" />
                        <span className="text-violet-300 text-xs font-medium whitespace-nowrap">Fact. Electrónica</span>
                      </label>
                    )}
                  </label>
                  {compraForm.tiene_factura && (
                    <div className="space-y-2 mt-2">
                      {compraFacturaCargada(editingCompra) && <p className="text-amber-400/80 text-[11px]">Factura ya cargada: no se puede modificar.</p>}
                      <Input value={compraForm.numero_factura}
                        disabled={compraFacturaCargada(editingCompra)}
                        onChange={e => setCompraForm(p => ({ ...p, numero_factura: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="Número de factura (Ej: 001-001-0000123)" required />
                      <div className={`grid gap-2 ${compraForm.factura_electronica ? "grid-cols-1" : "grid-cols-2"}`}>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Nro. Timbrado</label>
                          <Input value={compraForm.nro_timbrado}
                            disabled={compraFacturaCargada(editingCompra)}
                            onChange={e => setCompraForm(p => ({ ...p, nro_timbrado: e.target.value }))}
                            className="bg-arandu-dark border-white/10 text-white"
                            placeholder="Ej: 12345678" />
                        </div>
                        {!compraForm.factura_electronica && (
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Vigencia timbrado</label>
                          <DateInput value={compraForm.fecha_vigencia_timbrado}
                            disabled={compraFacturaCargada(editingCompra)}
                            onChange={e => setCompraForm(p => ({ ...p, fecha_vigencia_timbrado: e.target.value }))}
                            className="bg-arandu-dark border-white/10 text-white" />
                        </div>
                        )}
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Tipo de comprobante (retención de IVA)</label>
                        <select value={compraForm.tipo_comprobante || "normal"}
                          onChange={e => setCompraForm(p => ({ ...p, tipo_comprobante: e.target.value }))}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                          <option value="normal">Factura normal</option>
                          <option value="granos">Venta de granos / productor</option>
                          <option value="flete_granos">Flete de granos</option>
                          <option value="autofactura">Autofactura</option>
                          <option value="boleta_simple">Boleta / comprobante simple</option>
                          <option value="servicio_publico">Servicio público</option>
                        </select>
                      </div>
                      <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3">
                        <input type="checkbox" checked={compraForm.solo_iva}
                          onChange={e => setCompraForm(p => ({
                            ...p,
                            solo_iva: e.target.checked,
                            afecta_stock: e.target.checked ? false : p.afecta_stock,
                            cuenta_id: e.target.checked ? "" : p.cuenta_id,
                            cuenta_nombre: e.target.checked ? "" : p.cuenta_nombre,
                          }))}
                          className="mt-0.5 w-4 h-4 accent-cyan-500" />
                        <span>
                          <span className="text-cyan-200 text-sm font-medium block">Solo IVA fiscal</span>
                          <span className="text-slate-500 text-xs">Registra la factura para crédito fiscal, sin afectar caja, deuda, gasto ni stock.</span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Vincular presupuestos (multi-select) */}
                {hasModule?.("presupuestos") && (presupuestosForm.length > 0 || presupuestosDisp.length > 0) && (
                  <div>
                    {!compraForm.proveedor_id && <p className="text-amber-300/80 text-xs mb-1">Elegí el proveedor para ver sus presupuestos con mercadería pendiente.</p>}
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
                        Vincular presupuestos
                        {compraForm.presupuesto_ids?.length > 0 && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-300 text-[10px]">
                            {compraForm.presupuesto_ids.length} sel.
                          </span>
                        )}
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 text-xs">{presupuestosDisp.length} disponibles</span>
                        {compraForm.presupuesto_ids?.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setCompraForm(p => ({ ...p, presupuesto_ids: [] }))}
                            className="text-[10px] text-red-400 hover:text-red-300 transition-colors"
                          >
                            ✕ Limpiar
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Search */}
                    <input
                      type="text"
                      value={presupuestoSearch}
                      onChange={e => setPresupuestoSearch(e.target.value)}
                      placeholder="Buscar por número, nombre, monto..."
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-400 mb-2"
                    />
                    {/* List */}
                    <div className="max-h-52 overflow-y-auto space-y-1 pr-0.5">
                      {(() => {
                        const selIds = compraForm.presupuesto_ids || [];
                        const q = presupuestoSearch.trim().toLowerCase();
                        const filtrados = presupuestosDisp.filter(p =>
                          !q || (p.numero && String(p.numero).includes(q)) || (p.empresa_nombre || "").toLowerCase().includes(q) || (p.nombre_archivo || "").toLowerCase().includes(q) || (p.total != null && String(p.total).includes(q))
                        );
                        const renderItem = (p) => {
                          const isSelected = selIds.includes(p.id);
                          const montoFmt = p.moneda === "USD"
                            ? `US$ ${Number(p.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                            : p.moneda === "BRL"
                            ? `R$ ${Number(p.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                            : `₲ ${Math.round(p.total || 0).toLocaleString("es-PY")}`;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setCompraForm(prev => ({
                                    ...prev,
                                    presupuesto_ids: (prev.presupuesto_ids || []).filter(x => x !== p.id),
                                    items: (prev.items || []).filter(it => it.presupuesto_item_ref?.presupuesto_id !== p.id),
                                  }));
                                } else seleccionarPresupuestoForm(p);
                              }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                                isSelected
                                  ? "border-orange-400 bg-orange-500/15 text-white"
                                  : "border-white/10 text-slate-300 hover:border-white/25 hover:bg-white/5"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center text-[9px] ${isSelected ? "bg-orange-500 border-orange-400 text-white" : "border-white/20"}`}>
                                    {isSelected ? "✓" : ""}
                                  </span>
                                  <span className="text-sm font-medium truncate">
                                    {p.numero ? `#${p.numero}` : "—"}
                                    {p.nombre_archivo ? ` · ${p.nombre_archivo}` : ""}
                                  </span>
                                </div>
                                <span className="text-xs text-slate-400 flex-shrink-0">{montoFmt}</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 truncate pl-5">{p.empresa_nombre} · {p.fecha}</p>
                            </button>
                          );
                        };
                        return (
                          <>
                            {filtrados.length > 0 && (
                              <>
                                <p className="text-cyan-400/70 text-[10px] font-bold uppercase tracking-wider px-1 pt-1">Aprobados / Cobrados / Facturados ({filtrados.length})</p>
                                {filtrados.map(renderItem)}
                              </>
                            )}
                            {filtrados.length === 0 && (
                              <p className="text-slate-500 text-sm text-center py-4">Sin resultados</p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {(compraForm.presupuesto_ids?.length > 0) && (
                      <button
                        type="button"
                        onClick={cargarItemsPresupuestosCompra}
                        className="mt-2 w-full px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm hover:bg-emerald-500/20 transition-colors"
                      >
                        Cargar ítems aprobados del presupuesto
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                  <Input value={compraForm.notas}
                    onChange={e => setCompraForm(p => ({ ...p, notas: e.target.value }))}
                    className="bg-arandu-dark border-white/10 text-white" placeholder="Descripción, detalle..." />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCompraForm(false)}
                    className="flex-1 border-white/10 text-slate-400">
                    <X className="w-4 h-4 mr-2" /> Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                    <Save className="w-4 h-4 mr-2" />
                    {editingCompra ? "Actualizar" : "Registrar compra"}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Pago proveedor vinculado desde compra ════════════════════ */}
      <AnimatePresence>
        {selectedPagoProveedorDesdeCompra && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/85 z-[90] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Banknote className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold truncate">{selectedPagoProveedorDesdeCompra.proveedor_nombre || "Pago proveedor"}</p>
                    <p className="text-slate-500 text-xs">{selectedPagoProveedorDesdeCompra.fecha_pago || selectedPagoProveedorDesdeCompra.fecha_vencimiento || "—"}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedPagoProveedorDesdeCompra(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5 text-center mb-4">
                <p className="text-slate-400 text-xs mb-1">Monto pagado</p>
                <p className="font-heading text-3xl font-bold text-emerald-400">{fmt(selectedPagoProveedorDesdeCompra.monto, selectedPagoProveedorDesdeCompra.moneda)}</p>
                {selectedPagoProveedorDesdeCompra.tipo_cambio && (
                  <p className="text-slate-500 text-xs mt-1">TC: ₲ {Number(selectedPagoProveedorDesdeCompra.tipo_cambio).toLocaleString("es-PY")}</p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Cuenta</p>
                  <p className="text-white text-sm font-medium">{selectedPagoProveedorDesdeCompra.cuenta_nombre || "—"}</p>
                </div>
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3">
                  <p className="text-slate-500 text-xs">Recibo</p>
                  <p className="text-white text-sm font-medium">{selectedPagoProveedorDesdeCompra.recibo_numero || "No aplica / sin recibo"}</p>
                </div>
              </div>

              {(selectedPagoProveedorDesdeCompra.compras_detalle || []).length > 0 && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-2 flex items-center gap-1"><Receipt className="w-3 h-3" /> Compras cubiertas</p>
                  <div className="space-y-1.5">
                    {(selectedPagoProveedorDesdeCompra.compras_detalle || []).map(c => (
                      <div key={c.id} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 last:border-0 pb-1.5 last:pb-0">
                        <span className="text-slate-300 truncate">
                          {c.numero_factura ? `Fac. ${c.numero_factura}` : "Sin factura"} · {c.fecha || "—"}
                          <span className="text-slate-600 capitalize"> · {c.tipo_pago || "—"}</span>
                        </span>
                        <span className="text-emerald-300 font-medium whitespace-nowrap">{fmt(c.monto_pagado || c.monto_total || 0, c.moneda || selectedPagoProveedorDesdeCompra.moneda)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedPagoProveedorDesdeCompra.notas && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-1">Notas</p>
                  <p className="text-slate-300 text-sm">{selectedPagoProveedorDesdeCompra.notas}</p>
                </div>
              )}

              <button onClick={() => setSelectedPagoProveedorDesdeCompra(null)} className="w-full py-2.5 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all">
                Cerrar y volver a la compra
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Cargar / editar factura de compra ═════════════════════════ */}
      <AnimatePresence>
        {facturaCompraTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[65] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-lg p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-heading text-lg font-bold text-white">Cargar factura de compra</h3>
                  <p className="text-slate-500 text-xs mt-1">
                    Solo modifica los datos fiscales de la factura. La compra, stock, deuda y pagos no cambian.
                  </p>
                </div>
                <button onClick={() => setFacturaCompraTarget(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                <p className="text-slate-500 text-xs">Compra</p>
                <p className="text-white text-sm font-semibold">{facturaCompraTarget.proveedor_nombre || "—"}</p>
                <p className="text-slate-400 text-xs mt-0.5">
                  {facturaCompraTarget.fecha || "—"} · {fmt(facturaCompraTarget.monto_total, facturaCompraTarget.moneda)}
                </p>
              </div>

              <form onSubmit={handleSaveFacturaCompra} className="space-y-4">
                <label className="flex items-center gap-2 rounded-lg border border-violet-500/25 bg-violet-500/5 px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={facturaCompraForm.factura_electronica}
                    onChange={e => setFacturaCompraForm(f => ({
                      ...f,
                      factura_electronica: e.target.checked,
                      fecha_vigencia_timbrado: e.target.checked ? "" : f.fecha_vigencia_timbrado,
                    }))}
                    className="w-4 h-4 accent-violet-500"
                  />
                  <span className="text-violet-200 text-sm font-medium">Factura electrónica</span>
                  <span className="text-slate-500 text-xs ml-auto">sin vigencia de timbrado</span>
                </label>

                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Número de factura *</label>
                  <Input
                    value={facturaCompraForm.numero_factura}
                    onChange={e => setFacturaCompraForm(f => ({ ...f, numero_factura: e.target.value }))}
                    placeholder="Ej: 001-001-0000123"
                    className="bg-arandu-dark border-white/10 text-white"
                  />
                </div>

                <div className={`grid gap-3 ${facturaCompraForm.factura_electronica ? "grid-cols-1" : "md:grid-cols-2"}`}>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Nro. timbrado *</label>
                    <Input
                      value={facturaCompraForm.nro_timbrado}
                      onChange={e => setFacturaCompraForm(f => ({ ...f, nro_timbrado: e.target.value }))}
                      placeholder="Ej: 12345678"
                      className="bg-arandu-dark border-white/10 text-white"
                    />
                  </div>
                  {!facturaCompraForm.factura_electronica && (
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Vigencia timbrado *</label>
                      <DateInput
                        value={facturaCompraForm.fecha_vigencia_timbrado}
                        onChange={e => setFacturaCompraForm(f => ({ ...f, fecha_vigencia_timbrado: e.target.value }))}
                        className="bg-arandu-dark border-white/10 text-white"
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setFacturaCompraTarget(null)}
                    className="flex-1 border-white/10 text-slate-400">
                    <X className="w-4 h-4 mr-2" /> Cancelar
                  </Button>
                  <Button type="submit" disabled={savingFacturaCompra}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-60">
                    {savingFacturaCompra ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar factura
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Detalle de presupuesto vinculado ═══════════════════════════ */}
      <AnimatePresence>
        {presDetailEgresos && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
          >
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-arandu-dark-light border border-blue-500/20 rounded-xl w-full max-w-md p-5"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  📄 Presupuesto vinculado
                </h3>
                <button onClick={() => setPresDetailEgresos(null)} className="text-slate-400 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2 text-sm">
                {presDetailEgresos.numero && <div className="flex justify-between"><span className="text-slate-400">Número</span><span className="text-white font-medium">#{presDetailEgresos.numero}</span></div>}
                {presDetailEgresos.nombre_archivo && <div className="flex justify-between"><span className="text-slate-400">Nombre</span><span className="text-white">{presDetailEgresos.nombre_archivo}</span></div>}
                <div className="flex justify-between"><span className="text-slate-400">Cliente</span><span className="text-white">{presDetailEgresos.empresa_nombre || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Fecha</span><span className="text-slate-300">{presDetailEgresos.fecha}</span></div>
                {presDetailEgresos.estado && <div className="flex justify-between"><span className="text-slate-400">Estado</span><span className="capitalize text-blue-300">{presDetailEgresos.estado}</span></div>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Confirmar qué cubre la compra del presupuesto ══ */}
      {vincConfirm && (() => {
        const { pres, compraItems, disp } = vincConfirm;
        const sumaPorIdx = {};
        compraItems.forEach(ci => {
          const v = vincMap[ci._i];
          if (v !== "" && v != null) sumaPorIdx[v] = (sumaPorIdx[v] || 0) + Number(ci.cantidad || 0);
        });
        return (
          <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4">
            <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white font-semibold text-sm">Confirmar cobertura · Presupuesto #{pres.numero}</h3>
                <button type="button" onClick={() => setVincConfirm(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-slate-500 text-xs mb-3">Indicá qué mercadería de esta compra cubre cada renglón del presupuesto. Catálogo: se emparejó por producto; libre: elegí el renglón.</p>
              {disp.length === 0 && <p className="text-amber-300 text-xs mb-2">Este presupuesto no tiene renglones pendientes de compra para este proveedor.</p>}
              {compraItems.length === 0 && <p className="text-amber-300 text-xs mb-2">La compra todavía no tiene ítems cargados. Cargá los ítems y volvé a vincular.</p>}
              <div className="space-y-2">
                {compraItems.map(ci => {
                  const v = vincMap[ci._i] ?? "";
                  const target = disp.find(x => String(x.idx) === String(v));
                  const excede = target && (sumaPorIdx[v] || 0) > Number(target.pendiente_comprar);
                  return (
                    <div key={ci._i} className="rounded-lg border border-white/10 p-2.5">
                      <p className="text-white text-sm">{ci.cantidad} × {ci.descripcion || "(sin descripción)"}{ci.variante_texto ? ` · ${ci.variante_texto}` : ""}</p>
                      <select value={v} onChange={e => setVincMap(m => ({ ...m, [ci._i]: e.target.value }))}
                        className="mt-1.5 w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
                        <option value="">No cubre ningún renglón del presupuesto</option>
                        {disp.map(x => (
                          <option key={x.idx} value={String(x.idx)}>
                            {x.descripcion}{x.variante_texto ? ` · ${x.variante_texto}` : ""} — pendiente de comprar: {x.pendiente_comprar}
                          </option>
                        ))}
                      </select>
                      {target && !excede && <p className="text-emerald-300 text-[11px] mt-1">Cubre {Math.min(Number(ci.cantidad || 0), Number(target.pendiente_comprar))} de {target.pendiente_comprar} pendientes de este renglón</p>}
                      {excede && <p className="text-amber-300 text-[11px] mt-1">La cantidad supera lo pendiente ({target.pendiente_comprar}) del presupuesto</p>}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setVincConfirm(null)} className="flex-1 border-white/10 text-slate-400">Cancelar</Button>
                <Button type="button" onClick={confirmarVinculo} disabled={vincularPresLoading} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white">Confirmar y vincular</Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ MODAL: Vincular presupuestos a compra existente (multi-select) ══ */}
      <AnimatePresence>
        {showVincularPresModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-[90] flex items-center justify-center p-4"
          >
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-5"
              onClick={e => e.stopPropagation()}
            >
              {(() => {
                // Estado local de selección para este modal (inicializado desde la compra actual)
                const currentIds = selectedCompraView?.presupuesto_ids || (selectedCompraView?.presupuesto_id ? [selectedCompraView.presupuesto_id] : []);
                return (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                        📄 Vincular presupuestos
                      </h3>
                      <button onClick={() => setShowVincularPresModal(false)} className="text-slate-400 hover:text-white">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-slate-500 text-xs mb-3">Podés vincular más de un presupuesto. Hacé clic para seleccionar/deseleccionar.</p>
                    {/* Búsqueda */}
                    <input
                      autoFocus
                      type="text"
                      value={vincularPresSearch}
                      onChange={e => setVincularPresSearch(e.target.value)}
                      placeholder="Buscar por número, nombre..."
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-2 outline-none focus:border-blue-500/50"
                    />
                    {/* Desvincular todos */}
                    {currentIds.length > 0 && (
                      <button
                        onClick={() => handleVincularPresupuestoCompra([])}
                        disabled={vincularPresLoading}
                        className="w-full text-left px-3 py-2 rounded-lg border border-white/10 text-red-400 hover:border-red-500/30 hover:bg-red-500/10 text-xs transition-all mb-2 disabled:opacity-50"
                      >
                        ✕ Quitar todos los vinculados ({currentIds.length})
                      </button>
                    )}
                    {/* Lista */}
                    <div className="max-h-60 overflow-y-auto space-y-1 mb-3">
                      {(() => {
                        const q = vincularPresSearch.trim().toLowerCase();
                        const filtrados = presupuestosDisp.filter(p =>
                          !q ||
                          (p.numero && String(p.numero).includes(q)) ||
                          (p.nombre_archivo || "").toLowerCase().includes(q) ||
                          (p.empresa_nombre || "").toLowerCase().includes(q) ||
                          (p.total != null && String(p.total).includes(q))
                        );
                        const renderItem = (p) => {
                          const isSelected = currentIds.includes(p.id);
                          const montoFmt = p.moneda === "USD"
                            ? `US$ ${Number(p.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                            : p.moneda === "BRL"
                            ? `R$ ${Number(p.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                            : `₲ ${Math.round(p.total || 0).toLocaleString("es-PY")}`;
                          const newIds = isSelected ? currentIds.filter(x => x !== p.id) : [...currentIds, p.id];
                          return (
                            <button
                              key={p.id}
                              onClick={() => isSelected ? handleVincularPresupuestoCompra(newIds) : abrirConfirmVinculo(p, "view")}
                              disabled={vincularPresLoading}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all disabled:opacity-60 ${
                                isSelected
                                  ? "border-blue-400 bg-blue-500/15 text-white"
                                  : "border-white/10 text-slate-300 hover:border-white/25 hover:bg-white/5"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center text-[9px] ${isSelected ? "bg-blue-500 border-blue-400 text-white" : "border-white/20"}`}>
                                    {isSelected ? "✓" : ""}
                                  </span>
                                  <span className="text-sm font-medium truncate">
                                    {p.numero ? `#${p.numero}` : "—"}{p.nombre_archivo ? ` · ${p.nombre_archivo}` : ""}
                                  </span>
                                </div>
                                <span className="text-xs text-slate-400 flex-shrink-0">{montoFmt}</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 truncate pl-5">{p.empresa_nombre} · {p.fecha} · <span className="capitalize">{p.estado}</span></p>
                            </button>
                          );
                        };
                        return (
                          <>
                            {filtrados.length > 0 && (
                              <>
                                <p className="text-cyan-400/70 text-[10px] font-bold uppercase tracking-wider px-1 pt-1">Disponibles ({filtrados.length})</p>
                                {filtrados.map(renderItem)}
                              </>
                            )}
                            {filtrados.length === 0 && (
                              <p className="text-slate-500 text-sm text-center py-6">Sin resultados</p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-3">
                      <span className="text-slate-400 text-xs">{currentIds.length} vinculado(s)</span>
                      <button
                        onClick={() => setShowVincularPresModal(false)}
                        className="px-4 py-1.5 rounded-lg bg-white/10 text-white text-xs hover:bg-white/20 transition-colors"
                      >
                        Listo
                      </button>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Buscador de productos ════════════════════════════════════ */}
      <AnimatePresence>
        {showProductoBrowser && puedeUsarStockProductos && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-lg p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-400" /> Buscar producto
                </h3>
                <button onClick={() => setShowProductoBrowser(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  value={productoSearch}
                  onChange={e => setProductoSearch(e.target.value)}
                  placeholder="Buscar por nombre, SKU, código o variante..."
                  autoFocus
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-9 pr-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50 placeholder-slate-500"
                />
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1">
                {productos
                  .filter(p => !activeEmpresaPropia?.slug || p.logo_tipo === activeEmpresaPropia.slug)
                  .flatMap(prod => {
                    const variantes = (prod.variantes || []).filter(v => v.activo !== false);
                    if (prod.variantes_habilitadas && variantes.length > 0) {
                      return variantes.map(v => ({ prod, variante: v, key: `${prod.id}:${v.id}` }));
                    }
                    return [{ prod, variante: null, key: prod.id }];
                  })
                  .filter(({ prod, variante }) => {
                    const q = productoSearch.trim().toLowerCase();
                    return !q || productoVarianteSearchText(prod, variante).includes(q);
                  })
                  .slice(0, 40)
                  .map(({ prod, variante, key }) => (
                    <button key={key} type="button"
                      onClick={() => {
                        addCompraItemFromProduct(prod, false, variante);
                        setShowProductoBrowser(false);
                        setProductoSearch("");
                      }}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-arandu-dark border border-white/5 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all text-left"
                    >
                      <div className="min-w-0 pr-3">
                        <p className="text-white text-sm font-medium truncate">
                          {prod.nombre}
                        </p>
                        {variante && (
                          <p className="text-emerald-400 text-[10px] truncate mt-0.5">
                            Variante: {variante.atributos_texto || "Variante"}
                          </p>
                        )}
                        <p className="text-slate-500 text-xs">
                          {prod.sku || prod.codigo || "Sin código"} · Stock: {variante ? (variante.stock_actual ?? 0) : (prod.stock_actual ?? prod.stock ?? "−")}
                        </p>
                      </div>
                      <div className="text-right">
                        {(variante?.precio_costo ?? prod.precio_costo) ? (
                          <p className="text-orange-300 text-sm font-medium">
                            {prod.moneda === "USD"
                              ? `USD ${Number(variante?.precio_costo ?? prod.precio_costo).toFixed(2)}`
                              : prod.moneda === "BRL"
                              ? `R$ ${Number(variante?.precio_costo ?? prod.precio_costo).toFixed(2)}`
                              : `₲ ${Math.round(variante?.precio_costo ?? prod.precio_costo).toLocaleString("es-PY")}`}
                          </p>
                        ) : (
                          <p className="text-slate-600 text-xs">Sin costo</p>
                        )}
                      </div>
                    </button>
                  ))}
                {productos
                  .filter(p => !activeEmpresaPropia?.slug || p.logo_tipo === activeEmpresaPropia.slug)
                  .flatMap(prod => {
                    const variantes = (prod.variantes || []).filter(v => v.activo !== false);
                    if (prod.variantes_habilitadas && variantes.length > 0) {
                      return variantes.map(v => ({ prod, variante: v, key: `${prod.id}:${v.id}` }));
                    }
                    return [{ prod, variante: null, key: prod.id }];
                  })
                  .filter(({ prod, variante }) => {
                    const q = productoSearch.trim().toLowerCase();
                    return !q || productoVarianteSearchText(prod, variante).includes(q);
                  })
                  .length === 0 && (
                  <div className="text-center py-8 text-slate-500 text-sm">No se encontraron productos</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Registrar pago de compra a crédito ════════════════════════ */}
      <AnimatePresence>
        {showPagoModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-4 sm:p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white">Registrar Pago</h3>
                <button onClick={() => setShowPagoModal(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-arandu-dark rounded-lg p-3 mb-4 border border-white/5">
                <p className="text-slate-400 text-sm">{showPagoModal.proveedor_nombre}</p>
                <p className="text-white font-bold">Saldo: {fmt(showPagoModal.saldo_pendiente, showPagoModal.moneda)}</p>
              </div>

              {/* Tabs medio de pago */}
              {puedeUsarCheque && (
                <div className="flex gap-2 mb-4">
                  <button type="button" onClick={() => setPagoMedioCompra("transferencia")}
                    className={`flex-1 text-xs py-2 rounded-lg border transition-all ${pagoMedioCompra === "transferencia" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}>
                    Transf. / Efectivo
                  </button>
                  <button type="button" onClick={() => setPagoMedioCompra("cheque")}
                    className={`flex-1 text-xs py-2 rounded-lg border transition-all ${pagoMedioCompra === "cheque" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:border-white/20"}`}>
                    Cheque
                  </button>
                </div>
              )}

              {pagoMedioCompra === "cheque" ? (
                <ChequeEgresoForm
                  token={token}
                  logoTipo={showPagoModal?.logo_tipo || activeEmpresaPropia?.slug}
                  beneficiario={showPagoModal?.proveedor_nombre || ""}
                  monto={showPagoModal?.saldo_pendiente}
                  egresoMoneda={showPagoModal?.moneda || "PYG"}
                  egresoTipo="compra"
                  cuentaId={pagoForm.cuenta_id}
                  egresoId={showPagoModal?.id}
                  onSuccess={handlePagoCompraConCheque}
                  onCancel={() => setShowPagoModal(null)}
                />
              ) : (
                <form onSubmit={handlePagoCompra} className="space-y-3">
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Monto pagado *</label>
                    <MoneyInput min="0" step="any" moneda={showPagoModal?.moneda || "PYG"}
                      value={pagoForm.monto_pagado}
                      onChange={value => setPagoForm(p => ({ ...p, monto_pagado: value }))}
                      className="w-full rounded-md border px-3 py-2 bg-arandu-dark border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Fecha de pago *</label>
                    <DateInput
                      value={pagoForm.fecha_pago}
                      onChange={e => setPagoForm(p => ({ ...p, fecha_pago: e.target.value }))}
                      className="bg-arandu-dark border-white/10 text-white" />
                  </div>
                  <RetencionPreview token={token} moneda={showPagoModal?.moneda || "PYG"}
                    pagos={[{ compra_id: showPagoModal?.id, monto: pagoForm.monto_pagado }]}
                    fechaPago={pagoForm.fecha_pago}
                    omitir={!!pagoForm.sin_retencion}
                    onOmitir={hasPermission("retenciones.omitir_retencion") ? (v => setPagoForm(p => ({ ...p, sin_retencion: v }))) : null} />
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Cuenta bancaria *</label>
                    <select
                      value={pagoForm.cuenta_id}
                      onChange={e => {
                        const c = cuentasDisp.find(c => c.id === e.target.value);
                        setPagoForm(p => ({
                          ...p,
                          cuenta_id: e.target.value,
                          cuenta_nombre: c?.nombre || "",
                          cuenta_moneda: c?.moneda || "PYG",
                          tipo_cambio: "",
                        }));
                      }}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    >
                      <option value="">Seleccionar cuenta...</option>
                      {renderCuentasPorMoneda(cuentasDisp, showPagoModal?.logo_tipo || activeEmpresaPropia?.slug)}
                    </select>
                  </div>
                  {puedeUsarCheque && pagoForm.cuenta_id && (
                    <button type="button" onClick={() => setPagoMedioCompra("cheque")}
                      className="w-full text-xs py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-all">
                      Pagar con cheque de esta cuenta
                    </button>
                  )}
                  {pagoForm.cuenta_moneda && showPagoModal?.moneda && pagoForm.cuenta_moneda !== showPagoModal.moneda && (() => {
                    const monedaExtranjera = showPagoModal.moneda !== "PYG" ? showPagoModal.moneda : pagoForm.cuenta_moneda;
                    const soloLecturaTc = showPagoModal.moneda !== "PYG" && !canEditTcDoc;
                    return (
                      <div>
                        <label className="text-slate-400 text-sm mb-1 block">Tipo de cambio *</label>
                        <Input type="number" min="0" step="any"
                          value={pagoForm.tipo_cambio}
                          onChange={e => { if (soloLecturaTc) return; setPagoForm(p => ({ ...p, tipo_cambio: e.target.value })); }}
                          readOnly={soloLecturaTc}
                          title={soloLecturaTc ? "El tipo de cambio se configura en Financiero → Cotizaciones" : undefined}
                          className={`bg-arandu-dark border-white/10 text-white${soloLecturaTc ? " opacity-60 cursor-not-allowed" : ""}`}
                          placeholder={`Gs por ${monedaExtranjera}`} />
                      </div>
                    );
                  })()}
                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                    <Input value={pagoForm.notas}
                      onChange={e => setPagoForm(p => ({ ...p, notas: e.target.value }))}
                      className="bg-arandu-dark border-white/10 text-white" placeholder="Opcional" />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowPagoModal(null)}
                      className="flex-1 border-white/10 text-slate-400">Cancelar</Button>
                    <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Check className="w-4 h-4 mr-2" /> Registrar pago
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>



      {/* ═══ MODAL: Ver detalle de nota de crédito de compra ════════════════ */}
      <AnimatePresence>
        {selectedNotaCompraView && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs bg-rose-500/20 text-rose-300 border border-rose-500/30 rounded-full px-3 py-1 font-medium">
                  Nota de crédito de compra
                </span>
                <button onClick={() => setSelectedNotaCompraView(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-rose-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-5 h-5 text-rose-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-400 text-xs">Número</p>
                  <p className="text-white font-semibold truncate">{selectedNotaCompraView.numero}</p>
                </div>
                <p className="text-slate-400 text-xs">{selectedNotaCompraView.fecha}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                  <p className="text-slate-500 text-xs mb-0.5">Proveedor</p>
                  <p className="text-white text-sm font-medium">{selectedNotaCompraView.proveedor_nombre || "—"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                  <p className="text-slate-500 text-xs mb-0.5">Compra vinculada</p>
                  <p className="text-white text-sm font-medium">
                    {selectedNotaCompraView.compra_numero_factura || (selectedNotaCompraView.compra_id ? "Compra sin número de factura" : "Sin vincular")}
                  </p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Motivo</p>
                  <p className="text-white text-sm font-medium">{selectedNotaCompraView.motivo || "—"}</p>
                </div>
                <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Monto aplicado</p>
                  <p className="text-rose-300 text-sm font-bold">{fmt(selectedNotaCompraView.monto, selectedNotaCompraView.moneda)}</p>
                </div>
              </div>

              {selectedNotaCompraView.moneda !== "PYG" && selectedNotaCompraView.tipo_cambio && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-0.5">Tipo de cambio</p>
                  <p className="text-slate-300 text-sm">₲ {Number(selectedNotaCompraView.tipo_cambio).toLocaleString("es-PY")} / {selectedNotaCompraView.moneda}</p>
                </div>
              )}

              {selectedNotaCompraView.notas && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Notas
                  </p>
                  <p className="text-slate-300 text-sm">{selectedNotaCompraView.notas}</p>
                </div>
              )}

              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => { setNotaCompraDocModal(selectedNotaCompraView); setSelectedNotaCompraView(null); }}
                  className="flex-1 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm hover:bg-rose-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <Printer className="w-4 h-4" /> Ver / Imprimir
                </button>
                {/* editar nota crédito deshabilitado — para corregir, eliminar y recrear */}
                {hasPermission("notas_credito.eliminar") && (
                  <button
                    onClick={() => { handleDeleteNotaCompra(selectedNotaCompraView.id); setSelectedNotaCompraView(null); }}
                    className="flex-1 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Eliminar
                  </button>
                )}
              </div>

              <button
                onClick={() => setSelectedNotaCompraView(null)}
                className="w-full py-2.5 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all"
              >
                Cerrar
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Imprimir nota de crédito de compra ══════════════════════ */}
      {notaCompraDocModal && (
        <NotaCompraDocModal
          nota={notaCompraDocModal}
          activeEmpresaPropia={activeEmpresaPropia}
          onClose={() => setNotaCompraDocModal(null)}
        />
      )}

      {/* ═══ MODAL: Ver detalle de una Compra ════════════════════════════════ */}
      <AnimatePresence>
        {selectedCompraView && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
            >
              {/* Header fijo */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-500/20 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selectedCompraView.proveedor_nombre || "—"}</p>
                    {selectedCompraView.proveedor_ruc && (
                      <p className="text-slate-500 text-xs">RUC: {selectedCompraView.proveedor_ruc}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {modelosImpCompra.includes("a4") && (
                    <button onClick={() => imprimirCompra(selectedCompraView, activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "", "a4")}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg" title="Imprimir en A4">
                      <Printer className="w-3.5 h-3.5" /> A4
                    </button>
                  )}
                  {modelosImpCompra.includes("ticket") && (
                    <button onClick={() => imprimirCompra(selectedCompraView, activeEmpresaPropia?.razon_social || activeEmpresaPropia?.nombre || "", "ticket")}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg" title="Imprimir en ticket (80 mm)">
                      <Printer className="w-3.5 h-3.5" /> Ticket
                    </button>
                  )}
                  {selectedCompraView.estado_pago && (
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${ESTADO_STYLES[selectedCompraView.estado_pago] || ""}`}>
                      {ESTADO_LABELS[selectedCompraView.estado_pago] || selectedCompraView.estado_pago}
                    </span>
                  )}
                  <button onClick={() => setSelectedCompraView(null)} className="text-slate-400 hover:text-white ml-1">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Cuerpo scrollable */}
              <div className="overflow-y-auto flex-1 p-6 space-y-5">

                {/* Monto + saldo */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 bg-orange-500/10 border border-orange-500/20 rounded-2xl p-4 text-center">
                    <p className="text-slate-400 text-xs mb-1">Monto total</p>
                    <p className="font-heading text-3xl font-bold text-orange-400">{fmt(selectedCompraView.monto_total, selectedCompraView.moneda)}</p>
                    {selectedCompraView.moneda !== "PYG" && selectedCompraView.tipo_cambio && (
                      <p className="text-slate-500 text-xs mt-1">TC: ₲ {Number(selectedCompraView.tipo_cambio).toLocaleString("es-PY")} / {selectedCompraView.moneda}</p>
                    )}
                    {selectedCompraView.monto_iva > 0 && (
                      <p className="text-slate-500 text-xs mt-1">IVA incluido: {fmt(selectedCompraView.monto_iva, selectedCompraView.moneda)}</p>
                    )}
                    {(Number(selectedCompraView.total_descuentos || selectedCompraView.descuento_global_monto || 0) + Number(selectedCompraView.total_descuentos_items || 0)) > 0 && (
                      <div className="mt-2 pt-2 border-t border-orange-500/20 space-y-0.5">
                        <p className="text-slate-400 text-xs">
                          Subtotal bruto: {fmt(selectedCompraView.subtotal_bruto || 0, selectedCompraView.moneda)}
                        </p>
                        {Number(selectedCompraView.total_descuentos_items || 0) > 0 && (
                          <p className="text-rose-300 text-xs">
                            Descuentos por ítem: - {fmt(selectedCompraView.total_descuentos_items, selectedCompraView.moneda)}
                          </p>
                        )}
                        {Number(selectedCompraView.total_descuentos || selectedCompraView.descuento_global_monto || 0) > 0 && (
                          <p className="text-rose-300 text-xs">
                            Descuento global: - {fmt(selectedCompraView.total_descuentos || selectedCompraView.descuento_global_monto || 0, selectedCompraView.moneda)}
                          </p>
                        )}
                      </div>
                    )}
                    {(() => {
                      const totalCreditos = (selectedCompraView.creditos || []).reduce((s, n) => s + (n.monto || 0), 0);
                      if (!totalCreditos) return null;
                      return (
                        <div className="mt-2 pt-2 border-t border-orange-500/20">
                          <p className="text-teal-400 text-xs">
                            Nota de crédito: − {fmt(totalCreditos, selectedCompraView.moneda)}
                          </p>
                          <p className="text-slate-300 text-xs mt-0.5">
                            A pagar: {fmt(Math.max(0, (selectedCompraView.monto_total || 0) - totalCreditos), selectedCompraView.moneda)}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                  {selectedCompraView.saldo_pendiente > 0 && !selectedCompraView.solo_iva && (
                    <div className="flex sm:flex-col gap-3 sm:gap-2 sm:justify-center">
                      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 text-center min-w-[100px]">
                        <p className="text-slate-500 text-xs mb-0.5">Pagado</p>
                        <p className="text-emerald-400 text-sm font-bold">{fmt(selectedCompraView.total_pagado || 0, selectedCompraView.moneda)}</p>
                      </div>
                      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 text-center min-w-[100px]">
                        <p className="text-slate-500 text-xs mb-0.5">Pendiente</p>
                        <p className="text-red-400 text-sm font-bold">{fmt(selectedCompraView.saldo_pendiente || 0, selectedCompraView.moneda)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Datos de la compra */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-arandu-dark/60 rounded-xl p-3">
                    <p className="text-slate-500 text-xs mb-0.5">Fecha</p>
                    <p className="text-white text-sm font-medium">{selectedCompraView.fecha || "—"}</p>
                  </div>
                  <div className="bg-arandu-dark/60 rounded-xl p-3">
                    <p className="text-slate-500 text-xs mb-0.5">Tipo de pago</p>
                    <p className="text-white text-sm font-medium capitalize">{selectedCompraView.tipo_pago || "contado"}</p>
                  </div>
                  {selectedCompraView.numero_factura && (
                    <div className="bg-arandu-dark/60 rounded-xl p-3">
                      <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1"><Receipt className="w-3 h-3" /> Nº Factura</p>
                      <p className="text-white text-sm font-medium">{selectedCompraView.numero_factura}</p>
                    </div>
                  )}
                  {selectedCompraView.nro_timbrado && (
                    <div className="bg-arandu-dark/60 rounded-xl p-3">
                      <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1"><Shield className="w-3 h-3" /> Timbrado</p>
                      <p className="text-white text-sm font-medium">{selectedCompraView.nro_timbrado}</p>
                      {selectedCompraView.fecha_vigencia_timbrado && (
                        <p className="text-slate-500 text-xs mt-0.5">Vigencia: {selectedCompraView.fecha_vigencia_timbrado}</p>
                      )}
                    </div>
                  )}
                  {selectedCompraView.fecha_pago && (
                    <div className="bg-arandu-dark/60 rounded-xl p-3">
                      <p className="text-slate-500 text-xs mb-0.5">Fecha de pago</p>
                      <p className="text-white text-sm font-medium">{selectedCompraView.fecha_pago}</p>
                    </div>
                  )}
                  {(() => {
                    const cuentaNombre = selectedCompraView.cuenta_nombre
                      || (selectedCompraView.pagos || []).find(p => p.cuenta_nombre)?.cuenta_nombre;
                    if (!cuentaNombre) return null;
                    return (
                      <div className="bg-arandu-dark/60 rounded-xl p-3">
                        <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1"><Banknote className="w-3 h-3" /> Pagado desde</p>
                        <p className="text-white text-sm font-medium">{cuentaNombre}</p>
                      </div>
                    );
                  })()}
                  {selectedCompraView.plan_cuenta_nombre && (
                    <div className="bg-arandu-dark/60 rounded-xl p-3">
                      <p className="text-slate-500 text-xs mb-0.5">Plan de cuentas</p>
                      <p className="text-white text-sm font-medium">{selectedCompraView.plan_cuenta_nombre}</p>
                    </div>
                  )}
                </div>

                {/* Ítems / Mercaderías */}
                {(selectedCompraView.items || []).length > 0 && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5" /> Detalle de ítems ({selectedCompraView.items.length})
                    </p>
                    <div className="rounded-xl border border-white/10 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-white/5 border-b border-white/10 text-slate-400 text-xs uppercase">
                            <th className="px-3 py-2 text-left">Descripción / Producto</th>
                            <th className="px-3 py-2 text-right">Cant.</th>
                            <th className="px-3 py-2 text-right">P. Unitario</th>
                            <th className="px-3 py-2 text-right">IVA</th>
                            <th className="px-3 py-2 text-right">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCompraView.items.map((it, idx) => (
                            <tr key={idx} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                              <td className="px-3 py-2.5">
                                <p className="text-white">{it.descripcion}</p>
                                {it.variante_texto && (
                                  <p className="text-violet-400 text-xs">Variante: {it.variante_texto}</p>
                                )}
                                {it.producto_sku && (
                                  <p className="text-slate-500 text-xs font-mono">SKU: {it.producto_sku}</p>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right text-slate-300">{it.cantidad}</td>
                              <td className="px-3 py-2.5 text-right text-slate-300">{fmt(it.precio_unitario, selectedCompraView.moneda)}</td>
                              <td className="px-3 py-2.5 text-right text-slate-500 text-xs">{it.iva != null ? `${it.iva}%` : "—"}</td>
                              <td className="px-3 py-2.5 text-right text-white font-medium">{fmt(it.subtotal || (it.cantidad * it.precio_unitario), selectedCompraView.moneda)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-orange-500/5 border-t border-orange-500/20">
                            <td colSpan={4} className="px-3 py-2 text-right text-slate-400 text-xs font-medium">Total</td>
                            <td className="px-3 py-2 text-right text-orange-400 font-bold">{fmt(selectedCompraView.monto_total, selectedCompraView.moneda)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Pagos registrados */}
                {(selectedCompraView.pagos || []).length > 0 && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Pagos registrados</p>
                    <div className="space-y-1.5">
                      {selectedCompraView.pagos.map((p, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-arandu-dark/60 rounded-lg px-3 py-2 text-sm">
                          <div className="min-w-0">
                            <p className="text-slate-300 text-xs">
                              {p.fecha_pago || p.fecha || "—"} · {p.cuenta_nombre || "Sin cuenta"}
                            </p>
                            <p className="text-slate-500 text-[11px] truncate">
                              {p.recibo_numero ? `Recibo: ${p.recibo_numero}` : "Sin recibo"}
                              {p.registrado_por ? ` · Registrado por ${p.registrado_por}` : ""}
                              {p.pago_proveedor_id ? ` · Pago: ${String(p.pago_proveedor_id).slice(0, 8)}` : ""}
                            </p>
                            {(p.cheque_numero || /^\s*Cheque\s/.test(p.notas || "")) && (
                              <p className="text-violet-300 text-[11px]">Cheque N° {p.cheque_numero || String(p.notas).replace(/^\s*Cheque\s+/, "").split(/[\s—-]/)[0]}{p.cheque_estado ? ` (${p.cheque_estado})` : ""}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 justify-end">
                            <span className="text-emerald-400 font-medium">{fmt(p.monto_pagado, selectedCompraView.moneda)}</span>
                            {p.pago_proveedor_id && (
                              <button
                                type="button"
                                onClick={() => openPagoProveedorDesdeCompra(p.pago_proveedor_id)}
                                className="p-1 text-slate-400 hover:text-blue-300 rounded hover:bg-blue-500/10"
                                title="Abrir pago proveedor"
                              >
                                {loadingPagoProveedorDesdeCompra ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cuotas panel */}
                {selectedCompraView.es_credito_cuotas && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5" /> Cuotas ({cuotasCompra.filter(c => c.estado !== "cancelado").length})
                      </p>
                      {selectedCuotaCompraIds.size > 0 && (
                        <button
                          onClick={() => toast.info("Las cuotas de compras se pagan desde Financiero → Pagos proveedores.")}
                          className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
                        >
                          <CheckCircle className="w-3 h-3" /> Pagar ({selectedCuotaCompraIds.size})
                        </button>
                      )}
                    </div>
                    {loadingCuotasCompra ? (
                      <p className="text-slate-500 text-xs">Cargando cuotas...</p>
                    ) : (
                      <div className="space-y-1.5">
                        {cuotasCompra.filter(c => c.estado !== "cancelado").map(c => {
                          const isVencida = c.estado === "vencido";
                          const isPagada = c.estado === "pagado";
                          return (
                            <div key={c.id} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm border ${isVencida ? "border-red-500/30 bg-red-500/5" : isPagada ? "border-emerald-500/20 bg-emerald-500/5" : "border-white/10 bg-arandu-dark/60"}`}>
                              {!isPagada && (
                                <input type="checkbox"
                                  checked={selectedCuotaCompraIds.has(c.id)}
                                  onChange={() => setSelectedCuotaCompraIds(prev => {
                                    const n = new Set(prev);
                                    n.has(c.id) ? n.delete(c.id) : n.add(c.id);
                                    return n;
                                  })}
                                  className="w-4 h-4 flex-shrink-0" />
                              )}
                              {isPagada && <div className="w-4 h-4 flex-shrink-0" />}
                              <span className={`font-medium ${isVencida ? "text-red-300" : isPagada ? "text-emerald-300" : "text-white"}`}>
                                Cuota {c.numero}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${isPagada ? "bg-emerald-500/20 text-emerald-300" : isVencida ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>
                                {isPagada ? "Pagada" : isVencida ? "Vencida" : "Pendiente"}
                              </span>
                              <span className={`text-xs ${isVencida ? "text-red-400" : "text-slate-400"}`}>Vence: {c.fecha_vencimiento || "—"}</span>
                              <span className={`ml-auto font-semibold text-sm ${isVencida ? "text-red-300" : isPagada ? "text-emerald-300" : "text-white"}`}>
                                {fmt(c.monto, selectedCompraView.moneda)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Notas */}
                {selectedCompraView.notas && (
                  <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Notas</p>
                    <p className="text-slate-300 text-sm">{selectedCompraView.notas}</p>
                  </div>
                )}

                {/* Presupuestos vinculados (multi) */}
                {hasModule?.("presupuestos") && !selectedCompraView.eliminada && (
                  <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-slate-500 text-xs flex items-center gap-1">
                        📄 Presupuestos vinculados
                        {(selectedCompraView.presupuesto_ids?.length || 0) > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px]">
                            {selectedCompraView.presupuesto_ids.length}
                          </span>
                        )}
                      </p>
                      {hasPermission("compras.vincular_presupuesto") && (
                        <button
                          onClick={() => {
                            fetchPresupuestosForm(selectedCompraView.logo_tipo);
                            fetchPresupuestosDisp({ logo: selectedCompraView.logo_tipo, proveedor_id: selectedCompraView.proveedor_id, proveedor_nombre: selectedCompraView.proveedor_nombre, incluir: selectedCompraView.presupuesto_ids || [] });
                            setVincularPresSearch("");
                            setShowVincularPresModal(true);
                          }}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/20 hover:bg-blue-500/25 transition-colors"
                        >
                          {(selectedCompraView.presupuesto_ids?.length || 0) > 0 ? "Gestionar" : "+ Vincular"}
                        </button>
                      )}
                    </div>
                    {(selectedCompraView.presupuesto_ids?.length || 0) > 0 ? (
                      <div className="space-y-1">
                        {selectedCompraView.presupuesto_ids.map(pid => {
                          const pres = presupuestosForm.find(p => p.id === pid);
                          return pres ? (
                            <div key={pid} className="flex items-center justify-between gap-2">
                              <button
                                onClick={() => setPresDetailEgresos(pres)}
                                className="text-sm text-blue-300 hover:text-blue-200 underline-offset-2 hover:underline text-left truncate"
                              >
                                {pres.numero ? `#${pres.numero}` : "—"}{pres.nombre_archivo ? ` · ${pres.nombre_archivo}` : ""}
                              </button>
                              {hasPermission("compras.vincular_presupuesto") && (
                                <button
                                  onClick={() => handleVincularPresupuestoCompra(selectedCompraView.presupuesto_ids.filter(x => x !== pid))}
                                  disabled={vincularPresLoading}
                                  className="text-[10px] text-red-400 hover:text-red-300 disabled:opacity-50 flex-shrink-0"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ) : (
                            <p key={pid} className="text-slate-500 text-xs"># {pid.slice(0, 8)}</p>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-xs italic">Sin presupuestos vinculados</p>
                    )}
                  </div>
                )}
              </div>

              {/* Footer fijo con acciones */}
              <div className="px-6 py-4 border-t border-white/10 flex-shrink-0 flex gap-2">
                {!selectedCompraView.eliminada && selectedCompraView.estado_pago !== "pagado" && !selectedCompraView.solo_iva && (
                  <div className="flex-1 flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    <Banknote className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="text-amber-300 text-xs">El pago se registra desde <strong>Pagos a Proveedores</strong></span>
                  </div>
                )}
                {!selectedCompraView.eliminada && selectedCompraView.estado_pago !== "pagado" && selectedCompraView.estado_pago !== "parcial" && hasPermission("compras.editar_completa") && (
                  <button
                    onClick={() => {
                      const compra = selectedCompraView;
                      setSelectedCompraView(null);
                      openEditCompra(compra);
                    }}
                    className="py-2.5 px-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-sm hover:bg-cyan-500/20 transition-all flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" /> Editar compra
                  </button>
                )}
                {!selectedCompraView.eliminada && !selectedCompraView.tiene_factura && !selectedCompraView.numero_factura && hasPermission("compras.editar") && (
                  <button
                    onClick={() => {
                      const compra = selectedCompraView;
                      setSelectedCompraView(null);
                      openFacturaCompra(compra);
                    }}
                    className="py-2.5 px-4 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-300 text-sm hover:bg-orange-500/20 transition-all flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" /> Cargar factura
                  </button>
                )}
                {!selectedCompraView.eliminada && hasPermission("compras.eliminar") && (
                  <button
                    onClick={async () => {
                      const eliminado = await handleDeleteCompra(selectedCompraView.id);
                      if (eliminado) setSelectedCompraView(null);
                    }}
                    className="py-2.5 px-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-all flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Eliminar
                  </button>
                )}
                <button
                  onClick={() => setSelectedCompraView(null)}
                  className="py-2.5 px-4 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal cobrar cuotas compra ──────────────────────────── */}
      {cobrarCuotasCompraModal && (
        <div className="fixed inset-0 bg-black/70 z-[80] flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-white">Registrar pago de {selectedCuotaCompraIds.size} cuota(s)</h3>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Fecha de pago</label>
              <DateInput value={cobrarCuotasCompraFecha} onChange={e => setCobrarCuotasCompraFecha(e.target.value)}
                className="bg-arandu-dark border-white/10 text-white" />
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Cuenta bancaria (opcional)</label>
              <select value={cobrarCuotasCompraCuentaId} onChange={e => setCobrarCuotasCompraCuentaId(e.target.value)}
                className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">Sin cuenta específica</option>
                {renderCuentasPorMoneda(cuentasDisp, cobrarCuotasCompraModal?.logo_tipo || activeEmpresaPropia?.slug)}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setCobrarCuotasCompraModal(false)}
                className="flex-1 py-2 rounded-lg border border-white/10 text-slate-400 hover:text-white text-sm">Cancelar</button>
              <button onClick={cobrarCuotasCompra} disabled={cobrandoCuotasCompra}
                className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60">
                {cobrandoCuotasCompra ? "Guardando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: Ver detalle de un Gasto (Costo Fijo) ═══════════════════ */}
      <AnimatePresence>
        {selectedCostoView && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-full px-3 py-1 font-medium">
                  Gasto
                </span>
                <button onClick={() => { setCanalEdit(null); setSelectedCostoView(null); setEditFacturaPagoMode(false); setEditarMontoCostoMode(false); setEditarMontoCostoVal(""); setEditarMontoCostoMotivo(""); setShowHistorialAjustes(false); setAjusteAlcance("periodo"); }} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Nombre + estado */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-400 text-xs">Concepto</p>
                  <p className="text-white font-semibold truncate">{selectedCostoView.nombre || "—"}</p>
                </div>
                {selectedCostoView.estado && (
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${
                    { pagado:"bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
                      pendiente:"bg-amber-500/15 text-amber-400 border-amber-500/30",
                      vencido:"bg-red-500/15 text-red-400 border-red-500/30"
                    }[selectedCostoView.estado] || ""
                  }`}>
                    {selectedCostoView.estado}
                  </span>
                )}
              </div>

              {selectedCostoView.descripcion && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-0.5">Descripción</p>
                  <p className="text-slate-300 text-sm">{selectedCostoView.descripcion}</p>
                </div>
              )}

              {/* Monto destacado */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-5 mb-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-slate-400 text-xs">Monto del período</p>
                  <div className="flex items-center gap-2">
                    {selectedCostoView.ajuste_periodo && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">Ajustado</span>
                    )}
                    {selectedCostoView.estado !== "pagado" && selectedCostoView.estado !== "eliminado" && hasPermission("costos_fijos.editar") && !editarMontoCostoMode && (
                      <button
                        onClick={() => { setEditarMontoCostoVal(String(selectedCostoView.monto)); setEditarMontoCostoMode(true); }}
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
                      >
                        <Edit className="w-3 h-3" /> Ajustar
                      </button>
                    )}
                  </div>
                </div>
                {!editarMontoCostoMode ? (
                  <>
                    <p className="font-heading text-3xl font-bold text-blue-300 text-center">
                      {fmt(selectedCostoView.monto, selectedCostoView.moneda)}
                    </p>
                    {selectedCostoView.ajuste_periodo && (
                      <p className="text-slate-500 text-xs text-center mt-1">
                        Original: {fmt(selectedCostoView.ajuste_periodo.monto_original, selectedCostoView.ajuste_periodo.moneda_original)}
                      </p>
                    )}
                    {selectedCostoView.moneda !== "PYG" && selectedCostoView.tipo_cambio && (
                      <p className="text-slate-500 text-xs text-center mt-1">
                        TC: ₲ {Number(selectedCostoView.tipo_cambio).toLocaleString("es-PY")} / {selectedCostoView.moneda}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="mt-2 space-y-2">
                    <MoneyInput
                      min="1"
                      step="any"
                      moneda={selectedCostoView?.moneda || "PYG"}
                      value={editarMontoCostoVal}
                      onChange={value => setEditarMontoCostoVal(value)}
                      placeholder="Nuevo monto..."
                      className="w-full bg-arandu-dark border border-blue-500/40 rounded-lg px-3 py-2 text-white text-sm text-center font-bold focus:outline-none focus:border-blue-500"
                      autoFocus
                    />
                    <div className="space-y-1 text-xs text-slate-300">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="ajusteAlcance" checked={ajusteAlcance === "periodo"} onChange={() => setAjusteAlcance("periodo")} className="mt-0.5" />
                        <span>Solo este mes ({selectedCostoView?.periodo || filtroMes}) — los demás meses siguen con el monto original</span>
                      </label>
                      {hasPermission("costos_fijos.gestionar_recurrencia") && (
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" name="ajusteAlcance" checked={ajusteAlcance === "desde_ahora"} onChange={() => setAjusteAlcance("desde_ahora")} className="mt-0.5" />
                          <span>Desde ahora en adelante (desde {selectedCostoView?.periodo || filtroMes}) — los meses anteriores no cambian</span>
                        </label>
                      )}
                    </div>
                    <input
                      type="text"
                      value={editarMontoCostoMotivo}
                      onChange={e => setEditarMontoCostoMotivo(e.target.value)}
                      placeholder="Motivo del ajuste (opcional)..."
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-blue-500/50"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAjustarMontoCosto}
                        className="flex-1 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-all"
                      >
                        Guardar
                      </button>
                      <button
                        onClick={() => { setEditarMontoCostoMode(false); setEditarMontoCostoVal(""); setEditarMontoCostoMotivo(""); setAjusteAlcance("periodo"); }}
                        className="flex-1 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs transition-all"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Recurrencia: omitir este mes / dejar de generar */}
              {hasPermission("costos_fijos.gestionar_recurrencia") && selectedCostoView.costo_id && selectedCostoView.periodo
                && !(selectedCostoView.pagos?.length > 0 || selectedCostoView.pago) && selectedCostoView.estado !== "eliminado" && (
                <div className="flex gap-2 mb-4">
                  <button onClick={() => handleOmitirPeriodo(selectedCostoView)}
                    className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs transition-all">
                    Omitir este mes
                  </button>
                  <button onClick={() => handleFinalizarGasto(selectedCostoView)}
                    className="flex-1 py-2 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30 text-xs transition-all">
                    Ya no tengo este gasto
                  </button>
                </div>
              )}

              {/* Historial de ajustes */}
              {(selectedCostoView.historial_ajustes?.length > 0) && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowHistorialAjustes(v => !v)}
                    className="w-full flex items-center justify-between text-xs text-slate-400 hover:text-slate-300 py-1 transition-colors"
                  >
                    <span className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Historial de ajustes de monto ({selectedCostoView.historial_ajustes.length})
                    </span>
                    <span>{showHistorialAjustes ? "▲" : "▼"}</span>
                  </button>
                  {showHistorialAjustes && (
                    <div className="mt-2 bg-arandu-dark/60 border border-white/5 rounded-xl overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-white/10 text-slate-500 uppercase">
                            <th className="px-3 py-2 text-left">Período</th>
                            <th className="px-3 py-2 text-right">Original</th>
                            <th className="px-3 py-2 text-right">Ajustado</th>
                            <th className="px-3 py-2 text-left">Por</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCostoView.historial_ajustes.map((aj, i) => (
                            <tr key={i} className="border-b border-white/5">
                              <td className="px-3 py-2 text-slate-300 font-medium">{aj.periodo}</td>
                              <td className="px-3 py-2 text-right text-slate-500">{fmt(aj.monto_original, aj.moneda_original)}</td>
                              <td className="px-3 py-2 text-right text-amber-300 font-medium">{fmt(aj.monto, aj.moneda)}</td>
                              <td className="px-3 py-2 text-slate-500 truncate max-w-[80px]" title={aj.modificado_por}>{aj.modificado_por || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Grid de info */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Empresa</p>
                  <p className="text-white text-sm font-medium">{LOGO_LABEL_MAP[selectedCostoView.logo_tipo] || selectedCostoView.logo_tipo || "—"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Categoría</p>
                  <p className="text-white text-sm font-medium">{selectedCostoView.categoria || "—"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Frecuencia</p>
                  <p className="text-white text-sm font-medium capitalize">{selectedCostoView.frecuencia || "mensual"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3">
                  <p className="text-slate-500 text-xs mb-0.5">Día vencimiento</p>
                  <p className="text-white text-sm font-medium">{selectedCostoView.dia_vencimiento || "—"}</p>
                </div>
                <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-slate-500 text-xs">Canal de pago</p>
                    {!canalEdit && hasPermission("costos_fijos.editar") && (
                      <button onClick={() => setCanalEdit(canalPagoDe(selectedCostoView))} className="text-blue-400 hover:text-blue-300 text-xs">Editar</button>
                    )}
                  </div>
                  {canalEdit ? (
                    <div className="space-y-3">
                      <CanalPagoGastoFields value={canalEdit} onChange={(patch)=>setCanalEdit(c=>({...c,...patch}))} />
                      <div className="flex gap-2">
                        <button onClick={() => setCanalEdit(null)} className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs">Cancelar</button>
                        <button onClick={async () => {
                          const res = await fetch(`${API}/admin/costos-fijos/${selectedCostoView.costo_id || selectedCostoView.id}/canal-pago`, {
                            method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                            body: JSON.stringify(canalEdit),
                          });
                          if (res.ok) {
                            const d = await res.json();
                            setSelectedCostoView(v => ({ ...v, ...canalPagoDe(d) }));
                            setCanalEdit(null);
                            toast.success("Canal de pago actualizado");
                            fetchVencimientos(filtroMes, filtroTipo, filtroAnio);
                          } else {
                            const err = await res.json().catch(() => ({}));
                            toast.error(err.detail || "No se pudo guardar");
                          }
                        }} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs">Guardar</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-white text-sm font-medium">{selectedCostoView.canal_pago || "Sin especificar"}</p>
                      {(selectedCostoView.banco_pago || selectedCostoView.nro_cuenta_pago || selectedCostoView.titular_cuenta_pago || selectedCostoView.alias_pago) && (
                        <p className="text-slate-400 text-xs mt-1">
                          {[selectedCostoView.banco_pago, selectedCostoView.tipo_cuenta_pago, selectedCostoView.nro_cuenta_pago && `N° ${selectedCostoView.nro_cuenta_pago}`, selectedCostoView.alias_pago && `Alias ${selectedCostoView.alias_pago}`, selectedCostoView.titular_cuenta_pago && `Titular: ${selectedCostoView.titular_cuenta_pago}${selectedCostoView.doc_titular_pago ? ` (${selectedCostoView.doc_titular_pago})` : ""}`].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {selectedCostoView.pago?.fecha_pago && (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 col-span-2">
                    <p className="text-slate-500 text-xs mb-0.5">Pagado el</p>
                    <p className="text-emerald-400 text-sm font-medium">{selectedCostoView.pago.fecha_pago}</p>
                    {selectedCostoView.pago.monto_pagado && selectedCostoView.pago.monto_pagado !== selectedCostoView.monto && (
                      <p className="text-slate-400 text-xs mt-0.5">{fmt(selectedCostoView.pago.monto_pagado, selectedCostoView.moneda)} (parcial)</p>
                    )}
                  </div>
                )}
                {selectedCostoView.pago?.cuenta_nombre && (
                  <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                    <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1">
                      <Banknote className="w-3 h-3" /> Pagado desde
                    </p>
                    <p className="text-white text-sm font-medium">{selectedCostoView.pago.cuenta_nombre}</p>
                  </div>
                )}
                {(() => {
                  const ps = (selectedCostoView.pagos?.length > 0 ? selectedCostoView.pagos : (selectedCostoView.pago ? [selectedCostoView.pago] : []))
                    .filter(x => x.cheque_numero);
                  if (!ps.length) return null;
                  return (
                    <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-3 col-span-2">
                      <p className="text-slate-500 text-xs mb-0.5">Cheque utilizado</p>
                      {ps.map((x, i) => (
                        <p key={i} className="text-violet-300 text-sm font-medium">
                          N° {x.cheque_numero}{x.cheque_estado ? ` · ${x.cheque_estado}` : ""}
                        </p>
                      ))}
                      <p className="text-slate-500 text-[11px] mt-1">Para anular este pago primero liberá el cheque en Financiero → Cheques.</p>
                    </div>
                  );
                })()}
                {/* ── Factura del pago ── */}
                {selectedCostoView.pago?.fecha_pago && (
                  <div className={`rounded-xl p-3 col-span-2 border ${selectedCostoView.pago.tiene_factura ? "bg-blue-500/5 border-blue-500/20" : "bg-arandu-dark/60 border-white/5"}`}>
                    {!editFacturaPagoMode ? (
                      <>
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="text-slate-500 text-xs flex items-center gap-1">
                            <FileText className="w-3 h-3" /> Factura
                          </p>
                          {hasPermission("costos_fijos.editar") && (
                            <button
                              onClick={() => {
                                setEditFacturaPagoForm({
                                  tiene_factura: selectedCostoView.pago.tiene_factura || false,
                                  nro_factura: selectedCostoView.pago.nro_factura || "",
                                  items: (selectedCostoView.pago.items && selectedCostoView.pago.items.length > 0)
                                    ? selectedCostoView.pago.items
                                    : [],
                                });
                                setEditFacturaPagoMode(true);
                              }}
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              <Edit className="w-3 h-3" /> Editar
                            </button>
                          )}
                        </div>
                        {selectedCostoView.pago.tiene_factura ? (
                          <p className="text-blue-300 text-sm font-medium">Nro. {selectedCostoView.pago.nro_factura || "—"}</p>
                        ) : (
                          <p className="text-slate-500 text-sm">Sin factura</p>
                        )}
                      </>
                    ) : (
                      <div>
                        <p className="text-slate-500 text-xs mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> Editar factura</p>
                        <div
                          onClick={() => setEditFacturaPagoForm(f => ({ ...f, tiene_factura: !f.tiene_factura, nro_factura: "" }))}
                          className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center cursor-pointer mb-2 ${editFacturaPagoForm.tiene_factura ? "bg-emerald-500" : "bg-slate-600"}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${editFacturaPagoForm.tiene_factura ? "translate-x-4" : "translate-x-0"}`} />
                        </div>
                        {editFacturaPagoForm.tiene_factura && (
                          <>
                            <input
                              type="text"
                              value={editFacturaPagoForm.nro_factura}
                              onChange={e => setEditFacturaPagoForm(f => ({ ...f, nro_factura: e.target.value }))}
                              placeholder="Número de factura"
                              className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-2 focus:outline-none focus:border-blue-500/50"
                            />
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-slate-500 text-xs">Desglose de IVA *</p>
                              <button type="button"
                                onClick={() => setEditFacturaPagoForm(f => ({ ...f, items: [...(f.items || []), { descripcion: "", monto: "", iva: 10 }] }))}
                                className="text-emerald-400 text-xs hover:text-emerald-300 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> Agregar ítem
                              </button>
                            </div>
                            <div className="space-y-1.5 mb-2">
                              {(editFacturaPagoForm.items || []).map((it, idx) => (
                                <div key={idx} className="flex items-center gap-1.5">
                                  <input type="text" value={it.descripcion || ""}
                                    onChange={e => setEditFacturaPagoForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, descripcion: e.target.value } : x) }))}
                                    placeholder="Descripción"
                                    className="flex-1 min-w-0 bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-blue-500/40" />
                                  <MoneyInput min="0" step="any" moneda={selectedCostoView?.moneda || "PYG"} value={it.monto}
                                    onChange={value => setEditFacturaPagoForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, monto: value } : x) }))}
                                    className="w-24 flex-shrink-0 bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-blue-500/40"
                                    placeholder="Monto" />
                                  <select value={it.iva}
                                    onChange={e => setEditFacturaPagoForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, iva: Number(e.target.value) } : x) }))}
                                    className="w-16 flex-shrink-0 bg-arandu-dark border border-white/10 rounded-lg px-1 py-1.5 text-white text-xs focus:outline-none focus:border-blue-500/40">
                                    <option value={10}>10%</option>
                                    <option value={5}>5%</option>
                                    <option value={0}>Exenta</option>
                                  </select>
                                  <button type="button" onClick={() => setEditFacturaPagoForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                                    className="p-1 text-slate-500 hover:text-red-400 flex-shrink-0">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                              {(editFacturaPagoForm.items || []).length === 0 && (
                                <p className="text-amber-400 text-[11px]">Agregá al menos un ítem con monto y tasa de IVA.</p>
                              )}
                            </div>
                          </>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const itemsValidos = (editFacturaPagoForm.items || []).filter(it => (parseFloat(it.monto) || 0) > 0);
                              if (editFacturaPagoForm.tiene_factura && itemsValidos.length === 0) {
                                toast.error("Agregá al menos un ítem con monto y tasa de IVA");
                                return;
                              }
                              handleEditarFacturaPago(selectedCostoView.pago.id, {
                                tiene_factura: editFacturaPagoForm.tiene_factura,
                                nro_factura: editFacturaPagoForm.nro_factura,
                                items: editFacturaPagoForm.tiene_factura
                                  ? itemsValidos.map(it => ({ descripcion: it.descripcion || null, monto: parseFloat(it.monto) || 0, iva: Number(it.iva) }))
                                  : null,
                              });
                            }}
                            className="flex-1 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-all"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => setEditFacturaPagoMode(false)}
                            className="flex-1 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs transition-all"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Notas */}
              {selectedCostoView.notas && (
                <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                  <p className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Notas
                  </p>
                  <p className="text-slate-300 text-sm">{selectedCostoView.notas}</p>
                </div>
              )}

              {/* Acciones */}
              {selectedCostoView.estado !== "eliminado" && selectedCostoView.estado !== "pagado" && hasPermission("costos_fijos.editar") && (
                <button
                  onClick={() => {
                    setPagoCostoForm({ monto_pagado: selectedCostoView.monto, fecha_pago: hoy(), notas: "", tiene_factura: false, nro_factura: "", nro_timbrado: "", fecha_vigencia_timbrado: "", iva_tipo: "", items: [], cuenta_id: "", cuenta_nombre: "", tipo_cambio: "", cuenta_moneda: "" });
                    setPagoMedioCosto("transferencia");
                    setShowPagoCostoModal(selectedCostoView);
                    setSelectedCostoView(null);
                    setEditFacturaPagoMode(false);
                  }}
                  className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-all flex items-center justify-center gap-2 mb-2"
                >
                  <Check className="w-4 h-4" /> Registrar pago
                </button>
              )}
              {selectedCostoView.estado !== "eliminado" && selectedCostoView.estado === "pagado" && hasPermission("costos_fijos.editar") && (
                <button
                  onClick={() => { handleAnularPagoCosto(selectedCostoView); setSelectedCostoView(null); }}
                  className="w-full py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-all flex items-center justify-center gap-2 mb-2"
                >
                  <X className="w-4 h-4" /> Anular pago
                </button>
              )}

              <div className="flex gap-2 mb-2">
{/* editar costo fijo deshabilitado */}
                {selectedCostoView.estado !== "eliminado" && hasPermission("costos_fijos.eliminar") && (
                  <button
                    onClick={() => {
                      const src = costos.find(c => c.id === selectedCostoView.costo_id) || selectedCostoView;
                      if (src) handleDeleteCosto({ ...src, id: src.id || src.costo_id });
                      setSelectedCostoView(null);
                    }}
                    className="flex-1 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Eliminar
                  </button>
                )}
              </div>

              <button
                onClick={() => { setSelectedCostoView(null); setEditarMontoCostoMode(false); setEditarMontoCostoVal(""); setEditarMontoCostoMotivo(""); setShowHistorialAjustes(false); }}
                className="w-full py-2.5 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all"
              >
                Cerrar
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Detalle de un Sueldo Pagado ═════════════════════════════ */}
      <AnimatePresence>
        {sueldoView && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
            >
              {(() => {
                const { emp, sueldo, adelantos, extras } = sueldoView;
                const moneda = sueldo?.moneda || emp.moneda || "PYG";
                // Usar snapshot guardado en el registro; si es un registro viejo sin snapshot,
                // reconstruir desde los campos del pago para no mostrar el sueldo_base actual.
                const base = sueldo?.sueldo_base != null
                  ? parseFloat(sueldo.sueldo_base)
                  : (parseFloat(sueldo?.monto_pagado) || 0)
                    + (parseFloat(sueldo?.descuento_ips) || 0)
                    + (parseFloat(sueldo?.descuentos_adicionales) || 0)
                    + (parseFloat(sueldo?.total_adelantos) || 0)
                    - (parseFloat(sueldo?.total_extras) || 0);
                const ips = parseFloat(sueldo?.descuento_ips) || 0;
                const totAd = (adelantos || []).reduce((s, a) => s + (a.monto || 0), 0);
                const totEx = (extras || []).reduce((s, e) => s + (e.monto || 0), 0);
                const desAd = parseFloat(sueldo?.descuentos_adicionales) || 0;
                const cuentaPago = cuentasDisp.find(c => String(c.id) === String(sueldo?.cuenta_id));
                return (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs bg-violet-500/20 text-violet-300 border border-violet-500/30 rounded-full px-3 py-1 font-medium">
                        Sueldo pagado
                      </span>
                      <div className="flex items-center gap-1">
                        <AuditoriaRegistroBtn registro={sueldo} />
                        <button onClick={() => setSueldoView(null)} className="text-slate-400 hover:text-white">
                          <X className="w-5 h-5"/>
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-11 h-11 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-violet-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400 text-xs">Empleado</p>
                        <p className="text-white font-semibold truncate">{emp.nombre} {emp.apellido}</p>
                        {emp.cargo && <p className="text-slate-500 text-xs">{emp.cargo}</p>}
                      </div>
                      <span className="text-xs px-2.5 py-1 rounded-full border bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
                        Pagado
                      </span>
                    </div>

                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5 text-center mb-4">
                      <p className="text-slate-400 text-xs mb-1">Monto pagado</p>
                      <p className="font-heading text-3xl font-bold text-emerald-400">
                        {fmtSueldo(sueldo.monto_pagado, moneda)}
                      </p>
                      {sueldo.tipo_cambio && moneda === "USD" && (
                        <p className="text-slate-500 text-xs mt-1">
                          TC: ₲ {Number(sueldo.tipo_cambio).toLocaleString("es-PY")} / USD
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-arandu-dark/60 rounded-xl p-3">
                        <p className="text-slate-500 text-xs mb-0.5">Período</p>
                        <p className="text-white text-sm font-medium">{mesLabel(sueldo.periodo || filtroMes)}</p>
                      </div>
                      <div className="bg-arandu-dark/60 rounded-xl p-3">
                        <p className="text-slate-500 text-xs mb-0.5">Fecha de pago</p>
                        <p className="text-emerald-400 text-sm font-medium">{sueldo.fecha_pago || "—"}</p>
                      </div>
                      {(sueldo.cuenta_nombre || cuentaPago) && (
                        <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                          <p className="text-slate-500 text-xs mb-0.5 flex items-center gap-1">
                            <Banknote className="w-3 h-3" /> Cuenta de pago
                          </p>
                          <p className="text-white text-sm font-medium">{sueldo.cuenta_nombre || cuentaPago?.nombre}</p>
                          {(cuentaPago?.moneda || cuentaPago?.es_predeterminada) && (
                            <p className="text-slate-500 text-xs mt-0.5">
                              {cuentaPago?.moneda || ""}
                              {cuentaPago?.es_predeterminada ? `${cuentaPago?.moneda ? " · " : ""}Predeterminada` : ""}
                            </p>
                          )}
                          {sueldo.tipo_cambio && cuentaPago?.moneda && cuentaPago.moneda !== moneda && (
                            <p className="text-amber-400 text-xs mt-1">
                              TC: {Number(sueldo.tipo_cambio).toLocaleString("es-PY")}
                            </p>
                          )}
                        </div>
                      )}
                      {sueldo.tipo_factura_nombre && (
                        <div className="bg-arandu-dark/60 rounded-xl p-3 col-span-2">
                          <p className="text-slate-500 text-xs mb-0.5">Obra / Tipo de Factura</p>
                          <p className="text-violet-300 text-sm font-medium">{sueldo.tipo_factura_nombre}</p>
                        </div>
                      )}
                    </div>

                    {/* Desglose */}
                    <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4 text-sm">
                      <p className="text-slate-500 text-xs font-medium mb-2">Desglose</p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-slate-300"><span>Sueldo base</span><span>{fmtSueldo(base, moneda)}</span></div>
                        {totEx > 0 && <div className="flex justify-between text-green-700 dark:text-green-300"><span>+ Extras ({extras.length})</span><span>+ {fmtSueldo(totEx, moneda)}</span></div>}
                        {ips > 0 && <div className="flex justify-between text-blue-300"><span>− IPS</span><span>− {fmtSueldo(ips, "PYG")}</span></div>}
                        {totAd > 0 && <div className="flex justify-between text-amber-700 dark:text-amber-300"><span>− Adelantos ({adelantos.length})</span><span>− {fmtSueldo(totAd, moneda)}</span></div>}
                        {desAd > 0 && <div className="flex justify-between text-red-300"><span>− Descuentos adicionales</span><span>− {fmtSueldo(desAd, "PYG")}</span></div>}
                        <div className="border-t border-white/10 pt-1.5 mt-1.5 flex justify-between font-semibold">
                          <span className="text-white">Total pagado</span>
                          <span className="text-emerald-300">{fmtSueldo(sueldo.monto_pagado, moneda)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Adelantos del período */}
                    {adelantos.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-500/30 rounded-xl p-3 mb-4">
                        <p className="text-amber-700 dark:text-amber-300 text-xs font-medium mb-2">Adelantos del período ({adelantos.length})</p>
                        <div className="space-y-1.5">
                          {adelantos.map(a => (
                            <div key={a.id} className="flex items-center justify-between text-xs">
                              <div className="flex-1 min-w-0">
                                <p className="text-amber-900 dark:text-amber-200">{a.fecha}</p>
                                {a.notas && <p className="text-slate-500 text-[11px] truncate">{a.notas}</p>}
                              </div>
                              <span className="text-amber-700 dark:text-amber-300 font-medium">{fmtSueldo(a.monto, a.moneda || "PYG")}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Extras del período */}
                    {extras.length > 0 && (
                      <div className="bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-500/30 rounded-xl p-3 mb-4">
                        <p className="text-green-700 dark:text-green-300 text-xs font-medium mb-2">Extras incluidos en este sueldo ({extras.length})</p>
                        <div className="space-y-1.5">
                          {extras.map(e => (
                            <div key={e.id} className="flex items-center justify-between text-xs">
                              <div className="flex-1 min-w-0">
                                <p className="text-green-900 dark:text-green-200">{e.fecha} — {e.descripcion || "Extra"}</p>
                                {e.notas && <p className="text-slate-500 text-[11px] truncate">{e.notas}</p>}
                              </div>
                              <span className="text-green-700 dark:text-green-300 font-medium">{fmtSueldo(e.monto, e.moneda || "PYG")}</span>
                            </div>
                          ))}
                          <div className="border-t border-green-200 dark:border-green-500/20 pt-1.5 flex justify-between text-green-800 dark:text-green-200 font-medium">
                            <span>Total extras</span>
                            <span>{fmtSueldo(totEx, moneda)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notas */}
                    {sueldo.notas && (
                      <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4">
                        <p className="text-slate-500 text-xs mb-1 flex items-center gap-1"><FileText className="w-3 h-3"/> Notas</p>
                        <p className="text-slate-300 text-sm">{sueldo.notas}</p>
                      </div>
                    )}

                    {loadingSueldoView && (
                      <p className="text-center text-slate-500 text-xs mb-3">Cargando detalles...</p>
                    )}

                    {/* Acciones */}
                    <button
                      onClick={() => handlePrintSueldo(emp)}
                      className="w-full mb-2 py-2.5 rounded-lg bg-violet-500/10 border border-violet-500/30 text-violet-300 text-sm hover:bg-violet-500/20 transition-all flex items-center justify-center gap-2"
                    >
                      <Printer className="w-4 h-4"/> Imprimir recibo
                    </button>
                    {puedeDeshacerSueldo && (
                      <button
                        onClick={() => { handleDeleteSueldo(sueldo.id); setSueldoView(null); }}
                        className="w-full mb-2 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                      >
                        <X className="w-4 h-4"/> Deshacer pago
                      </button>
                    )}
                    <button
                      onClick={() => setSueldoView(null)}
                      className="w-full py-2.5 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all">
                      Cerrar
                    </button>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Subir Sueldo ════════════════════════════════════════════ */}
      <AnimatePresence>
        {aumentoEmp && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1 font-medium inline-flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5"/> Subir sueldo
                  </span>
                  <p className="text-white font-semibold mt-2">{aumentoEmp.nombre} {aumentoEmp.apellido}</p>
                </div>
                <button onClick={() => setAumentoEmp(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5"/>
                </button>
              </div>

              <div className="bg-arandu-dark/60 border border-white/5 rounded-xl p-3 mb-4 text-sm">
                <p className="text-slate-400">Sueldo actual</p>
                <p className="text-white font-bold text-xl">{fmt(aumentoEmp.sueldo_base, aumentoEmp.moneda_sueldo || aumentoEmp.moneda || "PYG")}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Sueldo nuevo *</label>
                  <MoneyInput min="0" step="any" autoFocus moneda={aumentoEmp?.moneda_sueldo || aumentoEmp?.moneda || "PYG"}
                    value={aumentoForm.sueldo_nuevo}
                    onChange={value => setAumentoForm(f => ({ ...f, sueldo_nuevo: value }))}
                    className="w-full bg-arandu-dark border border-emerald-500/30 rounded-lg px-3 py-2.5 text-white text-lg font-bold focus:outline-none focus:border-emerald-500"
                  />
                  {aumentoForm.sueldo_nuevo && parseFloat(aumentoForm.sueldo_nuevo) > Number(aumentoEmp.sueldo_base) && (
                    <p className="text-emerald-400 text-xs mt-1">
                      +{fmt(parseFloat(aumentoForm.sueldo_nuevo) - Number(aumentoEmp.sueldo_base), aumentoEmp.moneda_sueldo || aumentoEmp.moneda || "PYG")}
                      ({((parseFloat(aumentoForm.sueldo_nuevo) - Number(aumentoEmp.sueldo_base)) / Number(aumentoEmp.sueldo_base) * 100).toFixed(1)}%)
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Fecha del cambio</label>
                  <DateInput value={aumentoForm.fecha}
                    onChange={e => setAumentoForm(f => ({ ...f, fecha: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Motivo</label>
                  <input type="text" value={aumentoForm.motivo}
                    onChange={e => setAumentoForm(f => ({ ...f, motivo: e.target.value }))}
                    placeholder="Ej: aumento anual, ajuste por inflación, ascenso..."
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button onClick={() => setAumentoEmp(null)}
                  className="flex-1 py-2.5 rounded-lg border border-white/10 text-slate-400 text-sm hover:text-white transition-all">
                  Cancelar
                </button>
                <button onClick={handleSaveAumento} disabled={savingAumento}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium transition-all flex items-center justify-center gap-2">
                  <TrendingUp className="w-4 h-4"/> {savingAumento ? "Guardando..." : "Aplicar aumento"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ MODAL: Historial de sueldos ═════════════════════════════════════ */}
      <AnimatePresence>
        {historialEmp && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-xs bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 rounded-full px-3 py-1 font-medium inline-flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5"/> Historial de sueldos
                  </span>
                  <p className="text-white font-semibold mt-2">{historialEmp.nombre} {historialEmp.apellido}</p>
                </div>
                <button onClick={() => setHistorialEmp(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5"/>
                </button>
              </div>

              {loadingHistorial ? (
                <div className="text-center py-8 text-slate-500">Cargando...</div>
              ) : historialData ? (
                <>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center mb-4">
                    <p className="text-slate-400 text-xs mb-1">Sueldo actual</p>
                    <p className="font-heading text-2xl font-bold text-emerald-300">
                      {fmt(historialData.sueldo_actual, historialData.moneda)}
                    </p>
                    {historialData.fecha_ingreso && (
                      <p className="text-slate-500 text-xs mt-1">Ingreso: {historialData.fecha_ingreso}</p>
                    )}
                  </div>

                  {(historialData.historial || []).length === 0 ? (
                    <div className="text-center py-6 bg-arandu-dark/60 border border-white/5 rounded-xl">
                      <Clock className="w-10 h-10 text-slate-700 mx-auto mb-2"/>
                      <p className="text-slate-400 text-sm">Sin cambios registrados todavía</p>
                      <p className="text-slate-600 text-xs mt-1">El sueldo nunca fue modificado desde su carga inicial.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {[...historialData.historial].reverse().map((h, i) => {
                        const subio = h.sueldo_nuevo > h.sueldo_anterior;
                        const diff = h.sueldo_nuevo - h.sueldo_anterior;
                        const pct = h.sueldo_anterior > 0 ? (diff / h.sueldo_anterior * 100) : 0;
                        return (
                          <div key={i} className={`border rounded-xl p-3 ${subio ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`}>
                            <div className="flex justify-between items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-slate-300 text-xs">{h.fecha}</p>
                                <p className="text-white text-sm font-medium mt-1">
                                  {fmt(h.sueldo_anterior, h.moneda || "PYG")} → <span className={subio ? "text-emerald-300" : "text-amber-300"}>{fmt(h.sueldo_nuevo, h.moneda || "PYG")}</span>
                                </p>
                                {h.motivo && <p className="text-slate-500 text-xs mt-1">{h.motivo}</p>}
                                {h.usuario_nombre && <p className="text-slate-600 text-[10px] mt-1">por {h.usuario_nombre}</p>}
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${subio ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                                {subio ? "+" : ""}{fmt(diff, h.moneda || "PYG")} ({pct >= 0 ? "+" : ""}{pct.toFixed(1)}%)
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">No se pudo cargar el historial</div>
              )}

              <button onClick={() => setHistorialEmp(null)}
                className="w-full mt-4 py-2.5 rounded-lg bg-arandu-dark border border-white/10 text-slate-400 text-sm hover:text-white transition-all">
                Cerrar
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selector rápido de variante al escanear un código de barras
          compartido por más de una variante activa (mismo talle/color
          agrupados bajo un único código de barras a nivel de producto). */}
      {varianteCompraScanPending && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Elegí la variante</h3>
                <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">
                  {varianteCompraScanPending.prod.nombre}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setVarianteCompraScanPending(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto p-2">
              {varianteCompraScanPending.variantesActivas.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => handleVarianteCompraScanSelect(v)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left transition-colors mb-1 last:mb-0 hover:bg-orange-50 border border-transparent hover:border-orange-200"
                >
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {v.atributos_texto || "Variante"}
                  </span>
                  <span className="text-xs font-semibold whitespace-nowrap text-slate-500">
                    Stock: {v.stock_actual ?? 0}
                  </span>
                </button>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <p className="text-[11px] text-gray-400">
                Se agregará 1 unidad de la variante elegida a la compra.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EgresosPage;

// ═══════════════════════════════════════════════════════════════
// NotaCompraDocModal — vista visual de nota de crédito de compra (estilo factura)
// ═══════════════════════════════════════════════════════════════
function NotaCompraDocModal({ nota: n, activeEmpresaPropia = null, onClose }) {
  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const accentColor = n.logo_tipo === "jar" ? "#dc2626" : n.logo_tipo === "arandu" ? "#2563eb" : "#7c3aed";
  const emisorNombre = n.emisor_razon_social || (n.logo_tipo === "jar" ? "JAR Informática" : n.logo_tipo === "arandu" ? "Arandu Informática" : "AranduJAR Informática");
  const emisorRuc    = n.emisor_ruc       || activeEmpresaPropia?.ruc       || "";
  const emisorDir    = n.emisor_direccion || activeEmpresaPropia?.direccion  || "";
  const emisorTel    = n.emisor_telefono  || activeEmpresaPropia?.telefono   || "";
  const emisorEmail  = n.emisor_email     || activeEmpresaPropia?.email      || "";
  const emisorLogo   = n.emisor_logo_url  || activeEmpresaPropia?.logo_url   || null;

  const fmtAmt = (v) => {
    const m = n.moneda || "PYG";
    if (v == null) return "—";
    if (m === "USD") return `USD ${Number(v).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `₲ ${Math.round(v).toLocaleString("es-PY")}`;
  };

  const motivoLabel = n.tipo_motivo === "devolucion" ? "Devolución" : "Descuento";
  const tipoCobroLabel = n.tipo_cobro === "saldo_favor" ? "Saldo a favor con el proveedor"
    : n.tipo_cobro === "reembolso" ? "El proveedor nos reembolsa"
    : "Ajuste en plan de cuentas";

  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) { alert("Permita ventanas emergentes para imprimir."); return; }
    const docLogo = svgDocumentHeaderLogoHtml(n.logo_tipo, emisorLogo);

    const itemRows = (n.items_devueltos || []).length > 0
      ? (n.items_devueltos || []).map((c, i) => {
          const sub = (c.precio_unitario || 0) * (c.cantidad || 1);
          return `<tr style="background:${i%2===0?"#fff":"#fafafa"}">
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">${c.descripcion || ""}${c.variante_texto ? `<div style="font-size:9px;color:#7c3aed;margin-top:2px">Variante: ${c.variante_texto}</div>` : ""}</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:center;font-size:12px">${c.cantidad || 1}</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${fmtAmt(c.precio_unitario || 0)}</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px;font-weight:600">${fmtAmt(sub)}</td>
          </tr>`;
        }).join("")
      : `<tr><td colspan="4" style="padding:10px 8px;font-size:12px;border:1px solid #d1d5db;color:#6b7280">
          ${motivoLabel}${n.motivo_detalle ? ` — ${n.motivo_detalle}` : ""}
         </td></tr>`;

    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>NC Compra ${n.numero}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111827;padding:20px 24px;max-width:860px;margin:0 auto;background:#fff;font-size:12px}
        @media print{body{padding:10px 14px}@page{margin:0.8cm}}
        .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid ${accentColor};padding-bottom:12px;margin-bottom:10px;gap:16px}
        .company-info{font-size:10.5px;color:#475569;line-height:1.55;margin-top:6px}
        .doc-box{border:2px solid #374151;border-radius:6px;padding:8px 12px;text-align:center;min-width:200px}
        .client-section{border:1px solid #d1d5db;border-radius:4px;padding:8px 10px;margin-bottom:10px;font-size:11.5px}
        .cl-row{display:flex;gap:0;margin-bottom:3px}
        .cl-label{color:#6b7280;font-size:10px;font-weight:700;text-transform:uppercase;width:130px;flex-shrink:0}
        .cl-val{color:#111827;font-weight:600;border-bottom:1px dotted #d1d5db;flex:1}
        table.items{width:100%;border-collapse:collapse;font-size:11.5px;margin-bottom:0}
        table.items thead tr{background:${accentColor};color:#fff}
        table.items thead th{padding:6px 8px;font-size:10px;font-weight:700;text-transform:uppercase;border:1px solid rgba(255,255,255,0.15)}
        .total-row{background:${accentColor}12;font-weight:800;font-size:13px}
        .footer-note{margin-top:14px;font-size:9.5px;color:#94a3b8;text-align:center;border-top:1px dashed #e2e8f0;padding-top:8px}
      </style>
    </head><body>
      <div class="header">
        <div>
          ${docLogo}
          <div class="company-info">
            <div style="font-weight:700;color:#334155">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>
            <div>${emisorDir}</div>
            <div>${emisorTel ? `Tel: ${emisorTel}` : ""}${emisorTel && emisorEmail ? " &nbsp;|&nbsp; " : ""}${emisorEmail || ""}</div>
          </div>
        </div>
        <div class="doc-box">
          <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#374151">NOTA DE CRÉDITO</div>
          <div style="font-size:10px;color:#6b7280;margin-top:1px">COMPRA</div>
          <div style="font-size:20px;font-weight:900;color:${accentColor};letter-spacing:0.5px;margin-top:4px">${n.numero}</div>
          <div style="font-size:10px;color:#6b7280;margin-top:2px">Fecha: ${n.fecha || "—"}</div>
        </div>
      </div>

      <div class="client-section">
        <div class="cl-row"><span class="cl-label">Proveedor</span><span class="cl-val">${n.proveedor_nombre || "—"}</span></div>
        <div class="cl-row"><span class="cl-label">Compra vinculada</span><span class="cl-val" style="font-family:monospace">${n.compra_numero_factura || (n.compra_id ? "Sin nro. de factura" : "Sin vincular")}</span></div>
        <div class="cl-row"><span class="cl-label">Tipo</span><span class="cl-val">${motivoLabel}${n.motivo_detalle ? ` — ${n.motivo_detalle}` : ""}</span></div>
        <div class="cl-row"><span class="cl-label">Gestión crédito</span><span class="cl-val">${tipoCobroLabel}</span></div>
        ${n.cuenta_nombre ? `<div class="cl-row"><span class="cl-label">Cuenta</span><span class="cl-val">🏦 ${n.cuenta_nombre}</span></div>` : ""}
      </div>

      <table class="items">
        <thead><tr>
          <th style="text-align:left;width:50%">Descripción</th>
          <th style="text-align:center;width:12%">Cant.</th>
          <th style="text-align:right;width:19%">Precio unit.</th>
          <th style="text-align:right;width:19%">Monto</th>
        </tr></thead>
        <tbody>${itemRows}</tbody>
        <tfoot>
          <tr class="total-row">
            <td colspan="2" style="border:1px solid #d1d5db;padding:6px 8px;font-size:10px;color:#6b7280">
              ${n.notas ? `Notas: ${n.notas}` : ""}
              ${n.moneda !== "PYG" && n.tipo_cambio ? `Tipo de cambio: ₲ ${Number(n.tipo_cambio).toLocaleString("es-PY")} / ${n.moneda}` : ""}
            </td>
            <td style="border:1px solid #d1d5db;padding:6px 8px;text-align:right;font-size:12px">TOTAL CRÉDITO</td>
            <td style="border:1px solid #d1d5db;padding:6px 8px;text-align:right;font-size:14px;color:${accentColor}">${fmtAmt(n.monto)}</td>
          </tr>
        </tfoot>
      </table>

      <div class="footer-note">
        ${n.logo_tipo?.toUpperCase()} &nbsp;·&nbsp; Impreso el ${new Date().toLocaleDateString("es-PY")}
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()}>

        {/* Toolbar sticky */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-2">
          <span className="text-gray-600 font-medium text-sm">Nota de Crédito — Compra</span>
          <div className="flex gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-all">
              <Printer className="w-3.5 h-3.5" /> Imprimir
            </button>
            <button onClick={onClose}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-lg transition-all">
              <X className="w-3.5 h-3.5" /> Cerrar
            </button>
          </div>
        </div>

        {/* Cabecera */}
        <div className="relative p-6 border-b border-gray-200 overflow-hidden">
          <div className="flex justify-between items-start">
            <div className="text-sm text-gray-500">
              <p className="font-semibold text-gray-700">{emisorNombre}{emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</p>
              {emisorDir && <p>{emisorDir}</p>}
              {emisorTel && <p>Tel: {emisorTel}</p>}
              {emisorEmail && <p>{emisorEmail}</p>}
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-gray-800">NOTA DE CRÉDITO</h1>
              <p className="text-sm font-medium text-gray-500 -mt-0.5">COMPRA</p>
              <p className="text-xl font-semibold mt-1" style={{ color: accentColor }}>{n.numero}</p>
              <p className="text-gray-500 text-sm mt-1">Fecha: <strong className="text-gray-700">{n.fecha || "—"}</strong></p>
            </div>
          </div>
        </div>

        {/* Proveedor */}
        <div className="p-6 border-b bg-gray-50">
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider text-gray-400">PROVEEDOR</p>
          <p className="text-xl font-semibold text-gray-800">{n.proveedor_nombre || "—"}</p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-400 text-xs uppercase">Compra vinculada</span>
              <p className="font-mono font-semibold text-gray-700">{n.compra_numero_factura || (n.compra_id ? "Sin nro." : "Sin vincular")}</p></div>
            <div><span className="text-gray-400 text-xs uppercase">Tipo de crédito</span>
              <p className="font-semibold text-gray-700">{tipoCobroLabel}</p></div>
          </div>
        </div>

        {/* Items / motivo */}
        <div className="p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 p-2 text-left text-gray-700 font-bold text-sm">Descripción / Motivo</th>
                <th className="border border-gray-300 p-2 text-center text-gray-700 font-bold text-sm w-20">Cant.</th>
                <th className="border border-gray-300 p-2 text-right text-gray-700 font-bold text-sm w-36">Precio unit.</th>
                <th className="border border-gray-300 p-2 text-right text-gray-700 font-bold text-sm w-40">Monto</th>
              </tr>
            </thead>
            <tbody>
              {(n.items_devueltos || []).length > 0 ? (
                (n.items_devueltos || []).map((c, idx) => (
                  <tr key={idx} className="bg-white">
                    <td className="border border-gray-300 p-2 text-gray-800 text-sm">
                      {c.descripcion || ""}
                      {c.variante_texto && <p className="text-violet-600 text-xs mt-1">Variante: {c.variante_texto}</p>}
                    </td>
                    <td className="border border-gray-300 p-2 text-center text-gray-800 text-sm">{c.cantidad || 1}</td>
                    <td className="border border-gray-300 p-2 text-right text-gray-800 text-sm">{fmtAmt(c.precio_unitario || 0)}</td>
                    <td className="border border-gray-300 p-2 text-right text-gray-800 text-sm font-semibold">{fmtAmt((c.precio_unitario||0)*(c.cantidad||1))}</td>
                  </tr>
                ))
              ) : (
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3 text-gray-700" colSpan={4}>
                    {motivoLabel}{n.motivo_detalle ? ` — ${n.motivo_detalle}` : ""}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="border border-gray-300 p-2 text-right text-sm font-bold text-gray-600">TOTAL CRÉDITO</td>
                <td className="border border-gray-300 p-2 text-right text-base font-black" style={{ color: accentColor }}>{fmtAmt(n.monto)}</td>
              </tr>
            </tfoot>
          </table>
          {n.notas && <p className="text-xs text-gray-400 mt-3">Notas: {n.notas}</p>}
          {n.moneda !== "PYG" && n.tipo_cambio && (
            <p className="text-xs text-gray-400 mt-1">Tipo de cambio: ₲ {Number(n.tipo_cambio).toLocaleString("es-PY")} / {n.moneda}</p>
          )}
        </div>
      </div>
    </div>
  );
}
